import { useState, useMemo, useEffect, useRef, useCallback, Fragment, createContext, useContext } from "react";

const ROUTES = ["Eye Drop", "Drinking Water", "Spray", "Injection (SC)", "Injection (IM)", "Wing Web", "Intranasal", "Beak Dip", "Oral / Gavage", "Nasal Drop", "Coarse Spray", "Foot Stab", "Feed Mix", "Neck", "Other"];

function generateId() { return Date.now() + Math.random(); }
function addDays(date, days) { const d = new Date(date); d.setDate(d.getDate() + days); return d; }
function formatDate(date) {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date + "T00:00:00") : date;
  // DD/MM/YYYY everywhere in the app.
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
function daysBetween(a, b) { return Math.floor((b - a) / (1000 * 60 * 60 * 24)); }
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function dayDiff(dueDateObj, actualISO) {
  if (!actualISO) return null;
  const actual = new Date(actualISO + "T00:00:00");
  return Math.round((actual - dueDateObj) / 86400000);
}
// Format a unix-ms timestamp as IST date + time (e.g. "11/06/2026, 2:30 PM IST").
function fmtIST(ms) {
  try {
    const d = new Date(ms);
    // Get IST date parts as DD/MM/YYYY
    const datePart = d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric",
    }); // en-GB 2-digit/2-digit gives DD/MM/YYYY
    const timePart = d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit", hour12: true,
    });
    return `${datePart}, ${timePart} IST`;
  } catch {
    return new Date(ms).toISOString();
  }
}

// ─── ICS Calendar Export ──────────────────────────────────────────────────────
function buildICS(farm, flock, vaccines, platform = "apple") {
  const sorted = [...vaccines].filter(v => v.name && v.day).sort((a,b) => a.day - b.day);
  if (!flock.hatchDate || sorted.length === 0) return null;
  const hatch = new Date(flock.hatchDate + "T00:00:00");
  const stamp = new Date().toISOString().replace(/[-:.]/g,"").slice(0,15)+"Z";

  // Current balance (birds alive) at export time — so you can order vaccine amounts.
  const balance = flockBalance(flock);
  const balLabel = balance > 0 ? `${balance.toLocaleString()} birds` : "balance not set";

  // Format a JS Date as a UTC iCal timestamp: YYYYMMDDTHHMMSSZ
  const utcStamp = (d) =>
    d.getUTCFullYear().toString().padStart(4,"0") +
    (d.getUTCMonth()+1).toString().padStart(2,"0") +
    d.getUTCDate().toString().padStart(2,"0") + "T" +
    d.getUTCHours().toString().padStart(2,"0") +
    d.getUTCMinutes().toString().padStart(2,"0") + "00Z";

  // IST is UTC+5:30. Build a UTC Date for a given IST wall-clock time on a day.
  const istToUtc = (baseDate, istHour, istMin) => {
    const y = baseDate.getFullYear(), m = baseDate.getMonth(), d = baseDate.getDate();
    return new Date(Date.UTC(y, m, d, istHour - 5, istMin - 30, 0));
  };

  const blocks = [];

  sorted.forEach(v => {
    const due = addDays(hatch, v.day - 1);     // day of vaccination
    const twoBefore = addDays(due, -2);        // two days before
    const desc = `Farm: ${farm.name}\\nFlock: ${flock.name}\\nBalance: ${balLabel}\\nRoute: ${v.route}\\nDay of age: ${v.day}${v.notes?"\\nNotes: "+v.notes:""}`;

    if (platform === "google") {
      // ── GOOGLE: two SEPARATE timed events (Google ignores imported alarms,
      // but always shows the events themselves, each with a default reminder). ──

      // Event A: "order/prep" at 2pm IST, two days before
      const aStart = istToUtc(twoBefore, 14, 0);
      const aEnd   = istToUtc(twoBefore, 14, 30);
      blocks.push([
        "BEGIN:VEVENT",
        `UID:prep-${flock.id}-${v.id}@poultry`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${utcStamp(aStart)}`,
        `DTEND:${utcStamp(aEnd)}`,
        `SUMMARY:🛒 Prep/Order: ${v.name} — ${flock.name} (${balLabel})`,
        `DESCRIPTION:Vaccination in 2 days. Order vaccine for ${balLabel}.\\n${desc}`,
        // A relative alarm too (Google honors these on some events); 0 min before
        "BEGIN:VALARM","TRIGGER:PT0S","ACTION:DISPLAY",
        `DESCRIPTION:Prep ${v.name} for ${flock.name} — order for ${balLabel}`,
        "END:VALARM",
        "END:VEVENT",
      ].join("\r\n"));

      // Event B: "vaccinate" at 8am IST, on the day
      const bStart = istToUtc(due, 8, 0);
      const bEnd   = istToUtc(due, 8, 30);
      blocks.push([
        "BEGIN:VEVENT",
        `UID:vacc-${flock.id}-${v.id}@poultry`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${utcStamp(bStart)}`,
        `DTEND:${utcStamp(bEnd)}`,
        `SUMMARY:💉 Vaccinate: ${v.name} — ${flock.name} (${balLabel})`,
        `DESCRIPTION:Vaccination day. Route: ${v.route}.\\n${desc}`,
        "BEGIN:VALARM","TRIGGER:PT0S","ACTION:DISPLAY",
        `DESCRIPTION:Today — ${v.name} for ${flock.name}. Route: ${v.route}. ${balLabel}`,
        "END:VALARM",
        "END:VEVENT",
      ].join("\r\n"));

    } else {
      // ── APPLE: one timed event with two absolute-time alarms (works on iOS). ──
      const dtstart = istToUtc(due, 8, 0);
      const dtend   = istToUtc(due, 8, 30);
      const alarm1 = istToUtc(twoBefore, 14, 0);  // 2pm IST, 2 days before
      const alarm2 = istToUtc(due, 8, 0);         // 8am IST, on the day
      blocks.push([
        "BEGIN:VEVENT",
        `UID:vacc-${flock.id}-${v.id}@poultry`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${utcStamp(dtstart)}`,
        `DTEND:${utcStamp(dtend)}`,
        `SUMMARY:💉 ${v.name} — ${flock.name} (${balLabel})`,
        `DESCRIPTION:${desc}`,
        "BEGIN:VALARM",
        `TRIGGER;VALUE=DATE-TIME:${utcStamp(alarm1)}`,
        "ACTION:DISPLAY",
        `DESCRIPTION:In 2 days — ${v.name} for ${flock.name} (${farm.name}). Order for ${balLabel}.`,
        "END:VALARM",
        "BEGIN:VALARM",
        `TRIGGER;VALUE=DATE-TIME:${utcStamp(alarm2)}`,
        "ACTION:DISPLAY",
        `DESCRIPTION:Today — ${v.name} for ${flock.name} (${farm.name}). Route: ${v.route}. ${balLabel}.`,
        "END:VALARM",
        "END:VEVENT",
      ].join("\r\n"));
    }
  });

  const calName = `${flock.name} Vaccinations${platform==="google"?" (Android)":""}`;
  return ["BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//Poultry Manager//EN","CALSCALE:GREGORIAN","METHOD:PUBLISH","X-WR-CALNAME:"+calName, ...blocks,"END:VCALENDAR"].join("\r\n");
}

function downloadICS(content, filename) {
  const blob = new Blob([content], { type:"text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Browser Notifications ────────────────────────────────────────────────────
function getNotifPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function requestNotifPermission() {
  if (!("Notification" in window)) return "unsupported";
  try { return await Notification.requestPermission(); }
  catch { return "denied"; }
}

function fireBrowserNotifs(farms) {
  if (!("Notification" in window) || Notification.permission !== "granted") return 0;
  const today = new Date(); today.setHours(0,0,0,0);
  const twoDaysOut = addDays(today, 2);
  let fired = 0;
  farms.forEach(farm => {
    farm.flocks.forEach(flock => {
      if (!flock.hatchDate) return;
      if (!flockActiveNow(flock)) return;
      const hatch = new Date(flock.hatchDate+"T00:00:00");
      const bal = flockBalance(flock);
      const balLabel = bal > 0 ? `${bal.toLocaleString()} birds` : "balance not set";
      farm.vaccines.forEach(v => {
        if (!v.name || !v.day || flock.done[v.id]) return;
        const due = addDays(hatch, v.day - 1);
        if (isSameDay(due, today)) {
          new Notification("💉 Vaccination Due TODAY", { body:`${v.name} → ${flock.name} (${farm.name})\nRoute: ${v.route}\n🐔 ${balLabel}`, tag:`today-${flock.id}-${v.id}` });
          fired++;
        } else if (isSameDay(due, twoDaysOut)) {
          new Notification("📅 Vaccination in 2 Days", { body:`${v.name} → ${flock.name} (${farm.name})\nRoute: ${v.route}\n🐔 Order for ${balLabel}`, tag:`soon-${flock.id}-${v.id}` });
          fired++;
        }
      });
    });
  });
  return fired;
}

const FARM_COLORS = [
  { accent: "#7dd45a", dark: "#172614", border: "#2d5a22", pill: "#1e4a14", stripe: "#4a9a30" },
  { accent: "#5ab8e0", dark: "#101e2e", border: "#1e4a6a", pill: "#102040", stripe: "#2a7aaa" },
  { accent: "#e0b84a", dark: "#261e08", border: "#6a5010", pill: "#3a2e08", stripe: "#b08820" },
  { accent: "#d06abf", dark: "#261020", border: "#6a2058", pill: "#3a0830", stripe: "#9a3a8a" },
  { accent: "#a8d44a", dark: "#1c2610", border: "#4a6018", pill: "#303a08", stripe: "#708a20" },
  { accent: "#e07a5a", dark: "#261410", border: "#6a2818", pill: "#3a1008", stripe: "#aa4828" },
];

// flock.done = { [vaccineId]: "YYYY-MM-DD" }  ← actual date given, or undefined if not done

// ─── Notification Panel ───────────────────────────────────────────────────────
function NotificationPanel({ farms, isSuper, onClose }) {
  const [permission, setPermission] = useState(getNotifPermission);
  const [justFired, setJustFired] = useState(null);

  async function handleEnable() {
    const p = await requestNotifPermission();
    setPermission(p);
    if (p === "granted") { const n = fireBrowserNotifs(farms); setJustFired(n); }
  }

  function handleCheckNow() {
    const n = fireBrowserNotifs(farms);
    setJustFired(n);
  }

  const permLabel = { granted:"✅ Enabled", denied:"❌ Blocked", default:"Not set", unsupported:"Not supported" }[permission] || "Unknown";
  const permColor = { granted:"#5aaa5a", denied:"#c05050", default:"#a08020", unsupported:"#606060" }[permission] || "#606060";

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:100, backdropFilter:"blur(2px)", animation:"fadeIn 0.3s ease" }} />
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, width:"min(360px,100vw)",
        background:"#0a1208", borderLeft:"2px solid #2d5a27",
        zIndex:101, overflowY:"auto", display:"flex", flexDirection:"column",
        animation:"slideInRight 0.38s cubic-bezier(0.16,1,0.3,1)",
        boxShadow:"-8px 0 40px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#0f1a10,#081008)", borderBottom:"1px solid #2d5a27", padding:"20px 18px 16px", position:"sticky", top:0, zIndex:1 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:22 }}>🔔</span>
                <span style={{ fontSize:17, fontWeight:"bold", color:"#a8d48a" }}>Notifications</span>
              </div>
              <div style={{ fontSize:12, color:"#6b9a5a" }}>Reminders for upcoming vaccinations</div>
            </div>
            <button onClick={onClose} style={{ background:"#1a2a1a", border:"1px solid #3a5a3a", borderRadius:8, color:"#7aaa6a", fontSize:18, cursor:"pointer", padding:"4px 10px", lineHeight:1, fontFamily:"inherit" }}>×</button>
          </div>
        </div>

        <div style={{ padding:16, flex:1 }}>

          {/* ── Section 1: Browser Notifications ── */}
          <div style={{ background:"#0e1e14", border:"1px solid #2a5a3a", borderRadius:12, padding:16, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ fontSize:18 }}>🖥️</span>
              <span style={{ fontSize:14, fontWeight:"bold", color:"#a8d48a" }}>Browser Notifications</span>
              <span style={{ marginLeft:"auto", fontSize:11, color:permColor, background:permColor+"22", border:`1px solid ${permColor}55`, borderRadius:20, padding:"2px 8px" }}>{permLabel}</span>
            </div>
            <div style={{ fontSize:12, color:"#6a8a6a", marginBottom:12, lineHeight:1.6 }}>
              Shows a pop-up alert for vaccinations due <strong style={{ color:"#a8d48a" }}>today</strong> or <strong style={{ color:"#a8d48a" }}>in 2 days</strong>, including each flock's bird count so you can order vaccine <em>while this app is open</em>.
            </div>

            {permission === "unsupported" && (
              <div style={{ background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, padding:10, fontSize:12, color:"#6a6a6a" }}>Your browser doesn't support notifications.</div>
            )}
            {permission === "denied" && (
              <div style={{ background:"#2a1010", border:"1px solid #5a2020", borderRadius:8, padding:10, fontSize:12, color:"#c06060" }}>
                Notifications are blocked. Please enable them in your browser's site settings, then reload the page.
              </div>
            )}
            {(permission === "default") && (
              <button onClick={handleEnable} style={{ width:"100%", padding:"10px", background:"linear-gradient(135deg,#1a4a2a,#0e3018)", border:"1px solid #3a8a4a", borderRadius:8, color:"#7ada8a", fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:"bold" }}>
                🔔 Enable Browser Notifications
              </button>
            )}
            {permission === "granted" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <button onClick={handleCheckNow} style={{ width:"100%", padding:"10px", background:"#1a3a2a", border:"1px solid #3a7a4a", borderRadius:8, color:"#7ada8a", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                  🔍 Check & Fire Now (Today + In 2 Days)
                </button>
                {justFired !== null && (
                  <div style={{ background:justFired>0?"#0e2a0e":"#1a1a1a", border:`1px solid ${justFired>0?"#3a7a3a":"#3a3a3a"}`, borderRadius:8, padding:"8px 12px", fontSize:12, color:justFired>0?"#7ada7a":"#7a7a7a", textAlign:"center" }}>
                    {justFired > 0 ? `✅ Sent ${justFired} notification${justFired>1?"s":""}` : "✅ No vaccinations due today or in 2 days"}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Section 2: Calendar Export (super-admin only — vaccination feature) ── */}
          {isSuper && (
          <div style={{ background:"#0e1828", border:"1px solid #2a4a6a", borderRadius:12, padding:16, marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <span style={{ fontSize:18 }}>📲</span>
              <span style={{ fontSize:14, fontWeight:"bold", color:"#8ac8f0" }}>Export to Phone Calendar</span>
              <span style={{ marginLeft:"auto", fontSize:10, background:"#1a4a2a", border:"1px solid #3a7a4a", color:"#7ada5a", borderRadius:20, padding:"2px 8px" }}>RECOMMENDED</span>
            </div>
            <div style={{ fontSize:12, color:"#5a7a9a", marginBottom:14, lineHeight:1.7 }}>
              Download a <strong style={{ color:"#7aaad0" }}>.ics calendar file</strong> for each flock — pick <strong style={{ color:"#7aaad0" }}>iPhone</strong> or <strong style={{ color:"#7aaad0" }}>Android</strong>. Your phone sends native reminders <strong style={{ color:"#7aaad0" }}>2pm two days before</strong> and <strong style={{ color:"#7aaad0" }}>8am on the day</strong> of each vaccination (with the flock's bird count for ordering), even when this app is closed. Android uses two separate calendar entries since Google Calendar handles reminders differently.
            </div>
            <div style={{ background:"#0a1220", border:"1px dashed #2a4a6a", borderRadius:8, padding:"10px 12px", marginBottom:14, fontSize:11, color:"#4a6a8a" }}>
              💡 <strong style={{ color:"#6a9abf" }}>How to import:</strong> Download → open the file on your phone → tap "Add to Calendar" → Done. Works on iPhone, Android, and PC.
            </div>

            {farms.length === 0 && (
              <div style={{ textAlign:"center", padding:"20px 0", color:"#3a5a7a", fontSize:13 }}>No farms registered yet.</div>
            )}

            {farms.map(farm => {
              const color = FARM_COLORS[farm.colorIdx];
              const flocks = farm.flocks.filter(fl => fl.hatchDate && flockActiveNow(fl));
              if (flocks.length === 0) return null;
              return (
                <div key={farm.id} style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                    <span style={{ fontSize:12 }}>🏡</span>
                    <span style={{ fontSize:13, fontWeight:"bold", color:color.accent }}>{farm.name}</span>
                    {farm.location && <span style={{ fontSize:10, color:color.accent+"88" }}>· {farm.location}</span>}
                  </div>
                  {flocks.map(flock => {
                    const vCount = farm.vaccines.filter(v=>v.name&&v.day).length;
                    return (
                      <div key={flock.id} style={{ background:"#0a1620", border:"1px solid #1e3a5a", borderRadius:8, padding:"9px 12px", marginBottom:6 }}>
                        <div style={{ marginBottom:vCount>0?8:0 }}>
                          <div style={{ fontSize:13, fontWeight:"bold", color:"#a8c8e8", marginBottom:1 }}>🐥 {flock.name}</div>
                          <div style={{ fontSize:11, color:"#4a6a8a" }}>
                            {vCount} vaccine{vCount!==1?"s":""} · hatched {formatDate(flock.hatchDate)}
                          </div>
                        </div>
                        {vCount > 0 && (
                          <div style={{ display:"flex", gap:6 }}>
                            <button
                              onClick={() => {
                                const ics = buildICS(farm, flock, farm.vaccines, "apple");
                                if (ics) downloadICS(ics, `${farm.name}-${flock.name}-iphone.ics`.replace(/\s+/g,"-"));
                              }}
                              style={{ flex:1, padding:"8px 10px", background:"linear-gradient(135deg,#1a3a5a,#0e2a4a)", border:"1px solid #3a6a9a", borderRadius:7, color:"#7ab0e0", fontSize:11, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                              📱 iPhone
                            </button>
                            <button
                              onClick={() => {
                                const ics = buildICS(farm, flock, farm.vaccines, "google");
                                if (ics) downloadICS(ics, `${farm.name}-${flock.name}-android.ics`.replace(/\s+/g,"-"));
                              }}
                              style={{ flex:1, padding:"8px 10px", background:"linear-gradient(135deg,#1a3a2a,#0e2a1a)", border:"1px solid #3a8a5a", borderRadius:7, color:"#7ada9a", fontSize:11, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                              🤖 Android
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          )}
        </div>

        <div style={{ padding:"14px 18px", borderTop:"1px solid #1e3a1e", background:"#080e08", fontSize:11, color:"#3a5a3a", textAlign:"center" }}>
          {isSuper ? "Calendar export gives the most reliable device notifications" : "Enable browser notifications for vaccination & stock alerts"}
        </div>
      </div>
    </>
  );
}

// ─── Tomorrow Panel ───────────────────────────────────────────────────────────
function TomorrowPanel({ farms, onClose, onMarkDone, onOpenVaccine }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = addDays(today, 1);

  // Catch everything that needs attention: anything due on/before tomorrow that
  // isn't marked done. Each item is tagged overdue / today / tomorrow.
  const groups = useMemo(() => {
    const result = [];
    farms.forEach(farm => {
      const items = [];
      farm.flocks.forEach(flock => {
        if (!flock.hatchDate) return;
        if (!flockActiveNow(flock)) return;
        const hatch = new Date(flock.hatchDate); hatch.setHours(0,0,0,0);
        farm.vaccines.forEach(v => {
          if (!v.name || !v.day) return;
          if (flock.done[v.id]) return; // already done — skip
          const due = addDays(hatch, v.day - 1);
          if (due.getTime() > tomorrow.getTime()) return; // only up to tomorrow
          let cat;
          if (due.getTime() < today.getTime()) cat = "overdue";
          else if (due.getTime() === today.getTime()) cat = "today";
          else cat = "tomorrow";
          const daysLate = Math.round((today.getTime() - due.getTime()) / 86400000);
          items.push({ flock, vaccine: v, due, cat, daysLate });
        });
      });
      if (items.length > 0) {
        const order = { overdue:0, today:1, tomorrow:2 };
        items.sort((a,b) => order[a.cat]-order[b.cat] || b.daysLate-a.daysLate);
        result.push({ farm, items });
      }
    });
    return result;
  }, [farms]);

  const totalCount = groups.reduce((s, g) => s + g.items.length, 0);
  const overdueCount = groups.reduce((s,g) => s + g.items.filter(i=>i.cat==="overdue").length, 0);
  const todayCount   = groups.reduce((s,g) => s + g.items.filter(i=>i.cat==="today").length, 0);
  const tomorrowCount= groups.reduce((s,g) => s + g.items.filter(i=>i.cat==="tomorrow").length, 0);

  const catStyle = {
    overdue:  { label:"OVERDUE", chip:"#e05a3a", chipBg:"#2a1008", border:"#5a2010", rowBg:"#1a0e08" },
    today:    { label:"TODAY",   chip:"#e0b040", chipBg:"#2a2008", border:"#5a4010", rowBg:"#181408" },
    tomorrow: { label:"TOMORROW",chip:"#7ad44a", chipBg:"#142a10", border:"#2a5a1e", rowBg:"#0e180e" },
  };

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:100, backdropFilter:"blur(2px)", animation:"fadeIn 0.3s ease" }} />
      <div style={{
        position:"fixed", top:0, right:0, bottom:0, width:"min(360px,100vw)",
        background:"#0a1208", borderLeft:"2px solid #2d5a27",
        zIndex:101, overflowY:"auto", display:"flex", flexDirection:"column",
        animation:"slideInRight 0.38s cubic-bezier(0.16,1,0.3,1)",
        boxShadow:"-8px 0 40px rgba(0,0,0,0.6)",
      }}>
        <div style={{ background:"linear-gradient(135deg,#0f2010,#081508)", borderBottom:"1px solid #2d5a27", padding:"20px 18px 16px", position:"sticky", top:0, zIndex:1 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:22 }}>🔔</span>
                <span style={{ fontSize:17, fontWeight:"bold", color:"#a8d48a" }}>Vaccine Alerts</span>
              </div>
              <div style={{ fontSize:12, color:"#6b9a5a" }}>Overdue, today &amp; tomorrow</div>
            </div>
            <button onClick={onClose} style={{ background:"#1a2a1a", border:"1px solid #3a5a3a", borderRadius:8, color:"#7aaa6a", fontSize:18, cursor:"pointer", padding:"4px 10px", lineHeight:1, fontFamily:"inherit" }}>×</button>
          </div>
          {totalCount > 0 ? (
            <div style={{ marginTop:14, display:"flex", gap:6 }}>
              <div style={{ flex:1, background:overdueCount>0?"#2a1008":"#141014", border:`1px solid ${overdueCount>0?"#c0402a":"#2a2a2a"}`, borderRadius:9, padding:"8px 6px", textAlign:"center" }}>
                <div style={{ fontSize:20, fontWeight:"bold", color:overdueCount>0?"#e05a3a":"#4a4a4a" }}>{overdueCount}</div>
                <div style={{ fontSize:8, color:overdueCount>0?"#c0604a":"#4a4a4a", letterSpacing:0.5 }}>OVERDUE</div>
              </div>
              <div style={{ flex:1, background:todayCount>0?"#2a2008":"#141014", border:`1px solid ${todayCount>0?"#e0a040":"#2a2a2a"}`, borderRadius:9, padding:"8px 6px", textAlign:"center" }}>
                <div style={{ fontSize:20, fontWeight:"bold", color:todayCount>0?"#e0b040":"#4a4a4a" }}>{todayCount}</div>
                <div style={{ fontSize:8, color:todayCount>0?"#c0a040":"#4a4a4a", letterSpacing:0.5 }}>TODAY</div>
              </div>
              <div style={{ flex:1, background:tomorrowCount>0?"#142a10":"#141014", border:`1px solid ${tomorrowCount>0?"#6abf3a":"#2a2a2a"}`, borderRadius:9, padding:"8px 6px", textAlign:"center" }}>
                <div style={{ fontSize:20, fontWeight:"bold", color:tomorrowCount>0?"#7ad44a":"#4a4a4a" }}>{tomorrowCount}</div>
                <div style={{ fontSize:8, color:tomorrowCount>0?"#6abf3a":"#4a4a4a", letterSpacing:0.5 }}>TOMORROW</div>
              </div>
            </div>
          ) : (
            <div style={{ marginTop:14, background:"#141e14", border:"1px solid #2a4a2a", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:26 }}>✅</span>
              <span style={{ fontSize:13, color:"#6abf3a" }}>Nothing pending 🎉</span>
            </div>
          )}
        </div>

        <div style={{ padding:16, flex:1 }}>
          {groups.length===0 && (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"#3a5a3a" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
              <div style={{ fontSize:14 }}>All caught up!</div>
              <div style={{ fontSize:11, color:"#3a5a3a", marginTop:6 }}>No overdue, today, or tomorrow vaccines.</div>
            </div>
          )}
          {groups.map(({ farm, items }) => {
            const color = FARM_COLORS[farm.colorIdx];
            const byFlock = {};
            items.forEach(item => {
              if (!byFlock[item.flock.id]) byFlock[item.flock.id] = { flock:item.flock, items:[] };
              byFlock[item.flock.id].items.push(item);
            });
            return (
              <div key={farm.id} style={{ background:`linear-gradient(135deg,${color.dark}88,#0a120888)`, border:`1px solid ${color.border}`, borderRadius:12, marginBottom:14, overflow:"hidden" }}>
                <div style={{ background:color.pill+"cc", borderBottom:`1px solid ${color.border}`, padding:"10px 14px", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:14 }}>🏡</span>
                  <span style={{ fontSize:14, fontWeight:"bold", color:color.accent }}>{farm.name}</span>
                  {farm.location && <span style={{ fontSize:11, color:color.accent+"88" }}>· {farm.location}</span>}
                  <span style={{ marginLeft:"auto", background:color.accent+"22", border:`1px solid ${color.accent}55`, color:color.accent, fontSize:11, borderRadius:20, padding:"1px 8px" }}>{items.length} due</span>
                </div>
                {Object.values(byFlock).map(({ flock, items: flockItems }) => (
                  <div key={flock.id} style={{ padding:"12px 14px", borderBottom:`1px solid ${color.border}33` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                      <span style={{ fontSize:13 }}>🐥</span>
                      <span style={{ fontSize:13, fontWeight:"bold", color:"#c8e8b0" }}>{flock.name}</span>
                      <span style={{ fontSize:10, color:"#5a7a5a" }}>· hatched {formatDate(flock.hatchDate)}</span>
                    </div>
                    {flockItems.map(({ vaccine: v, due, cat, daysLate }) => {
                      const cs = catStyle[cat];
                      return (
                        <div key={v.id} style={{ display:"flex", alignItems:"center", gap:8, background:cs.rowBg, border:`1px solid ${cs.border}`, borderRadius:8, padding:"9px 10px", marginBottom:6 }}>
                          <div
                            onClick={() => onOpenVaccine && onOpenVaccine(farm.id, flock.id, v.id)}
                            style={{ flex:1, cursor: onOpenVaccine ? "pointer" : "default" }}
                            title="Open full vaccine view (partial dosing, change date, etc.)">
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2, flexWrap:"wrap" }}>
                              <span style={{ fontSize:13, fontWeight:"bold", color:"#d8e8c8" }}>💉 {v.name}</span>
                              <span style={{ fontSize:8, fontWeight:700, color:cs.chip, background:cs.chipBg, border:`1px solid ${cs.chip}66`, borderRadius:20, padding:"1px 6px", letterSpacing:0.5 }}>
                                {cat==="overdue" ? `${daysLate}d LATE` : cs.label}
                              </span>
                              {onOpenVaccine && <span style={{ fontSize:9, color:"#5a8abf" }}>›</span>}
                            </div>
                            <div style={{ fontSize:11, color:"#6a8a5a" }}>Day {v.day} · {v.route} · Due {formatDate(due)}</div>
                            {onOpenVaccine && <div style={{ fontSize:9, color:"#4a7a9a", marginTop:2 }}>tap to open · partial dose & more</div>}
                          </div>
                          {onMarkDone && <button onClick={() => onMarkDone(farm.id, flock.id, v.id, todayISO())} style={{
                            padding:"5px 10px", background:"#1e4a1e", border:"1px solid #4a9a4a",
                            borderRadius:7, color:"#7ada5a", fontSize:11, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
                          }}>✓ Done</button>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div style={{ padding:"14px 18px", borderTop:"1px solid #1e3a1e", background:"#080e08", fontSize:11, color:"#3a5a3a", textAlign:"center" }}>
          Marking Done here records today's date &amp; updates the flock tracker
        </div>
      </div>
    </>
  );
}

// ─── Vaccination Row in Flock ─────────────────────────────────────────────────
function VaccineRow({ vaccine, perms: pm = {}, dueDate, actualDate, doses = [], doseTotal = 0, target = 0, onMarkDone, onMarkUndone, onChangeDate, onAddDose, onEditDose, onDeleteDose }) {
  const today = new Date(); today.setHours(0,0,0,0);
  const isDone = !!actualDate;
  const diff = isDone ? dayDiff(dueDate, actualDate) : null;
  const hasDoses = doses.length > 0;
  const isPartial = hasDoses && !isDone; // started but not complete
  const remaining = Math.max(0, target - doseTotal);

  // Partial-dose panel state
  const [showDoses, setShowDoses] = useState(false);
  const [addingDose, setAddingDose] = useState(false);
  const [dDate, setDDate] = useState(todayISO());
  const [dCount, setDCount] = useState("");
  const [dNotes, setDNotes] = useState("");
  const [editDoseId, setEditDoseId] = useState(null);

  function submitDose() {
    if (!dCount || Number(dCount) <= 0) return;
    if (editDoseId) {
      onEditDose(editDoseId, { date: dDate, count: Number(dCount), notes: dNotes });
    } else {
      onAddDose({ date: dDate, count: Number(dCount), notes: dNotes });
    }
    setAddingDose(false); setEditDoseId(null); setDCount(""); setDNotes(""); setDDate(todayISO());
  }
  function startEditDose(dose) {
    setEditDoseId(dose.id); setDDate(dose.date); setDCount(String(dose.count||"")); setDNotes(dose.notes||""); setAddingDose(true); setShowDoses(true);
  }

  // Status when not done
  const diffDays = daysBetween(today, dueDate);
  let status = diffDays > 0 ? "upcoming" : diffDays === 0 ? "today" : "overdue";
  if (isDone) status = "done";
  if (isPartial) status = "partial";

  const statusStyle = {
    done:     { badge:"#3a8a3a", text:"✓ Given" },
    partial:  { badge:"#c08020", text:`◐ ${doseTotal}/${target}` },
    today:    { badge:"#c09a20", text:"⚡ Today!" },
    upcoming: { badge:"#3a6a9a", text:`In ${diffDays}d` },
    overdue:  { badge:"#c04040", text:`${Math.abs(diffDays)}d overdue` },
  }[status];

  return (
    <div style={{
      background: isDone ? "#0a1608" : isPartial ? "#16100400" : status==="overdue" ? "#160808" : status==="today" ? "#161200" : "#0c1608",
      border: `1px solid ${isDone ? "#1e4a1e" : isPartial ? "#5a4010" : status==="overdue" ? "#4a1010" : status==="today" ? "#4a3800" : "#1a2e18"}`,
      borderRadius: 10, padding: "8px 10px", marginBottom: 6,
      opacity: isDone ? 0.82 : 1, transition: "all 0.3s ease", fontFamily:"system-ui,sans-serif",
    }}>
      {/* Line 1: badge + name + day/route, all on one row */}
      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
        <span style={{ background:statusStyle.badge+"22", border:`1px solid ${statusStyle.badge}`, color:statusStyle.badge, fontSize:10, borderRadius:20, padding:"2px 8px", fontWeight:"bold", whiteSpace:"nowrap" }}>
          {statusStyle.text}
        </span>
        <span style={{ fontSize:14, fontWeight:"bold", color:"#c8e8b0", textDecoration:isDone?"line-through":"none", textDecorationColor:"#4a8a4a", flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          💉 {vaccine.name}
        </span>
        <span style={{ fontSize:10, color:"#5a7a5a", whiteSpace:"nowrap" }}>Day {vaccine.day} · {vaccine.route}</span>
      </div>

      {/* Optional tags (illness/company/dosage) — only if present, compact */}
      {(vaccine.illness || vaccine.company || vaccine.dosageNote) && (
        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:5 }}>
          {vaccine.illness && <span style={{ fontSize:9, color:"#9a8ac0", background:"#1a1630", border:"1px solid #3a2a5a", borderRadius:20, padding:"1px 7px" }}>🦠 {vaccine.illness}</span>}
          {vaccine.company && <span style={{ fontSize:9, color:"#7a9ac0", background:"#101e2e", border:"1px solid #2a4a6a", borderRadius:20, padding:"1px 7px" }}>🏭 {vaccine.company}</span>}
          {vaccine.dosageNote && <span style={{ fontSize:9, color:"#c0a87a", background:"#1e1810", border:"1px solid #4a3a1a", borderRadius:20, padding:"1px 7px" }}>💧 {vaccine.dosageNote}</span>}
        </div>
      )}

      {/* Line 2: due date + given date inline */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:6, fontSize:11 }}>
        <span style={{ color:"#5a7a5a", whiteSpace:"nowrap" }}>📅 Due <strong style={{ color:"#7aaa6a" }}>{formatDate(dueDate)}</strong></span>
        <span style={{ color:"#3a5a3a" }}>·</span>
        <div style={{ display:"flex", alignItems:"center", gap:4, flex:1, minWidth:0 }}>
          <span style={{ color: isDone ? "#5aaa5a" : "#4a6a4a", whiteSpace:"nowrap" }}>{isDone ? "✅ Given" : "📝 Given"}</span>
          <input
            type="date"
            value={actualDate || ""}
            disabled={isDone ? !pm.canEdit : !pm.canAdd}
            onChange={e => {
              if (e.target.value) onChangeDate(e.target.value);
              else if (pm.canUndo) onMarkUndone();
            }}
            onClick={e => { const el = e.currentTarget; if (el.showPicker) { try { el.showPicker(); } catch { /* native focus handles it */ } } }}
            style={{
              background:"transparent", border:"none", outline:"none",
              color: isDone ? "#7ada5a" : "#4a6a4a", fontSize:11,
              fontFamily:"inherit", fontWeight: isDone ? "bold" : "normal",
              cursor:(isDone?pm.canEdit:pm.canAdd)?"pointer":"default", colorScheme:"dark",
              padding:0, opacity:(isDone?pm.canEdit:pm.canAdd)?1:0.7, minWidth:0, flex:1,
            }}
          />
        </div>
        {isDone && diff !== null && diff !== 0 && (
          <span style={{ fontSize:10, color: diff > 0 ? "#e0a040" : "#40a0e0", whiteSpace:"nowrap" }}>
            {diff > 0 ? `⚠️ ${diff}d late` : `ℹ️ ${Math.abs(diff)}d early`}
          </span>
        )}
        {isDone && diff === 0 && <span style={{ fontSize:10, color:"#5aaa5a", whiteSpace:"nowrap" }}>✓ on time</span>}
      </div>

      {/* Dose progress (thin, only when there are doses) */}
      {target > 0 && hasDoses && (
        <div style={{ marginTop:6 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#8a8a5a", marginBottom:3 }}>
            <span>{doseTotal.toLocaleString()} / {target.toLocaleString()} birds</span>
            {remaining > 0 ? <span style={{ color:"#c08020" }}>{remaining.toLocaleString()} left</span> : <span style={{ color:"#5aaa5a" }}>Complete ✓</span>}
          </div>
          <div style={{ height:5, background:"#0a1208", borderRadius:4, overflow:"hidden", border:"1px solid #1e2e1a" }}>
            <div style={{ height:"100%", width:`${Math.min(100,(doseTotal/target)*100)}%`, background:remaining===0?"linear-gradient(90deg,#2a8a2a,#4aba4a)":"linear-gradient(90deg,#8a6020,#c08020)", transition:"width 0.4s ease" }} />
          </div>
        </div>
      )}

      {/* Per-day dose breakdown (collapsible) */}
      {hasDoses && (
        <div style={{ marginTop:6 }}>
          <button onClick={() => setShowDoses(s=>!s)} style={{ background:"none", border:"none", color:"#6a8a5a", fontSize:10, cursor:"pointer", padding:"1px 0", fontFamily:"inherit" }}>
            {showDoses ? "▼" : "▶"} {doses.length} dose{doses.length>1?"s":""}{doses.length>1?" · multiple days":""}
          </button>
          {showDoses && (
            <div style={{ marginTop:4 }}>
              {[...doses].sort((a,b)=>a.date.localeCompare(b.date)).map(d => (
                <div key={d.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 8px", background:"#0a1408", border:"1px solid #1a2a14", borderRadius:6, marginBottom:4 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, color:"#a8c890" }}>
                      <strong style={{ color:"#c8e8b0" }}>{(d.count||0).toLocaleString()}</strong> birds · {formatDate(d.date)}
                    </div>
                    {d.notes && <div style={{ fontSize:9, color:"#6a7a5a", fontStyle:"italic" }}>{d.notes}</div>}
                  </div>
                  {pm.canEdit && <button onClick={() => startEditDose(d)} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:5, color:"#7dd45a", fontSize:10, padding:"2px 7px", cursor:"pointer", fontFamily:"inherit" }}>Edit</button>}
                  {pm.canDelete && <button onClick={() => { if(window.confirm("Delete this dose?")) onDeleteDose(d.id); }} style={{ background:"none", border:"none", color:"#5a3030", fontSize:13, cursor:"pointer", padding:"0 2px" }}>×</button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit partial dose form */}
      {addingDose && (
        <div className="anim-roll" style={{ background:"#161004", border:"1px solid #4a3a10", borderRadius:8, padding:10, marginTop:6 }}>
          <div style={{ fontSize:10, color:"#c0a030", fontWeight:"bold", marginBottom:6, letterSpacing:0.5 }}>{editDoseId ? "EDIT DOSE" : "ADD DOSE (partial)"}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:7, marginBottom:7 }}>
            <div>
              <label style={{ fontSize:9, color:"#8a7030", display:"block", marginBottom:2 }}>DATE{dDate ? ` · ${formatDate(dDate)}` : ""}</label>
              <div style={{ position:"relative" }}>
                <div style={{ ...iS, colorScheme:"dark", padding:"8px 10px", display:"flex", alignItems:"center", justifyContent:"space-between", color: dDate ? "#c8f0a8" : "#6a8a5a" }}>
                  <span>{dDate ? formatDate(dDate) : "tap to pick a date"}</span>
                  <span style={{ fontSize:13, opacity:0.85 }}>📅</span>
                </div>
                <input type="date" value={dDate}
                  onChange={e=>setDDate(e.target.value)}
                  onClick={e=>{ const el=e.currentTarget; if (el.showPicker) { try { el.showPicker(); } catch { /* older browsers: native focus handles it */ } } }}
                  style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize:9, color:"#8a7030", display:"block", marginBottom:2 }}>BIRDS DONE</label>
              <input type="number" min="1" value={dCount} onChange={e=>setDCount(e.target.value)} placeholder={remaining>0?`e.g. ${remaining}`:"e.g. 5000"} style={{ ...iS, padding:"8px 10px" }} />
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <label style={{ fontSize:9, color:"#8a7030", display:"block", marginBottom:2 }}>NOTE (optional)</label>
            <input type="text" value={dNotes} onChange={e=>setDNotes(e.target.value)} placeholder="e.g. morning batch, house 2" style={{ ...iS, padding:"8px 10px" }} />
          </div>
          <div style={{ display:"flex", gap:7 }}>
            <button onClick={submitDose} style={{ flex:1, padding:"8px", background:"linear-gradient(135deg,#3a2e00,#2a2000)", border:"1px solid #7a6020", borderRadius:7, color:"#e0c040", fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>{editDoseId ? "Save Dose" : "Add Dose"}</button>
            <button onClick={() => { setAddingDose(false); setEditDoseId(null); setDCount(""); setDNotes(""); }} style={{ padding:"8px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:7, color:"#7a7a7a", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {((!isDone && pm.canAdd && !addingDose) || (!isDone && !pm.canAdd && !hasDoses) || (isDone && pm.canUndo)) && (
        <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginTop:7 }}>
          {!isDone && pm.canAdd && !addingDose && (
            <>
              <button onClick={() => onMarkDone(todayISO())} style={{
                flex:1, minWidth:120, padding:"7px 0", background:"#1e4a1e", border:"1px solid #4a9a4a",
                borderRadius:7, color:"#7ada5a", fontSize:12, cursor:"pointer", fontFamily:"inherit",
              }}>✓ {hasDoses ? "Mark All Done" : "Mark Done"}</button>
              <button onClick={() => { setAddingDose(true); setEditDoseId(null); setDDate(todayISO()); }} style={{
                flex:1, minWidth:100, padding:"7px 0", background:"#2a2008", border:"1px solid #6a5018",
                borderRadius:7, color:"#d4a840", fontSize:12, cursor:"pointer", fontFamily:"inherit",
              }}>◐ Partial Dose</button>
            </>
          )}
          {!isDone && !pm.canAdd && !hasDoses && (
            <div style={{ flex:1, padding:"6px 0", textAlign:"center", color:"#4a6a4a", fontSize:11, fontStyle:"italic" }}>Not yet given</div>
          )}
          {isDone && pm.canUndo && (
            <button onClick={onMarkUndone} style={{
              flex:1, padding:"7px 0", background:"#1a1a1a", border:"1px solid #3a3a3a",
              borderRadius:7, color:"#7a7a7a", fontSize:12, cursor:"pointer", fontFamily:"inherit",
            }}>↩ Undo</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
// API base — the backend server (see /server folder).
// Defaults to a RELATIVE "/api" path, which means the app calls the API on
// whatever domain/host is serving it (localhost, your domain via Cloudflare, etc).
// This avoids hardcoded-URL mismatches. Override with REACT_APP_API_URL only if
// the API lives on a DIFFERENT origin than the app.
const API = (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) || "/api";

// Auth token is held ONLY in memory (module-level), never in localStorage/sessionStorage.
// This is deliberate: a page reload wipes it, forcing a fresh login every time.
let AUTH_TOKEN = null;
function setAuthToken(t) { AUTH_TOKEN = t; }

// Tiny fetch helper. Attaches the bearer token; throws a typed error on failure
// so callers can detect 401 (session gone) vs 403 (no permission) vs other.
// ─── Resilient API layer ───────────────────────────────────────────────────────
// Two protections that apply to EVERY request in the app:
//  - Write queue: all mutating requests run ONE AT A TIME, so rapid edits
//    like tabbing through trays or marking many vaccines never flood the
//    server or collide on SQLite writes. Reads still run immediately.
//  - Auto-retry: transient failures like rate-limit, 5xx, network blips, or a
//    locked database are retried a few times with short backoff before we ever
//    surface an error. Real errors like 400/401/403/404 fail fast.
let __writeChain = Promise.resolve();
const __sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function __doFetch(path, options) {
  const res = await fetch(`${API}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    },
    ...options,
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    let retryAfter = null;
    try { const body = await res.json(); if (body.error) msg = body.error; if (body.retryAfterSeconds != null) retryAfter = Number(body.retryAfterSeconds); } catch {}
    const err = new Error(msg);
    err.status = res.status;
    err.retryAfter = retryAfter;
    throw err;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

function __isTransient(err) {
  // No status = network error (fetch threw). Retry those.
  if (err.status == null) return true;
  // Rate-limited or server-side transient conditions.
  if (err.status === 429 || err.status === 503 || err.status === 502 || err.status >= 500) return true;
  // Any other 4xx is a real answer from the server ("not found", "no permission",
  // "not enough stock"…). Retrying it only adds delay and load.
  if (err.status >= 400 && err.status < 500 && err.status !== 408) return false;
  // SQLite lock or any DB error surfaced as a generic message.
  if (/database|locked|busy|timeout|not found|SQLITE|ECONNREFUSED|Failed to fetch|network/i.test(err.message || "")) return true;
  return false;
}

async function __fetchWithRetry(path, options, attempts = 7) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await __doFetch(path, options);
    } catch (e) {
      lastErr = e;
      if (!__isTransient(e) || i === attempts - 1) throw e;
      if (e.status === 429) {
        // The server asked us to slow down. Hammering it again would only use up
        // more of the allowance, so wait as asked — and give up after two tries.
        // A long wait (e.g. a locked login) is shown to the user instead.
        const wait = (e.retryAfter != null ? e.retryAfter : 5) * 1000;
        if (i >= 2 || wait > 15000) throw e;
        await __sleep(wait + Math.floor(Math.random() * 400));
        continue;
      }
      // Backoff: 150ms → 300ms → 600ms → 1200ms → 2000ms → 3000ms (+ jitter).
      const delay = [150, 300, 600, 1200, 2000, 3000][i] + Math.floor(Math.random() * 150);
      await __sleep(delay);
    }
  }
  throw lastErr;
}

async function apiCall(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const isWrite = method !== "GET";
  if (!isWrite) {
    // Reads run immediately (with retry).
    return __fetchWithRetry(path, options);
  }
  // Writes are serialized: each waits for the previous to finish, so we never
  // fire a burst of concurrent writes. Chain errors don't break the chain.
  const run = __writeChain.then(
    () => __fetchWithRetry(path, options),
    () => __fetchWithRetry(path, options) // even if a prior write rejected, proceed
  );
  // Keep the chain alive regardless of this call's success/failure.
  __writeChain = run.then(() => {}, () => {});
  return run;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, serverError, clearError }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    if (!username.trim() || !password) { setErr("Enter username and password"); return; }
    setBusy(true); setErr(null); clearError();
    try { await onLogin(username.trim(), password); }
    catch (e) { setErr(e.message || "Login failed"); setBusy(false); }
  }

  return (
    <div style={{ minHeight:"100vh", background:"#080f07", fontFamily:"system-ui,sans-serif", color:"#ddeedd", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 18px" }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .login-in { transition: filter 0.15s, transform 0.1s; }
        .login-in:active { transform: scale(0.98); }
      `}</style>
      <div style={{ width:"100%", maxWidth:360, animation:"fadeUp 0.4s ease" }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:56, marginBottom:8 }}>🐣</div>
          <div style={{ fontSize:26, fontWeight:800, color:"#c8f0a8", letterSpacing:0.5 }}>Poultry Manager</div>
          <div style={{ fontSize:13, color:"#4a7040", marginTop:4, letterSpacing:1 }}>Sign in to continue</div>
        </div>

        {/* Card */}
        <div style={{ background:"#0c180a", border:"1px solid #1e3a1a", borderRadius:20, padding:"24px 22px", boxShadow:"0 12px 40px rgba(0,0,0,0.5)" }}>
          <label style={{ fontSize:11, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:6 }}>Username</label>
          <input autoFocus type="text" value={username} onChange={e=>setUsername(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{ background:"#0a1408", border:"1px solid #223a1e", borderRadius:12, color:"#c8f0a8", padding:"12px 14px", fontSize:15, width:"100%", boxSizing:"border-box", outline:"none", marginBottom:16, fontFamily:"inherit" }} />

          <label style={{ fontSize:11, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:6 }}>Password</label>
          <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&submit()}
            style={{ background:"#0a1408", border:"1px solid #223a1e", borderRadius:12, color:"#c8f0a8", padding:"12px 14px", fontSize:15, width:"100%", boxSizing:"border-box", outline:"none", marginBottom:20, fontFamily:"inherit" }} />

          {(err || serverError) && (
            <div style={{ background:"#1e0808", border:"1px solid #5a1818", borderRadius:10, padding:"10px 12px", marginBottom:16, fontSize:13, color:"#e09090", lineHeight:1.6 }}>
              ⚠️ {err || serverError}
              {/* Show helpful context for rate-limit errors */}
              {(err||"").includes("minute") && (
                <div style={{ marginTop:6, fontSize:11, color:"#c07070" }}>
                  Your IP has been temporarily blocked due to too many failed attempts. Wait for the block to expire, then try again. Contact your admin if you need immediate access.
                </div>
              )}
              {(err||"").includes("attempt") && !(err||"").includes("minute") && (
                <div style={{ marginTop:4, fontSize:11, color:"#c07070" }}>
                  Check your username and password carefully.
                </div>
              )}
            </div>
          )}

          <button className="login-in" onClick={submit} disabled={busy}
            style={{ width:"100%", padding:"13px", background:busy?"#1a3010":"linear-gradient(135deg,#2a6a1a,#1a4a0a)", border:"none", borderRadius:12, color:"#c8f0b0", fontSize:15, fontWeight:700, cursor:busy?"default":"pointer", fontFamily:"inherit" }}>
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </div>

        <div style={{ textAlign:"center", marginTop:20, fontSize:11, color:"#3a5a30", lineHeight:1.7 }}>
          For security, you'll be signed out on reload<br/>or after 10 minutes of inactivity.
        </div>
      </div>
    </div>
  );
}

// ─── Settings Panel (Super Admin only) ────────────────────────────────────────
function SettingsPanel({ currentUser, onClose, onError }) {
  const [tab, setTab] = useState("users"); // "users" | "audit" | "backups"
  const [users, setUsers] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null); // user object being edited

  // Audit log state
  const [audit, setAudit] = useState(null);
  const [auditErr, setAuditErr] = useState(null);
  const [auditFilter, setAuditFilter] = useState("all"); // all | login | add | edit | delete

  // Add-form state
  const [nu, setNu] = useState({ username:"", fullName:"", role:"viewer", password:"", modules:[] });
  const [formErr, setFormErr] = useState(null);

  async function load() {
    try { setUsers(await apiCall("/users")); setLoadErr(null); }
    catch (e) { setLoadErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function loadAudit() {
    try { setAudit(await apiCall("/audit?limit=300")); setAuditErr(null); }
    catch (e) { setAuditErr(e.message); }
  }
  useEffect(() => { if (tab === "audit" && audit === null) loadAudit(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backup status
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupMsg, setBackupMsg] = useState(null);
  async function loadBackupStatus() {
    try { setBackupStatus(await apiCall("/backup-status")); }
    catch (e) { /* silently ignore */ }
  }
  async function triggerBackup() {
    try {
      await apiCall("/backup-now", { method: "POST" });
      setBackupMsg("Backup started — refreshing status in 5 seconds…");
      setTimeout(() => { loadBackupStatus(); setBackupMsg(null); }, 5000);
    } catch (e) { setBackupMsg("Error: " + e.message); }
  }
  // Security status
  const [secStatus, setSecStatus] = useState(null);
  async function loadSecStatus() {
    try { setSecStatus(await apiCall("/security-status")); } catch {}
  }
  async function unblockIp(ip) {
    try {
      await apiCall("/unblock-ip", { method:"POST", body: JSON.stringify({ ip }) });
      loadSecStatus();
    } catch (e) { alert("Unblock failed: " + e.message); }
  }
  useEffect(() => { if (tab === "backups") { loadBackupStatus(); loadSecStatus(); } }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function createUser() {
    if (!nu.username.trim()) { setFormErr("Username required"); return; }
    if (nu.password.length < 4) { setFormErr("Password must be at least 4 characters"); return; }
    if (nu.role === "manager" && nu.modules.length === 0) { setFormErr("Pick at least one module for a Manager"); return; }
    try {
      await apiCall("/users", { method:"POST", body: JSON.stringify({ ...nu, modules: nu.role === "manager" ? nu.modules : [] }) });
      setNu({ username:"", fullName:"", role:"viewer", password:"", modules:[] });
      setShowAdd(false); setFormErr(null); load();
    } catch (e) { setFormErr(e.message); }
  }

  async function saveEdit() {
    try {
      if (editUser.role === "manager" && (editUser.modules || []).length === 0) { setFormErr("Pick at least one module for a Manager"); return; }
      const body = { fullName: editUser.fullName, role: editUser.role, modules: editUser.role === "manager" ? (editUser.modules || []) : [] };
      if (editUser.newPassword) body.password = editUser.newPassword;
      await apiCall(`/users/${editUser.id}`, { method:"PUT", body: JSON.stringify(body) });
      setEditUser(null); load();
    } catch (e) { setFormErr(e.message); }
  }

  async function removeUser(u) {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try { await apiCall(`/users/${u.id}`, { method:"DELETE" }); load(); }
    catch (e) { onError(e.message); }
  }

  const roleColor = { super_admin:"#e0b84a", manager:"#5ab8e0", viewer:"#7a9a6a" };
  const roleIcon  = (r) => r === "super_admin" ? "👑" : r === "manager" ? "🛠️" : "👁️";
  // Role picker + (for Managers) the module roles, shared by the add and edit forms.
  const roleFields = (val, set) => (
    <>
      <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Role</label>
      <select value={val.role} onChange={e=>set({ ...val, role:e.target.value })} style={{ ...fieldStyle, marginBottom:10, cursor:"pointer", colorScheme:"dark" }}>
        <option value="viewer">Viewer — sees everything, changes nothing</option>
        <option value="manager">Manager — full control of chosen modules</option>
        <option value="super_admin">Super Admin — full access</option>
      </select>
      {val.role === "manager" && (
        <div style={{ background:"#0a1408", border:"1px solid #223a1e", borderRadius:10, padding:"10px 12px", marginBottom:10 }}>
          <div style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", marginBottom:7 }}>Module roles · all farms</div>
          {APP_MODULES.map(m => {
            const on = (val.modules || []).includes(m.key);
            return (
              <label key={m.key} style={{ display:"flex", alignItems:"center", gap:9, padding:"6px 0", cursor:"pointer", fontSize:13, color: on ? "#c8f0a8" : "#6a8a60" }}>
                <input type="checkbox" checked={on} style={{ width:16, height:16, accentColor:"#5ab8e0", cursor:"pointer" }}
                  onChange={() => set({ ...val, modules: on ? (val.modules || []).filter(k => k !== m.key) : [...(val.modules || []), m.key] })} />
                <span>{m.icon} {m.label}</span>
              </label>
            );
          })}
          <div style={{ fontSize:10, color:"#4a6a40", marginTop:5, lineHeight:1.5 }}>They can see and change these modules only; everything else is hidden from them.</div>
        </div>
      )}
    </>
  );
  const fieldStyle = { background:"#0a1408", border:"1px solid #223a1e", borderRadius:10, color:"#c8f0a8", padding:"10px 12px", fontSize:14, width:"100%", boxSizing:"border-box", outline:"none", fontFamily:"inherit" };

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:150, backdropFilter:"blur(3px)", animation:"fadeIn 0.3s ease" }} />
      <div style={{ position:"fixed", top:0, right:0, bottom:0, width:"min(440px,100vw)", background:"#080f07", borderLeft:"1px solid #1e3a1a", zIndex:151, overflowY:"auto", animation:"slideInRight 0.38s cubic-bezier(0.16,1,0.3,1)", boxShadow:"-8px 0 40px rgba(0,0,0,0.6)" }}>
        {/* Header */}
        <div style={{ background:"#0c1a0a", borderBottom:"1px solid #1e3a1a", padding:"18px 18px 16px", position:"sticky", top:0, zIndex:1, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, color:"#c8f0a8" }}>⚙️ Settings</div>
            <div style={{ fontSize:11, color:"#4a7040", letterSpacing:1, textTransform:"uppercase", marginTop:2 }}>{tab==="users"?"User Management":"Activity Log"}</div>
          </div>
          <button onClick={onClose} style={{ background:"#1a2e18", border:"1px solid #2d5020", borderRadius:10, color:"#7dd45a", fontSize:18, cursor:"pointer", padding:"4px 12px", lineHeight:1, fontFamily:"inherit" }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display:"flex", gap:4, padding:"8px 12px", background:"#0a1408", borderBottom:"1px solid #1a2e16", position:"sticky", top:54, zIndex:1 }}>
          {[["users","👥 Users"],["audit","📜 Activity Log"],["backups","💾 Backups"]].map(([k,l]) => (
            <button key={k} onClick={()=>setTab(k)} style={{
              flex:1, padding:"9px 0", background:tab===k?"#1a3a14":"transparent",
              border:"none", borderRadius:10, color:tab===k?"#a8f080":"#3a6030",
              fontSize:13, fontWeight:tab===k?700:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s",
            }}>{l}</button>
          ))}
        </div>

        {tab==="users" && (
        <div style={{ padding:18 }}>
          {/* Section: Users */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:13, color:"#5a8048", letterSpacing:1, textTransform:"uppercase" }}>Users {users?`(${users.length})`:""}</div>
            <button onClick={()=>{setShowAdd(true);setEditUser(null);setFormErr(null);}} style={{ background:"linear-gradient(135deg,#2a5a1a,#1a4010)", border:"none", borderRadius:20, color:"#c8f0a8", fontSize:12, padding:"8px 14px", cursor:"pointer", fontWeight:600, fontFamily:"inherit" }}>+ Add User</button>
          </div>

          {loadErr && <div style={{ background:"#1e0808", border:"1px solid #5a1818", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:13, color:"#e09090" }}>⚠️ {loadErr}</div>}

          {/* Add user form */}
          {showAdd && (
            <div className="anim-roll" style={{ background:"#0e1e0c", border:"1px solid #2a4a20", borderRadius:16, padding:16, marginBottom:14, animation:"fadeUp 0.35s ease" }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#a8d48a", marginBottom:12 }}>New User</div>
              <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Username</label>
              <input type="text" value={nu.username} onChange={e=>setNu({...nu,username:e.target.value})} style={{ ...fieldStyle, marginBottom:10 }} />
              <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Full Name (optional)</label>
              <input type="text" value={nu.fullName} onChange={e=>setNu({...nu,fullName:e.target.value})} style={{ ...fieldStyle, marginBottom:10 }} />
              {roleFields(nu, setNu)}
              <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Password</label>
              <input type="text" value={nu.password} onChange={e=>setNu({...nu,password:e.target.value})} placeholder="min 4 characters" style={{ ...fieldStyle, marginBottom:14 }} />
              {formErr && <div style={{ fontSize:12, color:"#e09090", marginBottom:12 }}>⚠️ {formErr}</div>}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={createUser} style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,#2a6a1a,#1a4a0a)", border:"none", borderRadius:10, color:"#c8f0b0", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Create User</button>
                <button onClick={()=>{setShowAdd(false);setFormErr(null);}} style={{ padding:"11px 16px", background:"#181e16", border:"1px solid #2a3228", borderRadius:10, color:"#6a7a68", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* User list */}
          {users && users.map(u => (
            <div key={u.id} style={{ background:"#0c180a", border:"1px solid #1e3a1a", borderRadius:14, marginBottom:10, overflow:"hidden" }}>
              {editUser?.id === u.id ? (
                <div style={{ padding:16 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#a8d48a", marginBottom:12 }}>Edit: {u.username}</div>
                  <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Full Name</label>
                  <input type="text" value={editUser.fullName} onChange={e=>setEditUser({...editUser,fullName:e.target.value})} style={{ ...fieldStyle, marginBottom:10 }} />
                  {roleFields(editUser, setEditUser)}
                  <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>New Password (leave blank to keep)</label>
                  <input type="text" value={editUser.newPassword||""} onChange={e=>setEditUser({...editUser,newPassword:e.target.value})} placeholder="••••••" style={{ ...fieldStyle, marginBottom:14 }} />
                  {formErr && <div style={{ fontSize:12, color:"#e09090", marginBottom:12 }}>⚠️ {formErr}</div>}
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={saveEdit} style={{ flex:1, padding:"11px", background:"#1a4a14", border:"1px solid #3a8a2a", borderRadius:10, color:"#a8f080", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Save</button>
                    <button onClick={()=>{setEditUser(null);setFormErr(null);}} style={{ padding:"11px 16px", background:"#181e16", border:"1px solid #2a3228", borderRadius:10, color:"#6a7a68", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:42, height:42, borderRadius:12, background:roleColor[u.role]+"22", border:`1px solid ${roleColor[u.role]}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                    {roleIcon(u.role)}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:600, color:"#c8e8b0" }}>
                      {u.username}
                      {u.id===currentUser.id && <span style={{ fontSize:10, color:"#4a7040", marginLeft:6 }}>(you)</span>}
                    </div>
                    {u.fullName && <div style={{ fontSize:12, color:"#5a7a50" }}>{u.fullName}</div>}
                    <span style={{ display:"inline-block", marginTop:4, fontSize:10, color:roleColor[u.role], background:roleColor[u.role]+"18", border:`1px solid ${roleColor[u.role]}44`, borderRadius:20, padding:"2px 8px" }}>{ROLE_LABELS[u.role] || u.role}</span>
                    {u.role === "manager" && (u.modules || []).length > 0 && (
                      <div style={{ fontSize:10.5, color:"#5a8a9a", marginTop:4, lineHeight:1.5 }}>
                        {(u.modules || []).map(k => { const m = APP_MODULES.find(x => x.key === k); return m ? `${m.icon} ${m.label}` : k; }).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    <button onClick={()=>{setEditUser({id:u.id,fullName:u.fullName,role:u.role,modules:u.modules||[],newPassword:""});setShowAdd(false);setFormErr(null);}} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:8, color:"#7dd45a", fontSize:11, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                    {u.id!==currentUser.id && <button onClick={()=>removeUser(u)} style={{ background:"none", border:"none", color:"#5a2828", fontSize:14, cursor:"pointer", padding:"2px" }}>🗑</button>}
                  </div>
                </div>
              )}
            </div>
          ))}

          {!users && !loadErr && <div style={{ textAlign:"center", padding:"30px", color:"#3a5a30", fontSize:13 }}>Loading users…</div>}

          {/* Role legend */}
          <div style={{ marginTop:20, padding:16, background:"#0a140880", border:"1px solid #1a2e16", borderRadius:14 }}>
            <div style={{ fontSize:11, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>Role Permissions</div>
            <div style={{ fontSize:12, color:"#7a9a6a", lineHeight:1.8 }}>
              <div><span style={{ color:"#e0b84a" }}>👑 Super Admin</span> — full access: every module, settings and users</div>
              <div><span style={{ color:"#5ab8e0" }}>🛠️ Manager</span> — full control of the modules you tick, on every farm; other modules are hidden</div>
              <div><span style={{ color:"#7a9a6a" }}>👁️ Viewer</span> — sees every module and dashboard, changes nothing</div>
            </div>
          </div>
        </div>
        )}

        {/* ── ACTIVITY LOG TAB ── */}
        {tab==="audit" && (
        <div style={{ padding:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:13, color:"#5a8048", letterSpacing:1, textTransform:"uppercase" }}>Activity {audit?`(${audit.length})`:""}</div>
            <button onClick={loadAudit} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:20, color:"#7dd45a", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit" }}>↻ Refresh</button>
          </div>

          {/* Filter chips */}
          <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
            {[["all","All"],["login","Logins"],["add","Added"],["edit","Edited"],["delete","Deleted"]].map(([k,l]) => (
              <button key={k} onClick={()=>setAuditFilter(k)} style={{
                padding:"5px 12px", borderRadius:20, fontSize:11, cursor:"pointer", fontFamily:"inherit",
                background: auditFilter===k ? "#1a3a14" : "#0e1a0c",
                border: `1px solid ${auditFilter===k ? "#3a7a2a" : "#1e3a1a"}`,
                color: auditFilter===k ? "#a8f080" : "#5a7a4a",
              }}>{l}</button>
            ))}
          </div>

          {auditErr && <div style={{ background:"#1e0808", border:"1px solid #5a1818", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:13, color:"#e09090" }}>⚠️ {auditErr}</div>}
          {!audit && !auditErr && <div style={{ textAlign:"center", padding:"30px", color:"#3a5a30", fontSize:13 }}>Loading activity…</div>}
          {audit && audit.filter(a => auditFilter==="all" || a.action===auditFilter).length === 0 && (
            <div style={{ textAlign:"center", padding:"30px", color:"#3a5a30", fontSize:13 }}>No activity to show.</div>
          )}

          {audit && audit.filter(a => auditFilter==="all" || a.action===auditFilter).map(a => {
            const actionStyle = {
              login:  { icon:"🔑", color:"#5ab8e0", label:"LOGIN" },
              add:    { icon:"➕", color:"#6abf4b", label:"ADDED" },
              edit:   { icon:"✏️", color:"#e0b84a", label:"EDITED" },
              delete: { icon:"🗑", color:"#e07a5a", label:"DELETED" },
            }[a.action] || { icon:"•", color:"#7a9a6a", label:a.action.toUpperCase() };
            return (
              <div key={a.id} style={{ background:"#0c180a", border:"1px solid #1a2e16", borderRadius:12, padding:"11px 13px", marginBottom:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:14 }}>{actionStyle.icon}</span>
                  <span style={{ fontSize:10, fontWeight:700, color:actionStyle.color, background:actionStyle.color+"18", border:`1px solid ${actionStyle.color}44`, borderRadius:20, padding:"2px 8px", letterSpacing:0.5 }}>{actionStyle.label}</span>
                  <span style={{ fontSize:13, color:"#c8e0b8", fontWeight:600, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.entity}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:11, color:"#6a8a5a" }}>
                  <span style={{ color:"#8aa87a" }}>👤 {a.username || "—"}</span>
                  <span style={{ color:"#3a5a30" }}>·</span>
                  <span>{fmtIST(a.ts)}</span>
                </div>
                {a.details && <div style={{ fontSize:11, color:"#7a7a4a", marginTop:5, fontStyle:"italic", lineHeight:1.5, wordBreak:"break-word" }}>{a.details}</div>}
              </div>
            );
          })}

          <div style={{ marginTop:14, fontSize:10, color:"#3a5a30", textAlign:"center", lineHeight:1.6 }}>
            Showing up to the 300 most recent actions.<br/>All times shown in IST.
          </div>
        </div>
        )}

        {/* ── BACKUPS TAB ── */}
        {tab==="backups" && (
        <div style={{ padding:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:13, color:"#5a8048", letterSpacing:1, textTransform:"uppercase" }}>Backup Status</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={loadBackupStatus} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:20, color:"#7dd45a", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit" }}>↻ Refresh</button>
              <button onClick={triggerBackup} style={{ background:"linear-gradient(135deg,#1a3a2a,#0e2a1a)", border:"1px solid #3a7a4a", borderRadius:20, color:"#a8f080", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>▶ Backup Now</button>
            </div>
          </div>

          {backupMsg && <div style={{ background:"#141e10", border:"1px solid #3a5a2a", borderRadius:10, padding:"10px 12px", marginBottom:12, fontSize:12, color:"#a8d48a" }}>{backupMsg}</div>}

          {!backupStatus && <div style={{ textAlign:"center", padding:"30px", color:"#3a5a30", fontSize:13 }}>Loading backup status…</div>}

          {backupStatus && (
            <div style={{ display:"grid", gap:10 }}>
              {/* Local backup status */}
              <div style={{ background:"#0c180a", border:"1px solid #1a2e16", borderRadius:12, padding:"14px 16px" }}>
                <div style={{ fontSize:12, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>💾 Local Backup</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div>
                    <div style={{ fontSize:9, color:"#4a6a3a", marginBottom:2 }}>LAST RUN</div>
                    <div style={{ fontSize:12, color:"#a8c890" }}>{backupStatus.lastRun ? fmtIST(new Date(backupStatus.lastRun).getTime()) : "Never"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"#4a6a3a", marginBottom:2 }}>LAST SUCCESS</div>
                    <div style={{ fontSize:12, color:"#a8c890" }}>{backupStatus.lastSuccess ? fmtIST(new Date(backupStatus.lastSuccess).getTime()) : "Never"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"#4a6a3a", marginBottom:2 }}>LAST FILE</div>
                    <div style={{ fontSize:11, color:"#8aaa7a", wordBreak:"break-all" }}>{backupStatus.lastFile || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:9, color:"#4a6a3a", marginBottom:2 }}>COMPRESSED SIZE</div>
                    <div style={{ fontSize:12, color:"#a8c890" }}>{backupStatus.lastSizeKB ? `${backupStatus.lastSizeKB} KB` : "—"}</div>
                  </div>
                </div>
                {backupStatus.error && <div style={{ marginTop:10, padding:"8px 10px", background:"#1e0808", border:"1px solid #5a1818", borderRadius:8, fontSize:11, color:"#e09090" }}>⚠️ {backupStatus.error}</div>}
                {!backupStatus.error && backupStatus.lastSuccess && <div style={{ marginTop:10, fontSize:11, color:"#5a8a5a" }}>✅ Local backup is healthy</div>}
              </div>

              {/* Google Drive status */}
              <div style={{ background:"#0c180a", border:"1px solid #1a2e16", borderRadius:12, padding:"14px 16px" }}>
                <div style={{ fontSize:12, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>☁️ Google Drive</div>
                {backupStatus.driveOk === null && (
                  <div style={{ fontSize:12, color:"#8a8a5a" }}>Not yet attempted — waiting for first backup run.</div>
                )}
                {backupStatus.driveOk === true && (
                  <div style={{ fontSize:12, color:"#5a8a5a" }}>✅ Last upload successful · Files saved to "PoultryManager Backups" folder on Drive</div>
                )}
                {backupStatus.driveOk === false && (
                  <div>
                    <div style={{ fontSize:12, color:"#c08040", marginBottom:6 }}>⚠️ Drive upload not working</div>
                    <div style={{ fontSize:11, color:"#8a7040", background:"#181008", border:"1px solid #3a2a10", borderRadius:8, padding:"8px 10px", wordBreak:"break-all" }}>{backupStatus.driveError}</div>
                    <div style={{ fontSize:10, color:"#5a6a4a", marginTop:8, lineHeight:1.6 }}>Local backups are still working. Set up Drive credentials to enable cloud backup — see SETUP.md.</div>
                  </div>
                )}
              </div>

              {/* Security status */}
              <div style={{ background:"#0c180a", border:"1px solid #1a2e16", borderRadius:12, padding:"14px 16px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontSize:12, color:"#5a8048", letterSpacing:1, textTransform:"uppercase" }}>🛡️ Security</div>
                  <button onClick={() => { loadSecStatus(); }} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:20, color:"#7dd45a", fontSize:11, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit" }}>↻</button>
                </div>
                {secStatus && (
                  <div>
                    <div style={{ display:"flex", gap:16, marginBottom:10, fontSize:12, color:"#8aa87a" }}>
                      <span>Active sessions: <strong style={{ color:"#c8e0b8" }}>{secStatus.sessionCount}</strong> / {secStatus.maxSessions}</span>
                      <span>Blocked IPs: <strong style={{ color: secStatus.blocked.length>0?"#e09090":"#c8e0b8" }}>{secStatus.blocked.length}</strong></span>
                    </div>
                    {secStatus.blocked.length === 0 && (
                      <div style={{ fontSize:11, color:"#5a7a5a" }}>✅ No IPs currently blocked — no ongoing attacks detected</div>
                    )}
                    {secStatus.blocked.map(b => (
                      <div key={b.ip} style={{ display:"flex", alignItems:"center", gap:8, background:"#1e0808", border:"1px solid #4a1818", borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, color:"#e09090", fontWeight:"bold" }}>🚫 {b.ip}</div>
                          <div style={{ fontSize:10, color:"#8a5050" }}>{b.fails} failed attempts · unblocks {fmtIST(new Date(b.unblockAt).getTime())}</div>
                        </div>
                        <button onClick={() => unblockIp(b.ip)} style={{ background:"#1a2e1a", border:"1px solid #3a6a3a", borderRadius:6, color:"#7ada7a", fontSize:11, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>Unblock</button>
                      </div>
                    ))}
                    <div style={{ fontSize:10, color:"#4a6a4a", marginTop:6, lineHeight:1.5 }}>
                      Rate limit: {5} failed logins per IP per 10 min → 30-min block · Global: 100 req/min/IP
                    </div>
                  </div>
                )}
              </div>

              {/* Info box */}
              <div style={{ background:"#0a100800", border:"1px solid #1a2a16", borderRadius:10, padding:"12px 14px", fontSize:11, color:"#5a7a4a", lineHeight:1.7 }}>
                Backups run automatically every hour. Local copies are kept for 48 hours ({backupStatus.running ? <span style={{ color:"#a8d48a" }}>running now…</span> : "idle"}). To enable Google Drive, see SETUP.md → "Google Drive Backup Setup".
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </>
  );
}


// (The server enforces the real rules; this just drives what the UI shows.)
const ROLE_LABELS = { super_admin: "Super Admin", viewer: "Viewer", manager: "Manager" };
// The five modules a Manager can be given. Keys match the server's MODULES list.
const APP_MODULES = [
  { key:"vaccination", label:"Vaccination",           icon:"💉" },
  { key:"production",  label:"Production & Mortality", icon:"📊" },
  { key:"eggstock",    label:"Egg Stock",              icon:"🥚" },
  { key:"packing",     label:"Packing Egg Store",      icon:"📦" },
  { key:"feed",        label:"Feed & Medicine",        icon:"🌾" },
];
// App-wide permissions for the signed-in user.
//   Super Admin — everything.  Viewer — sees all, edits nothing.
//   Manager — sees and edits ONLY the modules they hold, on every farm.
function perms(user) {
  const role = user && user.role;
  const mods = role === "manager" ? (Array.isArray(user.modules) ? user.modules : []) : [];
  const isSuper = role === "super_admin";
  const canManage = (m) => isSuper || (role === "manager" && mods.includes(m));
  const canView   = (m) => isSuper || role === "viewer" || canManage(m);
  return {
    role, modules: mods, isSuper,
    isViewer: role === "viewer",
    canManageUsers: isSuper,
    canManageFarms: isSuper,          // add / rename / delete farms
    canView, canManage,
    anyView: (...ms) => ms.some(canView),
  };
}
// Permissions handed to ONE module. Inside a module a Manager has full control
// (add, edit, delete, undo), exactly as a Super Admin does there; a Viewer gets
// read-only. The module code itself is unchanged — it still reads canEdit etc.
function modPerms(p, m) {
  const full = p.canManage(m);
  return { isSuper: full, canEdit: full, canAdd: full, canDelete: full, canUndo: full, canManageUsers: p.canManageUsers };
}

// Tiny dependency-free SVG sparkline for the dashboard production trend.
// `data` is an array of numbers; `up` tints it green (rising) or amber (falling).
function Sparkline({ data = [], up = true, height = 34 }) {
  const W = 150, H = height, PAD = 3;
  const vals = data.length ? data : [0];
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const range = (max - min) || 1;
  const n = vals.length;
  const stepX = n > 1 ? W / (n - 1) : W;
  const pts = vals.map((v, i) => {
    const x = n > 1 ? i * stepX : W / 2;
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return [x, y];
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  const stroke = up ? "#6ec06e" : "#e0954a";
  const fill = up ? "rgba(110,192,110,0.13)" : "rgba(224,149,74,0.13)";
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display:"block", width:"100%", height:H }}>
      <path d={area} fill={fill} stroke="none" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      {last && <circle cx={last[0]} cy={last[1]} r="2.3" fill={stroke} />}
    </svg>
  );
}
// Direction of a production trend: compares the recent half-window vs the earlier
// half. Returns { dir: 1|0|-1, pct, hasData }.
function prodTrend(trend) {
  const vals = (trend || []).map(t => t.trays || 0);
  const total = vals.reduce((s, x) => s + x, 0);
  if (vals.length < 2) return { dir: 0, pct: 0, hasData: total > 0 };
  const half = Math.floor(vals.length / 2);
  const avg = (a) => (a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0);
  const oAvg = avg(vals.slice(0, half)), rAvg = avg(vals.slice(half));
  let dir = 0;
  if (rAvg > oAvg * 1.02) dir = 1;
  else if (rAvg < oAvg * 0.98) dir = -1;
  const pct = oAvg > 0 ? Math.round(((rAvg - oAvg) / oAvg) * 100) : (rAvg > 0 ? 100 : 0);
  return { dir, pct, hasData: total > 0 };
}

export default function App() {
  // ── Auth state ──
  const [currentUser, setCurrentUser] = useState(null); // { id, username, fullName, role } or null
  const p = perms(currentUser);

  const [screen, setScreen] = useState("farms");
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [activeFarmId, setActiveFarmId] = useState(null);
  const [eggSummary, setEggSummary] = useState({}); // farmId -> { closing, asOf, trend:[{date,trays}] }
  const [showAddFarm, setShowAddFarm] = useState(false);
  const [newFarmName, setNewFarmName] = useState("");
  const [newFarmLocation, setNewFarmLocation] = useState("");
  const [detailTab, setDetailTab] = useState("schedule");
  const [showTomorrow, setShowTomorrow] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingFarm, setEditingFarm] = useState(null); // farm being name/location-edited
  // When a vaccine is tapped in the alert panel, jump to that flock's vaccine view
  // and focus the specific vaccine row. { farmId, flockId, vaccineId } or null.
  const [vaccineFocus, setVaccineFocus] = useState(null);

  // Keep a live ref to farms for debounced saves (avoids stale closures)
  const farmsRef = useRef(farms);
  useEffect(() => { farmsRef.current = farms; }, [farms]);
  const vaccineSaveTimers = useRef({});

  // ── Inactivity auto-logout (10 minutes). Any interaction resets the timer. ──
  const inactivityTimer = useRef(null);
  function resetInactivityTimer() {
    if (!currentUser) return;
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      doLogout("You were logged out after 10 minutes of inactivity.");
    }, 10 * 60 * 1000);
  }
  useEffect(() => {
    if (!currentUser) return;
    const events = ["mousedown", "keydown", "touchstart", "click", "scroll"];
    const reset = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, reset));
    resetInactivityTimer();
    return () => { events.forEach(e => window.removeEventListener(e, reset)); clearTimeout(inactivityTimer.current); };
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Super-admin login alerts: poll for new logins and pop a notification. ──
  // (No live server push, so we poll ~every 30s while a super admin is logged in.
  //  The pop-up may lag the actual login by up to the poll interval.)
  const lastLoginCheck = useRef(Date.now());
  useEffect(() => {
    if (!currentUser || currentUser.role !== "super_admin") return;
    lastLoginCheck.current = Date.now();
    const poll = async () => {
      try {
        const logins = await apiCall(`/recent-logins?since=${lastLoginCheck.current}`);
        if (logins && logins.length) {
          lastLoginCheck.current = Math.max(...logins.map(l => l.ts));
          logins.forEach(l => {
            // Don't notify the super admin about their own just-now login
            if (l.username === currentUser.username && Date.now() - l.ts < 60000) return;
            if ("Notification" in window && Notification.permission === "granted") {
              new Notification("👤 User signed in", {
                body: `${l.username} (${ROLE_LABELS[l.role] || l.role}) logged in\n${fmtIST(l.ts)}`,
                tag: `login-${l.id}`,
              });
            }
          });
        }
      } catch (e) { /* ignore poll errors silently */ }
    };
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dashboard egg-stock summary (current stock + production trend per farm). ──
  // Refreshes each time the farms dashboard is shown — e.g. on login or when
  // returning from a farm after editing its egg stock. Errors are ignored (extra).
  const loadDashboardSummary = useCallback(() => {
    return apiCall(`/dashboard/egg-summary?end=${todayISO()}`)
      .then(d => { if (d && d.farms) setEggSummary(d.farms); })
      .catch(() => { /* dashboard extra — ignore */ });
  }, []);
  useEffect(() => {
    if (!currentUser || (screen !== "farms" && screen !== "farmHome")) return;
    loadDashboardSummary();
  }, [currentUser, screen, loadDashboardSummary]);

  // Mark a feed/medicine item as ordered straight from the dashboard, then refresh.
  const markItemOrdered = useCallback(async (itemId) => {
    try { await apiCall(`/feed-items/${itemId}`, { method:"PUT", body: JSON.stringify({ ordered: true }) }); await loadDashboardSummary(); }
    catch (e) { alert(e && e.message ? e.message : "Could not update"); }
  }, [loadDashboardSummary]);

  // ── Login / Logout ──
  async function doLogin(username, password) {
    const { token, user } = await apiCall("/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    setAuthToken(token);
    setCurrentUser(user);
    setScreen("farms");
    setLoading(true);
    await loadFarms();
  }
  async function doLogout(message) {
    try { await apiCall("/logout", { method: "POST" }); } catch {}
    setAuthToken(null);
    setCurrentUser(null);
    setFarms([]);
    setActiveFarmId(null);
    setScreen("farms");
    setShowSettings(false);
    clearTimeout(inactivityTimer.current);
    if (message) setServerError(message);
  }

  // ── Load everything from the database server ──
  async function loadFarms() {
    try {
      const data = await apiCall("/farms");
      setFarms(data);
      setServerError(null);
    } catch (e) {
      if (e.status === 401) { doLogout("Your session expired. Please log in again."); return; }
      setServerError("Can't reach the database server. Make sure it's running (in the server folder, run: npm start).");
    } finally {
      setLoading(false);
    }
  }

  // Auto-fire browser notifications once data has loaded & permission granted
  useEffect(() => {
    if (currentUser && !loading && getNotifPermission() === "granted") fireBrowserNotifs(farms);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFarm = farms.find(f => f.id === activeFarmId);

  // Bell badge counts everything pending: overdue + today + tomorrow (not done).
  const tomorrowCount = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = addDays(today, 1);
    let count = 0;
    farms.forEach(farm => {
      farm.flocks.forEach(flock => {
        if (!flock.hatchDate) return;
        if (!flockActiveNow(flock)) return;
        const hatch = new Date(flock.hatchDate); hatch.setHours(0,0,0,0);
        farm.vaccines.forEach(v => {
          if (!v.name || !v.day) return;
          if (flock.done[v.id]) return;
          const due = addDays(hatch, v.day - 1);
          if (due.getTime() <= tomorrow.getTime()) count++; // overdue, today, or tomorrow
        });
      });
    });
    return count;
  }, [farms]);

  // Surface a write failure. 401 → session gone (log out); 403 → permission.
  function reportError(e) {
    console.error(e);
    if (e.status === 401) { doLogout("Your session expired. Please log in again."); return; }
    if (e.status === 403) { setServerError(e.message || "You don't have permission for that action."); loadFarms(); return; }
    setServerError("A change couldn't be saved to the database. Check the server is running.");
  }

  // ── FARMS ──
  async function addFarm() {
    if (!newFarmName.trim()) return;
    setNewFarmName(""); setNewFarmLocation(""); setShowAddFarm(false);
    try {
      const newFarm = await apiCall("/farms", {
        method: "POST",
        body: JSON.stringify({ name: newFarmName.trim(), location: newFarmLocation.trim() }),
      });
      setFarms(prev => [...prev, newFarm]);
    } catch (e) { reportError(e); }
  }

  async function editFarm(farmId, name, location) {
    setFarms(prev => prev.map(f => f.id === farmId ? { ...f, name, location } : f));
    setEditingFarm(null);
    try { await apiCall(`/farms/${farmId}`, { method: "PUT", body: JSON.stringify({ name, location }) }); }
    catch (e) { reportError(e); }
  }

  async function deleteFarm(id) {
    setFarms(prev => prev.filter(f => f.id !== id));
    if (activeFarmId === id) { setActiveFarmId(null); setScreen("farms"); }
    try { await apiCall(`/farms/${id}`, { method: "DELETE" }); }
    catch (e) { reportError(e); }
  }

  function openFarm(id) { setActiveFarmId(id); setScreen("farmHome"); }

  // From the alert panel: jump to a specific flock's vaccine view (flocks tab,
  // flock expanded, vaccine row focused) so the user can do partial dosing etc.
  function openVaccineFocus(farmId, flockId, vaccineId) {
    setActiveFarmId(farmId);
    setVaccineFocus({ farmId, flockId, vaccineId });
    setDetailTab("flocks");
    setScreen("farmDetail");
    setShowTomorrow(false); // close the alert panel
  }

  // ── VACCINES ──
  function updateFarmVaccines(farmId, updater) {
    setFarms(prev => prev.map(f => f.id === farmId ? { ...f, vaccines: updater(f.vaccines) } : f));
  }
  async function addVaccine(farmId) {
    try {
      const v = await apiCall(`/farms/${farmId}/vaccines`, { method: "POST" });
      updateFarmVaccines(farmId, vs => [...vs, v]);
    } catch (e) { reportError(e); }
  }
  // Optimistic local edit + debounced PUT so typing stays smooth
  function updateVaccine(farmId, vid, field, value) {
    updateFarmVaccines(farmId, vs => vs.map(v => v.id === vid ? { ...v, [field]: value } : v));
    clearTimeout(vaccineSaveTimers.current[vid]);
    vaccineSaveTimers.current[vid] = setTimeout(async () => {
      const farm = farmsRef.current.find(f => f.id === farmId);
      const v = farm && farm.vaccines.find(x => x.id === vid);
      if (!v) return;
      try {
        await apiCall(`/vaccines/${vid}`, {
          method: "PUT",
          body: JSON.stringify({
            day: v.day, name: v.name, route: v.route, notes: v.notes,
            illness: v.illness || "", company: v.company || "", dosageNote: v.dosageNote || "",
          }),
        });
      } catch (e) { reportError(e); }
    }, 500);
  }
  async function removeVaccine(farmId, vid) {
    updateFarmVaccines(farmId, vs => vs.filter(v => v.id !== vid));
    try { await apiCall(`/vaccines/${vid}`, { method: "DELETE" }); }
    catch (e) { reportError(e); }
  }

  // ── FLOCKS ──
  async function addFlock(farmId, flockName, hatchDate, initialSize, priorMortality, addedOn, birdType) {
    try {
      await apiCall(`/farms/${farmId}/flocks`, {
        method: "POST",
        body: JSON.stringify({ hatchDate, initialSize: initialSize || 0, priorMortality: priorMortality || 0, addedOn: addedOn || null, birdType: birdType || "" }),
      });
      // Reload so server-side auto-numbering (Flock 1,2,3 by age) is reflected.
      await loadFarms();
    } catch (e) { reportError(e); }
  }
  async function deleteFlock(farmId, flockId) {
    setFarms(prev => prev.map(f => f.id === farmId ? { ...f, flocks: f.flocks.filter(fl => fl.id !== flockId) } : f));
    try { await apiCall(`/flocks/${flockId}`, { method: "DELETE" }); await loadFarms(); }
    catch (e) { reportError(e); loadFarms(); }
  }
  // SOLD: mark a flock sold on a date (default today). The flock + all its history
  // are kept; it just disappears from the sold date onward. Previous days unchanged.
  async function sellFlock(farmId, flockId, date) {
    const soldOn = date || todayISO();
    setFarms(prev => prev.map(f => f.id === farmId ? { ...f, flocks: f.flocks.map(fl => fl.id === flockId ? { ...fl, soldOn } : fl) } : f));
    try { await apiCall(`/flocks/${flockId}/sell`, { method: "POST", body: JSON.stringify({ date: soldOn }) }); await loadFarms(); }
    catch (e) { reportError(e); loadFarms(); }
  }
  async function updateFlock(farmId, flockId, fields) {
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => fl.id !== flockId ? fl : { ...fl, ...fields })
    }));
    try {
      await apiCall(`/flocks/${flockId}`, { method: "PUT", body: JSON.stringify(fields) });
      // If hatch date changed, server renumbers by age → reload to show new names.
      if (fields.hatchDate !== undefined) await loadFarms();
    } catch (e) { reportError(e); loadFarms(); }
  }
  // Add birds to a flock (bought more hens of same age). Server records the dated
  // addition and bumps the flock's total; we reload so balances/targets update.
  async function addBirds(farmId, flockId, count, date, note) {
    try {
      const updatedFarm = await apiCall(`/flocks/${flockId}/additions`, {
        method: "POST",
        body: JSON.stringify({ count, date, note }),
      });
      // Server returns the full updated farm; merge it in.
      if (updatedFarm && updatedFarm.id) setFarms(prev => prev.map(f => f.id === updatedFarm.id ? updatedFarm : f));
      else await loadFarms();
    } catch (e) { reportError(e); loadFarms(); }
  }
  async function deleteAddition(farmId, additionId) {
    try {
      const updatedFarm = await apiCall(`/flock-additions/${additionId}`, { method: "DELETE" });
      if (updatedFarm && updatedFarm.id) setFarms(prev => prev.map(f => f.id === updatedFarm.id ? updatedFarm : f));
      else await loadFarms();
    } catch (e) { reportError(e); loadFarms(); }
  }

  // ── DAILY LOGS ──
  async function saveDailyLog(farmId, flockId, entry) {
    // Optimistic upsert by date
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => {
        if (fl.id !== flockId) return fl;
        const existing = (fl.dailyLogs||[]).find(e => e.date === entry.date);
        if (existing) return { ...fl, dailyLogs: fl.dailyLogs.map(e => e.date === entry.date ? { ...e, ...entry } : e) };
        return { ...fl, dailyLogs: [...(fl.dailyLogs||[]), { id: `tmp-${entry.date}`, ...entry }] };
      })
    }));
    try {
      const saved = await apiCall(`/flocks/${flockId}/daily-logs`, { method: "POST", body: JSON.stringify(entry) });
      // Reconcile the temp id with the real one from the database
      setFarms(prev => prev.map(f => f.id !== farmId ? f : {
        ...f, flocks: f.flocks.map(fl => fl.id !== flockId ? fl : {
          ...fl, dailyLogs: fl.dailyLogs.map(e => e.date === saved.date ? saved : e)
        })
      }));
    } catch (e) { reportError(e); }
  }
  async function deleteDailyLog(farmId, flockId, date) {
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => fl.id !== flockId ? fl : {
        ...fl, dailyLogs: (fl.dailyLogs||[]).filter(e => e.date !== date)
      })
    }));
    try { await apiCall(`/flocks/${flockId}/daily-logs/${date}`, { method: "DELETE" }); }
    catch (e) { reportError(e); }
  }

  // Copy schedule from one farm to another
  async function importSchedule(targetFarmId, sourceFarmId) {
    try {
      const updated = await apiCall(`/farms/${targetFarmId}/copy-schedule`, {
        method: "POST", body: JSON.stringify({ sourceFarmId }),
      });
      setFarms(prev => prev.map(f => f.id === targetFarmId ? updated : f));
    } catch (e) { reportError(e); }
  }

  // ── VACCINATION STATUS ──
  // Vaccination changes affect derived dose/completion state, so after writing
  // we reload from the server (which recomputes completion) rather than guessing.
  async function markDone(farmId, flockId, vaccineId, dateISO) {
    // Optimistic local update for instant feedback (no full re-render storm)…
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => {
        if (fl.id !== flockId) return fl;
        const target = fl.initialSize || 0;
        const newDoses = { ...(fl.doses||{}), [vaccineId]: [{ id:`tmp-${Date.now()}`, date:dateISO, count:target, notes:"" }] };
        return { ...fl, doses:newDoses, done: { ...(fl.done||{}), [vaccineId]: dateISO } };
      })
    }));
    // …then persist. If it fails, reportError shows a banner AND we resync so the
    // UI never lies about what's actually saved.
    try {
      await apiCall(`/flocks/${flockId}/vaccinations`, { method: "POST", body: JSON.stringify({ vaccineId, givenDate: dateISO }) });
    } catch (e) { reportError(e); loadFarms(); }
  }
  async function markUndone(farmId, flockId, vaccineId) {
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => {
        if (fl.id !== flockId) return fl;
        const nd = { ...(fl.done||{}) }; delete nd[vaccineId];
        const ndo = { ...(fl.doses||{}) }; delete ndo[vaccineId];
        return { ...fl, done:nd, doses:ndo };
      })
    }));
    try {
      await apiCall(`/flocks/${flockId}/vaccinations/${vaccineId}`, { method: "DELETE" });
    } catch (e) { reportError(e); loadFarms(); }
  }
  // Mark ALL of a farm's vaccines as fully done for one flock, in one go.
  async function markAllVaccinesDone(farmId, flockId) {
    const farm = farmsRef.current.find(f => f.id === farmId);
    if (!farm) return;
    const today = todayISO();
    const vaccines = (farm.vaccines || []).filter(v => v.name && v.name.trim());
    // Optimistic local update for all vaccines at once (single re-render)
    setFarms(prev => prev.map(f => f.id !== farmId ? f : {
      ...f, flocks: f.flocks.map(fl => {
        if (fl.id !== flockId) return fl;
        const target = fl.initialSize || 0;
        const newDone = { ...(fl.done||{}) };
        const newDoses = { ...(fl.doses||{}) };
        vaccines.forEach(v => {
          newDone[v.id] = today;
          newDoses[v.id] = [{ id:`tmp-${v.id}-${Date.now()}`, date:today, count:target, notes:"" }];
        });
        return { ...fl, done:newDone, doses:newDoses };
      })
    }));
    // ONE bulk request — server marks all vaccines done in a single transaction.
    // (Previously this looped one request per vaccine, which tripped the rate
    //  limiter and caused DB write contention when done across many flocks fast.)
    try {
      await apiCall(`/flocks/${flockId}/vaccinations-all`, {
        method: "POST", body: JSON.stringify({ givenDate: today }),
      });
    } catch (e) { reportError(e); loadFarms(); }
  }
  // Add one partial dose (date + bird count + note)
  async function addDose(farmId, flockId, vaccineId, dose) {
    try {
      await apiCall(`/flocks/${flockId}/vaccine-doses`, { method: "POST", body: JSON.stringify({ vaccineId, ...dose }) });
      await loadFarms();
    } catch (e) { reportError(e); }
  }
  // Edit an existing dose
  async function editDose(doseId, dose) {
    try {
      await apiCall(`/vaccine-doses/${doseId}`, { method: "PUT", body: JSON.stringify(dose) });
      await loadFarms();
    } catch (e) { reportError(e); }
  }
  // Delete one dose
  async function deleteDose(doseId) {
    try {
      await apiCall(`/vaccine-doses/${doseId}`, { method: "DELETE" });
      await loadFarms();
    } catch (e) { reportError(e); }
  }

  // ── Login screen (shown whenever nobody is logged in, including after reload) ──
  if (!currentUser) {
    return <LoginScreen onLogin={doLogin} serverError={serverError} clearError={() => setServerError(null)} />;
  }

  // ── Loading screen while we fetch from the database ──
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", background:"#080f07", fontFamily:"system-ui,sans-serif", color:"#7dd45a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        `}</style>
        <div style={{ width:56, height:56, border:"3px solid #1a3a14", borderTop:"3px solid #7dd45a", borderRadius:"50%", animation:"spin 0.9s linear infinite" }} />
        <div style={{ fontSize:16, fontWeight:600, letterSpacing:0.5, animation:"fadeUp 0.4s ease" }}>Loading Poultry Manager…</div>
        {serverError && (
          <div style={{ maxWidth:320, textAlign:"center", marginTop:8, padding:"16px 20px", background:"#1e0a0a", border:"1px solid #5a1a1a", borderRadius:16, color:"#e09090", fontSize:13, lineHeight:1.7, animation:"fadeUp 0.5s ease" }}>
            ⚠️ {serverError}
            <button onClick={() => { setLoading(true); loadFarms(); }} style={{ display:"block", margin:"14px auto 0", padding:"9px 22px", background:"#2a1010", border:"1px solid #7a2020", borderRadius:20, color:"#f0b0b0", fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Retry Connection</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", background:"#080f07", fontFamily:"system-ui,sans-serif", color:"#ddeedd", paddingBottom:96 }}>
      <style>{`
        * { box-sizing: border-box; }
        input, select, button, textarea { font-family: inherit; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: #2a4a2a; border-radius: 4px; }
        @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { transform:translateY(100%); } to { transform:translateY(0); } }
        @keyframes slideInRight { from { transform:translateX(100%); } to { transform:translateX(0); } }
        @keyframes pulse  { 0%,100% { transform:scale(1); } 50% { transform:scale(1.15); } }
        @keyframes shimmer { from { background-position: -200% center; } to { background-position: 200% center; } }
        /* Snappy (~350ms) transitions for navigation, tabs, and expand/collapse */
        @keyframes screenIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes tabIn    { from { opacity:0; transform:translateX(8px); } to { opacity:1; transform:translateX(0); } }
        @keyframes rollDown { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn    { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
        .anim-screen { animation: screenIn 0.35s cubic-bezier(0.16,1,0.3,1); }
        .anim-tab    { animation: tabIn 0.3s cubic-bezier(0.16,1,0.3,1); }
        .anim-roll   { animation: rollDown 0.25s cubic-bezier(0.16,1,0.3,1); }
        .anim-pop    { animation: popIn 0.28s cubic-bezier(0.16,1,0.3,1); }
        /* Cards get a gentle lift on hover (desktop) and press feedback on click */
        .lift { transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease; cursor: pointer; }
        .lift:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(0,0,0,0.4); }
        .lift:active { transform: scale(0.98) translateY(0); }
        .press { transition: transform 0.12s ease, filter 0.12s ease; }
        .press:hover { filter: brightness(1.1); }
        .press:active { transform: scale(0.96); }
        /* Respect users who prefer reduced motion */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
        .farm-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .farm-card:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.45); }
        .farm-card:active { transform: scale(0.985) translateY(0); }
        .mod-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .mod-card:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.45); }
        .mod-card:active  { transform: scale(0.98) translateY(0); }
        /* General interactive button style — apply to any clickable button */
        button { transition: filter 0.12s ease, opacity 0.12s ease; }
        button:active { opacity: 0.85; }
        button:focus-visible { outline: 2px solid #7dd45a; outline-offset: 2px; }
        .btn-primary { transition: filter 0.18s ease, transform 0.12s ease, box-shadow 0.18s ease; }
        .btn-primary:hover { filter: brightness(1.12); transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.35); }
        .btn-primary:active { transform: scale(0.97) translateY(0); filter: brightness(0.95); }

        /* Centered container — keeps content from stretching on wide desktop screens.
           On phones/tablets it just fills the width (with comfortable padding). */
        .app-container { max-width: 1100px; margin: 0 auto; width: 100%; }

        /* Responsive card grid: 1 column on phones, more as the screen widens.
           auto-fill + minmax means columns appear automatically when there's room. */
        .card-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 640px)  { .card-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1000px) { .card-grid { grid-template-columns: repeat(3, 1fr); } }

        /* Slightly wider minimum for flock cards (they hold more info) */
        /* Farm dashboard: stat tiles, widget columns and compact module tiles. */
        .stat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        @media (min-width: 700px)  { .stat-grid { grid-template-columns: repeat(4, 1fr); } }
        .dash-grid { display: grid; grid-template-columns: 1fr; gap: 12px; align-items: start; }
        @media (min-width: 980px)  { .dash-grid { grid-template-columns: 1.6fr 1fr; } }
        .mod-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
        @media (min-width: 700px)  { .mod-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width: 1100px) { .mod-grid { grid-template-columns: repeat(5, 1fr); } }
        .mod-tile { transition: transform 0.15s, border-color 0.15s; }
        .mod-tile:hover { transform: translateY(-2px); }
        .flock-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 720px)  { .flock-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1080px) { .flock-grid { grid-template-columns: repeat(2, 1fr); } }
        /* Daily log flocks are always a single vertical column (one flock per line),
           so up/down arrow navigation between flocks is natural. */
        .daily-flock-list { display: flex; flex-direction: column; gap: 10px; }

        /* Vaccine schedule rows: stacked labeled cards on mobile, grid columns on desktop.
           Columns: Day | Name | Illness | Company | Route | (delete) */
        .vac-head { display: none; }
        .vac-row {
          display: grid; grid-template-columns: 1fr; gap: 6px;
          background: #0e1a0c; border: 1px solid #1e3a1a; border-radius: 10;
          padding: 10px; margin-bottom: 10px;
        }
        .vac-row .vac-label { font-size: 9px; color: #5a7a4a; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 2px; display: block; }
        .vac-cell { min-width: 0; }
        .vac-del-wrap { display: flex; justify-content: flex-end; }
        @media (min-width: 760px) {
          .vac-head {
            display: grid; gap: 8px; padding: 6px 4px; margin-bottom: 6px;
            font-size: 10px; color: #5a7a4a; letter-spacing: 1px; text-transform: uppercase;
            border-bottom: 1px solid #2a3a2a;
          }
          .vac-row {
            display: grid; gap: 8px; align-items: center;
            background: transparent; border: none; border-radius: 0;
            padding: 0; margin-bottom: 8px;
          }
          .vac-row .vac-label { display: none; }
          .vac-del-wrap { display: block; }
        }
      `}</style>

      {/* Server error banner */}
      {serverError && (
        <div style={{ background:"#1e0808", borderBottom:"1px solid #5a1818", color:"#e09090", fontSize:12, padding:"10px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
          <span>⚠️ {serverError}</span>
          <button onClick={() => setServerError(null)} style={{ background:"none", border:"none", color:"#e09090", fontSize:18, cursor:"pointer", lineHeight:1, padding:0 }}>×</button>
        </div>
      )}

      {/* ── APP BAR (full-width background, centered inner content) ── */}
      <div style={{ background:"#0c1a0a", borderBottom:"1px solid #1e3a1a", position:"sticky", top:0, zIndex:50, backdropFilter:"blur(12px)" }}>
        <div className="app-container" style={{ padding:"14px 16px 12px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>

          {/* Back button */}
          {(screen==="farmHome" || screen==="farmDetail" || screen==="flockRecords" || screen==="eggStock" || screen==="packingStock" || screen==="feedStock") && (
            <button onClick={() => setScreen(screen==="farmHome" ? "farms" : "farmHome")}
              style={{ background:"#1a2e18", border:"1px solid #2d5020", borderRadius:20, color:"#7dd45a", fontSize:13, cursor:"pointer", padding:"6px 12px", display:"flex", alignItems:"center", gap:4, fontWeight:600, lineHeight:1, flexShrink:0 }}>
              ← {screen==="farmHome" ? "Farms" : "Home"}
            </button>
          )}

          {/* Logo + title */}
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:17, fontWeight:700, color:"#c8f0a8", letterSpacing:0.3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
              {screen==="farms" ? "🐣 Poultry Manager" : activeFarm ? `🏡 ${activeFarm.name}` : "🐣 Poultry Manager"}
            </div>
            <div style={{ fontSize:10, color:"#4a7a3a", letterSpacing:1.5, textTransform:"uppercase", marginTop:1 }}>
              {screen==="farms" ? "Multi-Farm Dashboard"
                : screen==="farmHome" ? (activeFarm?.location || "Dashboard")
                : screen==="farmDetail" ? "Vaccination Module"
                : screen==="flockRecords" ? "Production & Mortality"
                : screen==="eggStock" ? "Egg Stock Register"
                : screen==="packingStock" ? "Packing Egg Store"
                : screen==="feedStock" ? "Feed & Medicine Stock"
                : ""}
            </div>
          </div>

          {/* Icon buttons */}
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button onClick={() => setShowNotifPanel(true)} style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:12, padding:"8px 10px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:1, lineHeight:1 }}>
              <span style={{ fontSize:18 }}>🔔</span>
              <span style={{ fontSize:8, color:"#3a6030", letterSpacing:0.5, textTransform:"uppercase" }}>Alerts</span>
            </button>
            {p.canView("vaccination") && (
            <button onClick={() => setShowTomorrow(true)} style={{ position:"relative", background:tomorrowCount>0?"#1a3010":"#142212", border:`1px solid ${tomorrowCount>0?"#4a8020":"#2a4020"}`, borderRadius:12, padding:"8px 10px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:1, lineHeight:1 }}>
              <span style={{ fontSize:18 }}>📅</span>
              <span style={{ fontSize:8, color:tomorrowCount>0?"#8ad040":"#3a6030", letterSpacing:0.5, textTransform:"uppercase" }}>Tomorrow</span>
              {tomorrowCount>0 && (
                <span style={{ position:"absolute", top:-6, right:-6, background:"#d04010", color:"#fff", fontSize:10, fontWeight:700, borderRadius:20, minWidth:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", animation:"pulse 2s infinite" }}>{tomorrowCount}</span>
              )}
            </button>
            )}
            {p.canManageUsers && (
              <button onClick={() => setShowSettings(true)} style={{ background:"#1e1a04", border:"1px solid #4a3810", borderRadius:12, padding:"8px 10px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:1, lineHeight:1 }}>
                <span style={{ fontSize:18 }}>⚙️</span>
                <span style={{ fontSize:8, color:"#8a6a20", letterSpacing:0.5, textTransform:"uppercase" }}>Settings</span>
              </button>
            )}
          </div>
        </div>

        {/* User strip */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10, paddingTop:10, borderTop:"1px solid #16281244" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14 }}>{currentUser.role==="super_admin"?"👑":currentUser.role==="manager"?"🛠️":"👁️"}</span>
            <span style={{ fontSize:13, color:"#a8c898", fontWeight:600 }}>{currentUser.fullName || currentUser.username}</span>
            <span style={{ fontSize:10, color:"#4a7040", background:"#142212", border:"1px solid #2a4020", borderRadius:20, padding:"2px 8px" }}>{ROLE_LABELS[currentUser.role]}</span>
          </div>
          <button onClick={() => { if(window.confirm("Sign out now?")) doLogout(); }} style={{ background:"#1a1210", border:"1px solid #3a2218", borderRadius:20, color:"#b07050", fontSize:12, padding:"5px 14px", cursor:"pointer", fontFamily:"inherit" }}>Logout</button>
        </div>
        </div>{/* end app-bar inner container */}
      </div>{/* end app-bar background */}

      {/* Main content — centered on desktop, full width on mobile/tablet */}
      <div className="app-container">

      {/* Keyed wrapper: re-mounts on screen change so each screen animates in */}
      <div key={screen} className="anim-screen">

      {/* ── FARMS LIST ── */}
      {screen==="farms" && (
        <div style={{ padding:"20px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
            <div>
              <div style={{ fontSize:20, fontWeight:700, color:"#c8f0a8" }}>Your Farms</div>
              <div style={{ fontSize:12, color:"#4a7040", marginTop:2 }}>{farms.length} farm{farms.length!==1?"s":""} registered</div>
            </div>
            {p.canManageFarms && (
              <button className="btn-primary" onClick={() => setShowAddFarm(true)} style={{ background:"linear-gradient(135deg,#2a5a1a,#1a4010)", border:"none", borderRadius:22, color:"#c8f0a8", fontSize:13, padding:"10px 18px", cursor:"pointer", fontWeight:600 }}>+ Add Farm</button>
            )}
          </div>

          {showAddFarm && (
            <div style={{ background:"#0e1e0c", border:"1px solid #2a4a20", borderRadius:18, padding:18, marginBottom:18, animation:"fadeUp 0.35s ease", boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
              <div style={{ fontSize:15, color:"#a8d48a", marginBottom:14, fontWeight:700 }}>Register New Farm</div>
              <input type="text" value={newFarmName} onChange={e => setNewFarmName(e.target.value)} placeholder="Farm name" style={{ ...inputStyle, marginBottom:10 }} />
              <input type="text" value={newFarmLocation} onChange={e => setNewFarmLocation(e.target.value)} placeholder="Location (optional)" style={{ ...inputStyle, marginBottom:14 }} />
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn-primary" onClick={addFarm} style={{ flex:1, padding:"12px", background:"linear-gradient(135deg,#2a6a1a,#1a4a0a)", border:"none", borderRadius:12, color:"#c8f0b0", fontSize:14, cursor:"pointer", fontWeight:700 }}>Register Farm</button>
                <button onClick={() => { setShowAddFarm(false); setNewFarmName(""); setNewFarmLocation(""); }} style={{ padding:"12px 18px", background:"#1a1e18", border:"1px solid #2a3a28", borderRadius:12, color:"#6a8060", fontSize:14, cursor:"pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {farms.length===0 && !showAddFarm && (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"#2a4a28" }}>
              <div style={{ fontSize:56, marginBottom:14 }}>🏚️</div>
              <div style={{ fontSize:17, color:"#3a6038", marginBottom:6, fontWeight:600 }}>No farms yet</div>
              <div style={{ fontSize:13, color:"#2a4028" }}>Tap "+ Add Farm" to get started</div>
            </div>
          )}

          <div className="card-grid">
          {farms.map((farm, idx) => {
            const color = FARM_COLORS[farm.colorIdx];
            const today2 = new Date(); today2.setHours(0,0,0,0);
            const tomorrow2 = addDays(today2,1);
            let urgentCount=0, tomorrowFarmCount=0;
            farm.flocks.forEach(fl => {
              if (!fl.hatchDate) return;
              if (!flockActiveNow(fl)) return;
              const hatch=new Date(fl.hatchDate); hatch.setHours(0,0,0,0);
              farm.vaccines.forEach(v => {
                if (!v.name || fl.done[v.id]) return;
                const due=addDays(hatch,(v.day||1)-1);
                if (daysBetween(today2,due)<=0) urgentCount++;
                if (isSameDay(due,tomorrow2)) tomorrowFarmCount++;
              });
            });
            // Bird lifecycle tally: sum each ACTIVE flock's current balance, grouped by stage.
            let chickBirds=0, growerBirds=0, layerBirds=0;
            farm.flocks.forEach(fl => {
              if (!flockActiveNow(fl)) return;
              const bal = flockBalance(fl);
              const stage = flockStage(fl);
              if (stage === "chick") chickBirds += bal;
              else if (stage === "grower") growerBirds += bal;
              else layerBirds += bal;
            });
            const eggInfo = eggSummary[farm.id] || null;
            const activeFlockCount = farm.flocks.filter(flockActiveNow).length;
            return (
              <div key={farm.id} style={{ animation:`fadeUp 0.35s ease ${idx*0.05}s both` }}>
              {editingFarm?.id === farm.id ? (
                /* Inline edit form */
                <div style={{ background:"#0e1e0c", border:`1px solid ${color.border}`, borderRadius:18, padding:16, boxShadow:"0 8px 32px rgba(0,0,0,0.4)" }}>
                  <div style={{ fontSize:14, color:color.accent, marginBottom:12, fontWeight:700 }}>✏️ Edit Farm</div>
                  <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Farm Name</label>
                  <input type="text" value={editingFarm.name} onChange={e=>setEditingFarm({...editingFarm, name:e.target.value})} style={{ ...inputStyle, marginBottom:10 }} />
                  <label style={{ fontSize:10, color:"#5a8048", letterSpacing:1, textTransform:"uppercase", display:"block", marginBottom:4 }}>Location</label>
                  <input type="text" value={editingFarm.location} onChange={e=>setEditingFarm({...editingFarm, location:e.target.value})} placeholder="Optional" style={{ ...inputStyle, marginBottom:14 }} />
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="btn-primary" onClick={()=>{ if(editingFarm.name.trim()) editFarm(farm.id, editingFarm.name.trim(), editingFarm.location.trim()); }} style={{ flex:1, padding:"11px", background:"linear-gradient(135deg,#2a6a1a,#1a4a0a)", border:"none", borderRadius:10, color:"#c8f0b0", fontSize:14, fontWeight:700, cursor:"pointer" }}>Save</button>
                    <button onClick={()=>setEditingFarm(null)} style={{ padding:"11px 16px", background:"#181e16", border:"1px solid #2a3228", borderRadius:10, color:"#6a7a68", fontSize:14, cursor:"pointer" }}>Cancel</button>
                  </div>
                </div>
              ) : (
              <div className="farm-card lift" onClick={() => openFarm(farm.id)}
                style={{ background:"#0c180a", border:`1px solid ${color.border}`, borderRadius:18, cursor:"pointer", overflow:"hidden", transition:"transform 0.15s", boxShadow:`0 2px 16px rgba(0,0,0,0.3), inset 0 0 0 1px ${color.border}33` }}>
                {/* Color stripe */}
                <div style={{ height:4, background:`linear-gradient(90deg, ${color.stripe}, ${color.accent}44)` }} />
                <div style={{ padding:"14px 16px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:17, fontWeight:700, color:color.accent, marginBottom:2 }}>🏡 {farm.name}</div>
                      {farm.location && <div style={{ fontSize:12, color:"#4a6a40", marginBottom:8 }}>📍 {farm.location}</div>}
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:farm.location?0:8 }}>
                        {p.canView("vaccination") && <span style={{ background:color.dark, border:`1px solid ${color.border}`, color:color.accent, fontSize:11, borderRadius:20, padding:"3px 10px" }}>💉 {farm.vaccines.filter(v=>v.name.trim()).length} vaccines</span>}
                        <span style={{ background:color.dark, border:`1px solid ${color.border}`, color:color.accent, fontSize:11, borderRadius:20, padding:"3px 10px" }}>🐔 {activeFlockCount} flock{activeFlockCount!==1?"s":""}</span>
                        {p.canView("vaccination") && tomorrowFarmCount>0 && <span style={{ background:"#261a04", border:"1px solid #6a4a10", color:"#e0b040", fontSize:11, borderRadius:20, padding:"3px 10px", fontWeight:600 }}>📅 {tomorrowFarmCount} tmrw</span>}
                        {p.canView("vaccination") && urgentCount>0 && <span style={{ background:"#260808", border:"1px solid #6a1818", color:"#e05050", fontSize:11, borderRadius:20, padding:"3px 10px", fontWeight:600 }}>⚠️ {urgentCount} urgent</span>}
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
                      <span style={{ color:color.accent, fontSize:20, lineHeight:1 }}>›</span>
                      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                        {p.canManageFarms && (
                          <button onClick={e => { e.stopPropagation(); setEditingFarm({ id:farm.id, name:farm.name, location:farm.location||"" }); }}
                            style={{ background:"#142212", border:"1px solid #2a4020", borderRadius:8, color:"#7dd45a", fontSize:11, cursor:"pointer", padding:"4px 9px" }}>✏️ Edit</button>
                        )}
                        {p.canManageFarms && (
                          <button onClick={e => { e.stopPropagation(); if(window.confirm(`Delete farm "${farm.name}"? This removes all its flocks and records.`)) deleteFarm(farm.id); }}
                            style={{ background:"none", border:"none", color:"#4a2020", fontSize:13, cursor:"pointer", padding:"2px 4px" }}>🗑</button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Bird lifecycle tally — sum of each flock's current balance, grouped by stage */}
                  {p.canView("production") && (
                  <div style={{ display:"flex", gap:6, marginTop:12 }}>
                    {[
                      { key:"chick",  label:"Chicks",  emoji:"🐤", val:chickBirds,  c:"#e0c040", bg:"#1c1808", bd:"#5a4a18" },
                      { key:"grower", label:"Growers", emoji:"🐓", val:growerBirds, c:"#e0954a", bg:"#1e1408", bd:"#5a3a18" },
                      { key:"layer",  label:"Layers",  emoji:"🥚", val:layerBirds,  c:"#7dd45a", bg:"#0e1e0c", bd:"#2a4a20" },
                    ].map(s => (
                      <div key={s.key} style={{ flex:1, background:s.bg, border:`1px solid ${s.bd}`, borderRadius:10, padding:"7px 8px", textAlign:"center" }}>
                        <div style={{ fontSize:9, color:s.c, opacity:0.9, letterSpacing:0.5, textTransform:"uppercase" }}>{s.emoji} {s.label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color:s.c, marginTop:2 }}>{s.val.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                  )}
                  <EntryStatusPanel farmId={farm.id} todayInfo={eggInfo && eggInfo.entry} allowed={["production", "eggstock", "feed"].filter(m => p.canView(m))} />
                  {eggInfo && p.canView("eggstock") && (
                    <div style={{ marginTop:10, background:"#0f0c08", border:"1px solid #3a2a16", borderRadius:12, padding:"10px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span style={{ fontSize:10, color:"#b08858", letterSpacing:0.5, textTransform:"uppercase" }}>🥚 Eggs in Stock</span>
                        <span><strong style={{ fontSize:18, color:"#f0c890" }}>{(eggInfo.closing||0).toLocaleString()}</strong> <span style={{ fontSize:10, color:"#a07850" }}>trays</span></span>
                      </div>
                      {p.canView("production") && (() => {
                        const t = prodTrend(eggInfo.trend);
                        if (!t.hasData) return <div style={{ fontSize:9, color:"#6a5a4a", marginTop:6 }}>No production logged in the last {(eggInfo.trend||[]).length} days</div>;
                        return (
                          <div style={{ marginTop:8 }}>
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:3 }}>
                              <span style={{ fontSize:9, color:"#7a6a5a" }}>📈 Production · last {eggInfo.trend.length} days (trays/day)</span>
                              <span style={{ fontSize:10, fontWeight:700, color: t.dir>0?"#6ec06e":t.dir<0?"#e0954a":"#8a7a6a" }}>{t.dir>0?"▲":t.dir<0?"▼":"▬"} {Math.abs(t.pct)}%</span>
                            </div>
                            <Sparkline data={eggInfo.trend.map(x=>x.trays)} up={t.dir>=0} />
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {eggInfo && p.canView("feed") && eggInfo.reorder && eggInfo.reorder.length > 0 && (
                    <div onClick={e=>e.stopPropagation()} style={{ marginTop:10, background:"#1a0e06", border:"1px solid #6a4410", borderRadius:12, padding:"10px 12px" }}>
                      <div style={{ fontSize:10, color:"#e0a850", letterSpacing:0.5, textTransform:"uppercase", fontWeight:700, marginBottom:2 }}>🔔 Reorder Feed / Medicine ({eggInfo.reorder.length})</div>
                      {eggInfo.reorder.map(r => (
                        <div key={r.item_id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"6px 0 3px", borderTop:"1px solid #2a1a0a", marginTop:4 }}>
                          <div style={{ minWidth:0 }}>
                            <span style={{ fontSize:12, color:"#f0d0a0" }}>{r.kind==="medicine"?"💊":"🌾"} {r.name}</span>
                            <span style={{ fontSize:10, color:"#c08850", marginLeft:6, fontVariantNumeric:"tabular-nums" }}>{Math.round(r.qty).toLocaleString()}/{Math.round(r.minQty).toLocaleString()} kg</span>
                          </div>
                          {p.canManage("feed") && (r.ordered
                            ? <span style={{ fontSize:10, color:"#7ad0a0", fontWeight:600, flexShrink:0 }}>🛒 ordered</span>
                            : <button onClick={e=>{ e.stopPropagation(); markItemOrdered(r.item_id); }} style={{ background:"linear-gradient(135deg,#7a4a10,#5a3208)", border:"1px solid #9a6a1a", borderRadius:8, color:"#f0d0a0", fontSize:10.5, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:600, flexShrink:0 }}>Mark ordered</button>)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              )}
              </div>
            );
          })}
          </div>{/* end card-grid */}
        </div>
      )}

      {/* ── FARM HOME — module selector ── */}
      {screen==="farmHome" && activeFarm && (
        <FarmDashboard
          farm={activeFarm}
          perms={p}
          eggInfo={eggSummary[activeFarm.id] || null}
          modules={[
            // Vaccination Tracker is restricted to super admins only — others won't
            // even see this tile, and the screen itself is guarded below.
            ...(p.canView("vaccination") ? [{
              key:"farmDetail", icon:"💉", title:"Vaccination",
              color:FARM_COLORS[activeFarm.colorIdx],
              stats:`${activeFarm.vaccines.filter(v=>v.name.trim()).length} vaccines · ${activeFarm.flocks.filter(flockActiveNow).length} flocks`,
              action: () => { setDetailTab("schedule"); setScreen("farmDetail"); }
            }] : []),
            ...(p.canView("production") ? [{
              key:"flockRecords", icon:"📊", title:"Production & Mortality",
              color:{ accent:"#e0b84a", dark:"#1e1604", border:"#4a380a", pill:"#2e2008", stripe:"#a08020" },
              stats:`${activeFarm.flocks.filter(flockActiveNow).length} batch${activeFarm.flocks.filter(flockActiveNow).length!==1?"es":""} registered`,
              action: () => setScreen("flockRecords")
            }] : []),
            ...(p.canView("eggstock") ? [{
              key:"eggStock", icon:"🥚", title:"Egg Stock",
              color:{ accent:"#d8945a", dark:"#1e1206", border:"#4a2e12", pill:"#2e1c0a", stripe:"#a06830" },
              stats:"Daily register (trays)",
              action: () => setScreen("eggStock")
            }] : []),
            ...(p.canView("packing") ? [{
              key:"packingStock", icon:"📦", title:"Packing Egg Store",
              color:{ accent:"#b08ad0", dark:"#160e1e", border:"#3a2a4a", pill:"#241634", stripe:"#7a5a9a" },
              stats:"White · Brown · Loose",
              action: () => setScreen("packingStock")
            }] : []),
            ...(p.canView("feed") ? [{
              key:"feedStock", icon:"🌾", title:"Feed & Medicine",
              color:{ accent:"#5cc8a8", dark:"#08201a", border:"#1e4a3a", pill:"#0e2e24", stripe:"#3a8a6a" },
              stats:"Layers · Growers · Chicks",
              action: () => setScreen("feedStock")
            }] : [])
          ]}
        />
      )}

      {/* FARM DETAIL — Vaccination (SUPER ADMIN ONLY) */}
      {screen==="farmDetail" && activeFarm && p.canView("vaccination") && (
        <FarmDetail
          farm={activeFarm}
          perms={modPerms(p, "vaccination")}
          detailTab={detailTab}
          setDetailTab={setDetailTab}
          onAddVaccine={() => addVaccine(activeFarm.id)}
          onUpdateVaccine={(vid,f,v) => updateVaccine(activeFarm.id,vid,f,v)}
          onRemoveVaccine={(vid) => removeVaccine(activeFarm.id,vid)}
          onAddFlock={(n,hd,sz) => addFlock(activeFarm.id,n,hd,sz)}
          onDeleteFlock={(fid) => deleteFlock(activeFarm.id,fid)}
          onMarkDone={(flockId,vid,date) => markDone(activeFarm.id,flockId,vid,date)}
          onMarkUndone={(flockId,vid) => markUndone(activeFarm.id,flockId,vid)}
          onAddDose={(flockId,vid,dose) => addDose(activeFarm.id,flockId,vid,dose)}
          onEditDose={(doseId,dose) => editDose(doseId,dose)}
          onDeleteDose={(doseId) => deleteDose(doseId)}
          allFarms={farms}
          onImportSchedule={(sourceFarmId) => importSchedule(activeFarm.id, sourceFarmId)}
          onMarkAllDone={(flockId) => markAllVaccinesDone(activeFarm.id, flockId)}
          focusTarget={vaccineFocus && vaccineFocus.farmId === activeFarm.id ? vaccineFocus : null}
          onFocusHandled={() => setVaccineFocus(null)}
        />
      )}
      {/* If a non-super-admin somehow lands on farmDetail, block it */}
      {screen==="farmDetail" && activeFarm && !p.canView("vaccination") && (
        <div style={{ padding:"40px 20px", textAlign:"center" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
          <div style={{ fontSize:16, color:"#c8a850", fontWeight:600, marginBottom:8 }}>Restricted Area</div>
          <div style={{ fontSize:13, color:"#7a6a4a", marginBottom:20, lineHeight:1.6 }}>The Vaccination Tracker is only accessible to super admins.</div>
          <button onClick={() => setScreen("farmHome")} style={{ padding:"10px 18px", background:"#1a3a14", border:"1px solid #3a7a2a", borderRadius:10, color:"#a8f080", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>← Back to Modules</button>
        </div>
      )}

      {/* FLOCK RECORDS — Production & Mortality */}
      {screen==="flockRecords" && activeFarm && p.canView("production") && (
        <FlockRecords
          farm={activeFarm}
          perms={modPerms(p, "production")}
          onAddFlock={(n,hd,sz,pm,ao,bt) => addFlock(activeFarm.id,n,hd,sz,pm,ao,bt)}
          onDeleteFlock={(fid) => deleteFlock(activeFarm.id,fid)}
          onSellFlock={(fid, date) => sellFlock(activeFarm.id, fid, date)}
          onUpdateFlock={(fid,fields) => updateFlock(activeFarm.id,fid,fields)}
          onAddBirds={(fid,count,date,note) => addBirds(activeFarm.id,fid,count,date,note)}
          onDeleteAddition={(aid) => deleteAddition(activeFarm.id,aid)}
          onSaveDailyLog={(fid,entry) => saveDailyLog(activeFarm.id,fid,entry)}
          onDeleteDailyLog={(fid,date) => deleteDailyLog(activeFarm.id,fid,date)}
        />
      )}
      {screen==="eggStock" && activeFarm && p.canView("eggstock") && (
        <EggStock farm={activeFarm} perms={modPerms(p, "eggstock")} reportError={reportError} />
      )}
      {screen==="packingStock" && activeFarm && p.canView("packing") && (
        <PackingStock farm={activeFarm} allFarms={farms} perms={modPerms(p, "packing")} reportError={reportError} />
      )}
      {screen==="feedStock" && activeFarm && p.canView("feed") && (
        <FeedStock farm={activeFarm} allFarms={farms} perms={modPerms(p, "feed")} reportError={reportError} />
      )}
      </div>{/* end keyed screen wrapper */}
      </div>{/* end app-container */}

      {/* Floating Tomorrow button (super-admin only — vaccination feature) */}
      {p.canView("vaccination") && (
      <button onClick={() => setShowTomorrow(true)} style={{
        position:"fixed", bottom:24, right:20, zIndex:50,
        background: tomorrowCount>0 ? "linear-gradient(135deg,#2a5a10,#1a4a08)" : "linear-gradient(135deg,#1a2e1a,#0f1e0f)",
        border:`2px solid ${tomorrowCount>0?"#6abf2a":"#2a4a2a"}`,
        borderRadius:18, padding:"12px 18px",
        display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontFamily:"inherit",
        boxShadow: tomorrowCount>0 ? "0 6px 30px rgba(80,180,30,0.35)" : "0 4px 20px rgba(0,0,0,0.4)",
      }}>
        <span style={{ fontSize:20 }}>📅</span>
        <div style={{ textAlign:"left" }}>
          <div style={{ fontSize:12, fontWeight:"bold", color:tomorrowCount>0?"#a8e060":"#4a6a4a" }}>Tomorrow</div>
          <div style={{ fontSize:10, color:tomorrowCount>0?"#6abf2a":"#3a5a3a" }}>{tomorrowCount>0?`${tomorrowCount} vaccine${tomorrowCount>1?"s":""} due`:"All clear"}</div>
        </div>
        {tomorrowCount>0 && <span style={{ background:"#e05020", color:"#fff", fontSize:12, fontWeight:"bold", borderRadius:20, minWidth:22, height:22, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 5px", animation:"pulse 2s infinite" }}>{tomorrowCount}</span>}
      </button>
      )}

      {showTomorrow && (
        <TomorrowPanel
          farms={farms}
          onClose={() => setShowTomorrow(false)}
          onMarkDone={p.canManage("vaccination") ? ((farmId,flockId,vid,date) => markDone(farmId,flockId,vid,date)) : null}
          onOpenVaccine={openVaccineFocus}
        />
      )}

      {showNotifPanel && (
        <NotificationPanel
          farms={farms}
          isSuper={p.canView("vaccination")}
          onClose={() => setShowNotifPanel(false)}
        />
      )}

      {showSettings && p.canManageUsers && (
        <SettingsPanel
          currentUser={currentUser}
          onClose={() => setShowSettings(false)}
          onError={(msg) => setServerError(msg)}
        />
      )}

      <style>{`
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes slideInRight { from{transform:translateX(100%);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes slideUp { from{transform:translateY(100%);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes pulse { 0%,100%{box-shadow:0 0 0 0 rgba(224,80,32,0.5)} 50%{box-shadow:0 0 0 5px rgba(224,80,32,0)} }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.5) sepia(1) hue-rotate(90deg); }
      `}</style>
    </div>
  );
}

// ─── FlockRecords Helpers ─────────────────────────────────────────────────────
function fmtHatch(dateStr) {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  // DD/MM/YYYY (full year) for consistency across the app.
  return `${d.padStart(2,"0")}/${m.padStart(2,"0")}/${y}`;
}
function flockAge(hatchDate) {
  if (!hatchDate) return "—";
  return Math.floor((new Date() - new Date(hatchDate+"T00:00:00")) / 86400000);
}
function flockTotalMort(flock, upToDate) {
  // Prior mortality is the total deaths AS OF the "added-on" date (inclusive) —
  // for flocks entered mid-life. Daily-log entries dated ON or BEFORE the added-on
  // date are already included in that prior figure, so they're reference-only and
  // must NOT be re-counted. Only deaths logged AFTER the added-on date add to the total.
  const prior = flock.priorMortality || 0;
  const cutoff = flock.addedOn || null;   // YYYY-MM-DD; deaths after this count toward total
  const logged = (flock.dailyLogs||[])
    .filter(e => {
      if (cutoff && e.date <= cutoff) return false;     // on/before added-on → already in prior
      if (upToDate && e.date > upToDate) return false;  // respect the as-of date for views
      return true;
    })
    .reduce((s, e) => s + (e.mortality||0), 0);
  return prior + logged;
}
function flockBalance(flock, upToDate) {
  return Math.max(0, (flock.initialSize||0) - flockTotalMort(flock, upToDate));
}
// ── Per-flock feed cost allocation ────────────────────────────────────────────
// Feed is MADE per category (layer / grower / chick), never per flock, so a
// flock's share has to be apportioned. We split each day's kg and cost across
// the flocks of that stage in proportion to their bird counts that day
// ("bird-days"): 20,000 layers take twice the share of 10,000 for the same day.
// Category totals are therefore exact; per-flock figures are a fair allocation,
// not measured intake — two same-size layer flocks will always read the same.
function allocateFeedToFlocks(farm, byDay, fromISO, toISO) {
  const dayMap = {};
  (byDay || []).forEach(d => { dayMap[d.date] = d; });
  const per = {};                      // flockId -> { kg, cost, birdDays }
  let unallocKg = 0, unallocCost = 0;  // feed made for a stage with no birds
  let days = 0;
  let d = fromISO;
  for (let guard = 0; guard < 800 && d <= toISO; guard++) {
    days++;
    const info = dayMap[d];
    const byStage = { layer: [], grower: [], chick: [] };
    (farm.flocks || []).forEach(fl => {
      if (!flockOnFarmOn(fl, d)) return;
      const bal = flockBalance(fl, d);
      if (bal <= 0) return;
      const st = flockStageOn(fl, d);
      (byStage[st] || byStage.grower).push({ id: fl.id, bal });
    });
    ["layer", "grower", "chick"].forEach(cat => {
      const list = byStage[cat];
      const totalBirds = list.reduce((s, x) => s + x.bal, 0);
      const kg = info && info.kgByCat ? (info.kgByCat[cat] || 0) : 0;
      const cost = info && info.costByCat ? (info.costByCat[cat] || 0) : 0;
      if (totalBirds <= 0) { unallocKg += kg; unallocCost += cost; }
      list.forEach(x => {
        const rec = per[x.id] || (per[x.id] = { kg: 0, cost: 0, birdDays: 0, daysPresent: 0, catDays: {} });
        rec.birdDays += x.bal;
        rec.daysPresent += 1;
        rec.catDays[cat] = (rec.catDays[cat] || 0) + 1;   // which feed it ate, and for how long
        if (totalBirds > 0) { rec.kg += kg * (x.bal / totalBirds); rec.cost += cost * (x.bal / totalBirds); }
      });
    });
    d = nextISO(d);
  }
  return { per, days, unallocKg, unallocCost };
}

// Which feed a flock actually drew from over the window: "Layer", or
// "Grower→Layer" when it changed stage part-way. This is the usual reason one
// flock's g/bird/day differs from the rest — different feed, different rate.
function feedCatSummary(catDays) {
  const order = ["chick", "grower", "layer"];
  const used = order.filter(c => (catDays || {})[c] > 0);
  if (used.length === 0) return "—";
  const name = { chick: "Chick", grower: "Grower", layer: "Layer" };
  return used.map(c => name[c]).join("→");
}

// Monitor slab: what each flock ate and what it cost, over the selected range.
function FlockFeedCostSlab({ farm, from, to }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true); setFailed(false);
    apiCall(`/farms/${farm.id}/feed-usage-range?from=${from}&to=${to}`)
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) { setData(null); setFailed(true); } })
      .then(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [farm.id, from, to]);

  const rows = useMemo(() => {
    if (!data) return null;
    // Allocation runs over EVERY flock, including ones since sold — their birds
    // ate real feed on the days they were here, so leaving them out would inflate
    // everyone else's share. They're only hidden from the table below.
    const { per, days, unallocKg, unallocCost } = allocateFeedToFlocks(farm, data.byDay, from, to);
    let soldKg = 0, soldCost = 0, soldCount = 0;
    (farm.flocks || []).forEach(fl => {
      if (fl.soldOn && per[fl.id] && per[fl.id].birdDays > 0) { soldKg += per[fl.id].kg; soldCost += per[fl.id].cost; soldCount++; }
    });
    const list = (farm.flocks || [])
      .filter(fl => !fl.soldOn && per[fl.id] && per[fl.id].birdDays > 0)
      .map(fl => {
        const r = per[fl.id];
        return {
          id: fl.id, flock: fl,
          label: flockLabelOn(farm.flocks, fl, from),
          birdType: fl.birdType,
          soldOn: fl.soldOn,
          avgBirds: r.birdDays / Math.max(1, days),
          kg: r.kg, cost: r.cost, birdDays: r.birdDays,
          daysPresent: r.daysPresent, catLabel: feedCatSummary(r.catDays),
          partial: r.daysPresent < days,
          gPerBirdDay: r.birdDays > 0 ? (r.kg * 1000) / r.birdDays : 0,
          rsPerBirdDay: r.birdDays > 0 ? r.cost / r.birdDays : 0,
          rsPerKg: r.kg > 1e-9 ? r.cost / r.kg : 0,
        };
      })
      // Serial order: oldest flock first, matching the Flock 1, 2, 3… numbering.
      .sort((a, b) => {
        const ah = (a.flock.hatchDate || "9999-99-99"), bh = (b.flock.hatchDate || "9999-99-99");
        if (ah !== bh) return ah < bh ? -1 : 1;
        return (a.id || 0) - (b.id || 0);
      });
    return { list, days, unallocKg, unallocCost, soldKg, soldCost, soldCount };
  }, [data, farm, from, to]);

  const A = { accent:"#7ad0e8", dark:"#08181e", border:"#1e3a44" };
  const th  = { textAlign:"left", fontSize:8.5, color:"#5a8a96", fontWeight:700, letterSpacing:0.4, textTransform:"uppercase", padding:"6px 7px", borderBottom:`1px solid ${A.border}`, whiteSpace:"nowrap" };
  const thR = { ...th, textAlign:"right" };
  const td  = { fontSize:11.5, color:"#d8ecf0", padding:"7px 7px", borderBottom:"1px solid #12262c", verticalAlign:"middle" };
  const tdR = { ...td, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };
  const tf  = { fontSize:11.5, fontWeight:"bold", color:"#a8e4f0", padding:"8px 7px", borderTop:`1.5px solid ${A.accent}55` };
  const tfR = { ...tf, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };

  const list = rows ? rows.list : [];
  const tKg = list.reduce((s,r) => s + r.kg, 0);
  const tCost = list.reduce((s,r) => s + r.cost, 0);
  const tBirdDays = list.reduce((s,r) => s + r.birdDays, 0);

  return (
    <div style={{ background:"#0a1418", border:`1px solid ${A.border}`, borderRadius:14, padding:"14px 16px", marginTop:14 }}>
      <div style={{ fontSize:11, color:A.accent, letterSpacing:1, textTransform:"uppercase", fontWeight:700, marginBottom:3 }}>🍽️ Feed Cost per Flock</div>
      <div style={{ fontSize:9.5, color:"#4a7a86", marginBottom:10 }}>
        Feed is made per category, so each flock's share is apportioned by its bird numbers each day.
      </div>

      {busy && <div style={{ fontSize:12, color:"#4a7a86", padding:"8px 0" }}>Loading…</div>}
      {!busy && failed && <div style={{ fontSize:11.5, color:"#4a7a86" }}>Feed data unavailable.</div>}
      {!busy && !failed && list.length===0 && (
        <div style={{ fontSize:11.5, color:"#4a7a86" }}>No feed recorded for these dates — enter units in the Feed module's Daily tab.</div>
      )}

      {!busy && !failed && list.length>0 && (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:520 }}>
            <thead>
              <tr>
                <th style={th}>Flock</th>
                <th style={th}>Feed</th>
                <th style={thR}>Days</th>
                <th style={thR}>Birds avg</th>
                <th style={thR}>Feed kg</th>
                <th style={thR}>g/bird/day</th>
                <th style={thR}>₹/kg</th>
                <th style={thR}>Cost</th>
                <th style={thR}>₹/bird/day</th>
              </tr>
            </thead>
            <tbody>
              {list.map(r => (
                <tr key={r.id}>
                  <td style={td}>
                    🐥 {r.label}{r.soldOn && <span style={{ fontSize:9, color:"#e0a040" }}> · sold</span>}
                    <BirdTypeTag type={r.birdType} size={8} />
                  </td>
                  <td style={{ ...td, color: r.catLabel==="Layer" ? "#8ab8c4" : "#e0b060", fontSize:10.5 }}>{r.catLabel}</td>
                  <td style={{ ...tdR, color: r.partial ? "#e0b060" : "#5a8a96", fontSize:10.5 }}>{r.daysPresent}/{rows.days}</td>
                  <td style={{ ...tdR, color:"#8ab8c4" }}>{Math.round(r.avgBirds).toLocaleString()}</td>
                  <td style={tdR}>{Math.round(r.kg).toLocaleString()}</td>
                  <td style={{ ...tdR, color:"#7dd45a", fontWeight:600 }}>{r.gPerBirdDay>0 ? Math.round(r.gPerBirdDay).toLocaleString() : "—"}</td>
                  <td style={{ ...tdR, color:"#8ab8c4" }}>{r.rsPerKg>0 ? `₹${r.rsPerKg.toFixed(2)}` : "—"}</td>
                  <td style={{ ...tdR, color:"#e8c878", fontWeight:600 }}>₹{Math.round(r.cost).toLocaleString()}</td>
                  <td style={{ ...tdR, color:"#e8c878" }}>{r.rsPerBirdDay>0 ? `₹${r.rsPerBirdDay.toFixed(2)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={tf}>FARM TOTAL</td>
                <td style={tf}></td>
                <td style={tfR}></td>
                <td style={tfR}>{rows.days>0 ? Math.round(tBirdDays/rows.days).toLocaleString() : "—"}</td>
                <td style={tfR}>{Math.round(tKg).toLocaleString()}</td>
                <td style={tfR}>{tBirdDays>0 ? Math.round((tKg*1000)/tBirdDays).toLocaleString() : "—"}</td>
                <td style={tfR}>{tKg>1e-9 ? `₹${(tCost/tKg).toFixed(2)}` : "—"}</td>
                <td style={{ ...tfR, color:"#e8c878" }}>₹{Math.round(tCost).toLocaleString()}</td>
                <td style={{ ...tfR, color:"#e8c878" }}>{tBirdDays>0 ? `₹${(tCost/tBirdDays).toFixed(2)}` : "—"}</td>
              </tr>
            </tfoot>
          </table>
          <div style={{ fontSize:9, color:"#3a6a76", marginTop:8, lineHeight:1.5 }}>
            Over {rows.days} day{rows.days!==1?"s":""} · priced FIFO from the feed actually made in this window.
            {(() => {
              const cats = new Set(list.map(r => r.catLabel));
              const partial = list.filter(r => r.partial).length;
              if (cats.size <= 1 && partial === 0) return null;
              return (
                <span style={{ color:"#8ab8c4" }}>
                  {" "}Flocks on the same feed for the whole window always show the same g/bird/day.
                  {cats.size > 1 && " A different figure means a different feed — check the Feed column."}
                  {partial > 0 && " A flock present for only part of the window is averaged over its own days, so it can differ too."}
                </span>
              );
            })()}
            {rows.soldCount > 0 && (
              <span style={{ color:"#8ab8c4" }}>
                {" "}{rows.soldCount} sold flock{rows.soldCount!==1?"s are":" is"} not listed; the {Math.round(rows.soldKg).toLocaleString()} kg (₹{Math.round(rows.soldCost).toLocaleString()}) eaten before the sale is excluded from the total but still counted when working out every other flock's share.
              </span>
            )}
            {rows.unallocKg > 0.5 && (
              <span style={{ color:"#c08850" }}> ⚠️ {Math.round(rows.unallocKg).toLocaleString()} kg (₹{Math.round(rows.unallocCost).toLocaleString()}) was made for a category with no birds on those days, so it isn't charged to any flock.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Feed intake per bird ──────────────────────────────────────────────────────
// Feed MADE per category (kg, from the Feed module's FIFO engine) divided by the
// birds of that stage, day by day. Working per day matters: bird numbers change
// with mortality and a flock's stage changes as it starts laying, so a single
// end-of-window headcount would skew the average. Days with no feed entry are
// still counted in the divisor — feed made in batches covers the days between.
function FeedPerBirdCard({ farm }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const today = todayISO();
  const from = useMemo(() => { let d = today; for (let i = 1; i < days; i++) d = prevISO(d); return d; }, [today, days]);

  useEffect(() => {
    let alive = true;
    setBusy(true); setFailed(false);
    apiCall(`/farms/${farm.id}/feed-usage-range?from=${from}&to=${today}`)
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) { setData(null); setFailed(true); } })
      .then(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [farm.id, from, today]);

  const rows = useMemo(() => {
    if (!data) return [];
    const kgByCat = {}, birdDays = {}, fedDays = {};
    FEED_CATS_UI.forEach(c => { kgByCat[c.key] = 0; birdDays[c.key] = 0; fedDays[c.key] = 0; });
    const dayKg = {};
    (data.byDay || []).forEach(d => { dayKg[d.date] = d.kgByCat || {}; });
    let cur = from;
    for (let i = 0; i < days; i++) {
      const birds = farmBirdsByStageOn(farm, cur);
      FEED_CATS_UI.forEach(c => {
        const kg = (dayKg[cur] && dayKg[cur][c.key]) || 0;
        kgByCat[c.key] += kg;
        birdDays[c.key] += birds[c.key] || 0;   // bird-days: the correct divisor
        if (kg > 0) fedDays[c.key] += 1;
      });
      cur = nextISO(cur);
    }
    return FEED_CATS_UI.map(c => {
      const kg = kgByCat[c.key], bd = birdDays[c.key];
      return {
        key: c.key, label: c.label, emoji: c.emoji, kg, birdDays: bd, fedDays: fedDays[c.key],
        gramsPerBirdDay: bd > 0 ? (kg * 1000) / bd : 0,
        avgBirds: bd / days,
      };
    }).filter(r => r.kg > 0 || r.avgBirds >= 1);
  }, [data, farm, from, days]);

  const card = { background:"#0c180a", border:"1px solid #1e3418", borderRadius:16, padding:"14px 16px" };
  const capLbl = { fontSize:9, color:"#4a7040", letterSpacing:1, textTransform:"uppercase", fontWeight:700 };
  const totalKg = rows.reduce((s,r) => s + r.kg, 0);

  return (
    <div style={card}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:9 }}>
        <span style={capLbl}>🍽️ Feed per Bird</span>
        <div style={{ display:"flex", gap:4 }}>
          {[[1,"Today"],[7,"7d"],[30,"30d"]].map(([d,l]) => (
            <button key={d} onClick={()=>setDays(d)} style={{
              padding:"3px 8px", borderRadius:7, cursor:"pointer", fontFamily:"inherit", fontSize:10,
              background: days===d?"#1a3a14":"transparent", border:`1px solid ${days===d?"#3a7a2a":"#1e3418"}`,
              color: days===d?"#a8f080":"#4a6a40", fontWeight: days===d?700:400,
            }}>{l}</button>
          ))}
        </div>
      </div>
      {busy && <div style={{ fontSize:12, color:"#4a6a40" }}>Loading…</div>}
      {!busy && failed && <div style={{ fontSize:11.5, color:"#4a6a40" }}>Feed data unavailable.</div>}
      {!busy && !failed && rows.length===0 && (
        <div style={{ fontSize:11.5, color:"#4a6a40" }}>No feed recorded {days===1?"today":`in the last ${days} days`}. Enter units in the Feed module's Daily tab.</div>
      )}
      {!busy && !failed && rows.length>0 && (
        <>
          {rows.map(r => (
            <div key={r.key} style={{ padding:"7px 0", borderBottom:"1px solid #16281380" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8 }}>
                <span style={{ fontSize:12.5, color:"#b0c8a0" }}>{r.emoji} {r.label}</span>
                <span style={{ fontVariantNumeric:"tabular-nums" }}>
                  <strong style={{ fontSize:16, color: r.gramsPerBirdDay>0 ? "#7dd45a" : "#4a6a40" }}>
                    {r.gramsPerBirdDay>0 ? Math.round(r.gramsPerBirdDay).toLocaleString() : "—"}
                  </strong>
                  <span style={{ fontSize:9.5, color:"#4a7040" }}> g/bird/day</span>
                </span>
              </div>
              <div style={{ fontSize:9.5, color:"#4a6a40", marginTop:1 }}>
                {Math.round(r.kg).toLocaleString()} kg made · {Math.round(r.avgBirds).toLocaleString()} birds avg
                {r.kg>0 && days>1 && r.fedDays<days ? ` · made on ${r.fedDays}/${days} days` : ""}
              </div>
            </div>
          ))}
          <div style={{ fontSize:9.5, color:"#3a5a34", marginTop:7, lineHeight:1.45 }}>
            Based on feed <em>made</em> per category ÷ birds of that stage. Total {Math.round(totalKg).toLocaleString()} kg {days===1?"today":`over ${days} days`}.
          </div>
        </>
      )}
    </div>
  );
}

// ── FARM DASHBOARD ────────────────────────────────────────────────────────────
// The farm landing screen: overview stats, production/mortality chart, today's
// entry status, reorder alerts, then a compact module launcher. Built as a
// widget grid so new panels can be dropped in without reshuffling the layout.
function FarmDashboard({ farm, perms: pm = {}, eggInfo, modules = [] }) {
  const [days, setDays] = useState(14);
  const [metric, setMetric] = useState("eggs");
  const today = todayISO();

  const series = useMemo(() => farmDailySeries(farm, days, today), [farm, days, today]);

  const tally = useMemo(() => {
    let chick = 0, grower = 0, layer = 0, active = 0;
    (farm.flocks || []).forEach(fl => {
      if (!flockActiveNow(fl)) return;
      active++;
      const bal = flockBalance(fl);
      const st = flockStage(fl);
      if (st === "chick") chick += bal; else if (st === "grower") grower += bal; else layer += bal;
    });
    return { chick, grower, layer, active, total: chick + grower + layer };
  }, [farm]);

  const todayRow = series[series.length - 1] || { eggs:0, trays:0, mortality:0, logged:0 };
  // Trend: recent half vs earlier half, counting only days that were actually logged.
  const trend = useMemo(() => {
    const logged = series.filter(p => p.logged > 0);
    if (logged.length < 4) return { dir:0, pct:0, enough:false };
    const half = Math.floor(logged.length / 2);
    const avg = a => a.reduce((s,x) => s + x.trays, 0) / (a.length || 1);
    const o = avg(logged.slice(0, half)), r = avg(logged.slice(half));
    if (o <= 0) return { dir:0, pct:0, enough:false };
    const pct = ((r - o) / o) * 100;
    return { dir: pct > 1 ? 1 : pct < -1 ? -1 : 0, pct: Math.abs(pct), enough:true };
  }, [series]);

  const periodEggs = series.reduce((s,p) => s + p.eggs, 0);
  const periodMort = series.reduce((s,p) => s + p.mortality, 0);
  // Lay % = eggs laid today against the laying flock (layers only).
  const layPct = tally.layer > 0 ? (todayRow.eggs / tally.layer) * 100 : 0;

  const card = { background:"#0c180a", border:"1px solid #1e3418", borderRadius:16, padding:"14px 16px" };
  const capLbl = { fontSize:9, color:"#4a7040", letterSpacing:1, textTransform:"uppercase", fontWeight:700 };
  // Each widget belongs to a module; a Manager only sees widgets for modules they hold.
  const cv = (m) => (pm && typeof pm.canView === "function") ? pm.canView(m) : true;
  const showProd = cv("production"), showEgg = cv("eggstock"), showFeed = cv("feed");
  const entryMods = ["production", "eggstock", "feed"].filter(cv);
  const hasReorder = showFeed && eggInfo && eggInfo.reorder && eggInfo.reorder.length > 0;
  const showSide = showProd || showFeed || entryMods.length > 0 || hasReorder;
  const pill = (on) => ({
    padding:"4px 9px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:11,
    background:on?"#1a3a14":"transparent", border:`1px solid ${on?"#3a7a2a":"#1e3418"}`,
    color:on?"#a8f080":"#4a6a40", fontWeight:on?700:400,
  });

  const statTile = (label, value, sub, color) => (
    <div style={{ ...card, padding:"12px 14px" }}>
      <div style={capLbl}>{label}</div>
      <div style={{ fontSize:24, fontWeight:"bold", color, lineHeight:1.2, marginTop:3 }}>{value}</div>
      {sub && <div style={{ fontSize:10.5, color:"#4a6a40", marginTop:1 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding:"18px 16px 40px", animation:"fadeUp 0.35s ease" }}>
      {/* Overview stats */}
      {(showProd || showEgg) && (
      <div className="stat-grid" style={{ marginBottom:12 }}>
        {showProd && statTile("Total Birds", tally.total.toLocaleString(), `${tally.active} active flock${tally.active!==1?"s":""}`, "#c8f0a8")}
        {showProd && statTile("Today's Eggs", todayRow.logged ? Math.floor(todayRow.trays).toLocaleString() : "—",
          todayRow.logged ? `trays${layPct>0?` · ${layPct.toFixed(1)}% lay`:""}` : "not logged yet", "#e8c878")}
        {showEgg && statTile("Eggs in Stock", eggInfo ? Math.round(eggInfo.closing||0).toLocaleString() : "—", "trays in store", "#f0c890")}
        {showProd && statTile("Deaths · " + days + "d", periodMort.toLocaleString(), `${(periodMort/Math.max(1,days)).toFixed(1)}/day avg`, periodMort>0?"#e0954a":"#6a9050")}
      </div>
      )}

      {(showProd || showSide) && (
      <div className="dash-grid" style={{ marginBottom:12 }}>
        {/* Production chart */}
        {showProd && (
        <div style={card}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:10, flexWrap:"wrap" }}>
            <div>
              <div style={capLbl}>{metric==="eggs" ? "Egg Production" : "Mortality"}</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:2 }}>
                <span style={{ fontSize:20, fontWeight:"bold", color: metric==="eggs" ? "#7dd45a" : "#e0954a" }}>
                  {metric==="eggs" ? `${Math.floor(periodEggs/30).toLocaleString()} trays` : `${periodMort.toLocaleString()} birds`}
                </span>
                {metric==="eggs" && trend.enough && (
                  <span style={{ fontSize:11, fontWeight:600, color: trend.dir>0?"#7dd45a":trend.dir<0?"#e0954a":"#8a9a80" }}>
                    {trend.dir>0?"▲":trend.dir<0?"▼":"▬"} {trend.pct.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
              <button onClick={()=>setMetric("eggs")} style={pill(metric==="eggs")}>🥚 Eggs</button>
              <button onClick={()=>setMetric("mortality")} style={pill(metric==="mortality")}>💀 Deaths</button>
              <span style={{ width:8 }} />
              {[7,14,30].map(d => <button key={d} onClick={()=>setDays(d)} style={pill(days===d)}>{d}d</button>)}
            </div>
          </div>
          <DashChart series={series} metric={metric} />
          {metric==="eggs" && trend.enough && (
            <div style={{ fontSize:10.5, color:"#4a6a40", marginTop:8, borderTop:"1px solid #1a2e18", paddingTop:8 }}>
              {trend.dir>0 ? `Production is trending up ${trend.pct.toFixed(1)}% vs the earlier half of this window.`
                : trend.dir<0 ? `Production is trending down ${trend.pct.toFixed(1)}% vs the earlier half of this window.`
                : "Production is holding steady across this window."}
            </div>
          )}
          {!trend.enough && metric==="eggs" && (
            <div style={{ fontSize:10.5, color:"#4a6a40", marginTop:8, borderTop:"1px solid #1a2e18", paddingTop:8 }}>Log a few more days to see a trend. Faint marks along the base are days with no entry.</div>
          )}
        </div>

        )}

        {/* Side column: flock mix + entries + reorder */}
        {showSide && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {showProd && (
          <div style={card}>
            <div style={{ ...capLbl, marginBottom:8 }}>Flock Mix</div>
            {tally.total === 0 ? (
              <div style={{ fontSize:12, color:"#4a6a40" }}>No active birds recorded.</div>
            ) : (
              <>
                <div style={{ display:"flex", height:8, borderRadius:5, overflow:"hidden", background:"#132010", marginBottom:9 }}>
                  {[["#e8c878",tally.layer],["#7dd45a",tally.grower],["#5ab8e0",tally.chick]].map(([c,v],i) => (
                    v>0 ? <div key={i} style={{ width:`${(v/tally.total)*100}%`, background:c }} /> : null
                  ))}
                </div>
                {[["🥚 Layers",tally.layer,"#e8c878"],["🐓 Growers",tally.grower,"#7dd45a"],["🐤 Chicks",tally.chick,"#5ab8e0"]].map(([l,v,c]) => (
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"2.5px 0" }}>
                    <span style={{ color:"#8aa880" }}>{l}</span>
                    <span style={{ color:c, fontWeight:600, fontVariantNumeric:"tabular-nums" }}>{v.toLocaleString()}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          )}

          {showFeed && <FeedPerBirdCard farm={farm} />}

          {entryMods.length > 0 && <EntryStatusPanel farmId={farm.id} todayInfo={eggInfo && eggInfo.entry} embedded allowed={entryMods} />}

          {hasReorder && (
            <div style={{ background:"#1a0e06", border:"1px solid #6a4410", borderRadius:16, padding:"12px 14px" }}>
              <div style={{ fontSize:9, color:"#e0a850", letterSpacing:1, textTransform:"uppercase", fontWeight:700, marginBottom:6 }}>🔔 Reorder ({eggInfo.reorder.length})</div>
              {eggInfo.reorder.map(r => (
                <div key={r.item_id} style={{ display:"flex", justifyContent:"space-between", fontSize:11.5, padding:"3px 0" }}>
                  <span style={{ color:"#f0d0a0" }}>{r.kind==="medicine"?"💊":"🌾"} {r.name}{r.ordered && <span style={{ color:"#7ad0a0", fontSize:9 }}> 🛒</span>}</span>
                  <span style={{ color:"#e89060", fontVariantNumeric:"tabular-nums" }}>{Math.round(r.qty).toLocaleString()}/{Math.round(r.minQty).toLocaleString()} kg</span>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
      )}

      {/* Module launcher — compact tiles */}
      <div style={{ ...capLbl, margin:"18px 2px 9px" }}>Modules</div>
      <div className="mod-grid">
        {modules.map((mod, i) => (
          <div key={mod.key} className="mod-tile lift" onClick={mod.action}
            style={{ background:"#0c180a", border:`1px solid ${mod.color.border}`, borderRadius:14, cursor:"pointer", overflow:"hidden", padding:"12px 12px 11px", animation:`fadeUp 0.3s ease ${i*0.05}s both` }}>
            <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:6 }}>
              <div style={{ width:32, height:32, background:mod.color.dark, border:`1px solid ${mod.color.border}`, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, flexShrink:0 }}>{mod.icon}</div>
              <span style={{ color:mod.color.accent, fontSize:16, marginLeft:"auto", flexShrink:0 }}>›</span>
            </div>
            <div style={{ fontSize:13, fontWeight:700, color:mod.color.accent, lineHeight:1.25 }}>{mod.title}</div>
            <div style={{ fontSize:10, color:"#4a6a40", marginTop:3, lineHeight:1.4 }}>{mod.stats}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══ PRINT / SAVE-AS-PDF REPORTS ═════════════════════════════════════════════
// Every monitor tab can turn its selected date range into a printable report.
// The report is a plain HTML document opened in its own window with the
// browser's print dialog, so "Save as PDF" (PC) or Share → Save to Files (iPad)
// produce a real, searchable PDF with no extra software. Paper is white to save
// ink; the app itself keeps its dark theme.
const rptEsc = (v) => String(v == null ? "" : v).replace(/[&<>"']/g, c => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[c]));
const rptN = (n, d = 0) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
const rptRs = (n, d = 0) => "₹" + rptN(n, d);
const rptTE = (eggs) => { const e = Math.round(Number(eggs) || 0); const t = Math.floor(e / 30), r = e % 30; return r > 0 ? `${rptN(t)}T +${r}` : `${rptN(t)}T`; };
const rptPct = (v, d = 1) => (isFinite(v) && v > 0 ? `${v.toFixed(d)}%` : "—");
const rptDash = (v, fmt = rptN) => (Number(v) ? fmt(v) : "–");
const rptWeekday = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short" }); } catch (e) { return ""; } };
const rptDate = (iso) => ({ v: formatDate(iso), sub: rptWeekday(iso) });
function rptDaysInclusive(from, to) {
  const a = new Date(from + "T00:00:00"), b = new Date(to + "T00:00:00");
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

// One table cell: a plain value, or { v, sub, bold, muted }.
function rptCell(c, align) {
  const o = (c && typeof c === "object") ? c : { v: c };
  const cls = [align === "right" ? "r" : "", o.bold ? "b" : "", o.muted ? "m" : ""].filter(Boolean).join(" ");
  return `<td${cls ? ` class="${cls}"` : ""}>${rptEsc(o.v)}${o.sub ? ` <span class="sub">${rptEsc(o.sub)}</span>` : ""}</td>`;
}
function rptSection(s) {
  const head = s.title ? `<h2${s.big ? ' class="big"' : ""}>${rptEsc(s.title)}</h2>` : "";
  const note = s.note ? `<p class="note">${rptEsc(s.note)}</p>` : "";
  const brk = s.breakBefore ? " brk" : "";
  if (s.type === "stats") {
    const items = (s.items || []).map(it =>
      `<div class="stat"><div class="sl">${rptEsc(it.label)}</div><div class="sv">${rptEsc(it.value)}</div>${it.sub ? `<div class="ss">${rptEsc(it.sub)}</div>` : ""}</div>`).join("");
    return `<section class="${brk}">${head}<div class="stats">${items}</div>${note}</section>`;
  }
  if (s.type === "table") {
    const cols = s.columns || [];
    const th = cols.map(c => `<th${c.align === "right" ? ' class="r"' : ""}>${rptEsc(c.label)}</th>`).join("");
    const rows = (s.rows || []);
    const body = rows.length
      ? rows.map(r => `<tr>${r.map((c, i) => rptCell(c, (cols[i] || {}).align)).join("")}</tr>`).join("")
      : `<tr><td colspan="${cols.length}" class="m">${rptEsc(s.empty || "No entries in this range.")}</td></tr>`;
    const foot = (s.footer && rows.length)
      ? `<tfoot><tr>${s.footer.map((c, i) => rptCell(c, (cols[i] || {}).align)).join("")}</tr></tfoot>` : "";
    return `<section class="${brk}">${head}<table><thead><tr>${th}</tr></thead><tbody>${body}</tbody>${foot}</table>${note}</section>`;
  }
  if (s.type === "text") {
    return `<section class="${brk}">${head}${(s.lines || []).map(l => `<p class="txt">${rptEsc(l)}</p>`).join("")}</section>`;
  }
  return "";
}

function buildReportHTML(r) {
  const days = rptDaysInclusive(r.from, r.to);
  const generated = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const docTitle = `${r.farm} - ${r.title} ${formatDate(r.from).replace(/\//g, "-")} to ${formatDate(r.to).replace(/\//g, "-")}`;
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${rptEsc(docTitle)}</title>
<style>
  @page { size: A4 ${r.landscape ? "landscape" : "portrait"}; margin: 12mm 10mm 13mm 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; background: #e9ece6; }
  body { font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; color: #1d231b; font-size: 10.5px; line-height: 1.4; }
  .bar { position: sticky; top: 0; z-index: 2; background: #14261a; color: #e8f4e0; padding: 10px 16px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
  .bar b { font-size: 13px; }
  .bar span { font-size: 11.5px; opacity: .8; flex: 1; min-width: 200px; }
  .bar button { background: #3a8a2a; color: #fff; border: 0; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
  .bar button.ghost { background: transparent; border: 1px solid #5a7a50; }
  .sheet { background: #fff; max-width: ${r.landscape ? "277mm" : "190mm"}; margin: 14px auto; padding: 12mm 10mm; box-shadow: 0 2px 12px rgba(0,0,0,.12); }
  @media print {
    html, body { background: #fff; }
    .bar { display: none; }
    .sheet { margin: 0; padding: 0; max-width: none; box-shadow: none; }
  }
  header.rh { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; border-bottom: 2.5px solid #2d5a22; padding-bottom: 8px; margin-bottom: 12px; }
  header.rh .k { font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase; color: #5a7a50; font-weight: 700; }
  header.rh h1 { font-size: 19px; margin: 2px 0 1px; color: #14261a; }
  header.rh .farm { font-size: 12px; color: #2d5a22; font-weight: 600; }
  header.rh .rng { text-align: right; font-size: 10.5px; color: #333; }
  header.rh .rng strong { display: block; font-size: 13px; color: #14261a; }
  section { margin: 0 0 14px; }
  section.brk { break-before: page; page-break-before: always; }
  h2 { font-size: 12px; margin: 0 0 6px; color: #1f4418; border-left: 3px solid #3a8a2a; padding-left: 7px; break-after: avoid; page-break-after: avoid; }
  h2.big { font-size: 18px; margin: 0 0 10px; padding: 8px 10px; border-left-width: 5px; background: #eef6ea; border-radius: 4px; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
  .stat { border: 1px solid #d5dccf; border-radius: 6px; padding: 6px 8px; background: #f5f8f2; break-inside: avoid; }
  .sl { font-size: 8px; letter-spacing: .5px; text-transform: uppercase; color: #5a6a52; font-weight: 700; }
  .sv { font-size: 15px; font-weight: 700; color: #14261a; margin-top: 1px; }
  .ss { font-size: 8.5px; color: #6a7a62; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
  thead { display: table-header-group; }
  tfoot { display: table-row-group; }
  th { background: #e6ede0; text-align: left; padding: 4.5px 5px; border-bottom: 1.5px solid #8fa486; font-size: 8px; text-transform: uppercase; letter-spacing: .3px; color: #2f3f2a; white-space: nowrap; }
  td { padding: 3.5px 5px; border-bottom: 1px solid #e3e8df; vertical-align: top; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  tbody tr:nth-child(even) td { background: #fafcf8; }
  tfoot td { font-weight: 700; border-top: 1.5px solid #8fa486; border-bottom: 0; background: #e6ede0; }
  .r { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .b { font-weight: 700; }
  .m { color: #8a958a; }
  .sub { color: #8a958a; font-size: 8px; font-weight: 400; }
  .note { color: #59645a; font-size: 8.5px; margin: 4px 0 0; }
  .txt { margin: 0 0 3px; }
  footer.rf { margin-top: 18px; border-top: 1px solid #cfd6ca; padding-top: 6px; color: #7a857a; font-size: 8.5px; display: flex; justify-content: space-between; gap: 10px; }
</style></head><body>
<div class="bar"><b>🖨️ ${rptEsc(r.title)}</b><span>On a PC pick “Save as PDF” as the printer. On iPad use the Share button to save the PDF.</span>
<button onclick="window.print()">Print / Save PDF</button><button class="ghost" onclick="window.close()">Close</button></div>
<div class="sheet">
<header class="rh"><div><div class="k">${rptEsc(r.module)} report</div><h1>${rptEsc(r.title)}</h1><div class="farm">🏡 ${rptEsc(r.farm)}</div></div>
<div class="rng"><strong>${rptEsc(formatDate(r.from))} → ${rptEsc(formatDate(r.to))}</strong>${days} day${days !== 1 ? "s" : ""}</div></header>
${(r.sections || []).map(rptSection).join("\n")}
<footer class="rf"><span>Poultry Manager · ${rptEsc(r.farm)}</span><span>Generated ${rptEsc(generated)}</span></footer>
</div>
<script>window.addEventListener("load", function () { setTimeout(function () { try { window.print(); } catch (e) {} }, 450); });</script>
</body></html>`;
}

// Opens the report window straight away (inside the tap, so pop-up blockers
// allow it), shows "preparing" while any data loads, then writes the report.
function openReport(build) {
  let w = null;
  try { w = window.open("", "_blank"); } catch (e) { w = null; }
  if (w) {
    try { w.document.write('<!doctype html><title>Preparing report…</title><body style="font-family:system-ui;background:#0c180a;color:#a8d48a;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">Preparing report…</body>'); } catch (e) { /* ignore */ }
  }
  Promise.resolve().then(build).then(report => {
    const html = buildReportHTML(report);
    if (w && !w.closed) {
      w.document.open(); w.document.write(html); w.document.close();
      try { w.focus(); } catch (e) { /* ignore */ }
      return;
    }
    // Pop-ups blocked: print from a hidden frame instead.
    const f = document.createElement("iframe");
    f.setAttribute("aria-hidden", "true");
    f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    document.body.appendChild(f);
    const d = f.contentWindow.document;
    d.open(); d.write(html.replace(/<script>[\s\S]*?<\/script>/, "")); d.close();
    setTimeout(() => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch (e) { /* ignore */ } setTimeout(() => f.remove(), 60000); }, 400);
  }).catch(err => {
    try { if (w && !w.closed) w.close(); } catch (e) { /* ignore */ }
    alert("Couldn't prepare the report: " + (err && err.message ? err.message : "unknown error"));
  });
}

// The button every monitor shows under its date picker.
function ReportButton({ onClick, disabled = false, accent = "#7ad0e8" }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:7, width:"100%", margin:"0 0 12px",
        padding:"10px", background:"#0b1214", border:`1px dashed ${accent}88`, borderRadius:10, color:accent,
        fontSize:13, fontWeight:600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1, fontFamily:"inherit" }}>
      🖨️ Print / Save PDF <span style={{ fontSize:10.5, fontWeight:400, opacity:0.75 }}>· detailed report for these dates</span>
    </button>
  );
}

// ── Report builders, one per monitor ──────────────────────────────────────────

// Production & Mortality. Mirrors the Monitor tab's own maths, then adds a
// per-flock summary, each flock's daily log and the feed cost per flock.
async function buildProductionReport(farm, from, to) {
  const all = farm.flocks || [];
  const sorted = [...all].sort((a, b) => ((a.hatchDate || "") < (b.hatchDate || "") ? -1 : 1));
  const inRange = sorted.filter(fl => flockPresentOn(fl, from) || (fl.soldOn && fl.soldOn > from));

  const dateSet = new Set();
  all.forEach(fl => (fl.dailyLogs || []).forEach(e => { if (e.date >= from && e.date <= to) dateSet.add(e.date); }));
  const dates = [...dateSet].sort();
  const dayRows = dates.map(d => {
    let mort = 0, eggs = 0, birds = 0;
    all.forEach(fl => {
      const entry = (fl.dailyLogs || []).find(e => e.date === d);
      if (entry) { mort += entry.mortality || 0; eggs += calcEggs(entry); }
      birds += flockPresentOn(fl, d) ? flockBalance(fl, d) : 0;
    });
    return { d, mort, eggs, birds, lay: birds > 0 ? (eggs / birds) * 100 : 0 };
  });
  const tMort = dayRows.reduce((s, r) => s + r.mort, 0);
  const tEggs = dayRows.reduce((s, r) => s + r.eggs, 0);
  const tBirdDays = dayRows.reduce((s, r) => s + r.birds, 0);
  let aliveNow = 0, mortAll = 0, initAll = 0;
  all.forEach(fl => { if (!flockActiveNow(fl)) return; aliveNow += flockBalance(fl); mortAll += flockTotalMort(fl); initAll += fl.initialSize || 0; });

  const sections = [
    { type: "stats", title: "Summary", items: [
      { label: "Days logged", value: rptN(dates.length), sub: `of ${rptDaysInclusive(from, to)} in range` },
      { label: "Eggs produced", value: rptTE(tEggs), sub: `${rptN(tEggs)} eggs` },
      { label: "Avg per logged day", value: rptTE(dates.length ? tEggs / dates.length : 0), sub: `${rptN(dates.length ? tEggs / dates.length : 0)} eggs/day` },
      { label: "Avg lay %", value: rptPct(tBirdDays > 0 ? (tEggs / tBirdDays) * 100 : 0), sub: "eggs ÷ birds, logged days" },
      { label: "Deaths in range", value: rptN(tMort), sub: `${rptN(dates.length ? tMort / dates.length : 0, 1)} per logged day` },
      { label: "Birds alive now", value: rptN(aliveNow), sub: `${inRange.length} flock${inRange.length !== 1 ? "s" : ""} in report` },
      { label: "All-time deaths", value: rptN(mortAll), sub: "active flocks" },
      { label: "All-time mortality", value: rptPct(initAll > 0 ? (mortAll / initAll) * 100 : 0), sub: "active flocks" },
    ] },
    { type: "table", title: "Whole farm — day by day",
      columns: [{ label: "Date" }, { label: "Birds", align: "right" }, { label: "Deaths", align: "right" }, { label: "Trays", align: "right" }, { label: "Eggs", align: "right" }, { label: "Lay %", align: "right" }],
      rows: dayRows.map(r => [rptDate(r.d), rptN(r.birds), rptN(r.mort), rptTE(r.eggs), rptN(r.eggs), rptPct(r.lay)]),
      footer: ["Total / avg", "", rptN(tMort), rptTE(tEggs), rptN(tEggs), rptPct(tBirdDays > 0 ? (tEggs / tBirdDays) * 100 : 0)] },
  ];

  // Per-flock summary
  const fRows = inRange.map(fl => {
    const logs = (fl.dailyLogs || []).filter(e => e.date >= from && e.date <= to).sort((a, b) => a.date.localeCompare(b.date));
    const rDeaths = logs.reduce((s, e) => s + (e.mortality || 0), 0);
    const rEggs = logs.reduce((s, e) => s + calcEggs(e), 0);
    const bd = logs.reduce((s, e) => s + flockBalance(fl, e.date), 0);
    const tm = flockTotalMort(fl);
    const meta = birdTypeMeta(fl.birdType);
    return { fl, logs, rDeaths, rEggs, row: [
      { v: flockLabelOn(all, fl, from), sub: fl.soldOn ? `sold ${formatDate(fl.soldOn)}` : "", bold: true },
      meta ? meta.label : "—",
      formatDate(fl.hatchDate), `${flockAge(fl.hatchDate)}d`,
      rptN(fl.initialSize || 0), rptN(flockBalance(fl)), rptN(rDeaths), rptN(tm),
      rptPct(fl.initialSize > 0 ? (tm / fl.initialSize) * 100 : 0),
      rptN(logs.length), rptN(logs.length ? rEggs / logs.length : 0), rptPct(bd > 0 ? (rEggs / bd) * 100 : 0),
    ] };
  });
  sections.push({ type: "table", title: "Flock summary",
    columns: [{ label: "Flock" }, { label: "Type" }, { label: "Hatched" }, { label: "Age" }, { label: "Initial", align: "right" }, { label: "Balance", align: "right" },
      { label: "Deaths (range)", align: "right" }, { label: "Deaths (all)", align: "right" }, { label: "Mort %", align: "right" },
      { label: "Days", align: "right" }, { label: "Avg eggs/day", align: "right" }, { label: "Lay %", align: "right" }],
    rows: fRows.map(f => f.row),
    footer: ["Total", "", "", "", rptN(fRows.reduce((s, f) => s + (f.fl.initialSize || 0), 0)), rptN(fRows.reduce((s, f) => s + flockBalance(f.fl), 0)),
      rptN(fRows.reduce((s, f) => s + f.rDeaths, 0)), "", "", "", "", ""],
    note: "Balance, all-time deaths and mortality % are as of today; the other columns cover the selected dates only." });

  // Each flock's daily log
  fRows.filter(f => f.logs.length).forEach((f, i) => {
    let bdSum = 0;
    const rows = f.logs.map(e => {
      const eggs = calcEggs(e), birds = flockBalance(f.fl, e.date); bdSum += birds;
      return [rptDate(e.date), rptN(birds), rptN(e.mortality || 0), rptTE(eggs), rptN(eggs), rptPct(birds > 0 ? (eggs / birds) * 100 : 0)];
    });
    const meta = birdTypeMeta(f.fl.birdType);
    sections.push({ type: "table", breakBefore: i === 0,
      title: `${flockLabelOn(all, f.fl, from)}${meta ? ` · ${meta.label}` : ""} — daily log`,
      columns: [{ label: "Date" }, { label: "Birds", align: "right" }, { label: "Deaths", align: "right" }, { label: "Trays", align: "right" }, { label: "Eggs", align: "right" }, { label: "Lay %", align: "right" }],
      rows, footer: ["Total / avg", "", rptN(f.rDeaths), rptTE(f.rEggs), rptN(f.rEggs), rptPct(bdSum > 0 ? (f.rEggs / bdSum) * 100 : 0)] });
  });

  // Feed cost per flock (same allocation and rules as the Monitor's slab)
  try {
    const fd = await apiCall(`/farms/${farm.id}/feed-usage-range?from=${from}&to=${to}`);
    const alloc = allocateFeedToFlocks(farm, fd.byDay, from, to);
    const list = sorted.filter(fl => !fl.soldOn && alloc.per[fl.id] && alloc.per[fl.id].birdDays > 0);
    if (list.length) {
      let sKg = 0, sCost = 0, sBD = 0;
      const rows = list.map(fl => {
        const r = alloc.per[fl.id]; sKg += r.kg; sCost += r.cost; sBD += r.birdDays;
        return [{ v: flockLabelOn(all, fl, from), bold: true }, feedCatSummary(r.catDays), `${r.daysPresent}/${alloc.days}`,
          rptN(r.birdDays / Math.max(1, alloc.days)), rptN(r.kg), rptN(r.birdDays > 0 ? (r.kg * 1000) / r.birdDays : 0),
          r.kg > 0 ? rptRs(r.cost / r.kg, 2) : "—", rptRs(r.cost), r.birdDays > 0 ? rptRs(r.cost / r.birdDays, 2) : "—"];
      });
      sections.push({ type: "table", breakBefore: true, title: "Feed cost per flock",
        columns: [{ label: "Flock" }, { label: "Feed" }, { label: "Days", align: "right" }, { label: "Birds avg", align: "right" }, { label: "Feed kg", align: "right" },
          { label: "g/bird/day", align: "right" }, { label: "₹/kg", align: "right" }, { label: "Cost", align: "right" }, { label: "₹/bird/day", align: "right" }],
        rows, footer: ["Farm total", "", "", rptN(sBD / Math.max(1, alloc.days)), rptN(sKg), rptN(sBD > 0 ? (sKg * 1000) / sBD : 0),
          sKg > 0 ? rptRs(sCost / sKg, 2) : "—", rptRs(sCost), sBD > 0 ? rptRs(sCost / sBD, 2) : "—"],
        note: "Feed is made per category, so each flock's share is apportioned by its bird numbers each day. Sold flocks are left out of the table but still counted when splitting the rest." });
    }
  } catch (e) { /* feed data optional — report still prints without it */ }

  return { module: "Production & Mortality", title: "Production & Mortality", farm: farm.name, from, to, sections };
}

// Egg Stock ledger: summary, the day-by-day ledger, and who the eggs were sold to.
function buildEggStockReport(farm, data, from, to) {
  const days = [...((data && data.days) || [])].sort((a, b) => a.date.localeCompare(b.date));
  const t = (data && data.totals) || {};
  const first = days[0], last = days[days.length - 1];
  const sections = [
    { type: "stats", title: "Summary (trays)", items: [
      { label: "Opening", value: rptN(first ? first.opening : 0), sub: first ? formatDate(first.date) : "" },
      { label: "Production", value: rptN(t.production), sub: "+ added" },
      { label: "Received", value: rptN(t.received), sub: "+ added" },
      { label: "Issued", value: rptN(t.issued), sub: "− removed" },
      { label: "Sale", value: rptN(t.sale), sub: "− removed" },
      { label: "Breakage", value: rptN(t.breakage), sub: "− removed" },
      { label: "Difference", value: `${(t.difference || 0) > 0 ? "+" : ""}${rptN(t.difference)}`, sub: "± corrections" },
      { label: "Closing", value: rptN(last ? last.closing : 0), sub: last ? formatDate(last.date) : "" },
    ] },
    { type: "table", title: "Daily ledger (trays)",
      columns: [{ label: "Date" }, { label: "Opening", align: "right" }, { label: "+ Prod", align: "right" }, { label: "+ Recv", align: "right" },
        { label: "− Break", align: "right" }, { label: "− Issue", align: "right" }, { label: "− Sale", align: "right" }, { label: "± Diff", align: "right" }, { label: "Closing", align: "right" }],
      rows: days.map(d => [rptDate(d.date), rptN(d.opening), rptDash(d.production), rptDash(d.received), rptDash(d.breakage), rptDash(d.issued), rptDash(d.sale),
        d.difference ? `${d.difference > 0 ? "+" : ""}${rptN(d.difference)}` : "–", { v: rptN(d.closing), bold: true }]),
      footer: ["Total", first ? rptN(first.opening) : "", rptN(t.production), rptN(t.received), rptN(t.breakage), rptN(t.issued), rptN(t.sale),
        `${(t.difference || 0) > 0 ? "+" : ""}${rptN(t.difference)}`, last ? rptN(last.closing) : ""],
      note: "Closing = opening + production + received − breakage − issued − sale ± difference." },
  ];
  // Sales by buyer
  const buyers = {};
  const register = [];
  days.forEach(d => (d.saleLines || []).forEach(l => {
    const name = l.buyer || "(no buyer)";
    const b = buyers[name] || (buyers[name] = { trays: 0, sales: 0, days: new Set() });
    b.trays += l.trays || 0; b.sales += 1; b.days.add(d.date);
    register.push([rptDate(d.date), l.buyer || { v: "(no buyer)", muted: true }, l.vehicle || "—", rptN(l.trays)]);
  }));
  const bList = Object.entries(buyers).sort((a, b) => b[1].trays - a[1].trays);
  const saleTotal = bList.reduce((s, [, b]) => s + b.trays, 0);
  sections.push({ type: "table", title: "Sales by buyer",
    columns: [{ label: "Buyer" }, { label: "Sales", align: "right" }, { label: "Days", align: "right" }, { label: "Trays", align: "right" }, { label: "Share", align: "right" }],
    rows: bList.map(([n, b]) => [{ v: n, bold: true }, rptN(b.sales), rptN(b.days.size), rptN(b.trays), rptPct(saleTotal > 0 ? (b.trays / saleTotal) * 100 : 0)]),
    footer: ["Total", rptN(register.length), "", rptN(saleTotal), saleTotal > 0 ? "100%" : ""], empty: "No sales in this range." });
  if (register.length) sections.push({ type: "table", title: "Sale register",
    columns: [{ label: "Date" }, { label: "Buyer" }, { label: "Vehicle" }, { label: "Trays", align: "right" }],
    rows: register, footer: ["Total", "", "", rptN(saleTotal)] });
  return { module: "Egg Stock", title: "Egg Stock Register", farm: farm.name, from, to, sections };
}

// Packing — loading sale: every dispatch issue, then totals by driver and route.
function buildPackSaleReport(farm, data, from, to) {
  const days = [...((data && data.days) || [])].sort((a, b) => a.date.localeCompare(b.date));
  const kinds = [["white", "White"], ["brown", "Brown"]];
  const eggsOf = (t30, t25, t6) => 30 * t30 + 25 * t25 + 6 * t6;
  const perKind = { white: { t30: 0, t25: 0, t6: 0, prod: 0, direct: 0, issues: 0 }, brown: { t30: 0, t25: 0, t6: 0, prod: 0, direct: 0, issues: 0 } };
  const register = [], byDriver = {}, byPlace = {}, daily = [];
  days.forEach(day => {
    const dRow = { date: day.date };
    kinds.forEach(([k, label]) => {
      const st = day[k]; if (!st) return;
      const pk = perKind[k];
      pk.prod += st.production || 0;
      pk.direct += (st.directSale && st.directSale.eggs) || 0;
      (st.issued || []).forEach(iss => {
        const rs = (st.returns || []).filter(r => r.issueRef === iss.id);
        const sum = f => rs.reduce((s, r) => s + (r[f] || 0), 0);
        const s30 = iss.trays30 - sum("trays30"), s25 = iss.trays25 - sum("trays25"), s6 = iss.trays6 - sum("trays6");
        const eg = eggsOf(s30, s25, s6);
        pk.t30 += s30; pk.t25 += s25; pk.t6 += s6; pk.issues += 1;
        register.push([rptDate(day.date), label, { v: `#${iss.serial}`, bold: true }, iss.vehicle || "—", iss.driver || "—", iss.place || "—",
          `${iss.trays30}/${iss.trays25}/${iss.trays6}`, rs.length ? `${sum("trays30")}/${sum("trays25")}/${sum("trays6")}` : "–",
          rptN(s30), rptN(s25), rptN(s6), rptN(eg)]);
        const dk = iss.driver || "(no driver)", pkey = iss.place || "(no destination)";
        [[byDriver, dk], [byPlace, pkey]].forEach(([map, key]) => {
          const o = map[key] || (map[key] = { trips: 0, t30: 0, t25: 0, t6: 0, eggs: 0 });
          o.trips += 1; o.t30 += s30; o.t25 += s25; o.t6 += s6; o.eggs += eg;
        });
      });
      dRow[k] = st.sale || { t30: 0, t25: 0, t6: 0 };
      dRow[k + "Prod"] = st.production || 0;
    });
    daily.push(dRow);
  });
  const sumK = k => eggsOf(perKind[k].t30, perKind[k].t25, perKind[k].t6);
  const totEggs = sumK("white") + sumK("brown");
  const groupRows = (map) => Object.entries(map).sort((a, b) => b[1].eggs - a[1].eggs)
    .map(([n, o]) => [{ v: n, bold: true }, rptN(o.trips), rptN(o.t30), rptN(o.t25), rptN(o.t6), rptN(o.eggs), rptPct(totEggs > 0 ? (o.eggs / totEggs) * 100 : 0)]);
  const groupCols = (first) => [{ label: first }, { label: "Trips", align: "right" }, { label: "30-size", align: "right" }, { label: "25-size", align: "right" },
    { label: "6-size", align: "right" }, { label: "Eggs", align: "right" }, { label: "Share", align: "right" }];
  const sections = [
    { type: "stats", title: "Summary", items: [
      { label: "White sold", value: rptTE(sumK("white")), sub: `${perKind.white.t30}/${perKind.white.t25}/${perKind.white.t6} trays (30/25/6)` },
      { label: "Brown sold", value: rptTE(sumK("brown")), sub: `${perKind.brown.t30}/${perKind.brown.t25}/${perKind.brown.t6} trays (30/25/6)` },
      { label: "Total eggs sold", value: rptN(totEggs), sub: rptTE(totEggs) },
      { label: "Dispatch issues", value: rptN(register.length), sub: `${rptN(days.length)} day${days.length !== 1 ? "s" : ""} with sales` },
      { label: "White production", value: rptTE(perKind.white.prod), sub: `${rptN(perKind.white.prod)} eggs` },
      { label: "Brown production", value: rptTE(perKind.brown.prod), sub: `${rptN(perKind.brown.prod)} eggs` },
      { label: "Direct sale (white)", value: rptTE(perKind.white.direct), sub: "walk-in" },
      { label: "Direct sale (brown)", value: rptTE(perKind.brown.direct), sub: "walk-in" },
    ] },
    { type: "table", title: "Day by day (loading sale, trays)",
      columns: [{ label: "Date" }, { label: "White 30", align: "right" }, { label: "White 25", align: "right" }, { label: "White 6", align: "right" },
        { label: "Brown 30", align: "right" }, { label: "Brown 25", align: "right" }, { label: "Brown 6", align: "right" }, { label: "Production", align: "right" }],
      rows: daily.map(d => [rptDate(d.date),
        rptDash(d.white && d.white.t30), rptDash(d.white && d.white.t25), rptDash(d.white && d.white.t6),
        rptDash(d.brown && d.brown.t30), rptDash(d.brown && d.brown.t25), rptDash(d.brown && d.brown.t6),
        rptTE((d.whiteProd || 0) + (d.brownProd || 0))]),
      footer: ["Total", rptN(perKind.white.t30), rptN(perKind.white.t25), rptN(perKind.white.t6), rptN(perKind.brown.t30), rptN(perKind.brown.t25), rptN(perKind.brown.t6),
        rptTE(perKind.white.prod + perKind.brown.prod)] },
    { type: "table", title: "Sales by driver", columns: groupCols("Driver"), rows: groupRows(byDriver), empty: "No dispatches in this range." },
    { type: "table", title: "Sales by destination", columns: groupCols("Destination"), rows: groupRows(byPlace), empty: "No dispatches in this range." },
    { type: "table", breakBefore: true, title: "Dispatch register",
      columns: [{ label: "Date" }, { label: "Kind" }, { label: "Serial" }, { label: "Vehicle" }, { label: "Driver" }, { label: "Destination" },
        { label: "Issued 30/25/6" }, { label: "Returned" }, { label: "Sold 30", align: "right" }, { label: "Sold 25", align: "right" }, { label: "Sold 6", align: "right" }, { label: "Eggs", align: "right" }],
      rows: register,
      footer: ["Total", "", "", "", "", "", "", "", rptN(perKind.white.t30 + perKind.brown.t30), rptN(perKind.white.t25 + perKind.brown.t25), rptN(perKind.white.t6 + perKind.brown.t6), rptN(totEggs)],
      note: "Sold = issued − returned against that serial. Eggs = 30×30-size + 25×25-size + 6×6-size." },
  ];
  return { module: "Packing Egg Store", title: "Packing — Loading Sale", farm: farm.name, from, to, sections, landscape: true };
}

// Packing — month view: production and sales for white, brown and loose.
function buildPackMonthReport(farm, data, from, to) {
  const days = [...((data && data.days) || [])].sort((a, b) => a.date.localeCompare(b.date));
  const T = (data && data.totals) || {};
  const KINDS = [["white", "White", true], ["brown", "Brown", true], ["loose", "Loose", false]];
  const sizes = (o, p) => `${o[p + "T30"] || 0}/${o[p + "T25"] || 0}/${o[p + "T6"] || 0}`;
  const sections = [
    { type: "table", title: "Totals by kind",
      columns: [{ label: "Kind" }, { label: "Production (trays)", align: "right" }, { label: "Loading sale" }, { label: "Loading eggs", align: "right" },
        { label: "Direct sale" }, { label: "Direct eggs", align: "right" }, { label: "Total sold eggs", align: "right" }],
      rows: KINDS.map(([k, label, sized]) => {
        const t = T[k] || {};
        return [{ v: label, bold: true }, rptN(t.prodTrays), sized ? sizes(t, "load") : rptTE(t.loadEggs), rptN(t.loadEggs),
          sized ? sizes(t, "dir") : rptTE(t.dirEggs), rptN(t.dirEggs), rptN((t.loadEggs || 0) + (t.dirEggs || 0))];
      }),
      footer: ["All kinds", rptN(KINDS.reduce((s, [k]) => s + ((T[k] || {}).prodTrays || 0), 0)), "",
        rptN(KINDS.reduce((s, [k]) => s + ((T[k] || {}).loadEggs || 0), 0)), "",
        rptN(KINDS.reduce((s, [k]) => s + ((T[k] || {}).dirEggs || 0), 0)),
        rptN(KINDS.reduce((s, [k]) => s + ((T[k] || {}).loadEggs || 0) + ((T[k] || {}).dirEggs || 0), 0))],
      note: "Tray sizes are shown as 30/25/6. Loose is shown as trays + eggs." },
  ];
  KINDS.forEach(([k, label, sized]) => {
    const rows = days.filter(d => { const s = d[k] || {}; return s.prodTrays || s.loadEggs || s.dirEggs; }).map(d => {
      const s = d[k];
      return [rptDate(d.date), rptDash(s.prodTrays), s.loadEggs ? (sized ? sizes(s, "load") : rptTE(s.loadEggs)) : "–", rptDash(s.loadEggs),
        s.dirEggs ? (sized ? sizes(s, "dir") : rptTE(s.dirEggs)) : "–", rptDash(s.dirEggs), rptDash((s.loadEggs || 0) + (s.dirEggs || 0))];
    });
    const t = T[k] || {};
    sections.push({ type: "table", title: `${label} — day by day`,
      columns: [{ label: "Date" }, { label: "Production (trays)", align: "right" }, { label: sized ? "Loading 30/25/6" : "Loading" }, { label: "Loading eggs", align: "right" },
        { label: sized ? "Direct 30/25/6" : "Direct" }, { label: "Direct eggs", align: "right" }, { label: "Sold eggs", align: "right" }],
      rows, footer: ["Total", rptN(t.prodTrays), sized ? sizes(t, "load") : rptTE(t.loadEggs), rptN(t.loadEggs), sized ? sizes(t, "dir") : rptTE(t.dirEggs), rptN(t.dirEggs),
        rptN((t.loadEggs || 0) + (t.dirEggs || 0))],
      empty: `No ${label.toLowerCase()} activity in this range.` });
  });
  return { module: "Packing Egg Store", title: "Packing — Production & Sale", farm: farm.name, from, to, sections };
}

// Feed & Medicine usage: totals, every item, every day, and cost per flock.
function buildFeedReport(farm, data, from, to) {
  const byItem = (data && data.byItem) || [];
  const byDay = [...((data && data.byDay) || [])].sort((a, b) => a.date.localeCompare(b.date));
  const t = (data && data.totals) || { kg: 0, cost: 0, units: {} };
  const u = t.units || {};
  const sections = [
    { type: "stats", title: "Summary", items: [
      { label: "Raw material used", value: `${rptN(t.kg)} kg`, sub: `${byItem.length} item${byItem.length !== 1 ? "s" : ""}` },
      { label: "Total cost", value: rptRs(t.cost), sub: "priced FIFO" },
      { label: "Average rate", value: t.kg > 0 ? rptRs(t.cost / t.kg, 2) + "/kg" : "—", sub: "across all items" },
      { label: "Days with feed made", value: rptN(byDay.length), sub: `of ${rptDaysInclusive(from, to)} in range` },
      ...FEED_CATS_UI.map(c => ({ label: `${c.label} units`, value: rptN(u[c.key] || 0, 2),
        sub: (t.kgByCat && t.kgByCat[c.key]) ? `${rptN(t.kgByCat[c.key])} kg · ${rptRs((t.costByCat || {})[c.key] || 0)}` : "none made" })),
      { label: "Avg cost per feed day", value: byDay.length ? rptRs(t.cost / byDay.length) : "—", sub: byDay.length ? `${rptN(t.kg / byDay.length)} kg per day made` : "no feed made" },
    ] },
    { type: "table", title: "Usage by item",
      columns: [{ label: "Item" }, { label: "Type" }, { label: "Kg used", align: "right" }, { label: "Avg ₹/kg", align: "right" }, { label: "Cost", align: "right" }, { label: "Share of cost", align: "right" }],
      rows: byItem.map(it => [{ v: it.name, bold: true }, it.kind === "medicine" ? "Medicine" : "Feed", rptN(it.kg, 2), it.avgRate > 0 ? rptRs(it.avgRate, 2) : "—",
        rptRs(it.cost), rptPct(t.cost > 0 ? (it.cost / t.cost) * 100 : 0)]),
      footer: ["Total", "", rptN(t.kg, 2), t.kg > 0 ? rptRs(t.cost / t.kg, 2) : "—", rptRs(t.cost), t.cost > 0 ? "100%" : ""],
      empty: "No feed was made in this range." },
    { type: "table", title: "Day by day",
      columns: [{ label: "Date" }, ...FEED_CATS_UI.map(c => ({ label: `${c.label} units`, align: "right" })), { label: "Kg used", align: "right" }, { label: "Cost", align: "right" }],
      rows: byDay.map(d => [rptDate(d.date), ...FEED_CATS_UI.map(c => rptDash((d.units || {})[c.key], v => rptN(v, 2))), rptN(d.kg, 2), rptRs(d.cost)]),
      footer: ["Total", ...FEED_CATS_UI.map(c => rptN(u[c.key] || 0, 2)), rptN(t.kg, 2), rptRs(t.cost)] },
  ];
  // Per flock (allocation, same rules as the By Flock view)
  try {
    const alloc = allocateFeedToFlocks(farm || { flocks: [] }, byDay, from, to);
    const flocks = [...((farm && farm.flocks) || [])].sort((a, b) => ((a.hatchDate || "") < (b.hatchDate || "") ? -1 : 1))
      .filter(fl => !fl.soldOn && alloc.per[fl.id] && alloc.per[fl.id].birdDays > 0);
    if (flocks.length) {
      let sKg = 0, sCost = 0, sBD = 0;
      const rows = flocks.map(fl => {
        const r = alloc.per[fl.id]; sKg += r.kg; sCost += r.cost; sBD += r.birdDays;
        return [{ v: flockLabelOn(farm.flocks, fl, from), bold: true }, feedCatSummary(r.catDays), `${r.daysPresent}/${alloc.days}`,
          rptN(r.birdDays / Math.max(1, alloc.days)), rptN(r.kg), rptN(r.birdDays > 0 ? (r.kg * 1000) / r.birdDays : 0), rptRs(r.cost),
          r.birdDays > 0 ? rptRs(r.cost / r.birdDays, 2) : "—"];
      });
      sections.push({ type: "table", title: "Feed cost per flock",
        columns: [{ label: "Flock" }, { label: "Feed" }, { label: "Days", align: "right" }, { label: "Birds avg", align: "right" }, { label: "Feed kg", align: "right" },
          { label: "g/bird/day", align: "right" }, { label: "Cost", align: "right" }, { label: "₹/bird/day", align: "right" }],
        rows, footer: ["Total", "", "", rptN(sBD / Math.max(1, alloc.days)), rptN(sKg), rptN(sBD > 0 ? (sKg * 1000) / sBD : 0), rptRs(sCost), sBD > 0 ? rptRs(sCost / sBD, 2) : "—"],
        note: "Each flock's share is apportioned by its bird numbers each day. Sold flocks are hidden but still counted when splitting the rest." });
    }
  } catch (e) { /* optional */ }
  // One page per item: its name and totals as a heading, then every day it was used.
  byItem.filter(it => (it.rows || []).length).forEach(it => {
    const rows = [...it.rows].sort((a, b) => a.date.localeCompare(b.date) || String(a.category).localeCompare(String(b.category)));
    const usedDays = new Set(rows.map(r => r.date)).size;
    const type = it.kind === "medicine" ? "Medicine" : "Feed";
    sections.push({ type: "stats", breakBefore: true, big: true, title: `${it.kind === "medicine" ? "💊" : "🌾"} ${it.name} — ${type}`, items: [
      { label: "Kg used", value: rptN(it.kg, 2), sub: `${usedDays} day${usedDays !== 1 ? "s" : ""} used` },
      { label: "Cost", value: rptRs(it.cost), sub: rptPct(t.cost > 0 ? (it.cost / t.cost) * 100 : 0) + " of all feed cost" },
      { label: "Average rate", value: it.avgRate > 0 ? rptRs(it.avgRate, 2) + "/kg" : "—", sub: "priced FIFO" },
      { label: "Avg per day used", value: `${rptN(usedDays ? it.kg / usedDays : 0, 2)} kg`, sub: usedDays ? rptRs(it.cost / usedDays) + " per day" : "" },
    ] });
    sections.push({ type: "table", title: `${it.name} — day by day`,
      columns: [{ label: "Date" }, { label: "Feed" }, { label: "Units", align: "right" }, { label: "Kg/unit", align: "right" },
        { label: "Kg used", align: "right" }, { label: "₹/kg", align: "right" }, { label: "Cost", align: "right" }],
      rows: rows.map(r => [rptDate(r.date), feedCatLabel(r.category), rptN(r.units, 2), rptN(r.kgPerUnit, 2), rptN(r.kg, 2),
        r.avgRate > 0 ? rptRs(r.avgRate, 2) : (r.kg > 0 ? rptRs(r.cost / r.kg, 2) : "—"), rptRs(r.cost)]),
      footer: ["Total", "", "", "", rptN(it.kg, 2), it.avgRate > 0 ? rptRs(it.avgRate, 2) : "—", rptRs(it.cost)] });
  });
  return { module: "Feed & Medicine", title: "Feed & Medicine Usage", farm: farm ? farm.name : "", from, to, sections };
}

// ── Farm dashboard: daily series for the charts ───────────────────────────────
// Rolls every flock's daily logs up to farm level for the last `days` days.
// All data is already in memory from /api/farms, so this needs no extra fetch.
function farmDailySeries(farm, days, endISO) {
  const map = {};
  (farm.flocks || []).forEach(fl => (fl.dailyLogs || []).forEach(l => {
    if (!l.date) return;
    const m = map[l.date] || (map[l.date] = { eggs: 0, mortality: 0, logged: 0 });
    m.eggs += ((l.trays || 0) * 30) + (l.eggs || 0);
    m.mortality += (l.mortality || 0);
    m.logged += 1;
  }));
  const out = [];
  let d = endISO;
  for (let i = 0; i < days; i++) {
    const m = map[d] || { eggs: 0, mortality: 0, logged: 0 };
    out.unshift({ date: d, eggs: m.eggs, trays: m.eggs / 30, mortality: m.mortality, logged: m.logged });
    d = prevISO(d);
  }
  return out;
}

// Bar chart with an average line. Pure SVG, no dependencies, dark theme.
function DashChart({ series, metric = "eggs", height = 132 }) {
  const W = 320, H = height, PADB = 16, PADT = 8;
  const vals = series.map(p => metric === "mortality" ? p.mortality : p.trays);
  const max = Math.max(1, ...vals);
  const n = Math.max(1, vals.length);
  const slot = W / n;
  const bw = Math.max(2, Math.min(18, slot * 0.62));
  const withData = vals.filter((v, i) => series[i].logged > 0);
  const avg = withData.length ? withData.reduce((a, b) => a + b, 0) / withData.length : 0;
  const avgY = H - PADB - (avg / max) * (H - PADB - PADT);
  const up = metric !== "mortality";
  const barTop = up ? "#7dd45a" : "#e0954a";
  const barBot = up ? "#2d5a22" : "#6a3a12";
  const fmt = (v) => metric === "mortality" ? Math.round(v).toLocaleString() : Math.round(v).toLocaleString();
  // Hover/tap a bar for its date and value. The tooltip is plain HTML layered over
  // the chart — text inside this SVG would be stretched by preserveAspectRatio="none".
  const [hover, setHover] = useState(null);
  // Switching 7d/30d or eggs/deaths can leave a stale index behind — ignore any
  // index the current series no longer has, so bars never dim with no tooltip.
  const hi = hover != null && hover >= 0 && hover < series.length ? hover : null;
  const hv = hi != null ? series[hi] : null;
  const hoverPct = hi != null && n > 0 ? ((hi + 0.5) / n) * 100 : 50;
  return (
    <div style={{ position:"relative" }} onMouseLeave={()=>setHover(null)}>
      {hv && (
        <div style={{
          position:"absolute", left:`${Math.min(88, Math.max(12, hoverPct))}%`, top:-2, transform:"translate(-50%,-100%)",
          background:"#0a1408", border:`1px solid ${up ? "#3a7a2a" : "#7a4a10"}`, borderRadius:9,
          padding:"6px 10px", pointerEvents:"none", zIndex:5, whiteSpace:"nowrap",
          boxShadow:"0 6px 18px rgba(0,0,0,0.55)",
        }}>
          <div style={{ fontSize:9.5, color:"#6a8a60", letterSpacing:0.3 }}>{formatDate(hv.date)}</div>
          {hv.logged === 0 ? (
            <div style={{ fontSize:12, color:"#8a7a50", fontWeight:600 }}>No entry</div>
          ) : metric === "mortality" ? (
            <div style={{ fontSize:13.5, color:"#e0954a", fontWeight:700 }}>{Math.round(hv.mortality).toLocaleString()} <span style={{ fontSize:9.5, color:"#8a7a50", fontWeight:400 }}>deaths</span></div>
          ) : (
            <div>
              <div style={{ fontSize:13.5, color:"#7dd45a", fontWeight:700 }}>{Math.floor(hv.trays).toLocaleString()} <span style={{ fontSize:9.5, color:"#6a8a60", fontWeight:400 }}>trays</span></div>
              <div style={{ fontSize:9.5, color:"#6a8a60" }}>{Math.round(hv.eggs).toLocaleString()} eggs</div>
            </div>
          )}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display:"block", width:"100%", height:H }}>
        <defs>
          <linearGradient id={`dashbar-${metric}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={barTop} stopOpacity="0.95" />
            <stop offset="100%" stopColor={barBot} stopOpacity="0.75" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map(g => (
          <line key={g} x1="0" x2={W} y1={PADT + (H - PADB - PADT) * g} y2={PADT + (H - PADB - PADT) * g} stroke="#1a2e18" strokeWidth="1" />
        ))}
        {series.map((pt, i) => {
          const v = metric === "mortality" ? pt.mortality : pt.trays;
          const h = Math.max(v > 0 ? 2 : 0, (v / max) * (H - PADB - PADT));
          const x = i * slot + (slot - bw) / 2;
          const isLast = i === series.length - 1;
          return (
            <g key={pt.date}>
              {pt.logged === 0 && (
                <rect x={x} y={H - PADB - 2} width={bw} height={2} rx="1" fill="#24341f" />
              )}
              {h > 0 && <rect x={x} y={H - PADB - h} width={bw} height={h} rx="2" fill={`url(#dashbar-${metric})`} opacity={hi === i ? 1 : (hi != null ? 0.45 : (isLast ? 1 : 0.85))} />}
            </g>
          );
        })}
        {/* Full-height hit zones — the bars themselves are too thin to hover on a 30-day window. */}
        {series.map((pt, i) => (
          <rect key={`hit-${pt.date}`} x={i * slot} y="0" width={slot} height={H} fill="transparent"
            style={{ cursor:"pointer" }}
            onMouseEnter={()=>setHover(i)}
            onClick={()=>setHover(h => h === i ? null : i)} />
        ))}
        {avg > 0 && <line x1="0" x2={W} y1={avgY} y2={avgY} stroke={up ? "#8ac070" : "#c08850"} strokeWidth="1" strokeDasharray="4 3" opacity="0.65" />}
        <line x1="0" x2={W} y1={H - PADB} y2={H - PADB} stroke="#243a20" strokeWidth="1" />
      </svg>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#4a6a40", marginTop:2 }}>
        <span>{formatDate(series[0] && series[0].date).slice(0,5)}</span>
        <span style={{ color:"#6a8a60" }}>avg {fmt(avg)}{metric==="mortality" ? " deaths" : " trays"}/day</span>
        <span>{formatDate(series[series.length-1] && series[series.length-1].date).slice(0,5)}</span>
      </div>
    </div>
  );
}

// ── Dashboard "Entries" panel: which daily modules are filled in, for any date ──
// Defaults to today (rendered instantly from the summary already fetched); the
// mini stepper walks back through history, fetching that day on demand.
function EntryStatusPanel({ farmId, todayInfo, embedded = false, allowed = null }) {
  const today = todayISO();
  const [date, setDate] = useState(today);
  const [past, setPast] = useState(null);
  const [busy, setBusy] = useState(false);
  const isToday = date === today;

  useEffect(() => {
    if (isToday) { setPast(null); setBusy(false); return; }
    let alive = true;
    setBusy(true);
    apiCall(`/dashboard/entry-status?date=${date}&farmId=${farmId}`)
      .then(d => { if (alive) setPast((d && d.status) || null); })
      .catch(() => { if (alive) setPast(null); })
      .then(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [date, farmId, isToday]);

  // Only farms that actually track something get the panel at all — decided from
  // today's data so the stepper never disappears mid-browse.
  const t = todayInfo || {};
  // `allowed` = modules this user may see (null = all). Rows for others are hidden.
  const ok = (m) => !allowed || allowed.includes(m);
  if (!(((t.expectedFlocks > 0) && (ok("production") || ok("eggstock"))) || (t.feedHasItems && ok("feed")))) return null;

  const info = isToday ? t : past;
  const rows = [];
  if (info) {
    if (info.expectedFlocks > 0) {
      const logged = info.loggedFlocks || 0;
      if (ok("production")) rows.push({ key:"prod", icon:"🥚", label:"Production", st: logged >= info.expectedFlocks ? "done" : logged === 0 ? "pending" : "partial", extra:`${logged}/${info.expectedFlocks}` });
      if (ok("eggstock")) rows.push({ key:"egg", icon:"📦", label:"Egg Stock", st: info.eggStockEntered ? "done" : "pending" });
    }
    if (info.feedHasItems && ok("feed")) rows.push({ key:"feed", icon:"🌾", label:"Feed", st: info.feedEntered ? "done" : "pending" });
  }
  const pending = rows.filter(r => r.st !== "done").length;
  const stepBtn = (label, onClick, disabled) => (
    <button onClick={e=>{ e.stopPropagation(); if(!disabled) onClick(); }} disabled={disabled}
      style={{ background:disabled?"#0e1408":"#132010", border:`1px solid ${disabled?"#1a2416":"#2a4a20"}`, borderRadius:7, color:disabled?"#2a3a26":"#8ac070", fontSize:11, lineHeight:1, padding:"4px 7px", cursor:disabled?"default":"pointer", fontFamily:"inherit" }}>{label}</button>
  );

  return (
    <div onClick={e=>e.stopPropagation()} style={{ marginTop:embedded?0:12, background:"#0b1408", border:`1px solid ${rows.length===0?"#24401c":pending?"#4a3a12":"#24401c"}`, borderRadius:embedded?16:12, padding:embedded?"12px 14px":"9px 12px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginBottom:7 }}>
        <span style={{ fontSize:9.5, color:rows.length===0?"#6a8060":pending?"#c0a050":"#6a9050", letterSpacing:0.5, textTransform:"uppercase", fontWeight:700 }}>
          📋 {isToday ? "Today's Entries" : "Entries"}{rows.length===0 ? "" : pending ? ` · ${pending} pending` : " · all done"}
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
          {stepBtn("‹", () => setDate(prevISO(date)), false)}
          <span style={{ fontSize:10.5, color:isToday?"#7dd45a":"#c8b070", fontWeight:600, minWidth:52, textAlign:"center", fontVariantNumeric:"tabular-nums" }}>
            {isToday ? "Today" : formatDate(date).slice(0,5)}
          </span>
          {stepBtn("›", () => setDate(nextISO(date)), isToday)}
          {!isToday && stepBtn("today", () => setDate(today), false)}
        </div>
      </div>

      {busy && <div style={{ fontSize:11, color:"#5a7a50", padding:"3px 0" }}>Loading…</div>}
      {!busy && rows.length===0 && <div style={{ fontSize:11, color:"#5a7a50", padding:"3px 0" }}>Nothing was being tracked on {formatDate(date)}.</div>}
      {!busy && rows.length>0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {rows.map(r => (
            <div key={r.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:12, color:"#b0c8a0" }}>{r.icon} {r.label}</span>
              {r.st==="done"
                ? <span style={{ fontSize:11, color:"#7dd45a", fontWeight:600 }}>✓ Done</span>
                : r.st==="partial"
                  ? <span style={{ fontSize:11, color:"#e0c040", fontWeight:600 }}>🕓 {r.extra} logged</span>
                  : <span style={{ fontSize:11, color:"#f0a850", fontWeight:600 }}>⚠️ Pending</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Lifecycle stage for the dashboard bird tally:
//   layer  = the flock has laid at least one egg on any logged day (laying wins over age)
//   chick  = 0–60 days old and not yet laying
//   grower = 61+ days old and not yet laying
// A flock with no hatch date that has never laid is counted as a grower.
function flockStage(flock) {
  const hasLaid = (flock.dailyLogs || []).some(e => ((e.trays || 0) * 30 + (e.eggs || 0)) > 0);
  if (hasLaid) return "layer";
  if (!flock.hatchDate) return "grower";
  return flockAge(flock.hatchDate) <= 60 ? "chick" : "grower";
}
// The number a flock carries ON A GIVEN DATE: its rank (oldest first) among the
// flocks the farm had that day. Selling a flock closes the gap from the sale date
// onward — Flock 2 becomes Flock 1 — while every earlier day still shows the
// numbering that was in force then, because the sold flock is counted on those days.
function flockLabelOn(allFlocks, flock, dateISO) {
  const present = (allFlocks || [])
    .filter(f => flockPresentOn(f, dateISO))
    .sort((a, b) => {
      const ah = a.hatchDate || "9999-99-99", bh = b.hatchDate || "9999-99-99";
      if (ah !== bh) return ah < bh ? -1 : 1;
      return (a.id || 0) - (b.id || 0);
    });
  const i = present.findIndex(f => f.id === flock.id);
  return i >= 0 ? `Flock ${i + 1}` : (flock.name || "Flock");
}
const BIRD_TYPES = [
  { key:"white", label:"White", color:"#d8e8f0", bg:"#141c22", border:"#3a4a56" },
  { key:"brown", label:"Brown", color:"#e0a870", bg:"#1e1408", border:"#5a3e1a" },
];
function birdTypeMeta(t) { return BIRD_TYPES.find(b => b.key === String(t || "").toLowerCase()) || null; }
// Small colour-coded tag rendered next to a flock's name.
function BirdTypeTag({ type, size = 9 }) {
  const m = birdTypeMeta(type);
  if (!m) return null;
  return (
    <span style={{ fontSize:size, color:m.color, background:m.bg, border:`1px solid ${m.border}`,
      borderRadius:9, padding:"1px 7px", marginLeft:6, fontWeight:600, whiteSpace:"nowrap", verticalAlign:"middle" }}>
      {m.label}
    </span>
  );
}

// Same rule as flockStage but AS OF a date — a flock that lays today may have
// been a grower last month, so per-day feed maths must use the stage back then.
function flockStageOn(flock, dateISO) {
  const hasLaid = (flock.dailyLogs || []).some(e => e.date <= dateISO && ((e.trays || 0) * 30 + (e.eggs || 0)) > 0);
  if (hasLaid) return "layer";
  if (!flock.hatchDate) return "grower";
  const age = Math.floor((new Date(dateISO + "T00:00:00") - new Date(flock.hatchDate + "T00:00:00")) / 86400000);
  return age <= 60 ? "chick" : "grower";
}
// Was the flock on the farm on this date? It must already have arrived (added-on,
// else hatch date) and not yet have been sold.
function flockOnFarmOn(flock, dateISO) {
  if (!flock) return false;
  const arrived = flock.addedOn || flock.hatchDate;
  if (arrived && dateISO < arrived) return false;
  return flockPresentOn(flock, dateISO);
}
// Birds per stage on a given date, using each flock's balance as of that date.
function farmBirdsByStageOn(farm, dateISO) {
  const out = { layer: 0, grower: 0, chick: 0 };
  (farm.flocks || []).forEach(fl => {
    if (!flockOnFarmOn(fl, dateISO)) return;
    out[flockStageOn(fl, dateISO)] += flockBalance(fl, dateISO);
  });
  return out;
}
// A sold flock is KEPT for history. It is "present" on a date only if it hadn't
// been sold by then (present strictly before its sold_on), and "active now" if it
// hasn't been sold at all. This is how a sale removes a flock from the sale date
// onward without touching any earlier day's data.
function flockPresentOn(fl, dateISO) {
  if (!fl || !fl.soldOn) return true;
  return !!dateISO && dateISO < fl.soldOn;
}
function flockActiveNow(fl) {
  return flockPresentOn(fl, todayISO());
}
function calcEggs(entry) {
  if (!entry) return 0;
  return ((entry.trays||0)*30) + (entry.eggs||0);
}
function prevISO(dateISO) {
  const d = new Date(dateISO+"T00:00:00"); d.setDate(d.getDate()-1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function nextISO(dateISO) {
  const d = new Date(dateISO+"T00:00:00"); d.setDate(d.getDate()+1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// ─── FlockRecords ─────────────────────────────────────────────────────────────
function FlockRecords({ farm, perms: pm = {}, onAddFlock, onDeleteFlock, onSellFlock, onUpdateFlock, onAddBirds, onDeleteAddition, onSaveDailyLog, onDeleteDailyLog }) {
  const [tab, setTab] = useState("daily"); // "daily" | "monitor" | "batches"
  const [selDate, setSelDate] = useState(todayISO());
  const [monFrom, setMonFrom] = useState(todayISO().slice(0, 7) + "-01"); // Monitor tab range
  const [monTo, setMonTo] = useState(todayISO());
  const [editEntry, setEditEntry] = useState(null); // { flockId, date, ...values } or null
  const [showAddFlock, setShowAddFlock] = useState(false);
  const [newName, setNewName] = useState("");
  const [newHatch, setNewHatch] = useState("");
  const [newSize, setNewSize] = useState("");
  const [newBalance, setNewBalance] = useState("");  // current birds alive; prior deaths derived from this
  const [newAddedOn, setNewAddedOn] = useState("");  // date this flock is being added (required, no default)
  const [newBirdType, setNewBirdType] = useState("");  // "" | "white" | "brown"
  const [expandedFlock, setExpandedFlock] = useState(null);
  const [showSold, setShowSold] = useState(false);   // reveal sold batches in the Batches tab
  const [editFlock, setEditFlock] = useState(null); // { id, name, hatchDate, initialSize } or null
  const [addBirdsFor, setAddBirdsFor] = useState(null); // flockId we're adding birds to, or null
  const [abCount, setAbCount] = useState("");
  const [abDate, setAbDate] = useState(todayISO());
  const [abNote, setAbNote] = useState("");
  const [showImport, setShowImport] = useState(false); // bulk import modal

  // ── Inline daily-entry editing ──
  // Local draft values keyed by flockId so typing in the card boxes is smooth and
  // doesn't fight re-renders. Saved to the server on blur / Enter, using the SAME
  // onSaveDailyLog the modal uses (so math, balance, and audit behave identically).
  const [drafts, setDrafts] = useState({}); // { [flockId]: { mortality, trays, eggs } }

  function draftVal(flock, field) {
    const d = drafts[flock.id];
    if (d && d[field] !== undefined) return d[field];
    const entry = (flock.dailyLogs||[]).find(e => e.date === selDate);
    const v = entry ? entry[field] : "";
    return (v === 0 || v) ? String(v) : "";
  }
  function setDraft(flockId, field, value) {
    setDrafts(p => ({ ...p, [flockId]: { ...(p[flockId]||{}), [field]: value } }));
  }
  // Commit the current draft for a flock as one daily-log entry for selDate.
  function commitDraft(flock) {
    const entry = (flock.dailyLogs||[]).find(e => e.date === selDate);
    const m = draftVal(flock, "mortality");
    const t = draftVal(flock, "trays");
    const e = draftVal(flock, "eggs");
    // A field left BLANK ("") means "not entered". A typed "0" is a real value.
    // If ALL three fields are blank: with no existing entry there's nothing to save
    // (cells stay dashes); with an existing entry, the user cleared it back to dashes
    // so we DELETE the entry.
    const allBlank = m === "" && t === "" && e === "";
    if (allBlank) {
      if (entry && onDeleteDailyLog) onDeleteDailyLog(flock.id, selDate);
      setDrafts(p => { const n = { ...p }; delete n[flock.id]; return n; });
      return;
    }
    const payload = {
      date: selDate,
      mortality: m === "" ? 0 : (Number(m) || 0),
      trays:     t === "" ? 0 : (Number(t) || 0),
      eggs:      e === "" ? 0 : (Number(e) || 0),
      notes: entry?.notes || "",   // preserve any note set via the modal
    };
    // Save if values changed vs the stored entry (or if this is a brand-new entry).
    const changed = !entry
      ? true
      : (payload.mortality !== (entry.mortality||0) || payload.trays !== (entry.trays||0) || payload.eggs !== (entry.eggs||0));
    if (changed) onSaveDailyLog(flock.id, payload);
    // Clear the local draft so the card reflects server state again
    setDrafts(p => { const n = { ...p }; delete n[flock.id]; return n; });
  }
  // Clear drafts when the selected date changes (avoid stale values bleeding across days)
  useEffect(() => { setDrafts({}); }, [selDate]);

  // ── Arrow-key navigation between inline-edit boxes ──
  // Every editable input carries data-cell="N" where N = flockIndex*3 + column
  // (column 0=mortality, 1=trays, 2=eggs). Arrow keys navigate the grid:
  //   Left / Right  → move within the same flock's columns (and across flocks)
  //   Up / Down     → jump to the SAME column in the flock above / below
  //   Enter         → move to the next column (left→right, then next flock)
  // Focus skips any missing cells (e.g. read-only flocks render no inputs).
  function focusCell(targetIndex) {
    if (targetIndex < 0) return false;
    const el = document.querySelector(`[data-cell="${targetIndex}"]`);
    if (el) { el.focus(); if (el.select) el.select(); return true; }
    return false;
  }
  function handleCellKey(e, cellIndex) {
    const k = e.key;
    if (k !== "ArrowUp" && k !== "ArrowDown" && k !== "ArrowLeft" && k !== "ArrowRight" && k !== "Enter") return;
    if (k.startsWith("Arrow")) e.preventDefault();

    if (k === "ArrowUp" || k === "ArrowDown") {
      // Same column, flock above/below. Step by 3 (3 columns per flock).
      const dir = k === "ArrowUp" ? -3 : 3;
      let t = cellIndex + dir;
      // Skip up to 30 flocks worth in case some flocks are read-only (no inputs)
      for (let tries = 0; tries < 90 && t >= 0; tries++, t += dir) {
        if (focusCell(t)) return;
      }
      return; // nothing above/below — stay put
    }

    // Left / Right / Enter → move within columns (linear), skipping gaps
    const step = (k === "ArrowRight" || k === "Enter") ? 1 : -1;
    let t = cellIndex + step;
    for (let tries = 0; tries < 30 && t >= 0; tries++, t += step) {
      if (focusCell(t)) return;
    }
    if (k === "Enter") e.currentTarget.blur(); // last/first cell → just save
  }

  // Flocks sorted by hatch date
  const sortedFlocks = useMemo(() =>
    [...farm.flocks].sort((a,b) => (a.hatchDate||"") < (b.hatchDate||"") ? -1 : 1),
    [farm.flocks]);
  // Flocks present on the selected day — a sold flock shows up to the day before
  // its sale and disappears from the sale date onward (its history stays intact).
  const flocksOnDate = useMemo(() => sortedFlocks.filter(fl => flockPresentOn(fl, selDate)), [sortedFlocks, selDate]);
  // Batches list: the flocks the farm still has. Sold ones move into a collapsible
  // section so their history stays reachable without cluttering the live list —
  // and without two cards sharing a number after the live flocks renumber.
  const liveFlocks = useMemo(() => sortedFlocks.filter(fl => !fl.soldOn), [sortedFlocks]);
  const soldFlocks = useMemo(() => sortedFlocks.filter(fl => !!fl.soldOn)
    .sort((a, b) => (b.soldOn || "").localeCompare(a.soldOn || "")), [sortedFlocks]);

  function handleAddFlock() {
    if (!newAddedOn) { alert("Please pick the date you're adding this flock on."); return; }
    const size = Number(newSize) || 0;
    // If a current balance is given, derive prior deaths = initial − balance (min 0).
    // If balance is left blank, assume a fresh flock (no prior deaths).
    const bal = newBalance === "" ? size : Number(newBalance) || 0;
    const prior = Math.max(0, size - bal);
    // Name is auto-assigned server-side by age; we pass a placeholder.
    onAddFlock("Flock", newHatch, size, prior, newAddedOn, newBirdType);
    setNewName(""); setNewHatch(""); setNewSize(""); setNewBalance(""); setNewAddedOn(""); setNewBirdType(""); setShowAddFlock(false);
  }

  // ── Daily totals for selDate ──
  const dailyTotals = useMemo(() => {
    let totalMort=0, totalBalance=0, totalTrays=0, totalEggs=0, totalProd=0;
    flocksOnDate.forEach(fl => {
      const entry = (fl.dailyLogs||[]).find(e => e.date===selDate);
      const bal = flockBalance(fl, selDate);
      totalMort += entry?.mortality||0;
      totalBalance += bal;
      if (entry) { totalTrays+=entry.trays||0; totalEggs+=entry.eggs||0; totalProd+=calcEggs(entry); }
    });
    // Re-express TOTAL production (all trays + all loose eggs) as trays + remainder.
    // 1 tray = 30 eggs. So loose eggs roll up into full trays automatically.
    const trueTrays = Math.floor(totalProd / 30);
    const remainderEggs = totalProd % 30;
    return { totalMort, totalBalance, totalTrays, totalEggs, totalProd, trueTrays, remainderEggs };
  }, [flocksOnDate, selDate]);

  return (
    <div style={{ paddingBottom:100 }}>
      {/* Tabs */}
      <div style={{ display:"flex", background:"#0a1408", borderBottom:"1px solid #1a2e16", padding:"4px 8px", gap:4 }}>
        {[["daily","📅 Daily Log"],["monitor","📊 Monitor"],["batches","🐔 Batches"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex:1, padding:"10px 0", background:tab===k?"#2a1e04":"transparent",
            border:"none", borderRadius:12,
            color:tab===k?"#e0b84a":"#3a6030", fontSize:13, fontFamily:"system-ui,sans-serif",
            fontWeight:tab===k?700:400, cursor:"pointer", transition:"all 0.2s",
          }}>{l}</button>
        ))}
      </div>

      <div key={tab} className="anim-tab">
      {/* ── DAILY LOG TAB ── */}
      {tab==="daily" && (
        <div style={{ padding:"14px 14px" }}>
          {/* Date navigation */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14, background:"#1a1600", border:"1px solid #3a3010", borderRadius:10, padding:"10px 12px" }}>
            <button onClick={() => setSelDate(prevISO(selDate))} style={{ background:"#2a2000", border:"1px solid #5a4a00", borderRadius:6, color:"#d4a840", fontSize:16, cursor:"pointer", padding:"4px 10px", fontFamily:"inherit", transition:"transform 0.12s ease, filter 0.12s ease" }}>‹</button>
            <div style={{ flex:1, position:"relative", textAlign:"center" }}>
              <div style={{ fontSize:15, fontWeight:"bold", color:"#e0c040", pointerEvents:"none" }}>{formatDate(selDate)}</div>
              <div style={{ fontSize:9, color:"#8a6a2a", pointerEvents:"none" }}>tap to change</div>
              <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
                style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0, cursor:"pointer", colorScheme:"dark" }} />
            </div>
            <button onClick={() => { if(selDate<todayISO()) setSelDate(nextISO(selDate)); }} style={{ background:"#2a2000", border:"1px solid #5a4a00", borderRadius:6, color:selDate<todayISO()?"#d4a840":"#3a3010", fontSize:16, cursor:"pointer", padding:"4px 10px", fontFamily:"inherit", transition:"transform 0.12s ease, filter 0.12s ease" }}>›</button>
          </div>

          {/* Bulk import button (super + admin) */}
          {pm.canEdit && flocksOnDate.length > 0 && (
            <button onClick={() => setShowImport(true)} style={{ width:"100%", marginBottom:14, padding:"9px", background:"linear-gradient(135deg,#16280e,#0e1c08)", border:"1px solid #3a6a2a", borderRadius:9, color:"#8ad06a", fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>
              📋 Bulk Import a Day (paste from register)
            </button>
          )}

          {/* Daily totals banner */}
          {flocksOnDate.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6, marginBottom:14 }}>
              {[
                { l:"Total Birds", v:dailyTotals.totalBalance.toLocaleString(), c:"#6abf4b" },
                { l:"Deaths Today", v:dailyTotals.totalMort, c:"#c04040" },
                { l:"Total Eggs", v:dailyTotals.totalProd.toLocaleString(), c:"#d4a840" },
                { l:"Trays", v:dailyTotals.trueTrays.toLocaleString(), sub:dailyTotals.remainderEggs>0?`+${dailyTotals.remainderEggs} eggs`:null, c:"#4a9abf" },
              ].map(s => (
                <div key={s.l} style={{ background:"#0e1208", border:`1px solid ${s.c}33`, borderRadius:8, padding:"7px 4px", textAlign:"center" }}>
                  <div style={{ fontSize:16, fontWeight:"bold", color:s.c }}>
                    {s.v}{s.sub && <span style={{ fontSize:9, fontWeight:600, color:s.c+"cc", marginLeft:3 }}>{s.sub}</span>}
                  </div>
                  <div style={{ fontSize:8, color:s.c+"99", textTransform:"uppercase", letterSpacing:0.5 }}>{s.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Flock rows */}
          {flocksOnDate.length === 0 && (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"#4a3a1a" }}>
              <div style={{ fontSize:36, marginBottom:8 }}>🐔</div>
              <div style={{ fontSize:13 }}>{sortedFlocks.length===0 ? "No batches yet. Add one in the Batches tab." : "No flocks on this day."}</div>
            </div>
          )}

          <div className="daily-flock-list">
          {flocksOnDate.map((flock, flockIdx) => {
            const entry = (flock.dailyLogs||[]).find(e => e.date===selDate);
            const bal = flockBalance(flock, selDate);
            const totalMort = flockTotalMort(flock, selDate);
            const totalEggs = calcEggs(entry);
            const prodPct = bal > 0 && totalEggs > 0 ? ((totalEggs/bal)*100).toFixed(1) : null;
            const age = flockAge(flock.hatchDate);
            // Base cell index for this flock's 3 editable boxes (mort, trays, eggs)
            const cellBase = flockIdx * 3;
            const hasEntry = !!entry;
            // Inline editing allowed when the user can edit an existing entry, or add a new one.
            // Admin + super can add/edit any day's entry (past or present).
            const canInline = pm.canEdit;

            return (
              <div key={flock.id} style={{
                background: hasEntry ? "#0e1a08" : "#140e00",
                border: `1px solid ${hasEntry ? "#2a4a1a" : "#3a2a00"}`,
                borderRadius:10, overflow:"hidden",
              }}>
                {/* Flock header */}
                <div style={{ padding:"10px 12px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                    <div>
                      <span style={{ fontSize:14, fontWeight:"bold", color:hasEntry?"#a8d48a":"#c0a030" }}>
                        🐥 {flockLabelOn(farm.flocks, flock, selDate)}
                      </span>
                      <BirdTypeTag type={flock.birdType} />
                      <span style={{ fontSize:11, fontWeight:"bold", color:"#7a8a5a", marginLeft:8 }}>
                        {fmtHatch(flock.hatchDate)} · Age {age}d
                      </span>
                    </div>
                    {/* Note editor still available via the modal (for the optional note field) */}
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      {canInline ? (
                        <button onClick={() => setEditEntry({ flockId:flock.id, date:selDate, mortality:entry?.mortality||"", trays:entry?.trays||"", eggs:entry?.eggs||"", notes:entry?.notes||"" })}
                          style={{ padding:"4px 9px", background:"transparent", border:"1px solid #2a3a2a", borderRadius:6, color:"#5a7a4a", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                          📝 Note
                        </button>
                      ) : (
                        <span style={{ fontSize:10, color:"#4a5a30", fontStyle:"italic" }}>{hasEntry ? "view only" : "no entry"}</span>
                      )}
                      {/* SOLD — super-admin only. Permanently removes the flock from ALL
                          modules and renumbers the rest. Strong confirm since it deletes data. */}
                      {pm.canDelete && onSellFlock && !flock.soldOn && (
                        <button onClick={() => {
                          if (window.confirm(`Mark ${flockLabelOn(farm.flocks, flock, selDate)} as SOLD on ${formatDate(selDate)}?\n\nThe flock stays in all records BEFORE ${formatDate(selDate)} and is removed from ${formatDate(selDate)} onward. Earlier days (production, stock, analysis) are not affected.\n\nTip: use the ‹ › arrows to change the date first if the sale was on another day.`)) {
                            onSellFlock(flock.id, selDate);
                          }
                        }}
                        style={{ padding:"4px 10px", background:"#2a1500", border:"1px solid #6a4010", borderRadius:6, color:"#e0a040", fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                          💰 SOLD
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Stats row — Mortality / Trays / Eggs are editable inline.
                      6 columns; minmax(0,1fr) lets the number inputs shrink cleanly on phones. */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(6, minmax(0, 1fr))", gap:4, fontSize:10 }}>
                    {/* Balance (read-only) */}
                    <div style={{ background:"#0a1008", border:"1px solid #6abf4b22", borderRadius:5, padding:"5px 3px", textAlign:"center" }}>
                      <div style={{ fontSize:12, fontWeight:"bold", color:"#6abf4b" }}>{bal.toLocaleString()}</div>
                      <div style={{ fontSize:8, color:"#6abf4b88" }}>Balance</div>
                    </div>
                    {/* Mortality (input) */}
                    <div style={{ background: canInline?"#120a0a":"#0a1008", border:`1px solid #c0404033`, borderRadius:5, padding:"3px", textAlign:"center" }}>
                      {canInline ? (
                        <input type="number" min="0" inputMode="numeric"
                          data-cell={cellBase + 0}
                          value={draftVal(flock,"mortality")}
                          onChange={ev => setDraft(flock.id,"mortality",ev.target.value)}
                          onBlur={() => commitDraft(flock)}
                          onKeyDown={ev => handleCellKey(ev, cellBase + 0)}
                          placeholder="–"
                          style={{ width:"100%", background:"transparent", border:"none", outline:"none", textAlign:"center", fontSize:12, fontWeight:"bold", color:"#e07a6a", fontFamily:"inherit", padding:"2px 0" }} />
                      ) : (
                        <div style={{ fontSize:12, fontWeight:"bold", color:entry?.mortality>0?"#c04040":"#5a5a5a", padding:"2px 0" }}>{entry ? (entry.mortality||0) : "—"}</div>
                      )}
                      <div style={{ fontSize:8, color:"#c0404088" }}>Mort Today</div>
                    </div>
                    {/* Total Mort (read-only) */}
                    <div style={{ background:"#0a1008", border:"1px solid #bf6a4a22", borderRadius:5, padding:"5px 3px", textAlign:"center" }}>
                      <div style={{ fontSize:12, fontWeight:"bold", color:"#bf6a4a" }}>{totalMort.toLocaleString()}</div>
                      <div style={{ fontSize:8, color:"#bf6a4a88" }}>Total Mort</div>
                    </div>
                    {/* Trays (input) */}
                    <div style={{ background: canInline?"#0a1016":"#0a1008", border:"1px solid #4a9abf33", borderRadius:5, padding:"3px", textAlign:"center" }}>
                      {canInline ? (
                        <input type="number" min="0" inputMode="numeric"
                          data-cell={cellBase + 1}
                          value={draftVal(flock,"trays")}
                          onChange={ev => setDraft(flock.id,"trays",ev.target.value)}
                          onBlur={() => commitDraft(flock)}
                          onKeyDown={ev => handleCellKey(ev, cellBase + 1)}
                          placeholder="–"
                          style={{ width:"100%", background:"transparent", border:"none", outline:"none", textAlign:"center", fontSize:12, fontWeight:"bold", color:"#6ab6e0", fontFamily:"inherit", padding:"2px 0" }} />
                      ) : (
                        <div style={{ fontSize:12, fontWeight:"bold", color:"#4a9abf", padding:"2px 0" }}>{entry?entry.trays||0:"—"}</div>
                      )}
                      <div style={{ fontSize:8, color:"#4a9abf88" }}>Trays</div>
                    </div>
                    {/* Extra eggs (input) */}
                    <div style={{ background: canInline?"#0a1016":"#0a1008", border:"1px solid #4a9abf33", borderRadius:5, padding:"3px", textAlign:"center" }}>
                      {canInline ? (
                        <input type="number" min="0" inputMode="numeric"
                          data-cell={cellBase + 2}
                          value={draftVal(flock,"eggs")}
                          onChange={ev => setDraft(flock.id,"eggs",ev.target.value)}
                          onBlur={() => commitDraft(flock)}
                          onKeyDown={ev => handleCellKey(ev, cellBase + 2)}
                          placeholder="–"
                          style={{ width:"100%", background:"transparent", border:"none", outline:"none", textAlign:"center", fontSize:12, fontWeight:"bold", color:"#6ab6e0", fontFamily:"inherit", padding:"2px 0" }} />
                      ) : (
                        <div style={{ fontSize:12, fontWeight:"bold", color:"#4a9abf", padding:"2px 0" }}>{entry?entry.eggs||0:"—"}</div>
                      )}
                      <div style={{ fontSize:8, color:"#4a9abf88" }}>Extra Eggs</div>
                    </div>
                    {/* % Production (read-only) — percentage highlighted, eggs as sub-text */}
                    <div style={{ background:"#0a1008", border:"1px solid #d4a84022", borderRadius:5, padding:"5px 3px", textAlign:"center" }}>
                      <div style={{ fontSize:12, fontWeight:"bold", color:"#d4a840" }}>{prodPct?`${prodPct}%`:"—"}</div>
                      <div style={{ fontSize:8, color:"#d4a84088" }}>{totalEggs||"—"} eggs</div>
                    </div>
                  </div>

                  {entry?.notes && <div style={{ marginTop:6, fontSize:10, color:"#6a6a3a", fontStyle:"italic" }}>📝 {entry.notes}</div>}
                </div>
              </div>
            );
          })}
          </div>{/* end daily-flock-list */}
        </div>
      )}

      {/* ── MONITOR TAB (read-only study view, all flocks together) ── */}
      {tab==="monitor" && (
        <div style={{ padding:"14px 14px" }}>
          {farm.flocks.length===0 ? (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"#4a3a1a" }}>
              <div style={{ fontSize:40, marginBottom:8 }}>📊</div>
              <div style={{ fontSize:13 }}>No flocks to monitor yet</div>
            </div>
          ) : (
            <>
              <DateRangePicker from={monFrom} to={monTo} onChange={(f,t)=>{ setMonFrom(f); setMonTo(t); }} accent="#7ad0e8" />
              <div style={{ fontSize:10, color:"#5a7a4a", textAlign:"center", marginBottom:12, fontStyle:"italic" }}>
                👁️ View-only — deaths &amp; production across all flocks in the selected range.
              </div>
              <ReportButton accent="#7ad0e8" onClick={() => openReport(() => buildProductionReport(farm, monFrom, monTo))} />

              {/* Farm-wide totals */}
              {(() => {
                let tBirds=0, tMort=0;
                farm.flocks.forEach(fl => { if (!flockActiveNow(fl)) return; tBirds += flockBalance(fl); tMort += flockTotalMort(fl); });
                const tInit = farm.flocks.filter(flockActiveNow).reduce((s,fl)=>s+(fl.initialSize||0),0);
                const farmMortPct = tInit>0 ? ((tMort/tInit)*100).toFixed(1) : "—";

                // ── Per-day farm-wide breakdown (last 10 logged dates) ──
                // For each of the 10 most recent dates with any entry, sum the farm's
                // deaths, eggs, and birds-alive-that-day across all flocks.
                const dateSet = new Set();
                farm.flocks.forEach(fl => (fl.dailyLogs||[]).forEach(e => { if (e.date >= monFrom && e.date <= monTo) dateSet.add(e.date); }));
                const rangeDates = [...dateSet].sort((a,b)=>b.localeCompare(a));
                const dayRows = rangeDates.map(d => {
                  let mort=0, eggs=0, birds=0;
                  farm.flocks.forEach(fl => {
                    const entry = (fl.dailyLogs||[]).find(e => e.date === d);
                    if (entry) { mort += entry.mortality||0; eggs += calcEggs(entry); }
                    birds += flockPresentOn(fl, d) ? flockBalance(fl, d) : 0; // alive only while present
                  });
                  const layPct = birds > 0 ? (eggs / birds) * 100 : 0;
                  return { date:d, mort, eggs, birds, layPct };
                });

                return (
                  <div style={{ marginBottom:18 }}>
                    {/* Distinct FARM banner — bold blue/teal gradient, unlike the amber flock cards */}
                    <div style={{
                      background:"linear-gradient(120deg,#0a2a3a,#0e3a4a 55%,#0a2230)",
                      border:"1.5px solid #2a7a9a", borderRadius:16, padding:"14px 16px",
                      boxShadow:"0 4px 24px rgba(20,90,120,0.3)",
                    }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                        <span style={{ fontSize:20 }}>🏡</span>
                        <span style={{ fontSize:15, fontWeight:"bold", color:"#7ad0e8", letterSpacing:0.3 }}>WHOLE FARM · daily</span>
                        <span style={{ marginLeft:"auto", fontSize:10, color:"#5a9ab0", background:"#0a2230", border:"1px solid #2a6a8a", borderRadius:20, padding:"2px 10px" }}>{rangeDates.length} day{rangeDates.length!==1?"s":""}</span>
                      </div>

                      {/* Per-day table */}
                      {dayRows.length === 0 ? (
                        <div style={{ fontSize:12, color:"#5a9ab0", textAlign:"center", padding:"10px 0" }}>No entries in this range.</div>
                      ) : (
                        <div style={{ background:"rgba(8,30,40,0.5)", border:"1px solid #1a4a5a", borderRadius:12, overflow:"hidden" }}>
                          {/* Column headers */}
                          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", fontSize:8, color:"#5a9ab0", letterSpacing:0.5, textTransform:"uppercase", borderBottom:"1px solid #1a4a5a", background:"rgba(10,40,55,0.6)" }}>
                            <span style={{ minWidth:80 }}>Date</span>
                            <span style={{ flex:1, textAlign:"center" }}>💀 Deaths</span>
                            <span style={{ flex:1.3, textAlign:"center" }}>🥚 Eggs</span>
                            <span style={{ minWidth:54, textAlign:"right" }}>Lay %</span>
                          </div>
                          {dayRows.map(r => {
                            const tr = Math.floor(r.eggs/30), rem = r.eggs%30;
                            const pctColor = r.layPct >= 80 ? "#5ad0a0" : r.layPct >= 60 ? "#7ad0e8" : r.layPct >= 40 ? "#e0c060" : r.layPct > 0 ? "#e09060" : "#5a7a80";
                            return (
                              <div key={r.date} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", borderBottom:"1px solid #133a48", fontSize:12 }}>
                                <span style={{ minWidth:80, color:"#a8d0e0", fontWeight:600 }}>{formatDate(r.date)}</span>
                                <span style={{ flex:1, textAlign:"center", color:"#e89080", fontWeight:"bold" }}>{r.mort.toLocaleString()}</span>
                                <span style={{ flex:1.3, textAlign:"center", color:"#e8c878" }}>{tr.toLocaleString()}T{rem>0?` +${rem}`:""} <span style={{ fontSize:9, color:"#7a9aa8" }}>({r.eggs.toLocaleString()})</span></span>
                                <span style={{ minWidth:54, textAlign:"right", color:pctColor, fontWeight:"bold" }}>{r.eggs>0 ? r.layPct.toFixed(1)+"%" : "—"}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* All-time standing strip */}
                      <div style={{ display:"flex", gap:14, justifyContent:"center", borderTop:"1px solid #1a4a5a", paddingTop:10, marginTop:12, flexWrap:"wrap" }}>
                        <span style={{ fontSize:11, color:"#8ad0a0" }}>🐔 <strong style={{ color:"#a8e8c0" }}>{tBirds.toLocaleString()}</strong> birds alive</span>
                        <span style={{ fontSize:11, color:"#c08080" }}>💀 <strong style={{ color:"#e0a0a0" }}>{tMort.toLocaleString()}</strong> all-time</span>
                        <span style={{ fontSize:11, color:"#c0a860" }}>📉 <strong style={{ color:"#e0c880" }}>{farmMortPct}{farmMortPct==="—"?"":"%"}</strong> mort</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Per-flock label so the distinction is obvious */}
              <div style={{ fontSize:10, color:"#7a6a3a", letterSpacing:2, textTransform:"uppercase", marginBottom:8, paddingLeft:2 }}>🐥 Each Flock</div>

              <div className="flock-grid">
              {sortedFlocks.filter(fl => flockPresentOn(fl, monFrom) || (fl.soldOn && fl.soldOn > monFrom)).map(flock => {
                const totalMort = flockTotalMort(flock);
                const bal = flockBalance(flock);
                const mortPct = flock.initialSize > 0 ? ((totalMort/flock.initialSize)*100).toFixed(1) : "—";
                const logs = [...(flock.dailyLogs||[])].filter(e => e.date >= monFrom && e.date <= monTo).sort((a,b)=>b.date.localeCompare(a.date));
                const recent = logs;
                const avg10 = recent.length > 0 ? Math.round(recent.reduce((s,e)=>s+calcEggs(e),0)/recent.length) : 0;
                return (
                  <div key={flock.id} style={{ background:"linear-gradient(135deg,#161204,#0e0c06)", border:"1px solid #3a2e10", borderRadius:14, overflow:"hidden", marginBottom:14 }}>
                    {/* Header */}
                    <div style={{ padding:"12px 14px", borderBottom:"1px solid #2a2210" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:15 }}>🐥</span>
                        <span style={{ fontSize:15, fontWeight:"bold", color:"#e0c878" }}>{flockLabelOn(farm.flocks, flock, monFrom)}</span>
                        <BirdTypeTag type={flock.birdType} />
                        <span style={{ fontSize:10, color:"#7a6a3a" }}>· {formatDate(flock.hatchDate)} · Age {flockAge(flock.hatchDate)}d</span>
                        {flock.soldOn && <span style={{ marginLeft:"auto", fontSize:9, color:"#e0a040", background:"#2a1c00", border:"1px solid #5a3c10", borderRadius:8, padding:"2px 8px", fontWeight:700 }}>💰 SOLD {formatDate(flock.soldOn)}</span>}
                      </div>
                    </div>
                    {/* Stat tiles */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:1, background:"#2a2210" }}>
                      {[
                        { l:"Initial", v:(flock.initialSize||0).toLocaleString(), c:"#6abf4b" },
                        { l:"Balance", v:bal.toLocaleString(), c:"#e0b84a" },
                        { l:"Deaths", v:totalMort.toLocaleString(), c:"#c04040" },
                        { l:"Mort %", v:mortPct+(mortPct==="—"?"":"%"), c:"#d4884a" },
                      ].map(s => (
                        <div key={s.l} style={{ background:"#0e0c06", padding:"9px 4px", textAlign:"center" }}>
                          <div style={{ fontSize:14, fontWeight:"bold", color:s.c }}>{s.v}</div>
                          <div style={{ fontSize:8, color:s.c+"88", textTransform:"uppercase" }}>{s.l}</div>
                        </div>
                      ))}
                    </div>
                    {/* range avg */}
                    <div style={{ padding:"7px 14px", borderTop:"1px solid #1e1808", fontSize:10, color:"#8a7a4a" }}>
                      📈 Avg in range: <strong style={{ color:"#c0a860" }}>{avg10.toLocaleString()} eggs/day</strong> · {recent.length} day{recent.length!==1?"s":""}
                    </div>
                    {/* Daily log in range (read-only) */}
                    {recent.length > 0 && (
                      <div style={{ padding:"4px 14px 12px" }}>
                        {/* mini column headers */}
                        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0 4px", fontSize:8, color:"#5a5030", letterSpacing:0.5, textTransform:"uppercase", borderBottom:"1px solid #2a2210" }}>
                          <span style={{ minWidth:78 }}>Date</span>
                          <span style={{ minWidth:38 }}>Deaths</span>
                          <span style={{ flex:1 }}>Eggs</span>
                          <span style={{ minWidth:48, textAlign:"right" }}>Lay %</span>
                        </div>
                        {recent.map(e => {
                          const eggs = calcEggs(e);
                          const tr = Math.floor(eggs/30), rem = eggs%30;
                          // Hen-day lay % = eggs that day ÷ birds alive that day × 100
                          const birdsThatDay = flockBalance(flock, e.date);
                          const layPct = birdsThatDay > 0 ? (eggs / birdsThatDay) * 100 : 0;
                          // Color the % by how healthy the lay rate is
                          const pctColor = layPct >= 80 ? "#5ad05a" : layPct >= 60 ? "#c0d040" : layPct >= 40 ? "#e0a040" : layPct > 0 ? "#d07040" : "#5a5030";
                          return (
                            <div key={e.id||e.date} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", borderBottom:"1px solid #1a1608", fontSize:11 }}>
                              <span style={{ color:"#9a8a5a", minWidth:78 }}>{formatDate(e.date)}</span>
                              <span style={{ color:"#c06a4a", minWidth:38 }}>💀 {e.mortality||0}</span>
                              <span style={{ color:"#c0a060", flex:1 }}>🥚 {tr}T{rem>0?` +${rem}`:""}</span>
                              <span style={{ color:pctColor, minWidth:48, textAlign:"right", fontWeight:"bold" }}>{eggs>0 ? layPct.toFixed(1)+"%" : "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {recent.length === 0 && (
                      <div style={{ padding:"10px 14px 14px", fontSize:11, color:"#5a4a2a", fontStyle:"italic" }}>No entries in this range.</div>
                    )}
                  </div>
                );
              })}
              </div>

              <FlockFeedCostSlab farm={farm} from={monFrom} to={monTo} />
            </>
          )}
        </div>
      )}

      {/* ── BATCHES TAB ── */}
      {tab==="batches" && (
        <div style={{ padding:"14px 14px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:11, color:"#6a5a30", letterSpacing:2, textTransform:"uppercase" }}>Batches ({liveFlocks.length}){soldFlocks.length>0 && <span style={{ color:"#5a4a2a" }}> · {soldFlocks.length} sold</span>}</div>
            {pm.canAdd && <button onClick={() => setShowAddFlock(true)} style={{ background:"#2a2010", border:"1px solid #6a5020", borderRadius:8, color:"#d4a840", fontSize:12, padding:"7px 12px", cursor:"pointer", fontFamily:"inherit" }}>+ New Batch</button>}
          </div>

          {showAddFlock && (
            <div className="anim-roll" style={{ background:"#1a1600", border:"1px solid #4a4000", borderRadius:12, padding:14, marginBottom:14, animation:"fadeIn 0.3s ease" }}>
              <div style={{ fontSize:11, color:"#c0a030", marginBottom:6, fontWeight:"bold", letterSpacing:1 }}>NEW BATCH / FLOCK</div>
              <div style={{ fontSize:9, color:"#7a6a3a", marginBottom:10, lineHeight:1.5 }}>The name is assigned automatically by age (oldest = Flock 1). You can rename it later from the Batches tab.</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                <div>
                  <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3 }}>HATCH DATE{newHatch ? ` · ${formatDate(newHatch)}` : ""}</label>
                  <input type="date" value={newHatch} onChange={e => setNewHatch(e.target.value)} style={{ ...iS, colorScheme:"dark" }} />
                </div>
                <div>
                  <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3 }}>INITIAL BIRDS (at hatch)</label>
                  <input type="number" value={newSize} onChange={e => setNewSize(e.target.value)} placeholder="e.g. 10000" style={iS} />
                </div>
              </div>
              {/* Current balance — type the live count; deaths-so-far derived from it */}
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:9, color:"#8a6a20", display:"block", marginBottom:3 }}>🐔 CURRENT BALANCE (birds alive now)</label>
                <input type="number" min="0" value={newBalance} onChange={e => setNewBalance(e.target.value)} placeholder={newSize ? `leave blank if same as ${Number(newSize).toLocaleString()}` : "birds alive right now"} style={{ ...iS, borderColor:"#4a3a10" }} />
                {(Number(newSize)>0 && newBalance!=="") && (() => {
                  const size = Number(newSize)||0, bal = Number(newBalance)||0;
                  const prior = Math.max(0, size - bal);
                  const over = bal > size;
                  return (
                    <div style={{ fontSize:10, color: over ? "#c08040" : "#6a8a5a", marginTop:5 }}>
                      {over
                        ? `⚠️ Balance can't exceed initial (${size.toLocaleString()}). Deaths so far set to 0.`
                        : <>Deaths so far: <strong style={{ color:"#c0a060" }}>{prior.toLocaleString()}</strong> <span style={{ color:"#6a5a30" }}>({size.toLocaleString()} − {bal.toLocaleString()})</span> · balance will drop as you log daily deaths</>}
                    </div>
                  );
                })()}
              </div>
              {/* Added-on date — REQUIRED, no default. Deaths logged on/before this
                  date are treated as already inside the balance above (reference-only);
                  only deaths logged AFTER it increase total mortality. */}
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:9, color:"#c08040", display:"block", marginBottom:3 }}>📌 FLOCK ADDED ON (required){newAddedOn ? ` · ${formatDate(newAddedOn)}` : " — pick the date you're entering this batch"}</label>
                <input type="date" value={newAddedOn} onChange={e => setNewAddedOn(e.target.value)} style={{ ...iS, colorScheme:"dark", borderColor: newAddedOn ? "#4a3a10" : "#6a3020" }} />
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3 }}>BIRD TYPE (optional)</label>
                <div style={{ display:"flex", gap:6 }}>
                  {BIRD_TYPES.map(b => (
                    <button key={b.key} onClick={() => setNewBirdType(t => t === b.key ? "" : b.key)}
                      style={{ flex:1, padding:"8px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12.5,
                        background: newBirdType===b.key ? b.bg : "transparent",
                        border:`1px solid ${newBirdType===b.key ? b.border : "#3a3010"}`,
                        color: newBirdType===b.key ? b.color : "#6a5a30", fontWeight: newBirdType===b.key ? 700 : 400 }}>
                      {b.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:9, color:"#6a6a4a", marginTop:4, lineHeight:1.5 }}>
                  The balance above is treated as correct <em>as of this date</em>. Daily entries on or before it are reference-only; only entries <strong style={{ color:"#8a8a5a" }}>after</strong> this date add to total mortality.
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={handleAddFlock} style={{ flex:1, padding:"10px", background:"linear-gradient(135deg,#3a2e00,#2a2000)", border:"1px solid #7a6020", borderRadius:8, color:"#e0c040", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Add Batch</button>
                <button onClick={() => setShowAddFlock(false)} style={{ padding:"10px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#7a7a7a", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          )}

          {liveFlocks.length===0 && !showAddFlock && (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"#4a3a1a" }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🐔</div>
              <div style={{ fontSize:13 }}>{soldFlocks.length>0 ? "No active batches — all have been sold." : "No batches yet"}</div>
            </div>
          )}

          <div className="flock-grid">
          {(showSold ? [...liveFlocks, ...soldFlocks] : liveFlocks).map(flock => {
            const totalMort = flockTotalMort(flock);
            const bal = flockBalance(flock);
            const mortPct = flock.initialSize > 0 ? ((totalMort/flock.initialSize)*100).toFixed(1) : "—";
            const logs = flock.dailyLogs || [];
            const last7 = [...logs].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,7);
            const avg7 = last7.length > 0 ? Math.round(last7.reduce((s,e)=>s+calcEggs(e),0)/last7.length) : 0;
            const todayEntry = logs.find(e=>e.date===todayISO());
            const isExp = expandedFlock===flock.id;

            return (
              <div key={flock.id} style={{ background:"#0e1208", border:"1px solid #2a2a10", borderRadius:12, overflow:"hidden", gridColumn: isExp ? "1 / -1" : "auto" }}>
                <div style={{ padding:"12px 14px", cursor:"pointer" }} onClick={() => setExpandedFlock(isExp?null:flock.id)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:"bold", color: flock.soldOn ? "#a08850" : "#d4c080", marginBottom:2 }}>
                        🐥 {flock.soldOn ? flock.name : flockLabelOn(farm.flocks, flock, todayISO())}
                        {flock.soldOn && <span style={{ fontSize:10, color:"#e0a040", fontWeight:600 }}> · sold</span>}
                        <BirdTypeTag type={flock.birdType} />
                      </div>
                      <div style={{ fontSize:10, color:"#6a5a30" }}>
                        Hatched {formatDate(flock.hatchDate)} · Age {flockAge(flock.hatchDate)}d
                      </div>
                      {flock.soldOn && <div style={{ fontSize:9, color:"#e0a040", marginTop:2, fontWeight:600 }}>💰 Sold on {formatDate(flock.soldOn)}</div>}
                    </div>
                    <div style={{ textAlign:"right" }}>
                      {todayEntry && <div style={{ fontSize:10, color:"#d4a840", background:"#2a2000", borderRadius:10, padding:"2px 7px", marginBottom:4 }}>🥚 {calcEggs(todayEntry)} today</div>}
                      <div style={{ color:"#6a5a30", fontSize:16 }}>{isExp?"▲":"▼"}</div>
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:5 }}>
                    {[
                      { l:"Initial", v:(flock.initialSize||0).toLocaleString(), c:"#6abf4b" },
                      { l:"Balance", v:bal.toLocaleString(), c:bal<flock.initialSize*0.9?"#c09a20":"#5aaa5a" },
                      { l:"Deaths", v:totalMort.toLocaleString(), c:"#c04040" },
                      { l:"Mort%", v:`${mortPct}%`, c:"#bf6a4a" },
                    ].map(s => (
                      <div key={s.l} style={{ background:"#0a1008", border:`1px solid ${s.c}33`, borderRadius:6, padding:"5px 3px", textAlign:"center" }}>
                        <div style={{ fontSize:14, fontWeight:"bold", color:s.c }}>{s.v}</div>
                        <div style={{ fontSize:8, color:s.c+"99", textTransform:"uppercase", letterSpacing:0.5 }}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                  {avg7 > 0 && <div style={{ marginTop:6, fontSize:10, color:"#8a7030" }}>📊 7-day avg: {avg7} eggs/day · {logs.length} days logged</div>}
                </div>

                {isExp && (
                  <div className="anim-roll" style={{ borderTop:"1px solid #2a2a10" }}>
                    {/* Flock details editor */}
                    <div style={{ padding:"10px 14px", borderBottom:"1px solid #1a1a08" }}>
                      {editFlock?.id === flock.id ? (
                        <div>
                          <div style={{ fontSize:10, color:"#c0a030", fontWeight:"bold", letterSpacing:1, marginBottom:8 }}>EDIT BATCH DETAILS</div>
                          <div style={{ marginBottom:7 }}>
                            <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3, letterSpacing:1 }}>BATCH NAME</label>
                            <input type="text" value={editFlock.name} onChange={e => setEditFlock(p => ({ ...p, name: e.target.value }))} style={iS} />
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                            <div>
                              <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3, letterSpacing:1 }}>HATCH DATE</label>
                              <input type="date" value={editFlock.hatchDate} onChange={e => setEditFlock(p => ({ ...p, hatchDate: e.target.value }))} style={{ ...iS, colorScheme:"dark" }} />
                            </div>
                            <div>
                              <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3, letterSpacing:1 }}>INITIAL BIRDS</label>
                              <input type="number" value={editFlock.initialSize} onChange={e => setEditFlock(p => ({ ...p, initialSize: e.target.value }))} style={iS} />
                            </div>
                          </div>
                          <div style={{ marginBottom:10 }}>
                            <label style={{ fontSize:9, color:"#8a6a20", display:"block", marginBottom:3, letterSpacing:1 }}>💀 PRIOR DEATHS (as of the added-on date)</label>
                            <input type="number" min="0" value={editFlock.priorMortality} onChange={e => setEditFlock(p => ({ ...p, priorMortality: e.target.value }))} style={{ ...iS, borderColor:"#4a3a10" }} />
                          </div>
                          <div style={{ marginBottom:10 }}>
                            <label style={{ fontSize:9, color:"#c08040", display:"block", marginBottom:3, letterSpacing:1 }}>📌 FLOCK ADDED ON</label>
                            <input type="date" value={editFlock.addedOn || ""} onChange={e => setEditFlock(p => ({ ...p, addedOn: e.target.value }))} style={{ ...iS, colorScheme:"dark", borderColor:"#4a3a10" }} />
                          </div>
                          <div style={{ marginBottom:8 }}>
                            <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:3 }}>BIRD TYPE</label>
                            <div style={{ display:"flex", gap:6 }}>
                              {BIRD_TYPES.map(b => (
                                <button key={b.key} onClick={() => setEditFlock(p => ({ ...p, birdType: p.birdType === b.key ? "" : b.key }))}
                                  style={{ flex:1, padding:"7px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12,
                                    background: editFlock.birdType===b.key ? b.bg : "transparent",
                                    border:`1px solid ${editFlock.birdType===b.key ? b.border : "#3a3010"}`,
                                    color: editFlock.birdType===b.key ? b.color : "#6a5a30", fontWeight: editFlock.birdType===b.key ? 700 : 400 }}>
                                  {b.label}
                                </button>
                              ))}
                            </div>
                            <div style={{ fontSize:9, color:"#5a6a4a", marginTop:4, lineHeight:1.5 }}>Prior deaths count as the total up to this date. Daily entries after it add to the total; entries on/before are reference-only.</div>
                          </div>
                          <div style={{ display:"flex", gap:8 }}>
                            <button onClick={() => {
                              onUpdateFlock(flock.id, { name: editFlock.name, hatchDate: editFlock.hatchDate, initialSize: Number(editFlock.initialSize) || 0, priorMortality: Number(editFlock.priorMortality) || 0, addedOn: editFlock.addedOn || null, birdType: editFlock.birdType || "" });
                              setEditFlock(null);
                            }} style={{ flex:1, padding:"8px", background:"#1e4a1e", border:"1px solid #4a9a4a", borderRadius:7, color:"#7ada5a", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Save</button>
                            <button onClick={() => setEditFlock(null)} style={{ padding:"8px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:7, color:"#7a7a7a", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
                          <div style={{ fontSize:11, color:"#6a5a30" }}>
                            <span>🐔 {flock.initialSize||"?"} birds</span>
                            <span style={{ margin:"0 8px", color:"#3a3a20" }}>·</span>
                            <span>📅 {formatDate(flock.hatchDate)}</span>
                            {flock.priorMortality>0 && <><span style={{ margin:"0 8px", color:"#3a3a20" }}>·</span><span style={{ color:"#a07040" }}>💀 {flock.priorMortality} prior</span></>}
                            {flock.addedOn && <><span style={{ margin:"0 8px", color:"#3a3a20" }}>·</span><span style={{ color:"#7a8a5a" }}>📌 {formatDate(flock.addedOn)}</span></>}
                          </div>
                          {pm.isSuper && (
                            <div style={{ display:"flex", gap:6 }}>
                              <button onClick={() => { setAddBirdsFor(addBirdsFor===flock.id?null:flock.id); setAbCount(""); setAbDate(todayISO()); setAbNote(""); }}
                                style={{ padding:"4px 10px", background:"#142a1a", border:"1px solid #2a5a3a", borderRadius:6, color:"#6ada8a", fontSize:11, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>🐔➕ Add Birds</button>
                              <button onClick={() => setEditFlock({ id: flock.id, name: flock.name, hatchDate: flock.hatchDate, initialSize: flock.initialSize, priorMortality: flock.priorMortality || 0, addedOn: flock.addedOn || "", birdType: flock.birdType || "" })}
                                style={{ padding:"4px 10px", background:"#1a2a1a", border:"1px solid #2a4a2a", borderRadius:6, color:"#5a8a5a", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✏️ Edit</button>
                            </div>
                          )}
                        </div>

                        {/* Add-birds form */}
                        {addBirdsFor===flock.id && pm.isSuper && (
                          <div className="anim-roll" style={{ background:"#0e1e12", border:"1px solid #2a5a3a", borderRadius:10, padding:12, marginTop:10 }}>
                            <div style={{ fontSize:11, color:"#6ada8a", fontWeight:"bold", marginBottom:8, letterSpacing:0.5 }}>🐔➕ ADD BIRDS (bought more of same age)</div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                              <div>
                                <label style={{ fontSize:9, color:"#5a8a6a", display:"block", marginBottom:3 }}>NUMBER OF BIRDS</label>
                                <input type="number" min="1" autoFocus value={abCount} onChange={e=>setAbCount(e.target.value)} placeholder="e.g. 500" style={{ ...iS, padding:"9px 11px" }} />
                              </div>
                              <div>
                                <label style={{ fontSize:9, color:"#5a8a6a", display:"block", marginBottom:3 }}>DATE ADDED{abDate ? ` · ${formatDate(abDate)}` : ""}</label>
                                <input type="date" value={abDate} onChange={e=>setAbDate(e.target.value)} style={{ ...iS, padding:"9px 11px", colorScheme:"dark" }} />
                              </div>
                            </div>
                            <div style={{ marginBottom:10 }}>
                              <label style={{ fontSize:9, color:"#5a8a6a", display:"block", marginBottom:3 }}>NOTE (optional)</label>
                              <input type="text" value={abNote} onChange={e=>setAbNote(e.target.value)} placeholder="e.g. bought from Sharma farm" style={{ ...iS, padding:"9px 11px" }} />
                            </div>
                            <div style={{ background:"#0a1810", border:"1px dashed #2a5a3a", borderRadius:7, padding:"7px 10px", marginBottom:10, fontSize:10, color:"#5a9a6a" }}>
                              This adds {abCount && Number(abCount)>0 ? <strong style={{ color:"#8adaa0" }}>{Number(abCount).toLocaleString()}</strong> : "—"} birds to the flock total{abCount && Number(abCount)>0 ? <> (new total <strong style={{ color:"#8adaa0" }}>{((flock.initialSize||0)+Number(abCount)).toLocaleString()}</strong>)</> : ""}. Balance and vaccine targets update everywhere.
                            </div>
                            <div style={{ display:"flex", gap:8 }}>
                              <button onClick={() => {
                                const n = Number(abCount)||0;
                                if (n <= 0) { alert("Enter a positive number of birds."); return; }
                                if (!abDate) { alert("Pick the date the birds were added."); return; }
                                onAddBirds(flock.id, n, abDate, abNote);
                                setAddBirdsFor(null); setAbCount(""); setAbNote("");
                              }} style={{ flex:1, padding:"9px", background:"linear-gradient(135deg,#1e4a2a,#153a1f)", border:"1px solid #3a8a5a", borderRadius:7, color:"#8adaa0", fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Add Birds</button>
                              <button onClick={() => setAddBirdsFor(null)} style={{ padding:"9px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:7, color:"#7a7a7a", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                            </div>
                          </div>
                        )}

                        {/* Additions history */}
                        {(flock.additions && flock.additions.length > 0) && (
                          <div style={{ marginTop:10, background:"#0a160e", border:"1px solid #1e3a26", borderRadius:8, padding:"8px 12px" }}>
                            <div style={{ fontSize:9, color:"#5a8a6a", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>🐔➕ Birds Added ({flock.additions.length})</div>
                            {[...flock.additions].sort((a,b)=>b.date.localeCompare(a.date)).map(a => (
                              <div key={a.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", borderBottom:"1px solid #142a1a" }}>
                                <span style={{ fontSize:12, fontWeight:"bold", color:"#8adaa0", minWidth:60 }}>+{a.count.toLocaleString()}</span>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontSize:11, color:"#9aba8a" }}>{formatDate(a.date)}</div>
                                  {a.note && <div style={{ fontSize:10, color:"#6a8a5a", fontStyle:"italic" }}>{a.note}</div>}
                                </div>
                                {pm.isSuper && <button onClick={() => { if(window.confirm(`Remove this addition of ${a.count} birds? The flock total will decrease by ${a.count}.`)) onDeleteAddition(a.id); }}
                                  style={{ background:"none", border:"none", color:"#5a3030", fontSize:14, cursor:"pointer", padding:"0 4px" }}>×</button>}
                              </div>
                            ))}
                          </div>
                        )}
                        </>
                      )}
                    </div>

                    {/* Last 10 daily logs */}
                    <div style={{ padding:"10px 14px" }}>
                      <div style={{ fontSize:10, color:"#6a5a30", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Recent Log</div>
                      {[...logs].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,10).map(e => (
                        <div key={e.date} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 8px", background:"#0a1208", border:"1px solid #1a2010", borderRadius:7, marginBottom:5 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", gap:10, fontSize:11 }}>
                              <span style={{ color:"#8a8a50", minWidth:75 }}>{formatDate(e.date)}</span>
                              {e.mortality>0 && <span style={{ color:"#e08080" }}>💀 {e.mortality}</span>}
                              <span style={{ color:"#e0c040" }}>🥚 {calcEggs(e)} <span style={{ color:"#6a5a30" }}>({e.trays||0}T+{e.eggs||0})</span></span>
                              {bal>0 && <span style={{ color:"#8a7030" }}>{((calcEggs(e)/flockBalance(flock,e.date))*100).toFixed(1)}%</span>}
                            </div>
                            {e.notes && <div style={{ fontSize:9, color:"#5a5a30", fontStyle:"italic", marginTop:1 }}>{e.notes}</div>}
                          </div>
                          {pm.canDelete && <button onClick={()=>{ if(window.confirm("Delete this entry?")) onDeleteDailyLog(flock.id,e.date); }}
                            style={{ background:"none", border:"none", color:"#5a3a3a", fontSize:14, cursor:"pointer", padding:"0 2px" }}>×</button>}
                        </div>
                      ))}
                      {logs.length === 0 && <div style={{ fontSize:11, color:"#4a3a1a", textAlign:"center", padding:"10px 0" }}>No entries yet</div>}
                      {pm.canDelete && <button onClick={() => { if(window.confirm("Remove this batch?")) onDeleteFlock(flock.id); }}
                        style={{ marginTop:8, width:"100%", padding:"8px", background:"#1a0e0e", border:"1px solid #4a2020", borderRadius:7, color:"#a04040", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>🗑 Remove Batch</button>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>{/* end flock-grid */}

          {soldFlocks.length > 0 && (
            <button onClick={() => setShowSold(v => !v)}
              style={{ width:"100%", marginTop:14, padding:"10px", background:"#140f04", border:"1px dashed #4a3a10",
                borderRadius:10, color:"#a08040", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
              {showSold ? "▲ Hide sold batches" : `▼ Show ${soldFlocks.length} sold batch${soldFlocks.length!==1?"es":""}`}
            </button>
          )}
          {soldFlocks.length > 0 && showSold && (
            <div style={{ fontSize:9.5, color:"#6a5a30", textAlign:"center", marginTop:7, lineHeight:1.5 }}>
              Sold batches keep every record from before their sale date — they are no longer counted or numbered with the live flocks.
            </div>
          )}
        </div>
      )}
      </div>{/* end tab fade wrapper */}

      {/* ── ENTRY MODAL (bottom sheet) ── */}
      {editEntry && (
        <EntryModal
          flocks={sortedFlocks}
          entry={editEntry}
          onSave={(flockId, data) => { onSaveDailyLog(flockId, data); setEditEntry(null); }}
          onClose={() => setEditEntry(null)}
        />
      )}
      {showImport && (
        <BulkImportModal
          flocks={farm.flocks}
          onClose={() => setShowImport(false)}
          onSaveDailyLog={onSaveDailyLog}
        />
      )}
    </div>
  );
}

// ─── Entry Modal ──────────────────────────────────────────────────────────────
function EntryModal({ flocks, entry, onSave, onClose }) {
  const [flockId, setFlockId] = useState(entry.flockId || (flocks[0]?.id||""));
  const [date, setDate] = useState(entry.date || todayISO());
  const [mortality, setMortality] = useState(String(entry.mortality||""));
  const [trays, setTrays] = useState(String(entry.trays||""));
  const [eggs, setEggs] = useState(String(entry.eggs||""));
  const [notes, setNotes] = useState(entry.notes||"");

  const selFlock = flocks.find(f => f.id===flockId);
  const bal = selFlock ? flockBalance(selFlock, date) : 0;
  const totalEggsCalc = ((Number(trays)||0)*30) + (Number(eggs)||0);
  const prodPct = bal > 0 && totalEggsCalc > 0 ? ((totalEggsCalc/bal)*100).toFixed(1) : null;

  function handleSave() {
    if (!flockId) return;
    onSave(flockId, {
      date,
      mortality: Number(mortality)||0,
      trays: Number(trays)||0,
      eggs: Number(eggs)||0,
      notes,
    });
  }

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, backdropFilter:"blur(2px)" }} />
      <div style={{
        position:"fixed", left:0, right:0, bottom:0, zIndex:201,
        background:"#0e1208", borderTop:"2px solid #3a3010",
        borderRadius:"16px 16px 0 0", padding:"20px 18px 36px",
        animation:"slideUp 0.38s cubic-bezier(0.16,1,0.3,1)",
        maxHeight:"85vh", overflowY:"auto",
      }}>
        <div style={{ width:40, height:4, background:"#3a3010", borderRadius:4, margin:"0 auto 16px" }} />
        <div style={{ fontSize:14, fontWeight:"bold", color:"#e0c040", marginBottom:14 }}>📋 Log Daily Record</div>

        {/* Flock selector */}
        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:4, letterSpacing:1 }}>BATCH</label>
          <select value={flockId} onChange={e=>setFlockId(e.target.value)} style={{ ...iS, colorScheme:"dark" }}>
            {flocks.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({fmtHatch(f.hatchDate)})</option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:9, color:"#6a5a20", display:"block", marginBottom:4, letterSpacing:1 }}>DATE · {formatDate(date)}</label>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ ...iS, colorScheme:"dark" }} />
        </div>

        {/* Balance info */}
        {selFlock && (
          <div style={{ background:"#0a1008", border:"1px solid #2a2010", borderRadius:7, padding:"7px 10px", marginBottom:12, display:"flex", gap:16, fontSize:11 }}>
            <span style={{ color:"#5a8a5a" }}>🐔 Balance: <strong style={{ color:"#a8d48a" }}>{bal.toLocaleString()}</strong></span>
            <span style={{ color:"#5a5a40" }}>Total Mort: {flockTotalMort(selFlock, date).toLocaleString()}</span>
          </div>
        )}

        {/* Mortality */}
        <div style={{ marginBottom:10 }}>
          <label style={{ fontSize:9, color:"#8a3030", display:"block", marginBottom:4, letterSpacing:1 }}>💀 MORTALITY (deaths today)</label>
          <input type="number" min="0" value={mortality} onChange={e=>setMortality(e.target.value)} placeholder="–" style={{ ...iS, borderColor:"#4a2020", color:"#e8a8a8" }} />
        </div>

        {/* Production */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
          <div>
            <label style={{ fontSize:9, color:"#8a6a00", display:"block", marginBottom:4, letterSpacing:1 }}>🥚 TRAYS (×30 eggs)</label>
            <input type="number" min="0" value={trays} onChange={e=>setTrays(e.target.value)} placeholder="–" style={{ ...iS, borderColor:"#4a3a00", color:"#e8d080" }} />
          </div>
          <div>
            <label style={{ fontSize:9, color:"#8a6a00", display:"block", marginBottom:4, letterSpacing:1 }}>EXTRA EGGS</label>
            <input type="number" min="0" max="29" value={eggs} onChange={e=>setEggs(e.target.value)} placeholder="–" style={{ ...iS, borderColor:"#4a3a00", color:"#e8d080" }} />
          </div>
        </div>

        {/* Auto-calc preview */}
        {totalEggsCalc > 0 && (
          <div style={{ background:"#1a1600", border:"1px solid #4a3a00", borderRadius:8, padding:"8px 12px", marginBottom:10, display:"flex", justifyContent:"space-between", fontSize:12 }}>
            <span style={{ color:"#8a7030" }}>Total eggs: <strong style={{ color:"#e0c040" }}>{totalEggsCalc}</strong></span>
            {prodPct && <span style={{ color:"#8a7030" }}>Prod %: <strong style={{ color:"#d4a840" }}>{prodPct}%</strong></span>}
          </div>
        )}

        {/* Notes */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:9, color:"#5a5a30", display:"block", marginBottom:4, letterSpacing:1 }}>NOTES (optional)</label>
          <input type="text" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Any observations..." style={iS} />
        </div>

        <div style={{ display:"flex", gap:8 }}>
          <button onClick={handleSave} style={{ flex:1, padding:"12px", background:"linear-gradient(135deg,#3a2e00,#2a1e00)", border:"1px solid #7a6020", borderRadius:8, color:"#e0c040", fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:"bold" }}>Save Entry</button>
          <button onClick={onClose} style={{ padding:"12px 18px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#7a7a7a", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
        </div>
      </div>
    </>
  );
}

// ─── Bulk Import Modal ────────────────────────────────────────────────────────
// Paste a day's register rows and save all flocks at once. Each row:
//   hatchdate, mortality, trays, eggs        (eggs optional)
// hatchdate accepts DD/MM/YYYY, DD/MM/YY, D/M/YY, or YYYY-MM-DD. Rows are matched
// to flocks by EXACT hatch date; unmatched rows are flagged and skipped.
function BulkImportModal({ flocks, onClose, onSaveDailyLog }) {
  const [date, setDate] = useState(todayISO());
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Normalize any supported date string → ISO YYYY-MM-DD, or null if unparseable.
  function toISO(s) {
    if (!s) return null;
    s = s.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already ISO
    const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
    if (!m) return null;
    let [, d, mo, y] = m;
    if (y.length === 2) y = "20" + y; // 26 → 2026
    return `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
  }

  // Build a hatchDate → flock lookup
  const byHatch = useMemo(() => {
    const map = {};
    flocks.forEach(f => { if (f.hatchDate) map[f.hatchDate] = f; });
    return map;
  }, [flocks]);

  // Parse the pasted text into preview rows.
  const parsed = useMemo(() => {
    if (!raw.trim()) return [];
    return raw.split("\n").map(line => line.trim()).filter(Boolean).map((line, i) => {
      // split on comma OR tab OR multiple spaces
      const parts = line.split(/[,\t]+|\s{2,}/).map(p => p.trim()).filter(p => p !== "");
      const iso = toISO(parts[0]);
      const flock = iso ? byHatch[iso] : null;
      const mortality = Number(parts[1]) || 0;
      const trays = Number(parts[2]) || 0;
      const eggs = Number(parts[3]) || 0;
      return {
        lineNo: i + 1, rawDate: parts[0] || "", iso, flock,
        mortality, trays, eggs,
        ok: !!flock,
      };
    });
  }, [raw, byHatch]);

  const matched = parsed.filter(r => r.ok);
  const unmatched = parsed.filter(r => !r.ok);

  async function saveAll() {
    if (matched.length === 0) return;
    setSaving(true);
    for (const r of matched) {
      // Same payload shape as a normal daily-log save.
      await onSaveDailyLog(r.flock.id, {
        date, mortality: r.mortality, trays: r.trays, eggs: r.eggs, notes: "",
      });
    }
    setSaving(false); setDone(true);
    setTimeout(() => onClose(), 1200);
  }

  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, backdropFilter:"blur(2px)" }} />
      <div style={{ position:"fixed", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"min(520px,94vw)", maxHeight:"90vh", overflowY:"auto", background:"#0c140a", border:"2px solid #2d5a27", borderRadius:16, zIndex:201, padding:18, boxShadow:"0 10px 50px rgba(0,0,0,0.6)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <span style={{ fontSize:16, fontWeight:"bold", color:"#8ad06a" }}>📋 Bulk Import a Day</span>
          <button onClick={onClose} style={{ background:"#1a2a1a", border:"1px solid #3a5a3a", borderRadius:8, color:"#7aaa6a", fontSize:18, cursor:"pointer", padding:"2px 10px", fontFamily:"inherit" }}>×</button>
        </div>

        {/* Date for the whole import */}
        <label style={{ fontSize:10, color:"#6a8a5a", display:"block", marginBottom:3, letterSpacing:1 }}>DATE FOR ALL ROWS</label>
        <div style={{ position:"relative", marginBottom:12 }}>
          <div style={{ ...iS, colorScheme:"dark", display:"flex", alignItems:"center", justifyContent:"space-between", pointerEvents:"none" }}>
            <span style={{ color:"#c8f0a8", fontWeight:"bold" }}>{formatDate(date)}</span>
            <span style={{ fontSize:11, color:"#6a8a5a" }}>📅 tap to change</span>
          </div>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0, cursor:"pointer", colorScheme:"dark" }} />
        </div>

        {/* Paste box */}
        <label style={{ fontSize:10, color:"#6a8a5a", display:"block", marginBottom:3, letterSpacing:1 }}>PASTE ROWS</label>
        <div style={{ fontSize:10, color:"#5a7a4a", marginBottom:6, lineHeight:1.6 }}>
          One row per flock: <code style={{ background:"#0a1606", padding:"1px 5px", borderRadius:4, color:"#8ad06a" }}>hatchdate, mortality, trays, eggs</code><br/>
          Example: <code style={{ background:"#0a1606", padding:"1px 5px", borderRadius:4, color:"#a8c890" }}>26/1/24, 8, 59, 19</code> &nbsp;(eggs optional)
        </div>
        <textarea value={raw} onChange={e => setRaw(e.target.value)} rows={7}
          placeholder={"26/1/24, 8, 59, 19\n30/4/24, 10, 134, 22\n6/12/25, 6, 245, 3"}
          style={{ ...iS, fontFamily:"monospace", fontSize:13, resize:"vertical", marginBottom:12 }} />

        {/* Preview */}
        {parsed.length > 0 && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:"#6a8a5a", marginBottom:6 }}>
              Preview: <strong style={{ color:"#8ad06a" }}>{matched.length} matched</strong>
              {unmatched.length > 0 && <span style={{ color:"#e08040" }}> · {unmatched.length} not found (will be skipped)</span>}
            </div>
            <div style={{ border:"1px solid #1e3a1a", borderRadius:8, overflow:"hidden" }}>
              {parsed.map(r => (
                <div key={r.lineNo} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px", background:r.ok?"#0c1a0a":"#1c1208", borderBottom:"1px solid #15240f", fontSize:12 }}>
                  <span style={{ fontSize:13 }}>{r.ok ? "✅" : "⚠️"}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    {r.ok ? (
                      <div style={{ color:"#c8e8b0" }}><strong>{r.flock.name}</strong> <span style={{ color:"#5a7a4a", fontSize:10 }}>({formatDate(r.iso)})</span></div>
                    ) : (
                      <div style={{ color:"#e0a060" }}>No flock with hatch date "{r.rawDate}"{r.iso ? ` (${formatDate(r.iso)})` : " — unreadable date"}</div>
                    )}
                  </div>
                  {r.ok && (
                    <div style={{ display:"flex", gap:10, fontSize:11, color:"#8aaa7a", whiteSpace:"nowrap" }}>
                      <span>💀 {r.mortality}</span>
                      <span>🥚 {r.trays}T</span>
                      {r.eggs > 0 && <span>+{r.eggs}e</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {matched.length > 0 && (
              <div style={{ fontSize:10, color:"#5a7a4a", marginTop:6 }}>
                These will be saved as the daily entry for <strong style={{ color:"#8ad06a" }}>{formatDate(date)}</strong>. Existing entries for that date on these flocks will be overwritten.
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {done ? (
          <div style={{ textAlign:"center", padding:"12px", background:"#0e2410", border:"1px solid #2a6a2a", borderRadius:8, color:"#8ad06a", fontSize:13, fontWeight:600 }}>✓ Imported {matched.length} entries!</div>
        ) : (
          <div style={{ display:"flex", gap:8 }}>
            <button disabled={matched.length===0 || saving} onClick={saveAll}
              style={{ flex:1, padding:"11px", background:matched.length===0||saving?"#1a2a1a":"linear-gradient(135deg,#1e4a2a,#153a1f)", border:`1px solid ${matched.length===0||saving?"#2a3a2a":"#3a8a5a"}`, borderRadius:8, color:matched.length===0||saving?"#4a5a4a":"#8adaa0", fontSize:14, cursor:matched.length===0||saving?"default":"pointer", fontFamily:"inherit", fontWeight:"bold" }}>
              {saving ? "Saving…" : `Import ${matched.length} ${matched.length===1?"entry":"entries"}`}
            </button>
            <button onClick={onClose} style={{ padding:"11px 16px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#7a7a7a", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        )}
      </div>
    </>
  );
}

// Compact shared style shortcuts used inside new components
const iS = { background:"#0c1a0a", border:"1px solid #223a1e", borderRadius:10, color:"#c8f0a8", padding:"11px 14px", fontSize:14, fontFamily:"system-ui,sans-serif", width:"100%", boxSizing:"border-box", outline:"none" };
const btnGreen = { padding:"9px 16px", background:"#1a4a14", border:"1px solid #3a8a2a", borderRadius:20, color:"#a8f080", fontSize:13, cursor:"pointer", fontFamily:"system-ui,sans-serif", fontWeight:600 };
const btnGray  = { padding:"9px 16px", background:"#181e16", border:"1px solid #2a3228", borderRadius:20, color:"#6a7a68", fontSize:13, cursor:"pointer", fontFamily:"system-ui,sans-serif" };

// ─── Farm Detail ──────────────────────────────────────────────────────────────
function FarmDetail({ farm, perms: pm = {}, detailTab, setDetailTab, onAddVaccine, onUpdateVaccine, onRemoveVaccine, onAddFlock, onDeleteFlock, onMarkDone, onMarkUndone, onAddDose, onEditDose, onDeleteDose, allFarms, onImportSchedule, onMarkAllDone, focusTarget, onFocusHandled }) {
  const [showAddFlock, setShowAddFlock] = useState(false);
  const [newFlockName, setNewFlockName] = useState("");
  const [newHatchDate, setNewHatchDate] = useState("");
  const [expandedFlock, setExpandedFlock] = useState(null);
  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [confirmCopyFrom, setConfirmCopyFrom] = useState(null); // farm object to confirm copy from
  // When arriving from the alert panel, auto-expand the target flock, scroll to
  // the vaccine row, and briefly highlight it.
  const [highlightVaccineId, setHighlightVaccineId] = useState(null);
  const focusRowRef = useRef(null);
  useEffect(() => {
    if (focusTarget && focusTarget.flockId != null) {
      setDetailTab("flocks");
      setExpandedFlock(focusTarget.flockId);
      setHighlightVaccineId(focusTarget.vaccineId);
      // Scroll after the flock expands & renders.
      const t = setTimeout(() => {
        if (focusRowRef.current) {
          focusRowRef.current.scrollIntoView({ behavior:"smooth", block:"center" });
        }
        onFocusHandled && onFocusHandled();
      }, 250);
      // Clear the highlight after a few seconds.
      const t2 = setTimeout(() => setHighlightVaccineId(null), 3200);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, [focusTarget]);

  const otherFarms = (allFarms || []).filter(f => f.id !== farm.id && f.vaccines.some(v => v.name.trim()));

  const sorted = useMemo(() => [...farm.vaccines].sort((a,b) => (a.day||0)-(b.day||0)), [farm.vaccines]);
  const scheduleComplete = farm.vaccines.length>0 && farm.vaccines.every(v => v.name.trim()!=="" && v.day!=="");
  const today = new Date(); today.setHours(0,0,0,0);

  function handleAddFlock() {
    if (!newFlockName.trim() || !newHatchDate) return;
    onAddFlock(newFlockName.trim(), newHatchDate);
    setNewFlockName(""); setNewHatchDate(""); setShowAddFlock(false);
  }

  function getRows(flock) {
    if (!flock.hatchDate) return [];
    const hatch = new Date(flock.hatchDate); hatch.setHours(0,0,0,0);
    return sorted.map(v => {
      const dueDate = addDays(hatch,(v.day||1)-1);
      const doses = (flock.doses && flock.doses[v.id]) || [];
      const doseTotal = doses.reduce((s,d) => s + (d.count||0), 0);
      const target = flock.initialSize || 0;
      const isDone = !!flock.done[v.id];
      const diff = daysBetween(today,dueDate);
      let status = isDone ? "done" : diff>0 ? "upcoming" : diff===0 ? "today" : "overdue";
      return { ...v, dueDate, diff, status, actualDate: flock.done[v.id] || "", doses, doseTotal, target };
    });
  }

  return (
    <div>
      <div style={{ display:"flex", background:"#0a1408", borderBottom:"1px solid #1a2e16", padding:"4px 8px", gap:4 }}>
        {[["schedule","💉 Schedule"],["flocks","🐔 Flocks"]].map(([key,label]) => (
          <button key={key} onClick={() => setDetailTab(key)} style={{
            flex:1, padding:"10px 0", background:detailTab===key?"#1a3a14":"transparent",
            border:"none", borderRadius:12,
            color:detailTab===key?"#a8f080":"#3a6030", fontSize:13, fontFamily:"system-ui,sans-serif",
            fontWeight:detailTab===key?700:400, cursor:"pointer", transition:"all 0.2s",
          }}>{label}</button>
        ))}
      </div>

      <div key={detailTab} className="anim-tab">
      {detailTab==="schedule" && (
        <div style={{ padding:"18px 16px" }}>
          <div style={{ background:"#162216", border:"1px solid #2d5a27", borderRadius:10, padding:"12px 14px", marginBottom:16, fontSize:12, color:"#7aaa6a" }}>
            Define the vaccination plan for <strong style={{ color:"#a8d48a" }}>{farm.name}</strong>. Applied to all flocks on this farm.
          </div>

          {/* Copy from farm button — adding a schedule, so super-admin only */}
          {pm.canAdd && otherFarms.length > 0 && (
            <button onClick={() => setShowCopyPanel(true)} style={{
              width:"100%", padding:"11px 14px", background:"#0e1e2e",
              border:"1px dashed #2a5a8a", borderRadius:8, color:"#5a9abf",
              fontSize:13, cursor:"pointer", marginBottom:14, fontFamily:"inherit",
              display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            }}>
              <span>📋</span> Copy Schedule from Another Farm
            </button>
          )}

          {/* Copy Panel — slides up from bottom */}
          {showCopyPanel && (
            <>
              <div onClick={() => { setShowCopyPanel(false); setConfirmCopyFrom(null); }} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, backdropFilter:"blur(2px)", animation:"fadeIn 0.3s ease" }} />
              <div style={{
                position:"fixed", left:0, right:0, bottom:0, zIndex:201,
                background:"#0a1a0e", borderTop:"2px solid #2d5a27",
                borderRadius:"18px 18px 0 0", padding:"20px 18px 36px",
                animation:"slideUp 0.38s cubic-bezier(0.16,1,0.3,1)",
                maxHeight:"75vh", overflowY:"auto",
              }}>
                {/* Handle bar */}
                <div style={{ width:40, height:4, background:"#2d5a27", borderRadius:4, margin:"0 auto 18px" }} />

                {confirmCopyFrom ? (
                  /* Confirmation step */
                  <div>
                    <div style={{ fontSize:15, fontWeight:"bold", color:"#a8d48a", marginBottom:6 }}>Confirm Copy</div>
                    <div style={{ fontSize:13, color:"#7aaa6a", marginBottom:16, lineHeight:1.6 }}>
                      Replace <strong style={{ color:"#e8d48a" }}>{farm.name}</strong>'s schedule with{" "}
                      <strong style={{ color:FARM_COLORS[confirmCopyFrom.colorIdx].accent }}>{confirmCopyFrom.name}</strong>'s{" "}
                      <strong style={{ color:"#a8d48a" }}>{confirmCopyFrom.vaccines.filter(v=>v.name.trim()).length} vaccines</strong>?
                      <br/><span style={{ color:"#8a6a4a", fontSize:11 }}>⚠️ This will replace the current schedule. Flock vaccination records are kept.</span>
                    </div>
                    {/* Preview of source schedule */}
                    <div style={{ background:"#0e1a0e", border:"1px solid #2a4a2a", borderRadius:8, padding:"10px 12px", marginBottom:16, maxHeight:180, overflowY:"auto" }}>
                      {confirmCopyFrom.vaccines.filter(v=>v.name.trim()).sort((a,b)=>(a.day||0)-(b.day||0)).map(v => (
                        <div key={v.id} style={{ display:"flex", gap:10, padding:"4px 0", borderBottom:"1px solid #1a2a1a", fontSize:12 }}>
                          <span style={{ color:"#4a8a4a", minWidth:42 }}>Day {v.day}</span>
                          <span style={{ color:"#c8e8b0", flex:1 }}>{v.name}</span>
                          <span style={{ color:"#4a6a4a" }}>{v.route}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => {
                        onImportSchedule(confirmCopyFrom.id);
                        setConfirmCopyFrom(null); setShowCopyPanel(false);
                      }} style={{
                        flex:1, padding:"12px", background:"linear-gradient(135deg,#1a4a8a,#0e2e5a)",
                        border:"1px solid #3a6aaa", borderRadius:8, color:"#a8d0f0",
                        fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:"bold",
                      }}>✓ Yes, Copy Schedule</button>
                      <button onClick={() => setConfirmCopyFrom(null)} style={{
                        padding:"12px 18px", background:"#1a1a1a", border:"1px solid #3a3a3a",
                        borderRadius:8, color:"#7a7a7a", fontSize:14, cursor:"pointer", fontFamily:"inherit",
                      }}>← Back</button>
                    </div>
                  </div>
                ) : (
                  /* Farm selection step */
                  <div>
                    <div style={{ fontSize:15, fontWeight:"bold", color:"#a8d48a", marginBottom:4 }}>📋 Copy Schedule From</div>
                    <div style={{ fontSize:12, color:"#5a7a5a", marginBottom:16 }}>Select a farm to copy its vaccine schedule into <strong style={{ color:"#a8d48a" }}>{farm.name}</strong></div>
                    {otherFarms.map(f => {
                      const color = FARM_COLORS[f.colorIdx];
                      const vCount = f.vaccines.filter(v=>v.name.trim()).length;
                      return (
                        <div key={f.id} onClick={() => setConfirmCopyFrom(f)} style={{
                          background:`linear-gradient(135deg,${color.dark}88,#0a120888)`,
                          border:`1px solid ${color.border}`, borderRadius:10,
                          padding:"13px 14px", marginBottom:10, cursor:"pointer",
                          display:"flex", alignItems:"center", gap:12,
                          transition:"all 0.15s",
                        }}>
                          <span style={{ fontSize:22 }}>🏡</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:14, fontWeight:"bold", color:color.accent }}>{f.name}</div>
                            {f.location && <div style={{ fontSize:11, color:"#5a7a5a" }}>📍 {f.location}</div>}
                            <div style={{ fontSize:11, color:"#5a8a5a", marginTop:2 }}>
                              💉 {vCount} vaccine{vCount!==1?"s":""}
                              {" · "}
                              {f.vaccines.filter(v=>v.name.trim()).sort((a,b)=>(a.day||0)-(b.day||0)).slice(0,2).map(v=>v.name).join(", ")}
                              {vCount>2 ? `… +${vCount-2} more` : ""}
                            </div>
                          </div>
                          <span style={{ color:color.accent, fontSize:20 }}>›</span>
                        </div>
                      );
                    })}
                    <button onClick={() => setShowCopyPanel(false)} style={{
                      width:"100%", padding:"11px", background:"#1a1a1a", border:"1px solid #3a3a3a",
                      borderRadius:8, color:"#7a7a7a", fontSize:13, cursor:"pointer", marginTop:4, fontFamily:"inherit",
                    }}>Cancel</button>
                  </div>
                )}
              </div>
            </>
          )}

          {!pm.canEdit && farm.vaccines.filter(v=>v.name.trim()).length===0 && (
            <div style={{ textAlign:"center", padding:"30px 20px", color:"#3a5a3a", fontSize:13 }}>No vaccines in the schedule yet.</div>
          )}

          {(() => {
            // Column template shared by header and rows. Delete column only if allowed.
            const cols = pm.canDelete
              ? "64px 1.3fr 1fr 1fr 1.1fr 100px 34px"
              : "64px 1.3fr 1fr 1fr 1.1fr 100px";
            return (
              <>
                <div className="vac-head" style={{ gridTemplateColumns: cols }}>
                  <div>Day</div><div>Vaccine</div><div>Illness</div><div>Company</div><div>Dosage Note</div><div>Route</div>{pm.canDelete && <div></div>}
                </div>
                {farm.vaccines.map(v => (
                  <div key={v.id} className="vac-row" style={{ gridTemplateColumns: cols }}>
                    <div className="vac-cell">
                      <span className="vac-label">Day</span>
                      <input type="number" min="1" max="180" value={v.day} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"day",Number(e.target.value))} placeholder="Day" style={{ ...inputStyle, padding:"11px 6px", textAlign:"center", opacity:pm.canEdit?1:0.7 }} />
                    </div>
                    <div className="vac-cell">
                      <span className="vac-label">Vaccine Name</span>
                      <input type="text" value={v.name} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"name",e.target.value)} placeholder="Vaccine name" style={{ ...inputStyle, opacity:pm.canEdit?1:0.7 }} />
                    </div>
                    <div className="vac-cell">
                      <span className="vac-label">For Illness</span>
                      <input type="text" value={v.illness||""} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"illness",e.target.value)} placeholder="e.g. Newcastle" style={{ ...inputStyle, opacity:pm.canEdit?1:0.7 }} />
                    </div>
                    <div className="vac-cell">
                      <span className="vac-label">Company</span>
                      <input type="text" value={v.company||""} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"company",e.target.value)} placeholder="e.g. Ventri" style={{ ...inputStyle, opacity:pm.canEdit?1:0.7 }} />
                    </div>
                    <div className="vac-cell">
                      <span className="vac-label">Dosage Note</span>
                      <input type="text" value={v.dosageNote||""} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"dosageNote",e.target.value)} placeholder="e.g. 0.5ml / bird" style={{ ...inputStyle, opacity:pm.canEdit?1:0.7 }} />
                    </div>
                    <div className="vac-cell">
                      <span className="vac-label">Route</span>
                      <select value={v.route} disabled={!pm.canEdit} onChange={e => onUpdateVaccine(v.id,"route",e.target.value)} style={{ ...inputStyle, cursor:pm.canEdit?"pointer":"default", opacity:pm.canEdit?1:0.7 }}>
                        {ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    {pm.canDelete && (
                      <div className="vac-del-wrap">
                        <button onClick={() => onRemoveVaccine(v.id)} style={{ background:"#2a1515", border:"1px solid #5a2a2a", borderRadius:6, color:"#c06060", fontSize:16, cursor:"pointer", padding:"6px 12px", lineHeight:1 }}>×</button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            );
          })()}
          {pm.canAdd && <button onClick={onAddVaccine} style={{ width:"100%", padding:"11px", background:"#162616", border:"1px dashed #3a6a2a", borderRadius:8, color:"#6abf4b", fontSize:13, cursor:"pointer", marginTop:6, fontFamily:"inherit" }}>+ Add Vaccination</button>}
          {scheduleComplete && (
            <button onClick={() => setDetailTab("flocks")} style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#2a6a1a,#1e5014)", border:"none", borderRadius:8, color:"#c8f0b0", fontSize:14, cursor:"pointer", marginTop:14, fontFamily:"inherit", fontWeight:"bold" }}>Go to Flocks →</button>
          )}
        </div>
      )}

      {detailTab==="flocks" && (
        <div style={{ padding:"18px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:12, color:"#5a7a4a", letterSpacing:2, textTransform:"uppercase" }}>Flocks ({farm.flocks.filter(flockActiveNow).length})</div>
            {pm.canAdd && <button onClick={() => setShowAddFlock(true)} style={{ background:"#1e3a1e", border:"1px solid #3a7a2a", borderRadius:8, color:"#6abf4b", fontSize:12, padding:"7px 12px", cursor:"pointer", fontFamily:"inherit" }}>+ New Flock</button>}
          </div>

          {showAddFlock && (
            <div className="anim-roll" style={{ background:"#0e1e0e", border:"1px solid #2d5a27", borderRadius:12, padding:14, marginBottom:14, animation:"fadeIn 0.3s ease" }}>
              <div style={{ fontSize:12, color:"#7aaa6a", marginBottom:10, fontWeight:"bold", letterSpacing:1 }}>NEW FLOCK / BATCH</div>
              <input type="text" value={newFlockName} onChange={e => setNewFlockName(e.target.value)} placeholder="Flock name (e.g. Batch A)" style={{ ...inputStyle, marginBottom:8 }} />
              <label style={{ fontSize:10, color:"#5a7a4a", display:"block", marginBottom:4 }}>HATCH DATE</label>
              <input type="date" value={newHatchDate} onChange={e => setNewHatchDate(e.target.value)} style={{ ...inputStyle, marginBottom:12, colorScheme:"dark" }} />
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={handleAddFlock} style={{ flex:1, padding:"10px", background:"linear-gradient(135deg,#2a6a1a,#1e5014)", border:"none", borderRadius:8, color:"#c8f0b0", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Add Flock</button>
                <button onClick={() => { setShowAddFlock(false); setNewFlockName(""); setNewHatchDate(""); }} style={{ padding:"10px 14px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#7a7a7a", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          )}

          {farm.flocks.filter(flockActiveNow).length===0 && !showAddFlock && (
            <div style={{ textAlign:"center", padding:"40px 20px", color:"#3a5a3a" }}>
              <div style={{ fontSize:40, marginBottom:10 }}>🐔</div>
              <div style={{ fontSize:14 }}>No flocks added yet</div>
            </div>
          )}

          <div className="flock-grid">
          {farm.flocks.filter(flockActiveNow).map(flock => {
            const rows = getRows(flock);
            const counts = { done:0, today:0, upcoming:0, overdue:0 };
            rows.forEach(r => counts[r.status]++);
            const isExpanded = expandedFlock===flock.id;

            return (
              <div key={flock.id} style={{ background:"#0e1a0e", border:"1px solid #2a4a2a", borderRadius:12, overflow:"hidden", gridColumn: isExpanded ? "1 / -1" : "auto" }}>
                {/* Flock header */}
                <div style={{ padding:"14px 16px", cursor:"pointer" }} onClick={() => setExpandedFlock(isExpanded?null:flock.id)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:15, fontWeight:"bold", color:"#a8d48a", marginBottom:3 }}>🐥 {flock.name}</div>
                      <div style={{ fontSize:11, color:"#5a7a5a" }}>🥚 Hatched: {formatDate(flock.hatchDate)} · <span style={{ color:"#7a9a6a", fontWeight:600 }}>Age {flockAge(flock.hatchDate)}d</span></div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ display:"flex", gap:6, justifyContent:"flex-end", marginBottom:4 }}>
                        {counts.overdue>0 && <span style={{ background:"#3a1010", border:"1px solid #8a2020", color:"#e06060", fontSize:10, borderRadius:12, padding:"2px 7px" }}>⚠️ {counts.overdue} late</span>}
                        {counts.today>0 && <span style={{ background:"#2a2010", border:"1px solid #8a6020", color:"#e0b040", fontSize:10, borderRadius:12, padding:"2px 7px" }}>⚡ {counts.today} today</span>}
                      </div>
                      <div style={{ color:"#4a6a4a", fontSize:18 }}>{isExpanded?"▲":"▼"}</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ marginTop:10 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#4a6a4a", marginBottom:4 }}>
                      <span>{counts.done} of {rows.length} given</span>
                      <span>{rows.length>0?Math.round(counts.done/rows.length*100):0}%</span>
                    </div>
                    <div style={{ background:"#1a2a1a", borderRadius:4, height:6, overflow:"hidden" }}>
                      <div style={{ background:"linear-gradient(90deg,#3a9a2a,#6abf4b)", height:"100%", width:`${rows.length>0?counts.done/rows.length*100:0}%`, borderRadius:4, transition:"width 0.4s ease" }} />
                    </div>
                  </div>

                  <div style={{ display:"flex", gap:8, marginTop:8 }}>
                    {[
                      { label:"Done", count:counts.done, color:"#4a9a3a" },
                      { label:"Today", count:counts.today, color:"#c09a20" },
                      { label:"Soon", count:counts.upcoming, color:"#3a6a9a" },
                      { label:"Late", count:counts.overdue, color:"#bf4a4a" },
                    ].map(s => (
                      <div key={s.label} style={{ flex:1, background:"#0a100a", border:`1px solid ${s.color}33`, borderRadius:6, padding:"5px 4px", textAlign:"center" }}>
                        <div style={{ fontSize:15, fontWeight:"bold", color:s.color }}>{s.count}</div>
                        <div style={{ fontSize:9, color:s.color+"99", textTransform:"uppercase", letterSpacing:0.5 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expanded: vaccination rows */}
                {isExpanded && (
                  <div className="anim-roll" style={{ borderTop:"1px solid #1e3a1e", padding:"12px 14px" }}>
                    {rows.length===0 && <div style={{ color:"#4a6a4a", fontSize:12, textAlign:"center", padding:"10px 0" }}>No vaccines in schedule. Add them in the 💉 Schedule tab.</div>}
                    {/* Bulk action: mark every vaccine done at once (for old flocks already fully vaccinated) */}
                    {rows.length > 0 && pm.canAdd && (() => {
                      const pending = rows.filter(r => !(r.doseTotal >= r.target && r.target > 0)).length;
                      return pending > 0 ? (
                        <button
                          onClick={() => {
                            if (window.confirm(`Mark all ${pending} remaining vaccine(s) as fully done for ${flock.name}? This uses today's date for each. Use this for old flocks that are already fully vaccinated.`)) {
                              onMarkAllDone(flock.id);
                            }
                          }}
                          style={{ width:"100%", padding:"10px", marginBottom:12, background:"linear-gradient(135deg,#1e4a1a,#15380f)", border:"1px solid #3a7a2a", borderRadius:8, color:"#a8f080", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                          ✓ Mark all {pending} vaccine{pending!==1?"s":""} done (old flock)
                        </button>
                      ) : (
                        <div style={{ textAlign:"center", padding:"8px", marginBottom:8, background:"#0e1a0c", border:"1px solid #2a4a1e", borderRadius:8, fontSize:12, color:"#6aaa5a" }}>
                          ✓ All vaccines complete for this flock
                        </div>
                      );
                    })()}
                    {rows.map(row => {
                      const isFocus = highlightVaccineId === row.id;
                      return (
                      <div
                        key={row.id}
                        ref={isFocus ? focusRowRef : null}
                        style={isFocus ? { outline:"2px solid #6abf3a", outlineOffset:2, borderRadius:10, transition:"outline 0.3s ease", animation:"pulse 1.5s ease 2" } : undefined}>
                      <VaccineRow
                        vaccine={row}
                        perms={pm}
                        dueDate={row.dueDate}
                        actualDate={row.actualDate}
                        doses={row.doses}
                        doseTotal={row.doseTotal}
                        target={row.target}
                        onMarkDone={(date) => onMarkDone(flock.id, row.id, date)}
                        onMarkUndone={() => onMarkUndone(flock.id, row.id)}
                        onChangeDate={(date) => onMarkDone(flock.id, row.id, date)}
                        onAddDose={(dose) => onAddDose(flock.id, row.id, dose)}
                        onEditDose={onEditDose}
                        onDeleteDose={onDeleteDose}
                      />
                      </div>
                      );
                    })}
                    {pm.canDelete && <button onClick={() => { if (window.confirm("Delete this flock?")) onDeleteFlock(flock.id); }} style={{ marginTop:8, width:"100%", padding:"8px", background:"#1a0e0e", border:"1px solid #4a2020", borderRadius:7, color:"#a04040", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>🗑 Remove Flock</button>}
                  </div>
                )}
              </div>
            );
          })}
          </div>{/* end flock-grid */}
        </div>
      )}
      </div>{/* end tab fade wrapper */}
      </div>
  );
}

// ─── EGG STOCK MODULE ─────────────────────────────────────────────────────────
// Daily egg-store ledger per farm, all in whole TRAYS.
//   closing = opening + production + Σreceived − breakage − Σissued − Σsale ± difference
// Single-value fields (opening, production, breakage, difference) save on blur.
// received / issued / sale are line-item lists you add/edit/delete.
function EggStock({ farm, perms: pm = {}, reportError }) {
  const [tab, setTab] = useState("ledger"); // "ledger" | "monitor"
  const [selDate, setSelDate] = useState(todayISO());
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addKind, setAddKind] = useState(null); // 'received' | 'issued' | 'sale' | null

  const canEdit = pm.canEdit;

  // Load the stock for the selected date (server auto-creates with carried opening).
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall(`/farms/${farm.id}/egg-stock?date=${selDate}`);
      setStock(data);
    } catch (e) { reportError && reportError(e); }
    setLoading(false);
  }, [farm.id, selDate]);

  useEffect(() => { load(); }, [load]);

  // Save a single-value field (optimistic), then persist.
  async function saveField(field, value) {
    if (!stock) return;
    const v = value === "" ? 0 : Math.trunc(Number(value) || 0);
    setStock(s => ({ ...s, [field]: v }));
    try {
      // Send ONLY the changed field — the server preserves all others from the DB.
      // This prevents a fast second edit from overwriting the first with stale state.
      const updated = await apiCall(`/egg-stock/${stock.id}`, {
        method: "PUT",
        body: JSON.stringify({ [field]: v }),
      });
      setStock(updated);
    } catch (e) { reportError && reportError(e); load(); }
  }
  // Clear a field back to dash (unentered): value 0 + flag cleared.
  async function clearField(flagKey) {
    if (!stock) return;
    try { setStock(await apiCall(`/egg-stock/${stock.id}`, { method:"PUT", body: JSON.stringify({ [flagKey]: false }) })); }
    catch (e) { reportError && reportError(e); load(); }
  }

  async function refreshProduction() {
    if (!stock) return;
    try { setStock(await apiCall(`/egg-stock/${stock.id}/refresh-production`, { method:"POST" })); }
    catch (e) { reportError && reportError(e); }
  }

  async function addLine(kind, payload) {
    if (!stock) return;
    try {
      const updated = await apiCall(`/egg-stock/${stock.id}/lines`, { method:"POST", body: JSON.stringify({ kind, ...payload }) });
      setStock(updated); setAddKind(null);
    } catch (e) { reportError && reportError(e); }
  }
  async function editLine(lineId, payload) {
    try { setStock(await apiCall(`/egg-stock-lines/${lineId}`, { method:"PUT", body: JSON.stringify(payload) })); }
    catch (e) { reportError && reportError(e); }
  }
  async function deleteLine(lineId) {
    try { setStock(await apiCall(`/egg-stock-lines/${lineId}`, { method:"DELETE" })); }
    catch (e) { reportError && reportError(e); }
  }

  function shiftDate(days) {
    const d = new Date(selDate); d.setDate(d.getDate()+days);
    setSelDate(d.toISOString().slice(0,10));
  }

  const _dld = new Date(selDate + "T00:00:00");
  const _wd = _dld.toLocaleDateString("en-GB", { weekday:"short" });
  const dateLabel = `${_wd}, ${formatDate(selDate)}`; // e.g. "Mon, 01/06/2026"
  const ORANGE = { accent:"#d8945a", dark:"#1e1206", border:"#4a2e12", pill:"#2e1c0a" };

  // A read-only computed line in the ledger
  const LedgerRow = ({ label, value, sign, bold, color, sub }) => (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid #1a1510" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        {sign && <span style={{ fontSize:15, fontWeight:"bold", color:sign==="+"?"#6aaa4a":"#c06a4a", width:14, textAlign:"center" }}>{sign}</span>}
        {!sign && <span style={{ width:14 }} />}
        <span style={{ fontSize:bold?14:13, fontWeight:bold?"bold":500, color:color||"#c8b8a0" }}>{label}</span>
      </div>
      <div style={{ textAlign:"right" }}>
        <span style={{ fontSize:bold?18:15, fontWeight:"bold", color:color||"#e8d8c0" }}>{value}</span>
        <span style={{ fontSize:10, color:"#7a6a5a", marginLeft:4 }}>trays</span>
        {sub && <div style={{ fontSize:9, color:"#7a6a5a" }}>{sub}</div>}
      </div>
    </div>
  );

  // An editable single-value field row. When `flag` is provided, the field shows
  // a dash until the user deliberately enters a value (including 0). Backspacing
  // to empty clears it back to a dash.
  const EditRow = ({ label, field, sign, allowNegative, hint, badge, flag }) => {
    const val = stock ? stock[field] : 0;
    const entered = flag ? !!(stock && stock[flag]) : true;
    return (
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid #1a1510" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {sign && <span style={{ fontSize:15, fontWeight:"bold", color:sign==="+"?"#6aaa4a":"#c06a4a", width:14, textAlign:"center" }}>{sign}</span>}
          {!sign && <span style={{ width:14 }} />}
          <div>
            <span style={{ fontSize:13, fontWeight:500, color:"#c8b8a0" }}>{label}</span>
            {badge}
            {hint && <div style={{ fontSize:9, color:"#6a5a4a" }}>{hint}</div>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <PackNumInput
            value={entered ? val : ""}
            placeholder="–"
            width={90}
            onCommit={(raw) => {
              if (!canEdit) return;
              if (raw === "") { if (entered && flag) clearField(flag); return; }
              const nv = allowNegative ? Math.trunc(Number(raw)||0) : Math.max(0, Math.trunc(Number(raw)||0));
              if (nv !== val || !entered) saveField(field, nv);
            }}
          />
          <span style={{ fontSize:10, color:"#7a6a5a", width:30 }}>trays</span>
        </div>
      </div>
    );
  };

  // Source badge: "auto" if the current value equals the auto value (even if it was
  // once edited to that same number), otherwise "edited".
  const SourceBadge = ({ isAuto }) => (
    <span style={{
      display:"inline-block", marginLeft:7, fontSize:8, fontWeight:700, letterSpacing:0.5,
      borderRadius:20, padding:"1px 7px", verticalAlign:"middle",
      color: isAuto ? "#5a9ab0" : "#d8945a",
      background: isAuto ? "#0a2230" : "#2a1808",
      border: `1px solid ${isAuto ? "#2a6a8a" : "#6a4018"}`,
    }}>{isAuto ? "⟳ AUTO" : "✎ EDITED"}</span>
  );

  return (
    <div className="anim-tab">
      {/* Tabs */}
      <div style={{ display:"flex", background:"#140c04", border:`1px solid ${ORANGE.border}`, padding:"4px 8px", gap:4, marginBottom:14, borderRadius:12 }}>
        {[["ledger","📒 Daily Ledger"],["monitor","📊 Monitor"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex:1, padding:"10px 0", background:tab===k?"#2a1808":"transparent",
            border:"none", borderRadius:10,
            color:tab===k?ORANGE.accent:"#7a5a3a", fontSize:13, fontFamily:"system-ui,sans-serif",
            fontWeight:tab===k?700:400, cursor:"pointer", transition:"all 0.2s",
          }}>{l}</button>
        ))}
      </div>

      {tab==="monitor" && <EggMonthlyMonitor farm={farm} selDate={selDate} reportError={reportError} />}

      {tab==="ledger" && (<>
      {/* Date selector */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:`linear-gradient(135deg,${ORANGE.dark},#140c04)`, border:`1px solid ${ORANGE.border}`, borderRadius:14, padding:"10px 12px", marginBottom:14 }}>
        <button onClick={() => shiftDate(-1)} style={{ background:ORANGE.pill, border:`1px solid ${ORANGE.border}`, borderRadius:8, color:ORANGE.accent, fontSize:16, cursor:"pointer", padding:"4px 12px", fontFamily:"inherit" }}>‹</button>
        <div style={{ textAlign:"center", position:"relative" }}>
          <div style={{ fontSize:15, fontWeight:"bold", color:ORANGE.accent }}>{dateLabel}</div>
          <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
            style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%" }} />
          <div style={{ fontSize:9, color:"#8a6a4a" }}>tap to pick date</div>
        </div>
        <button onClick={() => shiftDate(1)} style={{ background:ORANGE.pill, border:`1px solid ${ORANGE.border}`, borderRadius:8, color:ORANGE.accent, fontSize:16, cursor:"pointer", padding:"4px 12px", fontFamily:"inherit" }}>›</button>
      </div>

      {loading && <div style={{ textAlign:"center", padding:40, color:"#7a6a5a", fontSize:13 }}>Loading stock…</div>}

      {!loading && stock && (
        <>
          {/* Closing balance hero */}
          <div style={{ background:`linear-gradient(135deg,${ORANGE.dark},#1a0e04)`, border:`1.5px solid ${ORANGE.accent}66`, borderRadius:16, padding:"16px 18px", marginBottom:14, textAlign:"center" }}>
            <div style={{ fontSize:11, color:"#a07850", letterSpacing:1, textTransform:"uppercase" }}>Closing Stock</div>
            <div style={{ fontSize:34, fontWeight:"bold", color:"#f0c890", lineHeight:1.1, marginTop:2 }}>{stock.closing.toLocaleString()}</div>
            <div style={{ fontSize:11, color:"#a07850" }}>trays{stock.closing!==0?` · = ${(stock.closing*30).toLocaleString()} eggs`:""}</div>
            <div style={{ fontSize:9, color:"#7a5a3a", marginTop:4 }}>carries to tomorrow's opening</div>
          </div>

          {/* The ledger */}
          <div style={{ background:"#0e0c08", border:`1px solid ${ORANGE.border}`, borderRadius:14, overflow:"hidden", marginBottom:14 }}>
            <EditRow label="Opening Stock" field="opening" hint="carried from yesterday · editable"
              badge={<SourceBadge isAuto={stock.opening === stock.autoOpening} />} />
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid #1a1510" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:15, fontWeight:"bold", color:"#6aaa4a", width:14, textAlign:"center" }}>+</span>
                <div>
                  <span style={{ fontSize:13, fontWeight:500, color:"#c8b8a0" }}>Production</span>
                  <SourceBadge isAuto={stock.production === stock.autoProduction} />
                  <div style={{ fontSize:9, color:"#6a5a4a" }}>auto from Production: total eggs ÷ 30 (whole trays)</div>
                </div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                {canEdit && <button onClick={refreshProduction} title="Refresh from Production module" style={{ background:"#1a2e1a", border:"1px solid #2a4a2a", borderRadius:6, color:"#7aaa6a", fontSize:11, cursor:"pointer", padding:"5px 8px", fontFamily:"inherit" }}>↻</button>}
                <PackNumInput value={stock.production} placeholder="0" width={90}
                  onCommit={(raw) => { if (!canEdit || raw === "") return; const nv=Math.trunc(Number(raw)||0); if (nv!==stock.production) saveField("production", nv); }} />
                <span style={{ fontSize:10, color:"#7a6a5a", width:30 }}>trays</span>
              </div>
            </div>

            {/* Received line items */}
            <LineGroupSection kind="received" sign="+" label="Received" subtitle="from another farm / store"
              items={stock.received} total={stock.receivedTotal} canEdit={canEdit}
              addOpen={addKind==="received"} onOpenAdd={() => setAddKind(addKind==="received"?null:"received")}
              onAdd={(p)=>addLine("received",p)} onEdit={editLine} onDelete={deleteLine} />

            <EditRow label="Breakage" field="breakage" sign="−" hint="damaged / cracked trays" flag="breakageSet" />

            {/* Issued line items */}
            <LineGroupSection kind="issued" sign="−" label="Issued" subtitle="to another farm / vehicle"
              items={stock.issued} total={stock.issuedTotal} canEdit={canEdit}
              addOpen={addKind==="issued"} onOpenAdd={() => setAddKind(addKind==="issued"?null:"issued")}
              onAdd={(p)=>addLine("issued",p)} onEdit={editLine} onDelete={deleteLine} />

            {/* Sale line items */}
            <LineGroupSection kind="sale" sign="−" label="Sale" subtitle="buyer + vehicle no."
              items={stock.sale} total={stock.saleTotal} canEdit={canEdit}
              addOpen={addKind==="sale"} onOpenAdd={() => setAddKind(addKind==="sale"?null:"sale")}
              onAdd={(p)=>addLine("sale",p)} onEdit={editLine} onDelete={deleteLine} />

            <EditRow label="Difference (±)" field="difference" hint="manual correction (can be negative)" allowNegative flag="differenceSet" />

            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px", background:`${ORANGE.accent}11` }}>
              <span style={{ fontSize:15, fontWeight:"bold", color:"#f0c890", marginLeft:22 }}>Closing Stock</span>
              <div style={{ textAlign:"right" }}>
                <span style={{ fontSize:20, fontWeight:"bold", color:"#f0c890" }}>{stock.closing.toLocaleString()}</span>
                <span style={{ fontSize:10, color:"#a07850", marginLeft:4 }}>trays</span>
              </div>
            </div>
          </div>

          {!canEdit && <div style={{ textAlign:"center", fontSize:11, color:"#6a5a4a", marginBottom:10 }}>👁️ View only — ask a super admin or admin to edit stock.</div>}
          <div style={{ textAlign:"center", fontSize:10, color:"#5a4a3a", paddingBottom:20 }}>
            1 tray = 30 eggs · Closing carries to the next day automatically
          </div>
        </>
      )}
      </>)}
    </div>
  );
}

// Shared From→To date range picker used by every monitor tab. Two tap-to-open
// native date fields + quick presets (This month / Last 7d / Last 30d). Calls
// onChange(fromISO, toISO). `accent` tints it to match each module's colour.
function DateRangePicker({ from, to, onChange, accent = "#7ad0e8" }) {
  const today = todayISO();
  const border = "#2a4a5a";
  const fieldBox = { flex:1, minWidth:0, position:"relative" };
  const fieldFace = (val) => ({ background:"#0b1218", border:`1px solid ${border}`, borderRadius:9, color: val?"#e0eef4":"#5a7a88", padding:"8px 10px", fontSize:13, fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"space-between", boxSizing:"border-box" });
  const hidden = { position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" };
  const openNative = (e) => { const el = e.currentTarget; if (el.showPicker) { try { el.showPicker(); } catch { /* older browsers focus normally */ } } };
  const spanDays = (() => { const a = new Date(from + "T00:00:00"), b = new Date(to + "T00:00:00"); const n = Math.round((b - a) / 86400000) + 1; return isNaN(n) ? 0 : n; })();
  const isoBack = (n) => { const d = new Date(today + "T00:00:00"); d.setDate(d.getDate() - (n - 1)); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
  function preset(kind) {
    if (kind === "month") { onChange(today.slice(0, 7) + "-01", today); return; }
    onChange(isoBack(kind === "7" ? 7 : 30), today);
  }
  const isMonth = from === today.slice(0,7) + "-01" && to === today;
  const isN = (n) => from === isoBack(n) && to === today;
  const chip = (label, on, fn) => (
    <button onClick={fn} style={{ fontSize:10, padding:"4px 10px", borderRadius:20, cursor:"pointer", fontFamily:"inherit",
      background: on ? `${accent}22` : "#0b1218", color: on ? accent : "#6a8a98", border:`1px solid ${on ? accent : "#243a44"}` }}>{label}</button>
  );
  return (
    <div style={{ background:"linear-gradient(135deg,#0e1a22,#0a1218)", border:`1px solid ${border}`, borderRadius:14, padding:"10px 12px", marginBottom:14 }}>
      <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
        <div style={fieldBox}>
          <div style={{ fontSize:8, color:"#6a8a98", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 }}>From</div>
          <div style={fieldFace(from)}><span>{from ? formatDate(from) : "pick"}</span><span style={{ opacity:0.8 }}>📅</span></div>
          <input type="date" value={from} max={to || today} onChange={e=>e.target.value && onChange(e.target.value, to)} onClick={openNative} style={hidden} />
        </div>
        <div style={{ color:accent, fontSize:14, paddingBottom:9 }}>→</div>
        <div style={fieldBox}>
          <div style={{ fontSize:8, color:"#6a8a98", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 }}>To</div>
          <div style={fieldFace(to)}><span>{to ? formatDate(to) : "pick"}</span><span style={{ opacity:0.8 }}>📅</span></div>
          <input type="date" value={to} min={from || undefined} max={today} onChange={e=>e.target.value && onChange(from, e.target.value)} onClick={openNative} style={hidden} />
        </div>
      </div>
      <div style={{ display:"flex", gap:6, marginTop:9, alignItems:"center", flexWrap:"wrap" }}>
        {chip("This month", isMonth, () => preset("month"))}
        {chip("Last 7d", isN(7), () => preset("7"))}
        {chip("Last 30d", isN(30), () => preset("30"))}
        <span style={{ marginLeft:"auto", fontSize:10, color:"#5a7a88" }}>{spanDays > 0 ? `${spanDays} day${spanDays !== 1 ? "s" : ""}` : ""}</span>
      </div>
    </div>
  );
}

// Egg Stock "Monitor" tab: the whole month's daily ledger on one screen —
// opening, production, received, issued, sale, breakage, difference, closing per
// day (newest first), plus month totals of the flow figures. Read-only.
function EggMonthlyMonitor({ farm, selDate, reportError }) {
  const anchor = selDate || todayISO();
  const [from, setFrom] = useState(anchor.slice(0, 7) + "-01");
  const [to, setTo] = useState(anchor);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try { const d = await apiCall(`/farms/${farm.id}/egg-stock-monthly?from=${from}&to=${to}`); if (alive) setData(d); }
      catch (e) { reportError && reportError(e); }
      if (alive) setBusy(false);
    })();
    return () => { alive = false; };
  }, [farm.id, from, to]);

  const rangeLabel = `${formatDate(from)} → ${formatDate(to)}`;
  const O = { accent:"#d8945a", dark:"#1e1206", border:"#4a2e12" };
  const [saleTip, setSaleTip] = useState(null);
  // The tooltip is viewport-fixed, so any scroll or resize would leave it stranded.
  useEffect(() => {
    if (!saleTip) return;
    const clear = () => setSaleTip(null);
    window.addEventListener("scroll", clear, true);
    window.addEventListener("resize", clear);
    return () => { window.removeEventListener("scroll", clear, true); window.removeEventListener("resize", clear); };
  }, [saleTip]);

  const NumCell = ({ v, color, strong }) => (
    <td style={{ padding:"7px 9px", textAlign:"right", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums",
      color: v ? (color||"#e8d8c0") : "#5a4a3a", fontWeight: strong?700:400, fontSize:12 }}>
      {v ? v.toLocaleString() : "–"}
    </td>
  );
  // Hovering a day's Sale figure breaks it down by buyer. The table scrolls
  // sideways, which would clip an absolutely-placed tooltip, so it is positioned
  // fixed from the cell's own rect instead.
  const showSaleTip = (e, d) => {
    if (!d.sale || !(d.saleLines || []).length) return;
    const r = e.currentTarget.getBoundingClientRect();
    setSaleTip({ date: d.date, lines: d.saleLines, total: d.sale, x: r.left + r.width / 2, y: r.top });
  };
  const SaleCell = ({ d }) => {
    const has = !!d.sale && (d.saleLines || []).length > 0;
    return (
      <td
        onMouseEnter={e => showSaleTip(e, d)}
        onMouseLeave={() => setSaleTip(null)}
        onClick={e => { if (!has) return; if (saleTip && saleTip.date === d.date) setSaleTip(null); else showSaleTip(e, d); }}
        style={{ padding:"7px 9px", textAlign:"right", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums",
          color: d.sale ? "#f0c890" : "#5a4a3a", fontSize:12, cursor: has ? "pointer" : "default",
          textDecoration: has ? "underline dotted rgba(240,200,144,0.45)" : "none", textUnderlineOffset:3 }}>
        {d.sale ? d.sale.toLocaleString() : "–"}
      </td>
    );
  };
  const Th = ({ children, sign }) => (
    <th style={{ padding:"8px 9px", textAlign:"right", fontSize:8, color:"#9a7a5a", letterSpacing:0.5, textTransform:"uppercase", whiteSpace:"nowrap", fontWeight:600, borderBottom:`1px solid ${O.border}` }}>
      {sign && <span style={{ color: sign==="+"?"#6aaa4a":sign==="−"?"#c06a4a":"#c8b0e0" }}>{sign} </span>}{children}
    </th>
  );
  const Stat = ({ label, val, color, emoji }) => (
    <div style={{ flex:"1 1 90px", minWidth:90, background:"#160e04", border:`1px solid ${O.border}`, borderRadius:10, padding:"8px 10px" }}>
      <div style={{ fontSize:9, color:"#a07850", letterSpacing:0.3, textTransform:"uppercase" }}>{emoji} {label}</div>
      <div style={{ fontSize:16, fontWeight:700, color:color||"#f0c890", marginTop:2 }}>{(val||0).toLocaleString()}<span style={{ fontSize:9, color:"#7a5a3a", fontWeight:400 }}> trays</span></div>
    </div>
  );

  return (
    <div>
      <DateRangePicker from={from} to={to} onChange={(f,t)=>{ setFrom(f); setTo(t); }} accent={O.accent} />
      <ReportButton accent={O.accent} disabled={busy || !data} onClick={() => openReport(() => buildEggStockReport(farm, data, from, to))} />

      <div style={{ fontSize:11, color:"#7a5a3a", textAlign:"center", marginBottom:14, fontStyle:"italic" }}>
        👁️ View-only — every day's stock movement in the selected range. All figures in trays.
      </div>

      {busy && <div style={{ textAlign:"center", padding:40, color:"#7a6a5a", fontSize:13 }}>Loading…</div>}

      {!busy && data && data.days.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#5a4a3a" }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📅</div>
          <div style={{ fontSize:13 }}>No egg-stock entries in this range</div>
        </div>
      )}

      {!busy && data && data.days.length > 0 && (
        <>
          {/* Range totals banner */}
          <div style={{ background:"linear-gradient(120deg,#2a1808,#3a2410 55%,#241606)", border:`1.5px solid ${O.accent}77`, borderRadius:16, padding:"14px 16px", marginBottom:16, boxShadow:"0 4px 24px rgba(120,70,20,0.25)" }}>
            <div style={{ fontSize:13, fontWeight:"bold", color:O.accent, letterSpacing:0.5, textTransform:"uppercase", marginBottom:12 }}>📊 Total · {rangeLabel}</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <Stat emoji="📦" label="Production" val={data.totals.production} color="#a8e8c0" />
              <Stat emoji="📥" label="Received" val={data.totals.received} color="#a8d0e8" />
              <Stat emoji="📤" label="Issued" val={data.totals.issued} color="#e8c8a0" />
              <Stat emoji="🛒" label="Sale" val={data.totals.sale} color="#f0c890" />
              <Stat emoji="💔" label="Breakage" val={data.totals.breakage} color="#e8a090" />
              {data.totals.difference !== 0 && <Stat emoji="±" label="Difference" val={data.totals.difference} color="#c8b0e0" />}
            </div>
          </div>

          {/* Daily table — scroll sideways on small screens */}
          <div style={{ background:"#0e0c08", border:`1px solid ${O.border}`, borderRadius:14, overflow:"hidden", marginBottom:14 }}>
            <div style={{ padding:"10px 14px", borderBottom:`1px solid ${O.border}`, background:"#140c04" }}>
              <span style={{ fontSize:14, fontWeight:"bold", color:O.accent }}>Day-by-day</span>
              <span style={{ fontSize:10, color:"#7a5a3a", marginLeft:8 }}>newest first · swipe sideways for all columns →</span>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ borderCollapse:"collapse", width:"100%", minWidth:560 }}>
                <thead>
                  <tr>
                    <th style={{ padding:"8px 9px 8px 14px", textAlign:"left", fontSize:8, color:"#9a7a5a", letterSpacing:0.5, textTransform:"uppercase", whiteSpace:"nowrap", fontWeight:600, borderBottom:`1px solid ${O.border}`, position:"sticky", left:0, background:"#0e0c08" }}>Date</th>
                    <Th>Open</Th>
                    <Th sign="+">Prod</Th>
                    <Th sign="+">Recv</Th>
                    <Th sign="−">Break</Th>
                    <Th sign="−">Issue</Th>
                    <Th sign="−">Sale</Th>
                    <Th sign="±">Diff</Th>
                    <Th>Close</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map(d => (
                    <tr key={d.date} style={{ borderBottom:"1px solid #1a1208" }}>
                      <td style={{ padding:"7px 9px 7px 14px", textAlign:"left", color:"#d0a878", fontWeight:600, fontSize:12, whiteSpace:"nowrap", position:"sticky", left:0, background:"#0e0c08" }}>{formatDate(d.date)}</td>
                      <NumCell v={d.opening} color="#c8b8a0" />
                      <NumCell v={d.production} color="#a8e8c0" />
                      <NumCell v={d.received} color="#a8d0e8" />
                      <NumCell v={d.breakage} color="#e8a090" />
                      <NumCell v={d.issued} color="#e8c8a0" />
                      <SaleCell d={d} />
                      <td style={{ padding:"7px 9px", textAlign:"right", whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums", color: d.difference ? "#c8b0e0" : "#5a4a3a", fontSize:12 }}>{d.difference ? (d.difference>0?"+":"")+d.difference.toLocaleString() : "–"}</td>
                      <NumCell v={d.closing} color="#f0c890" strong />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {saleTip && (
        <div style={{
          position:"fixed", zIndex:400, pointerEvents:"none",
          left: Math.min(typeof window!=="undefined" ? window.innerWidth-110 : saleTip.x, Math.max(110, saleTip.x)),
          top: saleTip.y - 8, transform:"translate(-50%,-100%)",
          background:"#160e04", border:`1px solid ${O.accent}`, borderRadius:10,
          padding:"9px 12px", boxShadow:"0 8px 24px rgba(0,0,0,0.6)", maxWidth:260,
        }}>
          <div style={{ fontSize:9, color:"#a07850", letterSpacing:0.5, textTransform:"uppercase", marginBottom:5 }}>Sold on {formatDate(saleTip.date)}</div>
          {saleTip.lines.map((l, i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", gap:12, fontSize:11.5, padding:"2px 0" }}>
              <span style={{ color: l.buyer ? "#e8d8c0" : "#8a7a5a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {l.buyer || "(no buyer)"}{l.vehicle ? <span style={{ color:"#a07850" }}> · 🚚 {l.vehicle}</span> : ""}
              </span>
              <strong style={{ color:"#f0c890", fontVariantNumeric:"tabular-nums", flexShrink:0 }}>{(l.trays||0).toLocaleString()}</strong>
            </div>
          ))}
          {saleTip.lines.length > 1 && (
            <div style={{ display:"flex", justifyContent:"space-between", gap:12, fontSize:11.5, borderTop:`1px solid ${O.border}`, marginTop:5, paddingTop:5 }}>
              <span style={{ color:"#a07850" }}>Total</span>
              <strong style={{ color:"#f0c890", fontVariantNumeric:"tabular-nums" }}>{saleTip.total.toLocaleString()} trays</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// A ledger section that holds multiple line items (received / issued / sale).
function LineGroupSection({ kind, sign, label, subtitle, items, total, canEdit, addOpen, onOpenAdd, onAdd, onEdit, onDelete }) {
  const isSale = kind === "sale";
  const signColor = sign === "+" ? "#6aaa4a" : "#c06a4a";
  return (
    <div style={{ borderBottom:"1px solid #1a1510" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:signColor, width:14, textAlign:"center" }}>{sign}</span>
          <div>
            <span style={{ fontSize:13, fontWeight:500, color:"#c8b8a0" }}>{label}</span>
            <div style={{ fontSize:9, color:"#6a5a4a" }}>{subtitle}</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:"#e8d8c0" }}>{total.toLocaleString()}</span>
          <span style={{ fontSize:10, color:"#7a6a5a" }}>trays</span>
          {canEdit && <button onClick={onOpenAdd} style={{ background:addOpen?"#3a2410":"#1e1408", border:"1px solid #4a3018", borderRadius:6, color:"#d8945a", fontSize:14, cursor:"pointer", padding:"2px 9px", fontFamily:"inherit", lineHeight:1 }}>{addOpen?"×":"+"}</button>}
        </div>
      </div>

      {/* Existing line items */}
      {items.length > 0 && (
        <div style={{ padding:"0 14px 8px 36px" }}>
          {items.map(item => (
            <LineItem key={item.id} item={item} isSale={isSale} canEdit={canEdit}
              onEdit={(p)=>onEdit(item.id,p)} onDelete={()=>onDelete(item.id)} />
          ))}
        </div>
      )}

      {/* Add form */}
      {addOpen && canEdit && (
        <div style={{ padding:"0 14px 12px 36px" }}>
          <AddLineForm kind={kind} isSale={isSale} onAdd={onAdd} onCancel={onOpenAdd} />
        </div>
      )}
    </div>
  );
}

function LineItem({ item, isSale, canEdit, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [trays, setTrays] = useState(item.trays);
  const [note, setNote] = useState(item.note);
  const [buyer, setBuyer] = useState(item.buyer);
  const [vehicle, setVehicle] = useState(item.vehicle);
  const iS = { background:"#0c0a06", border:"1px solid #3a2e1e", borderRadius:7, color:"#e8d8c0", padding:"6px 8px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };

  if (editing) {
    return (
      <div style={{ background:"#120e08", border:"1px solid #3a2818", borderRadius:8, padding:8, marginBottom:6 }}>
        <div style={{ display:"flex", gap:6, marginBottom:6 }}>
          <input type="number" value={trays} onChange={e=>setTrays(e.target.value)} placeholder="trays" style={{ ...iS, width:70 }} />
          {isSale ? (
            <input type="text" value={buyer} onChange={e=>setBuyer(e.target.value)} placeholder="Buyer name" style={{ ...iS, flex:1 }} />
          ) : (
            <input type="text" value={note} onChange={e=>setNote(e.target.value)} placeholder="Note (from/to)" style={{ ...iS, flex:1 }} />
          )}
        </div>
        {isSale && (
          <input type="text" value={vehicle} onChange={e=>setVehicle(e.target.value)} placeholder="Vehicle no." style={{ ...iS, width:"100%", marginBottom:6 }} />
        )}
        <div style={{ display:"flex", gap:6 }}>
          <button onClick={() => { onEdit(isSale?{trays,buyer,vehicle}:{trays,note}); setEditing(false); }} style={{ flex:1, background:"#1e3a1a", border:"1px solid #3a6a2a", borderRadius:6, color:"#a8e088", fontSize:11, padding:"6px", cursor:"pointer", fontFamily:"inherit" }}>Save</button>
          <button onClick={() => { setTrays(item.trays); setNote(item.note); setBuyer(item.buyer); setVehicle(item.vehicle); setEditing(false); }} style={{ background:"#2a2a2a", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:11, padding:"6px 12px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #15110a" }}>
      <span style={{ fontSize:13, fontWeight:"bold", color:"#e8c8a0", minWidth:42 }}>{item.trays}T</span>
      <div style={{ flex:1, minWidth:0 }}>
        {isSale ? (
          <div style={{ fontSize:11, color:"#b8a890" }}>
            {item.buyer || "(no buyer)"}{item.vehicle ? <span style={{ color:"#8a7a6a" }}> · 🚚 {item.vehicle}</span> : ""}
          </div>
        ) : (
          <div style={{ fontSize:11, color:"#b8a890" }}>{item.note || "(no note)"}</div>
        )}
      </div>
      {canEdit && (
        <div style={{ display:"flex", gap:4 }}>
          <button onClick={() => setEditing(true)} style={{ background:"transparent", border:"1px solid #3a2e1e", borderRadius:5, color:"#a08860", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>edit</button>
          <button onClick={onDelete} style={{ background:"transparent", border:"1px solid #4a2020", borderRadius:5, color:"#a05050", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>×</button>
        </div>
      )}
    </div>
  );
}

function AddLineForm({ kind, isSale, onAdd, onCancel }) {
  const [trays, setTrays] = useState("");
  const [note, setNote] = useState("");
  const [buyer, setBuyer] = useState("");
  const [vehicle, setVehicle] = useState("");
  const iS = { background:"#0c0a06", border:"1px solid #3a2e1e", borderRadius:7, color:"#e8d8c0", padding:"7px 9px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };
  const placeholder = kind==="received" ? "From which farm / store" : kind==="issued" ? "To which farm / vehicle" : "";

  function submit() {
    const t = Math.trunc(Number(trays)||0);
    if (t <= 0) { alert("Enter the number of trays."); return; }
    onAdd(isSale ? { trays:t, buyer, vehicle } : { trays:t, note });
  }

  return (
    <div style={{ background:"#120e08", border:"1px solid #3a2818", borderRadius:8, padding:10, marginTop:4 }}>
      <div style={{ display:"flex", gap:6, marginBottom:6 }}>
        <input type="number" autoFocus value={trays} onChange={e=>setTrays(e.target.value)} placeholder="Trays" style={{ ...iS, width:80 }} />
        {isSale ? (
          <input type="text" value={buyer} onChange={e=>setBuyer(e.target.value)} placeholder="Buyer name" style={{ ...iS, flex:1 }} />
        ) : (
          <input type="text" value={note} onChange={e=>setNote(e.target.value)} placeholder={placeholder} style={{ ...iS, flex:1 }} />
        )}
      </div>
      {isSale && (
        <input type="text" value={vehicle} onChange={e=>setVehicle(e.target.value)} placeholder="Vehicle no. (e.g. MH14 AB 1234)" style={{ ...iS, width:"100%", marginBottom:6 }} />
      )}
      <div style={{ display:"flex", gap:6 }}>
        <button onClick={submit} style={{ flex:1, background:"linear-gradient(135deg,#3a2410,#2a1808)", border:"1px solid #6a4020", borderRadius:6, color:"#e8b070", fontSize:12, padding:"7px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Add {kind}</button>
        <button onClick={onCancel} style={{ background:"#2a2a2a", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── PACKING EGG STORE ────────────────────────────────────────────────────────
// Three sub-stores: White with tray sizes 30/25/6 · Brown with 30/25/6 · Loose.
// All quantities in EGGS internally. closing = opening + production − issued
// + return − breakage. Issued/return for white/brown are line items with
// per-size tray counts plus vehicle/driver/place. Loose uses single T+e values.
// Access: super_admin + admin only; the module card is hidden from viewers.
const PACK_TABS = [
  { key:"white", label:"⚪ White", sizes:[30,25,6] },
  { key:"brown", label:"🟤 Brown", sizes:[30,25,6] },
  { key:"loose", label:"🧺 Loose", sizes:[] },
  { key:"sale",  label:"💰 Sale",  sizes:[] },
  { key:"month", label:"📅 Month", sizes:[] },
];
// Fixed choice lists so entries are picked, not typed.
// Drivers are managed by the user (add/remove in the dropdown), so this array is
// only the fallback shown if the list can't be loaded — the live list comes from
// the server and is cached here between form openings.
const PACK_DRIVERS = ["Ravi", "Balkar", "Sukhbir"];
// A non-farm destination for "Issued to Farm". Stored in the line's `place` just
// like a farm name, so it can be received back later the same way.
const PACK_COLD_STORAGE = "Cold Storage";
const packDriverCache = { drivers: PACK_DRIVERS.map((n, i) => ({ driver_id: -1 - i, name: n })), archived: [], loaded: false };
function usePackDrivers() {
  const [state, setState] = useState({ drivers: packDriverCache.drivers, archived: packDriverCache.archived });
  const apply = (d) => {
    if (!d || !Array.isArray(d.drivers)) return;
    packDriverCache.drivers = d.drivers; packDriverCache.archived = d.archived || []; packDriverCache.loaded = true;
    setState({ drivers: packDriverCache.drivers, archived: packDriverCache.archived });
  };
  const reload = async () => {
    try { apply(await apiCall("/pack-drivers")); } catch (e) { /* keep whatever we have */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  return { ...state, apply, reload };
}

// Add / remove the names offered in the driver dropdown.
function PackDriverManager({ drivers, archived, onApply, onClose }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const iS = { background:"#12091a", border:"1px solid #3a2a4a", borderRadius:8, color:"#e0d0f0", padding:"9px 11px", fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box" };
  async function call(path, opts) {
    setBusy(true);
    try { const d = await apiCall(path, opts); onApply(d); return true; }
    catch (e) { alert(e && e.message ? e.message : "Could not save"); return false; }
    finally { setBusy(false); }
  }
  async function add() {
    const n = name.trim();
    if (!n) return alert("Type the driver's name.");
    if (await call("/pack-drivers", { method:"POST", body: JSON.stringify({ name:n }) })) setName("");
  }
  return (
    <>
      <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", zIndex:500, backdropFilter:"blur(2px)" }} />
      <div style={{ position:"fixed", left:0, right:0, bottom:0, zIndex:501, background:"#0e0814", borderTop:"2px solid #7a5a9a", borderRadius:"18px 18px 0 0", padding:"18px 18px 34px", maxHeight:"78vh", overflowY:"auto" }}>
        <div style={{ width:40, height:4, background:"#3a2a4a", borderRadius:4, margin:"0 auto 14px" }} />
        <div style={{ fontSize:15, fontWeight:"bold", color:"#b08ad0", marginBottom:3 }}>👤 Drivers</div>
        <div style={{ fontSize:11, color:"#7a6a8a", marginBottom:14 }}>These names fill the driver dropdown for new entries. Removing a driver never changes past records — every issue note keeps the driver it was saved with.</div>

        <div style={{ display:"flex", gap:7, marginBottom:14 }}>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="New driver's name" style={iS}
            onKeyDown={e=>{ if (e.key==="Enter") add(); }} />
          <button onClick={add} disabled={busy} style={{ background:"linear-gradient(135deg,#5a3a7a,#3a2454)", border:"1px solid #7a5a9a", borderRadius:8, color:"#e0d0f0", fontSize:13, fontWeight:600, padding:"9px 16px", cursor:"pointer", fontFamily:"inherit", opacity:busy?0.6:1, whiteSpace:"nowrap" }}>Add</button>
        </div>

        {drivers.length === 0 ? (
          <div style={{ fontSize:12, color:"#6a5a7a", textAlign:"center", padding:"12px 0" }}>No drivers in the list yet.</div>
        ) : drivers.map(d => (
          <div key={d.driver_id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, background:"#140c1e", border:"1px solid #2a1c3a", borderRadius:9, padding:"9px 12px", marginBottom:6 }}>
            <span style={{ fontSize:13.5, color:"#e0d0f0" }}>👤 {d.name}</span>
            <button onClick={()=>{ if (window.confirm(`Remove ${d.name} from the driver list?\n\nEvery issue note already saved keeps his name — this only takes him out of the dropdown for new entries. You can restore him later.`)) call(`/pack-drivers/${d.driver_id}`, { method:"DELETE" }); }}
              disabled={busy || d.driver_id < 0}
              style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:11, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>Remove</button>
          </div>
        ))}

        {archived.length > 0 && (
          <>
            <div style={{ fontSize:9.5, color:"#6a5a7a", letterSpacing:1, textTransform:"uppercase", margin:"14px 2px 7px", fontWeight:700 }}>Removed ({archived.length})</div>
            {archived.map(d => (
              <div key={d.driver_id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, background:"#0f0a16", border:"1px dashed #2a1c3a", borderRadius:9, padding:"8px 12px", marginBottom:6 }}>
                <span style={{ fontSize:13, color:"#7a6a8a" }}>👤 {d.name}</span>
                <button onClick={()=>call(`/pack-drivers/${d.driver_id}`, { method:"PUT", body: JSON.stringify({ archived:false }) })} disabled={busy}
                  style={{ background:"#0e2a20", border:"1px solid #2a5a48", borderRadius:7, color:"#7ab8a0", fontSize:11, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>Restore</button>
              </div>
            ))}
          </>
        )}

        <button onClick={onClose} style={{ width:"100%", marginTop:14, background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:9, color:"#9a9a9a", fontSize:13, padding:"10px", cursor:"pointer", fontFamily:"inherit" }}>Done</button>
      </div>
    </>
  );
}
const PACK_VEHICLES = ["HR67C2878", "HR68C3222", "HR68C0395", "HR68C5367", "HR68D6680"];
const PACK_DESTS = ["Panchkula","Zirakpur","Dhakoli","Mohali","Chandigarh","Ambala","Manimajra","Himachal Pradesh","Baddi","Gurgaon","Faridabad","Rohtak","Sonipat","Karnal","Panipat","Ludhiana","Kurukshetra","Noida","Dehradun","Khanna"];
// Up/down arrows move between this module's number fields instead of changing
// the value; Enter commits the field.
function packKeyNav(e) {
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    const els = Array.from(document.querySelectorAll("input.pack-num"));
    const i = els.indexOf(e.target);
    const n = e.key === "ArrowDown" ? i + 1 : i - 1;
    if (n >= 0 && n < els.length) { els[n].focus(); if (els[n].select) els[n].select(); }
  } else if (e.key === "Enter") { e.target.blur(); }
}
const fmtTE = (eggs) => {
  const t = Math.floor((eggs||0)/30), r = (eggs||0)%30;
  return r > 0 ? `${t.toLocaleString()}T +${r}` : `${t.toLocaleString()}T`;
};

// Controlled number input for the packing module. Holds its own text so typing
// and backspacing are smooth; syncs from the external value only when it changes
// AND the field isn't focused (so a save-driven re-render never fights the user).
// Commits on blur. Up/down arrows jump between fields (packKeyNav).
// Set by the Packing screen: true when the user can view Packing but not change it
// (a Viewer, or a Manager of other modules). Every input and add/edit/delete
// control in the module reads it, so read-only can't be missed in any sub-view.
const PackReadOnlyCtx = createContext(false);

function PackNumInput({ value, onCommit, placeholder = "–", width = 80, max }) {
  const readOnly = useContext(PackReadOnlyCtx);
  const [text, setText] = useState(value === "" || value == null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value === "" || value == null ? "" : String(value));
  }, [value, focused]);
  return (
    <input
      type="number" inputMode="numeric" className="pack-num" placeholder={placeholder}
      value={text} max={max} readOnly={readOnly} tabIndex={readOnly ? -1 : undefined}
      onChange={e => { if (!readOnly) setText(e.target.value); }}
      onFocus={() => { if (!readOnly) setFocused(true); }}
      onBlur={() => { if (readOnly) return; setFocused(false); onCommit(text.trim()); }}
      onKeyDown={packKeyNav}
      style={{ textAlign:"right", background: readOnly ? "#0a0810" : "#0e0a14", border:`1px solid ${readOnly ? "#241a30" : "#3a2a4a"}`, borderRadius:8, color: readOnly ? "#a898c0" : "#e0d0f0", padding:"7px 8px", fontSize:14, fontFamily:"inherit", width, cursor: readOnly ? "default" : "text" }}
    />
  );
}

function PackingStock({ farm, allFarms = [], perms: pm = {}, reportError }) {
  const [kind, setKind] = useState("white");
  const [selDate, setSelDate] = useState(todayISO());
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addKind, setAddKind] = useState(null); // 'issued'|'return'|'direct'|'farmissue'|'received'|null

  const sizes = (PACK_TABS.find(t => t.key === kind) || {}).sizes || [];
  // Destinations for "issue to a farm": the packing store's OWN farm first — the
  // packing store is a separate ledger from that farm's egg stock, so it can issue
  // to its own farm — followed by every other farm in the app.
  const issueFarms = (() => {
    const names = [
      ...(allFarms || []).filter(f => f.id === farm.id),
      ...(allFarms || []).filter(f => f.id !== farm.id),
    ].map(f => f.name).filter(Boolean);
    // Cold storage is a destination, not a farm — offered last unless a real farm
    // already goes by that name.
    if (!names.some(n => String(n).trim().toLowerCase() === PACK_COLD_STORAGE.toLowerCase())) names.push(PACK_COLD_STORAGE);
    return names;
  })();
  const PURPLE = { accent:"#b08ad0", dark:"#160e1e", border:"#3a2a4a", pill:"#241634" };

  const load = useCallback(async () => {
    if (kind === "sale" || kind === "month") { setLoading(false); return; } // these fetch their own data
    setLoading(true);
    try { setStock(await apiCall(`/farms/${farm.id}/packing-stock?date=${selDate}&kind=${kind}`)); }
    catch (e) { reportError && reportError(e); }
    setLoading(false);
  }, [farm.id, selDate, kind]);
  useEffect(() => { load(); setAddKind(null); }, [load]);

  async function saveField(field, value) {
    if (!stock) return;
    const v = Math.trunc(Number(value) || 0);
    setStock(s => ({ ...s, [field]: v }));
    // The loose tab's issued/return map to the server's issuedEggs/returnEggs keys.
    const bodyKey = field === "issuedEggsLoose" ? "issuedEggs" : field === "returnEggsLoose" ? "returnEggs" : field;
    try { setStock(await apiCall(`/packing-stock/${stock.id}`, { method:"PUT", body: JSON.stringify({ [bodyKey]: v }) })); }
    catch (e) { reportError && reportError(e); load(); }
  }
  // Reset a field back to a dash (unentered): value 0 + flag cleared. flagKey is
  // the *Set name the server understands (productionSet, breakageSet, etc.).
  async function clearField(flagKey) {
    if (!stock) return;
    try { setStock(await apiCall(`/packing-stock/${stock.id}`, { method:"PUT", body: JSON.stringify({ [flagKey]: false }) })); }
    catch (e) { reportError && reportError(e); load(); }
  }
  async function addLine(lkind, payload) {
    if (!stock) return;
    try { setStock(await apiCall(`/packing-stock/${stock.id}/lines`, { method:"POST", body: JSON.stringify({ kind:lkind, ...payload }) })); setAddKind(null); }
    catch (e) { alert(e && e.message ? e.message : "Could not save entry"); }
  }
  async function editLine(lineId, payload) {
    try { setStock(await apiCall(`/packing-lines/${lineId}`, { method:"PUT", body: JSON.stringify(payload) })); }
    catch (e) { alert(e && e.message ? e.message : "Could not save edit"); }
  }
  async function deleteLine(lineId) {
    try { setStock(await apiCall(`/packing-lines/${lineId}`, { method:"DELETE" })); }
    catch (e) { alert(e && e.message ? e.message : "Could not delete"); }
  }
  function shiftDate(days) {
    const d = new Date(selDate + "T00:00:00"); d.setDate(d.getDate()+days);
    setSelDate(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
  }

  const _pd = new Date(selDate + "T00:00:00");
  const dateLabel = `${_pd.toLocaleDateString("en-GB",{weekday:"short"})}, ${formatDate(selDate)}`;
  const isLoose = kind === "loose";

  const Badge = ({ isAuto }) => (
    <span style={{ display:"inline-block", marginLeft:7, fontSize:8, fontWeight:700, letterSpacing:0.5, borderRadius:20, padding:"1px 7px", verticalAlign:"middle",
      color: isAuto ? "#5a9ab0" : "#d8945a", background: isAuto ? "#0a2230" : "#2a1808", border:`1px solid ${isAuto ? "#2a6a8a" : "#6a4018"}` }}>
      {isAuto ? "⟳ AUTO" : "✎ EDITED"}</span>
  );

  // One editable value row. mode: 'eggs' plain egg count · 'trays30' input trays
  // saved ×30 · 'te' trays + eggs pair saved as total eggs.
  // One editable value row with dash-before-entry. flag tells whether the field
  // was ever deliberately entered; until then inputs are blank with a dash
  // placeholder. Typing any value (including 0) saves and sets the flag.
  const NumRow = ({ label, hint, sign, field, mode, badge, flag }) => {
    const val = stock ? stock[field] : 0;
    const entered = flag === undefined ? true : !!(stock && stock[flag]);
    const signColor = sign === "+" ? "#7aaa5a" : sign === "−" ? "#c06a5a" : "#8a7a9a";
    return (
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid #1a1420", gap:8, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:140 }}>
          {sign ? <span style={{ fontSize:15, fontWeight:"bold", color:signColor, width:14, textAlign:"center" }}>{sign}</span> : <span style={{ width:14 }} />}
          <div>
            <span style={{ fontSize:13, fontWeight:500, color:"#c0b0d0" }}>{label}</span>
            {badge}
            {hint && <div style={{ fontSize:9, color:"#5a4a6a" }}>{hint}</div>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          {mode === "trays30" && (
            <>
              <PackNumInput value={entered ? Math.floor(val/30) : ""} placeholder="–" width={72}
                onCommit={(raw) => { if (raw === "") { if (entered && flag) clearField(flag); return; } const tv = Math.trunc(Number(raw)||0); if (tv*30 !== val || !entered) saveField(field, tv*30); }} />
              <span style={{ fontSize:10, color:"#7a6a8a" }}>trays</span>
              <span style={{ fontSize:11, color:"#9a8ab0", minWidth:84, textAlign:"right" }}>{entered ? `= ${(val||0).toLocaleString()} eggs` : "–"}</span>
            </>
          )}
          {mode === "te" && (
            <>
              <PackNumInput value={entered ? Math.floor(val/30) : ""} placeholder="–" width={60}
                onCommit={(raw) => { if (raw === "") { if (entered && flag) clearField(flag); return; } const tv=Math.trunc(Number(raw)||0); const rem=val%30; if (tv*30+rem !== val || !entered) saveField(field, tv*30+rem); }} />
              <span style={{ fontSize:10, color:"#7a6a8a" }}>T</span>
              <PackNumInput value={entered ? val%30 : ""} placeholder="–" width={52} max={29}
                onCommit={(raw) => { if (raw === "") { if (entered && flag) clearField(flag); return; } const ev=Math.min(29,Math.max(0,Math.trunc(Number(raw)||0))); const tr=Math.floor(val/30); if (tr*30+ev !== val || !entered) saveField(field, tr*30+ev); }} />
              <span style={{ fontSize:10, color:"#7a6a8a" }}>eggs</span>
            </>
          )}
          {mode === "eggs" && (
            <>
              <PackNumInput value={entered ? val : ""} placeholder="–" width={90}
                onCommit={(raw) => { if (raw === "") { if (entered && flag) clearField(flag); return; } const nv=Math.trunc(Number(raw)||0); if (nv !== val || !entered) saveField(field, nv); }} />
              <span style={{ fontSize:10, color:"#7a6a8a", width:30 }}>eggs</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <PackReadOnlyCtx.Provider value={!pm.canEdit}>
    <div className="anim-tab">
      {/* Remove the number-input stepper arrows in this module; arrow keys
          navigate between fields instead (see packKeyNav). */}
      <style>{`
        input.pack-num::-webkit-outer-spin-button, input.pack-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input.pack-num { -moz-appearance: textfield; appearance: textfield; }
      `}</style>
      {/* Kind tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:12 }}>
        {PACK_TABS.map(t => (
          <button key={t.key} onClick={() => setKind(t.key)} style={{
            flex:1, padding:"10px 0", background: kind===t.key ? PURPLE.pill : "transparent",
            border:`1px solid ${kind===t.key ? PURPLE.border : "#1e1428"}`, borderRadius:10,
            color: kind===t.key ? PURPLE.accent : "#4a3a5a", fontSize:13, fontFamily:"inherit",
            fontWeight: kind===t.key ? 700 : 400, cursor:"pointer",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Date selector */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:`linear-gradient(135deg,${PURPLE.dark},#0e081a)`, border:`1px solid ${PURPLE.border}`, borderRadius:14, padding:"10px 12px", marginBottom:14 }}>
        <button onClick={() => shiftDate(-1)} style={{ background:PURPLE.pill, border:`1px solid ${PURPLE.border}`, borderRadius:8, color:PURPLE.accent, fontSize:16, cursor:"pointer", padding:"4px 12px", fontFamily:"inherit" }}>‹</button>
        <div style={{ textAlign:"center", position:"relative" }}>
          <div style={{ fontSize:15, fontWeight:"bold", color:PURPLE.accent }}>{dateLabel}</div>
          <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
            style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%" }} />
          <div style={{ fontSize:9, color:"#6a5a8a" }}>tap to pick date</div>
        </div>
        <button onClick={() => shiftDate(1)} style={{ background:PURPLE.pill, border:`1px solid ${PURPLE.border}`, borderRadius:8, color:PURPLE.accent, fontSize:16, cursor:"pointer", padding:"4px 12px", fontFamily:"inherit" }}>›</button>
      </div>

      {kind === "sale" && <PackSaleMonitor farm={farm} selDate={selDate} reportError={reportError} />}
      {kind === "month" && <PackMonthlyMonitor farm={farm} selDate={selDate} reportError={reportError} />}

      {kind !== "sale" && kind !== "month" && loading && <div style={{ textAlign:"center", padding:40, color:"#6a5a7a", fontSize:13 }}>Loading…</div>}

      {kind !== "sale" && kind !== "month" && !loading && stock && (
        <>
          {/* Closing hero */}
          <div style={{ background:`linear-gradient(135deg,${PURPLE.dark},#120a1c)`, border:`1.5px solid ${PURPLE.accent}66`, borderRadius:16, padding:"16px 18px", marginBottom:14, textAlign:"center" }}>
            <div style={{ fontSize:11, color:"#8a7aa0", letterSpacing:1, textTransform:"uppercase" }}>Closing Stock · {(PACK_TABS.find(t=>t.key===kind)||{}).label}</div>
            <div style={{ fontSize:34, fontWeight:"bold", color:"#d8c0f0", lineHeight:1.1, marginTop:2 }}>
              {isLoose ? fmtTE(stock.closing) : stock.closing.toLocaleString()}
            </div>
            <div style={{ fontSize:11, color:"#8a7aa0" }}>{isLoose ? `= ${stock.closing.toLocaleString()} eggs` : `eggs · = ${fmtTE(stock.closing)}`}</div>
            <div style={{ fontSize:9, color:"#6a5a7a", marginTop:4 }}>carries to tomorrow's opening · auto-calculated</div>
          </div>

          {/* Ledger */}
          <div style={{ background:"#0c0a10", border:`1px solid ${PURPLE.border}`, borderRadius:14, overflow:"hidden", marginBottom:14 }}>
            <NumRow label="Opening Stock" hint="carried from yesterday · editable"
              mode={isLoose ? "te" : "eggs"} field="opening"
              badge={<Badge isAuto={stock.opening === stock.autoOpening} />} />
            <NumRow label="Production" sign="+" hint={isLoose ? "trays + extra eggs" : "enter trays · ×30 = eggs"}
              mode={isLoose ? "te" : "trays30"} field="production" flag="productionSet" />

            {!isLoose ? (
              <PackLineGroup label="Issued / Loading" sign="−" sizes={sizes}
                items={stock.issued} allIssues={stock.issued} allReturns={stock.returns} totalEggs={stock.issuedEggs}
                addOpen={addKind==="issued"} onOpenAdd={() => setAddKind(addKind==="issued"?null:"issued")}
                onAdd={(p2)=>addLine("issued",p2)} onEdit={editLine} onDelete={deleteLine} />
            ) : (
              <NumRow label="Issued" sign="−" hint="trays + extra eggs" mode="te" field="issuedEggsLoose" flag="issuedSet" />
            )}

            {/* Issued to another FARM — separate from vehicle loading; not counted as a sale. */}
            {!isLoose && (
              <PackLineGroup label="Issued to Farm" sign="−" sizes={sizes}
                items={stock.farmIssues||[]} allIssues={stock.farmIssues||[]} allReturns={stock.received||[]} totalEggs={stock.farmIssuedEggs||0}
                destMode="farm" farms={issueFarms} ownFarm={farm.name}
                farmId={farm.id} storeKind={kind} currentDate={selDate}
                addOpen={addKind==="farmissue"} onOpenAdd={() => setAddKind(addKind==="farmissue"?null:"farmissue")}
                onAdd={(p2)=>addLine("farmissue",p2)} onEdit={editLine} onDelete={deleteLine} />
            )}

            {!isLoose ? (
              <PackLineGroup label="Return" sign="+" sizes={sizes}
                items={stock.returns} allIssues={stock.issued} allReturns={stock.returns} totalEggs={stock.returnEggs}
                farmId={farm.id} storeKind={kind} currentDate={selDate}
                addOpen={addKind==="return"} onOpenAdd={() => setAddKind(addKind==="return"?null:"return")}
                onAdd={(p2)=>addLine("return",p2)} onEdit={editLine} onDelete={deleteLine} />
            ) : (
              <NumRow label="Return" sign="+" hint="trays + extra eggs" mode="te" field="returnEggsLoose" flag="returnSet" />
            )}

            {/* Received back FROM a farm — mirrors Return, linked to a farm-issue serial. */}
            {!isLoose && (
              <PackLineGroup label="Received (from Farm)" sign="+" sizes={sizes}
                items={stock.received||[]} allIssues={stock.farmIssues||[]} allReturns={stock.received||[]} totalEggs={stock.receivedEggs||0}
                sourceLineKind="farmissue"
                farmId={farm.id} storeKind={kind} currentDate={selDate}
                addOpen={addKind==="received"} onOpenAdd={() => setAddKind(addKind==="received"?null:"received")}
                onAdd={(p2)=>addLine("received",p2)} onEdit={editLine} onDelete={deleteLine} />
            )}

            {/* Direct (walk-in store) sale — subtracts from stock. Buyer + vehicle,
                quantity freely typed (no dropdowns). */}
            {!isLoose ? (
              <PackDirectGroup label="Direct Sale" sizes={sizes}
                items={stock.direct} totalEggs={stock.directEggs}
                addOpen={addKind==="direct"} onOpenAdd={() => setAddKind(addKind==="direct"?null:"direct")}
                onAdd={(p2)=>addLine("direct",p2)} onEdit={editLine} onDelete={deleteLine} />
            ) : (
              <NumRow label="Direct Sale" sign="−" hint="walk-in store sale · trays + extra eggs" mode="te" field="directEggsLoose" flag="directSet" />
            )}

            <NumRow label="Breakage" sign="−" hint={isLoose ? "trays + extra eggs" : "in eggs directly"}
              mode={isLoose ? "te" : "eggs"} field="breakage" flag="breakageSet" />

            {/* Manual miscount correction — available on every store (white/brown/loose). */}
            <NumRow label="Difference (±)" hint="miscount fix · type +/− eggs (e.g. -5)" mode="eggs" field="difference" flag="differenceSet" />

            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px", background:`${PURPLE.accent}11` }}>
              <span style={{ fontSize:15, fontWeight:"bold", color:"#d8c0f0", marginLeft:22 }}>Closing Stock</span>
              <div style={{ textAlign:"right" }}>
                <span style={{ fontSize:20, fontWeight:"bold", color:"#d8c0f0" }}>{isLoose ? fmtTE(stock.closing) : stock.closing.toLocaleString()}</span>
                <span style={{ fontSize:10, color:"#8a7aa0", marginLeft:4 }}>{isLoose ? "" : "eggs"}</span>
              </div>
            </div>
          </div>

          {/* SALE summary = issued − return */}
          <div style={{ background:"linear-gradient(135deg,#1a1206,#140e04)", border:"1px solid #5a4418", borderRadius:14, padding:"12px 16px", marginBottom:14 }}>
            <div style={{ fontSize:11, color:"#c0a050", fontWeight:"bold", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>💰 Sale (issued − return)</div>
            {isLoose ? (
              <div style={{ fontSize:16, fontWeight:"bold", color:"#e8c878" }}>{fmtTE(stock.sale.eggs)} <span style={{ fontSize:10, fontWeight:400, color:"#9a8050" }}>= {stock.sale.eggs.toLocaleString()} eggs</span></div>
            ) : (
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {sizes.map(sz => {
                  const v = sz===30 ? stock.sale.t30 : sz===25 ? stock.sale.t25 : stock.sale.t6;
                  return (
                    <div key={sz} style={{ flex:1, minWidth:80, background:"#0e0a04", border:"1px solid #4a3814", borderRadius:9, padding:"8px 6px", textAlign:"center" }}>
                      <div style={{ fontSize:17, fontWeight:"bold", color:"#e8c878" }}>{v.toLocaleString()}</div>
                      <div style={{ fontSize:8, color:"#9a8050", letterSpacing:0.5 }}>{sz}-SIZE TRAYS</div>
                    </div>
                  );
                })}
                <div style={{ flex:1.2, minWidth:90, background:"#0e0a04", border:"1px solid #4a3814", borderRadius:9, padding:"8px 6px", textAlign:"center" }}>
                  <div style={{ fontSize:17, fontWeight:"bold", color:"#e8c878" }}>{stock.sale.eggs.toLocaleString()}</div>
                  <div style={{ fontSize:8, color:"#9a8050", letterSpacing:0.5 }}>TOTAL EGGS</div>
                </div>
              </div>
            )}
          </div>

          <div style={{ textAlign:"center", fontSize:10, color:"#4a3a5a", paddingBottom:20 }}>
            Packed tray sizes: 30 / 25 / 6 eggs · each tab carries its own closing forward
          </div>
        </>
      )}
    </div>
    </PackReadOnlyCtx.Provider>
  );
}

// Line section for issued/return with per-size tray inputs.
function PackLineGroup({ label, sign, sizes, items, allIssues, allReturns, totalEggs, addOpen: addOpenRaw, onOpenAdd, onAdd, onEdit, onDelete, farmId, storeKind, currentDate, destMode = "cities", farms = [], sourceLineKind = "issued", ownFarm = "" }) {
  const readOnly = useContext(PackReadOnlyCtx);
  const addOpen = addOpenRaw && !readOnly;   // the add form can't open when read-only
  const signColor = sign === "+" ? "#7aaa5a" : "#c06a5a";
  const isReturn = sign === "+";
  return (
    <div style={{ borderBottom:"1px solid #1a1420" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:signColor, width:14, textAlign:"center" }}>{sign}</span>
          <div>
            <span style={{ fontSize:13, fontWeight:500, color:"#c0b0d0" }}>{label}</span>
            <div style={{ fontSize:9, color:"#5a4a6a" }}>{isReturn ? (sourceLineKind === "farmissue" ? "linked to a farm-issue serial · can't exceed what was issued to that farm" : "linked to an issue serial · can't exceed what was issued") : (destMode === "farm" ? `tray sizes ${sizes.join(" / ")} · serial auto-assigned · destination, vehicle & driver` : `tray sizes ${sizes.join(" / ")} · serial auto-assigned · vehicle, driver & destinations`)}</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:"#e0d0f0" }}>{totalEggs.toLocaleString()}</span>
          <span style={{ fontSize:10, color:"#7a6a8a" }}>eggs</span>
          {!readOnly && <button onClick={onOpenAdd} style={{ background:addOpen?"#34244a":"#1e1428", border:"1px solid #4a3464", borderRadius:6, color:"#b08ad0", fontSize:14, cursor:"pointer", padding:"2px 9px", fontFamily:"inherit", lineHeight:1 }}>{addOpen?"×":"+"}</button>}
        </div>
      </div>

      {items.length > 0 && (
        <div style={{ padding:"0 14px 8px 36px" }}>
          {items.map(item => (
            <PackLineItem key={item.id} item={item} sizes={sizes} allIssues={allIssues} onEdit={(p2)=>onEdit(item.id,p2)} onDelete={()=>onDelete(item.id)} />
          ))}
        </div>
      )}

      {addOpen && (
        <div style={{ padding:"0 14px 12px 36px" }}>
          <PackAddForm sizes={sizes} kindLabel={label} isReturn={isReturn} issues={allIssues} returns={allReturns} farmId={farmId} storeKind={storeKind} currentDate={currentDate} destMode={destMode} farms={farms} sourceLineKind={sourceLineKind} ownFarm={ownFarm} onAdd={onAdd} onCancel={onOpenAdd} />
        </div>
      )}
    </div>
  );
}

function PackLineItem({ item, sizes, allIssues, onEdit, onDelete }) {
  const readOnly = useContext(PackReadOnlyCtx);
  const [editing, setEditing] = useState(false);
  const [t30, setT30] = useState(item.trays30);
  const [t25, setT25] = useState(item.trays25);
  const [t6, setT6] = useState(item.trays6);
  const iS2 = { background:"#0e0a14", border:"1px solid #3a2a4a", borderRadius:7, color:"#e0d0f0", padding:"6px 8px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };
  const isReturn = item.issueRef != null;
  const refIssue = isReturn && allIssues ? allIssues.find(i => i.id === item.issueRef) : null;
  const refSerial = item.issueSerial != null ? item.issueSerial : (refIssue ? refIssue.serial : "?");

  if (editing) {
    return (
      <div style={{ background:"#120e1a", border:"1px solid #34244a", borderRadius:8, padding:8, marginBottom:6 }}>
        <div style={{ fontSize:9, color:"#9a7ab8", marginBottom:6 }}>{isReturn ? `Editing return against issue #${refSerial}` : `Editing issue #${item.serial}`} · vehicle/driver/destinations stay as recorded</div>
        <div style={{ display:"flex", gap:6, marginBottom:6, flexWrap:"wrap" }}>
          {sizes.includes(30) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>30-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t30} onChange={e=>setT30(e.target.value)} style={{ ...iS2, width:64 }} /></div>}
          {sizes.includes(25) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>25-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t25} onChange={e=>setT25(e.target.value)} style={{ ...iS2, width:64 }} /></div>}
          {sizes.includes(6) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>6-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t6} onChange={e=>setT6(e.target.value)} style={{ ...iS2, width:64 }} /></div>}
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <button onClick={() => { onEdit({ trays30:Number(t30)||0, trays25:Number(t25)||0, trays6:Number(t6)||0 }); setEditing(false); }} style={{ flex:1, background:"#241a3a", border:"1px solid #4a3a6a", borderRadius:6, color:"#c8a8e8", fontSize:11, padding:"6px", cursor:"pointer", fontFamily:"inherit" }}>Save</button>
          <button onClick={() => { setT30(item.trays30); setT25(item.trays25); setT6(item.trays6); setEditing(false); }} style={{ background:"#222", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:11, padding:"6px 12px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
        </div>
      </div>
    );
  }

  const sizeBits = [];
  if (item.trays30 > 0) sizeBits.push(`30×${item.trays30}`);
  if (item.trays25 > 0) sizeBits.push(`25×${item.trays25}`);
  if (item.trays6 > 0) sizeBits.push(`6×${item.trays6}`);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #140e1e" }}>
      <span style={{ fontSize:11, fontWeight:700, color:isReturn?"#7aaa8a":"#d0a850", background:isReturn?"#0e1a12":"#1e1608", border:`1px solid ${isReturn?"#2a4a34":"#4a3a14"}`, borderRadius:6, padding:"2px 7px", whiteSpace:"nowrap" }}>
        {isReturn ? `↩ #${refSerial}` : `#${item.serial}`}
      </span>
      <span style={{ fontSize:13, fontWeight:"bold", color:"#d0b8f0", minWidth:50 }}>{item.eggs.toLocaleString()}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, color:"#a890c0" }}>{sizeBits.join(" · ") || "(no trays)"}</div>
        <div style={{ fontSize:9, color:"#7a6a8a" }}>
          {item.vehicle && <>🚚 {item.vehicle}</>}{item.driver && <> · 👤 {item.driver}</>}{item.place && <> · 📍 {item.place}</>}{isReturn && item.issueCrossDay && item.issueDate && <span style={{ color:"#9a8ac0" }}> · 📅 from {formatDate(item.issueDate)}</span>}
        </div>
      </div>
      {!readOnly && (<>
      <button onClick={() => setEditing(true)} style={{ background:"transparent", border:"1px solid #34244a", borderRadius:5, color:"#9a7ab8", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>edit</button>
      <button onClick={() => { if(window.confirm("Delete this entry?")) onDelete(); }} style={{ background:"transparent", border:"1px solid #4a2030", borderRadius:5, color:"#a05060", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>×</button>
      </>)}
    </div>
  );
}

function PackAddForm({ sizes, kindLabel, isReturn, issues = [], returns = [], farmId, storeKind, currentDate, destMode = "cities", farms = [], sourceLineKind = "issued", ownFarm = "", onAdd, onCancel }) {
  const [t30, setT30] = useState("");
  const [t25, setT25] = useState("");
  const [t6, setT6] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [driver, setDriver] = useState("");
  const [driverOther, setDriverOther] = useState(""); // typed name when driver = Other
  const driverList = usePackDrivers();               // user-managed dropdown names
  const [manageDrivers, setManageDrivers] = useState(false);
  const [dests, setDests] = useState([]);
  const [farmDest, setFarmDest] = useState(""); // chosen farm when destMode = "farm"
  const [issueRef, setIssueRef] = useState("");
  const [err, setErr] = useState("");
  const srcLabel = sourceLineKind === "farmissue" ? "farm issue" : "issue"; // Received vs Return wording
  const backNoun = sourceLineKind === "farmissue" ? "received" : "return";
  // Issuing to the store's OWN farm = internal egg-stock↔packing-store transfer in
  // the same farm — no truck involved, so vehicle & driver aren't required.
  const isOwnFarm = destMode === "farm" && !!ownFarm && String(farmDest).trim() === String(ownFarm).trim();
  // Optional: link this return/received to a source from an EARLIER day. Blank =
  // today's sources (the default). Picking a date fetches that day's list instead.
  const [srcDate, setSrcDate] = useState("");
  const [fetchedIssues, setFetchedIssues] = useState(null); // null = use same-day `issues`
  const [fetchingIssues, setFetchingIssues] = useState(false);
  useEffect(() => {
    if (!isReturn) return;
    setIssueRef("");
    if (!srcDate) { setFetchedIssues(null); return; }
    let cancelled = false;
    setFetchingIssues(true);
    apiCall(`/farms/${farmId}/packing-issues?date=${srcDate}&kind=${storeKind}&line=${sourceLineKind}`)
      .then(data => { if (!cancelled) setFetchedIssues((data && data.issues) || []); })
      .catch(() => { if (!cancelled) { setFetchedIssues([]); setErr(`Couldn't load that date's ${srcLabel}s — try again.`); } })
      .finally(() => { if (!cancelled) setFetchingIssues(false); });
    return () => { cancelled = true; };
  }, [srcDate, isReturn, farmId, storeKind, sourceLineKind]); // eslint-disable-line react-hooks/exhaustive-deps
  const iS2 = { background:"#0e0a14", border:"1px solid #3a2a4a", borderRadius:7, color:"#e0d0f0", padding:"7px 9px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };
  const eggs = 30*(Number(t30)||0) + 25*(Number(t25)||0) + 6*(Number(t6)||0);

  // Loadings to choose from: a picked earlier date uses the fetched list, else
  // today's loadings (the default). selIssue is the chosen loading.
  const activeIssues = srcDate ? (fetchedIssues || []) : issues;
  const selIssue = isReturn ? activeIssues.find(i => String(i.id) === String(issueRef)) : null;
  // What's still returnable per size on the selected loading. Fetched loadings
  // carry returnedT* (summed across ALL days); same-day loadings fall back to the
  // returns already listed for today.
  const remaining = (() => {
    if (!selIssue) return null;
    if (selIssue.returnedT30 !== undefined) {
      return { t30: selIssue.trays30 - selIssue.returnedT30, t25: selIssue.trays25 - selIssue.returnedT25, t6: selIssue.trays6 - selIssue.returnedT6 };
    }
    const rs = returns.filter(r => r.issueRef === selIssue.id);
    const sum = (f) => rs.reduce((s, r) => s + (r[f] || 0), 0);
    return { t30: selIssue.trays30 - sum("trays30"), t25: selIssue.trays25 - sum("trays25"), t6: selIssue.trays6 - sum("trays6") };
  })();

  function toggleDest(d) {
    setDests(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  }

  function submit() {
    setErr("");
    if (eggs <= 0) { setErr("Enter trays in at least one size."); return; }
    if (isReturn) {
      if (!selIssue) { setErr(`Select the ${srcLabel} this ${backNoun} belongs to.`); return; }
      const cap = sourceLineKind === "farmissue" ? "receivable" : "returnable";
      const over = [];
      if ((Number(t30)||0) > remaining.t30) over.push(`30-size: only ${remaining.t30} ${cap} from ${srcLabel} #${selIssue.serial}`);
      if ((Number(t25)||0) > remaining.t25) over.push(`25-size: only ${remaining.t25} ${cap}`);
      if ((Number(t6)||0)  > remaining.t6)  over.push(`6-size: only ${remaining.t6} ${cap}`);
      if (over.length) { setErr(`${sourceLineKind==="farmissue"?"Received":"Return"} exceeds ${sourceLineKind==="farmissue"?"issued to farm":"issued"} — ` + over.join("; ")); return; }
      onAdd({ trays30:Number(t30)||0, trays25:Number(t25)||0, trays6:Number(t6)||0, issueRef:selIssue.id, vehicle:selIssue.vehicle, driver:selIssue.driver, place:selIssue.place });
    } else {
      // Work out the destination first — that tells us if this is an internal
      // same-farm transfer (own farm) which needs no truck.
      let place;
      if (destMode === "farm") {
        if (!farmDest) { setErr("Pick where the eggs are going."); return; }
        place = farmDest;
      } else {
        if (dests.length === 0) { setErr("Pick at least one destination."); return; }
        place = dests.join(", ");
      }
      const effDriver = driver === "__other__" ? driverOther.trim() : driver;
      if (!isOwnFarm) {
        if (!vehicle) { setErr("Pick the vehicle."); return; }
        if (!effDriver) { setErr(driver === "__other__" ? "Type the temporary driver's name." : "Pick the driver."); return; }
      }
      onAdd({ trays30:Number(t30)||0, trays25:Number(t25)||0, trays6:Number(t6)||0, vehicle: isOwnFarm ? "" : vehicle, driver: isOwnFarm ? "" : effDriver, place });
    }
  }

  return (
    <div style={{ background:"#120e1a", border:"1px solid #34244a", borderRadius:8, padding:10, marginTop:4 }}>
      {isReturn && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>{sourceLineKind==="farmissue" ? "FARM-ISSUE DATE · blank = today's farm issues" : "LOADING DATE · blank = today's loadings"}</div>
          <div style={{ position:"relative", marginBottom:6 }}>
            <div style={{ ...iS2, width:"100%", boxSizing:"border-box", display:"flex", alignItems:"center", justifyContent:"space-between", color: srcDate ? "#e0d0f0" : "#6a5a7a" }}>
              <span>{srcDate ? formatDate(srcDate) : "tap to pick a date"}</span>
              <span style={{ fontSize:13, opacity:0.85 }}>📅</span>
            </div>
            <input type="date" value={srcDate} max={currentDate}
              onChange={e=>setSrcDate(e.target.value)}
              onClick={e=>{ const el=e.currentTarget; if (el.showPicker) { try { el.showPicker(); } catch { /* older browsers: native focus handles it */ } } }}
              style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" }} />
          </div>
          {srcDate && (
            <div style={{ fontSize:9, color:"#9a8ac0", marginBottom:6 }}>
              Showing {srcLabel}s from {formatDate(srcDate)} · this {backNoun} is recorded on {formatDate(currentDate)}
              <span onClick={()=>setSrcDate("")} style={{ color:"#b08ad0", cursor:"pointer", marginLeft:6, textDecoration:"underline" }}>use today</span>
            </div>
          )}
          <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>AGAINST {srcLabel.toUpperCase()} SERIAL</div>
          <select value={issueRef} onChange={e=>setIssueRef(e.target.value)} style={{ ...iS2, width:"100%" }}>
            <option value="">{fetchingIssues ? "loading…" : `— select ${srcLabel} —`}</option>
            {activeIssues.map(i => (
              <option key={i.id} value={i.id}>{[`#${i.serial}`, i.vehicle, i.driver, i.place].filter(Boolean).join(" · ")}</option>
            ))}
          </select>
          {srcDate && !fetchingIssues && activeIssues.length === 0 && (
            <div style={{ fontSize:9, color:"#c08a6a", marginTop:4 }}>No {srcLabel}s recorded on {formatDate(srcDate)} for this store.</div>
          )}
          {selIssue && remaining && (
            <div style={{ fontSize:9, color:"#7aaa8a", marginTop:4 }}>
              {sourceLineKind==="farmissue"?"Receivable":"Returnable"} from #{selIssue.serial}:{sizes.includes(30)?` 30-size ${remaining.t30}`:""}{sizes.includes(25)?` · 25-size ${remaining.t25}`:""}{sizes.includes(6)?` · 6-size ${remaining.t6}`:""}
            </div>
          )}
        </div>
      )}

      <div style={{ display:"flex", gap:6, marginBottom:6, flexWrap:"wrap", alignItems:"flex-end" }}>
        {sizes.includes(30) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>30-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t30} onChange={e=>setT30(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        {sizes.includes(25) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>25-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t25} onChange={e=>setT25(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        {sizes.includes(6) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>6-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t6} onChange={e=>setT6(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        <div style={{ fontSize:11, color:"#b08ad0", fontWeight:600, paddingBottom:8 }}>= {eggs.toLocaleString()} eggs</div>
      </div>

      {!isReturn && (() => {
        const vehDriver = (
          <>
            <div style={{ display:"flex", gap:6, marginBottom:6 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>VEHICLE</div>
                <select value={vehicle} onChange={e=>setVehicle(e.target.value)} style={{ ...iS2, width:"100%" }}>
                  <option value="">— select —</option>
                  {PACK_VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:2 }}>
                  <span style={{ fontSize:8, color:"#7a6a8a" }}>DRIVER</span>
                  <button onClick={()=>setManageDrivers(true)} style={{ background:"transparent", border:"none", color:"#9a7ab8", fontSize:9, cursor:"pointer", fontFamily:"inherit", padding:0 }}>✎ manage</button>
                </div>
                <select value={driver} onChange={e=>{ setDriver(e.target.value); if (e.target.value!=="__other__") setDriverOther(""); }} style={{ ...iS2, width:"100%" }}>
                  <option value="">— select —</option>
                  {driverList.drivers.map(d => <option key={d.driver_id} value={d.name}>{d.name}</option>)}
                  <option value="__other__">Other (type name)…</option>
                </select>
              </div>
            </div>
            {driver === "__other__" && (
              <div style={{ marginBottom:6 }}>
                <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>TEMPORARY DRIVER NAME</div>
                <input type="text" value={driverOther} onChange={e=>setDriverOther(e.target.value)} placeholder="Type the driver's name" style={{ ...iS2, width:"100%" }} />
              </div>
            )}
          </>
        );
        if (destMode === "farm") {
          // FARM chosen first — vehicle & driver only appear for a DIFFERENT farm.
          return (
            <>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:3 }}>ISSUED TO</div>
                {farms.length > 0 ? (
                  <select value={farmDest} onChange={e=>setFarmDest(e.target.value)} style={{ ...iS2, width:"100%" }}>
                    <option value="">— select destination —</option>
                    {farms.map(fn => (
                      <option key={fn} value={fn}>
                        {fn === PACK_COLD_STORAGE ? `❄️ ${fn}` : fn}{fn === ownFarm ? " (this farm)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ fontSize:10, color:"#c08a6a" }}>No farms in the app to issue to.</div>
                )}
                {isOwnFarm && (
                  <div style={{ fontSize:9, color:"#7aaa8a", marginTop:5, background:"#0e1a12", border:"1px solid #24402c", borderRadius:7, padding:"5px 8px" }}>↔ Same-farm transfer (egg stock → packing store) — no vehicle or driver needed.</div>
                )}
              </div>
              {!isOwnFarm && vehDriver}
            </>
          );
        }
        return (
          <>
            {vehDriver}
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:8, color:"#7a6a8a", marginBottom:3 }}>DESTINATIONS · tap to select multiple</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                {PACK_DESTS.map(d => {
                  const on = dests.includes(d);
                  return (
                    <button key={d} onClick={() => toggleDest(d)} style={{
                      fontSize:10, padding:"4px 9px", borderRadius:20, cursor:"pointer", fontFamily:"inherit",
                      background: on ? "#34244a" : "#0e0a14", color: on ? "#d8c0f0" : "#7a6a8a",
                      border: `1px solid ${on ? "#7a5aa4" : "#2a2038"}`,
                    }}>{on ? "✓ " : ""}{d}</button>
                  );
                })}
              </div>
            </div>
          </>
        );
      })()}
      {isReturn && selIssue && (
        <div style={{ fontSize:9, color:"#7a6a8a", marginBottom:8 }}>{[selIssue.vehicle && `🚚 ${selIssue.vehicle}`, selIssue.driver && `👤 ${selIssue.driver}`, selIssue.place && `📍 ${selIssue.place}`].filter(Boolean).join(" · ")} — copied from {srcLabel} #{selIssue.serial}</div>
      )}

      {err && <div style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:11, padding:"7px 10px", marginBottom:8 }}>⚠️ {err}</div>}

      <div style={{ display:"flex", gap:6 }}>
        <button onClick={submit} style={{ flex:1, background:"linear-gradient(135deg,#34244a,#241a3a)", border:"1px solid #5a4484", borderRadius:6, color:"#c8a8e8", fontSize:12, padding:"7px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Add {kindLabel}</button>
        <button onClick={onCancel} style={{ background:"#222", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
      </div>

      {manageDrivers && (
        <PackDriverManager drivers={driverList.drivers} archived={driverList.archived}
          onApply={driverList.apply} onClose={()=>setManageDrivers(false)} />
      )}
    </div>
  );
}

// Direct (walk-in) sale section. Like issued, but buyer + vehicle are free text
// (no dropdowns) and there's no serial. Per-size trays for white/brown.
function PackDirectGroup({ label, sizes, items, totalEggs, addOpen: addOpenRaw, onOpenAdd, onAdd, onEdit, onDelete }) {
  const readOnly = useContext(PackReadOnlyCtx);
  const addOpen = addOpenRaw && !readOnly;
  return (
    <div style={{ borderBottom:"1px solid #1a1420" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:"#c06a5a", width:14, textAlign:"center" }}>−</span>
          <div>
            <span style={{ fontSize:13, fontWeight:500, color:"#c0b0d0" }}>{label}</span>
            <div style={{ fontSize:9, color:"#5a4a6a" }}>walk-in store purchase · buyer &amp; vehicle typed freely · subtracts from stock</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:15, fontWeight:"bold", color:"#e0d0f0" }}>{totalEggs.toLocaleString()}</span>
          <span style={{ fontSize:10, color:"#7a6a8a" }}>eggs</span>
          {!readOnly && <button onClick={onOpenAdd} style={{ background:addOpen?"#34244a":"#1e1428", border:"1px solid #4a3464", borderRadius:6, color:"#b08ad0", fontSize:14, cursor:"pointer", padding:"2px 9px", fontFamily:"inherit", lineHeight:1 }}>{addOpen?"×":"+"}</button>}
        </div>
      </div>

      {items.length > 0 && (
        <div style={{ padding:"0 14px 8px 36px" }}>
          {items.map(item => (
            <PackDirectItem key={item.id} item={item} sizes={sizes} onEdit={(p2)=>onEdit(item.id,p2)} onDelete={()=>onDelete(item.id)} />
          ))}
        </div>
      )}

      {addOpen && (
        <div style={{ padding:"0 14px 12px 36px" }}>
          <PackDirectForm sizes={sizes} onAdd={onAdd} onCancel={onOpenAdd} />
        </div>
      )}
    </div>
  );
}

function PackDirectItem({ item, sizes, onEdit, onDelete }) {
  const readOnly = useContext(PackReadOnlyCtx);
  const [editing, setEditing] = useState(false);
  const [t30, setT30] = useState(item.trays30);
  const [t25, setT25] = useState(item.trays25);
  const [t6, setT6] = useState(item.trays6);
  const [buyer, setBuyer] = useState(item.buyer);
  const [vehicle, setVehicle] = useState(item.vehicle);
  const iS2 = { background:"#0e0a14", border:"1px solid #3a2a4a", borderRadius:7, color:"#e0d0f0", padding:"6px 8px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };

  if (editing) {
    return (
      <div style={{ background:"#120e1a", border:"1px solid #34244a", borderRadius:8, padding:8, marginBottom:6 }}>
        <div style={{ display:"flex", gap:6, marginBottom:6, flexWrap:"wrap" }}>
          {sizes.includes(30) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>30-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t30} onChange={e=>setT30(e.target.value)} placeholder="–" style={{ ...iS2, width:64 }} /></div>}
          {sizes.includes(25) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>25-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t25} onChange={e=>setT25(e.target.value)} placeholder="–" style={{ ...iS2, width:64 }} /></div>}
          {sizes.includes(6) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>6-SIZE</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t6} onChange={e=>setT6(e.target.value)} placeholder="–" style={{ ...iS2, width:64 }} /></div>}
        </div>
        <div style={{ display:"flex", gap:6, marginBottom:6 }}>
          <input type="text" value={buyer} onChange={e=>setBuyer(e.target.value)} placeholder="Buyer name" style={{ ...iS2, flex:1 }} />
          <input type="text" value={vehicle} onChange={e=>setVehicle(e.target.value)} placeholder="Vehicle no." style={{ ...iS2, flex:1 }} />
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <button onClick={() => { onEdit({ trays30:Number(t30)||0, trays25:Number(t25)||0, trays6:Number(t6)||0, buyer, vehicle }); setEditing(false); }} style={{ flex:1, background:"#241a3a", border:"1px solid #4a3a6a", borderRadius:6, color:"#c8a8e8", fontSize:11, padding:"6px", cursor:"pointer", fontFamily:"inherit" }}>Save</button>
          <button onClick={() => { setT30(item.trays30); setT25(item.trays25); setT6(item.trays6); setBuyer(item.buyer); setVehicle(item.vehicle); setEditing(false); }} style={{ background:"#222", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:11, padding:"6px 12px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
        </div>
      </div>
    );
  }

  const sizeBits = [];
  if (item.trays30 > 0) sizeBits.push(`30×${item.trays30}`);
  if (item.trays25 > 0) sizeBits.push(`25×${item.trays25}`);
  if (item.trays6 > 0) sizeBits.push(`6×${item.trays6}`);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #140e1e" }}>
      <span style={{ fontSize:11, fontWeight:700, color:"#d08a6a", background:"#1e1208", border:"1px solid #4a2a14", borderRadius:6, padding:"2px 7px", whiteSpace:"nowrap" }}>🛒 direct</span>
      <span style={{ fontSize:13, fontWeight:"bold", color:"#d0b8f0", minWidth:50 }}>{item.eggs.toLocaleString()}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:11, color:"#a890c0" }}>{sizeBits.join(" · ") || "(no trays)"}</div>
        <div style={{ fontSize:9, color:"#7a6a8a" }}>
          {item.buyer && <>👤 {item.buyer}</>}{item.vehicle && <> · 🚚 {item.vehicle}</>}
        </div>
      </div>
      {!readOnly && (<>
      <button onClick={() => setEditing(true)} style={{ background:"transparent", border:"1px solid #34244a", borderRadius:5, color:"#9a7ab8", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>edit</button>
      <button onClick={() => { if(window.confirm("Delete this direct sale?")) onDelete(); }} style={{ background:"transparent", border:"1px solid #4a2030", borderRadius:5, color:"#a05060", fontSize:10, cursor:"pointer", padding:"3px 7px", fontFamily:"inherit" }}>×</button>
      </>)}
    </div>
  );
}

function PackDirectForm({ sizes, onAdd, onCancel }) {
  const [t30, setT30] = useState("");
  const [t25, setT25] = useState("");
  const [t6, setT6] = useState("");
  const [buyer, setBuyer] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [err, setErr] = useState("");
  const iS2 = { background:"#0e0a14", border:"1px solid #3a2a4a", borderRadius:7, color:"#e0d0f0", padding:"7px 9px", fontSize:12, fontFamily:"inherit", boxSizing:"border-box" };
  const eggs = 30*(Number(t30)||0) + 25*(Number(t25)||0) + 6*(Number(t6)||0);

  function submit() {
    setErr("");
    if (eggs <= 0) { setErr("Enter trays in at least one size."); return; }
    if (!buyer.trim()) { setErr("Enter the buyer's name."); return; }
    onAdd({ trays30:Number(t30)||0, trays25:Number(t25)||0, trays6:Number(t6)||0, buyer, vehicle });
  }

  return (
    <div style={{ background:"#120e1a", border:"1px solid #34244a", borderRadius:8, padding:10, marginTop:4 }}>
      <div style={{ fontSize:9, color:"#d08a6a", fontWeight:600, marginBottom:6 }}>🛒 DIRECT STORE SALE (subtracts from stock)</div>
      <div style={{ display:"flex", gap:6, marginBottom:6, flexWrap:"wrap", alignItems:"flex-end" }}>
        {sizes.includes(30) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>30-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t30} onChange={e=>setT30(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        {sizes.includes(25) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>25-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t25} onChange={e=>setT25(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        {sizes.includes(6) && <div><div style={{ fontSize:8, color:"#7a6a8a", marginBottom:2 }}>6-SIZE TRAYS</div><input type="number" min="0" className="pack-num" onKeyDown={packKeyNav} value={t6} onChange={e=>setT6(e.target.value)} placeholder="–" style={{ ...iS2, width:78 }} /></div>}
        <div style={{ fontSize:11, color:"#b08ad0", fontWeight:600, paddingBottom:8 }}>= {eggs.toLocaleString()} eggs</div>
      </div>
      <div style={{ display:"flex", gap:6, marginBottom:8 }}>
        <input type="text" value={buyer} onChange={e=>setBuyer(e.target.value)} placeholder="Buyer name" style={{ ...iS2, flex:1 }} />
        <input type="text" value={vehicle} onChange={e=>setVehicle(e.target.value)} placeholder="Vehicle no. (optional)" style={{ ...iS2, flex:1 }} />
      </div>
      {err && <div style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:11, padding:"7px 10px", marginBottom:8 }}>⚠️ {err}</div>}
      <div style={{ display:"flex", gap:6 }}>
        <button onClick={submit} style={{ flex:1, background:"linear-gradient(135deg,#3a241a,#2a1a12)", border:"1px solid #6a4434", borderRadius:6, color:"#e8b098", fontSize:12, padding:"7px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Add Direct Sale</button>
        <button onClick={onCancel} style={{ background:"#222", border:"1px solid #444", borderRadius:6, color:"#999", fontSize:12, padding:"7px 14px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
      </div>
    </div>
  );
}

// ── 4th tab: read-only daily SALE monitor for white + brown ───────────────────
// Wide layout that uses the full screen width on PC: issue cards lay out in a
// responsive grid and the figures are large and legible.
function PackSaleMonitor({ farm, selDate, reportError }) {
  const anchor = selDate || todayISO();
  const [from, setFrom] = useState(anchor.slice(0, 7) + "-01");
  const [to, setTo] = useState(anchor);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const d = await apiCall(`/farms/${farm.id}/packing-sale-range?from=${from}&to=${to}`);
        if (alive) setData(d);
      } catch (e) { reportError && reportError(e); }
      if (alive) setBusy(false);
    })();
    return () => { alive = false; };
  }, [farm.id, from, to]);

  const KIND_META = { white: { label:"⚪ White", sizes:[30,25,6] }, brown: { label:"🟤 Brown", sizes:[30,25,6] } };
  const trayCell = (label, v, big) => (
    <div style={{ flex:1, minWidth:96, background:"#1a1206", border:"1px solid #4a3814", borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
      <div style={{ fontSize:big?26:20, fontWeight:"bold", color:"#e8c878", lineHeight:1 }}>{v.toLocaleString()}</div>
      <div style={{ fontSize:9, color:"#9a8050", letterSpacing:0.5, marginTop:4 }}>{label}</div>
    </div>
  );

  const issueSale = (issue, returns) => {
    const rs = returns.filter(r => r.issueRef === issue.id);
    const sum = (f) => rs.reduce((s, r) => s + (r[f]||0), 0);
    return { t30: issue.trays30 - sum("trays30"), t25: issue.trays25 - sum("trays25"), t6: issue.trays6 - sum("trays6"), rs };
  };

  const renderKind = (stock, key) => {
    if (!stock) return null;
    const meta = KIND_META[key];
    return (
      <div key={key} style={{ background:"#0c0a10", border:"1px solid #3a2a4a", borderRadius:16, padding:"16px 18px", marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
          <span style={{ fontSize:18, fontWeight:"bold", color:"#c8b0e0" }}>{meta.label}</span>
          <div style={{ display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:13, color:"#9a8ab0" }}>Production: <strong style={{ color:"#d0b8f0", fontSize:16 }}>{Math.floor(stock.production/30).toLocaleString()}</strong> trays</span>
            {stock.directSale.eggs > 0 && <span style={{ fontSize:13, color:"#c0907a" }}>Direct: <strong style={{ color:"#e8b098", fontSize:16 }}>{meta.sizes.map(sz=>sz===30?stock.directSale.t30:sz===25?stock.directSale.t25:stock.directSale.t6).join("/")}</strong> trays</span>}
          </div>
        </div>

        {stock.issued.length === 0 && <div style={{ fontSize:13, color:"#5a4a6a", fontStyle:"italic", padding:"10px 0" }}>No dispatch issues today.</div>}

        {/* Issue cards in a responsive grid that fills width on PC */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(340px, 1fr))", gap:12 }}>
          {stock.issued.map(issue => {
            const sale = issueSale(issue, stock.returns);
            return (
              <div key={issue.id} style={{ background:"#120e1a", border:"1px solid #2a2038", borderRadius:12, padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:14, fontWeight:700, color:"#d0a850", background:"#1e1608", border:"1px solid #4a3a14", borderRadius:7, padding:"3px 11px" }}>#{issue.serial}</span>
                  <span style={{ fontSize:14, color:"#c0a8e0", fontWeight:600 }}>🚚 {issue.vehicle}</span>
                  <span style={{ fontSize:13, color:"#a890c0" }}>👤 {issue.driver}</span>
                </div>
                {issue.place && <div style={{ fontSize:12, color:"#8a7aa0", marginBottom:8 }}>📍 {issue.place}</div>}
                <div style={{ display:"flex", gap:16, marginBottom:8, fontSize:12, flexWrap:"wrap" }}>
                  <span style={{ color:"#9a8ab0" }}>Issued: <strong style={{ color:"#c8b0e0" }}>{meta.sizes.map(sz => `${sz}×${sz===30?issue.trays30:sz===25?issue.trays25:issue.trays6}`).join(" · ")}</strong></span>
                </div>
                {sale.rs.length > 0 && (
                  <div style={{ fontSize:12, color:"#7aaa8a", marginBottom:8 }}>
                    Returned: {sale.rs.map(r => meta.sizes.map(sz => `${sz}×${sz===30?r.trays30:sz===25?r.trays25:r.trays6}`).join(" · ")).join(" | ")}
                  </div>
                )}
                <div style={{ display:"flex", gap:8, borderTop:"1px solid #2a2038", paddingTop:10, marginTop:4 }}>
                  {meta.sizes.map(sz => {
                    const v = sz===30 ? sale.t30 : sz===25 ? sale.t25 : sale.t6;
                    return (
                      <div key={sz} style={{ flex:1, textAlign:"center", background:"#1a1206", border:"1px solid #3a2c10", borderRadius:8, padding:"7px 4px" }}>
                        <div style={{ fontSize:18, fontWeight:"bold", color:"#e8c878" }}>{v.toLocaleString()}</div>
                        <div style={{ fontSize:8, color:"#9a8050" }}>{sz}-SIZE SOLD</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Day total for this kind */}
        <div style={{ marginTop:14 }}>
          <div style={{ fontSize:10, color:"#9a8050", letterSpacing:1, textTransform:"uppercase", marginBottom:6 }}>{meta.label} day total (loading sale, trays)</div>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
            {meta.sizes.map(sz => trayCell(`${sz}-size`, sz===30 ? stock.sale.t30 : sz===25 ? stock.sale.t25 : stock.sale.t6, false))}
          </div>
        </div>
      </div>
    );
  };

  const rangeLabel = `${formatDate(from)} → ${formatDate(to)}`;
  const days = (data && data.days) || [];
  const totals = data && data.totals;
  const combined = totals
    ? { t30: totals.white.t30 + totals.brown.t30, t25: totals.white.t25 + totals.brown.t25, t6: totals.white.t6 + totals.brown.t6 }
    : { t30:0, t25:0, t6:0 };
  const prodTrays = totals ? Math.floor(totals.productionEggs / 30) : 0;

  return (
    <div>
      <DateRangePicker from={from} to={to} onChange={(f,t)=>{ setFrom(f); setTo(t); }} accent="#e8c878" />
      <ReportButton accent="#e8c878" disabled={busy || !data} onClick={() => openReport(() => buildPackSaleReport(farm, data, from, to))} />
      <div style={{ fontSize:11, color:"#6a5a7a", textAlign:"center", marginBottom:14, fontStyle:"italic" }}>
        👁️ View-only — white &amp; brown dispatch sale, per issue, grouped by day. Figures in trays.
      </div>

      {busy && <div style={{ textAlign:"center", padding:40, color:"#6a5a7a", fontSize:13 }}>Loading…</div>}

      {!busy && days.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#3a4a5a" }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📅</div>
          <div style={{ fontSize:13 }}>No dispatch sales in this range</div>
        </div>
      )}

      {!busy && days.length > 0 && (
        <>
          {/* Range grand total (loading sale, white + brown) */}
          <div style={{ background:"linear-gradient(120deg,#1a1206,#14100a)", border:"1.5px solid #7a6020", borderRadius:16, padding:"16px 18px", marginBottom:20 }}>
            <div style={{ fontSize:12, color:"#e0c060", fontWeight:"bold", letterSpacing:1, textTransform:"uppercase", marginBottom:10 }}>📊 Range total · {rangeLabel} (loading sale)</div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {trayCell("30-size sale", combined.t30, true)}
              {trayCell("25-size sale", combined.t25, true)}
              {trayCell("6-size sale", combined.t6, true)}
              <div style={{ flex:1.2, minWidth:110, background:"#0e0a14", border:"1px solid #3a2a4a", borderRadius:10, padding:"12px 8px", textAlign:"center" }}>
                <div style={{ fontSize:26, fontWeight:"bold", color:"#d0b8f0", lineHeight:1 }}>{prodTrays.toLocaleString()}</div>
                <div style={{ fontSize:9, color:"#9a8ab0", marginTop:4 }}>PRODUCTION TRAYS</div>
              </div>
            </div>
          </div>

          {/* One section per day, newest first */}
          {days.map(day => (
            <div key={day.date} style={{ marginBottom:18 }}>
              <div style={{ marginBottom:8 }}>
                <span style={{ fontSize:13, fontWeight:"bold", color:"#c8b0e0", background:"#160e04", border:"1px solid #4a3814", borderRadius:20, padding:"3px 12px" }}>📅 {formatDate(day.date)}</span>
              </div>
              {renderKind(day.white, "white")}
              {renderKind(day.brown, "brown")}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ── 5th tab: monthly monitor — production + sale (loading & direct) for all 3
// kinds, month-total summary + daily breakdown (like the production farm banner).
function PackMonthlyMonitor({ farm, selDate, reportError }) {
  const anchor = selDate || todayISO();
  const [from, setFrom] = useState(anchor.slice(0, 7) + "-01");
  const [to, setTo] = useState(anchor);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try { const d = await apiCall(`/farms/${farm.id}/packing-monthly?from=${from}&to=${to}`); if (alive) setData(d); }
      catch (e) { reportError && reportError(e); }
      if (alive) setBusy(false);
    })();
    return () => { alive = false; };
  }, [farm.id, from, to]);

  const rangeLabel = `${formatDate(from)} → ${formatDate(to)}`;

  const KINDS = [
    { key:"white", label:"⚪ White", sizes:[30,25,6], accent:"#c8c8d8" },
    { key:"brown", label:"🟤 Brown", sizes:[30,25,6], accent:"#c8a878" },
    { key:"loose", label:"🧺 Loose", sizes:[], accent:"#a8c8a8" },
  ];

  return (
    <div>
      <DateRangePicker from={from} to={to} onChange={(f,t)=>{ setFrom(f); setTo(t); }} accent="#7ad0e8" />
      <ReportButton accent="#7ad0e8" disabled={busy || !data} onClick={() => openReport(() => buildPackMonthReport(farm, data, from, to))} />

      <div style={{ fontSize:11, color:"#5a6a7a", textAlign:"center", marginBottom:14, fontStyle:"italic" }}>
        👁️ View-only — production &amp; sale (loading + direct) for the selected range.
      </div>

      {busy && <div style={{ textAlign:"center", padding:40, color:"#5a6a7a", fontSize:13 }}>Loading…</div>}

      {!busy && data && data.days.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#3a4a5a" }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📅</div>
          <div style={{ fontSize:13 }}>No packing entries in this range</div>
        </div>
      )}

      {!busy && data && data.days.length > 0 && (
        <>
          {/* MONTH TOTAL summary per kind (distinct teal banner) */}
          <div style={{ background:"linear-gradient(120deg,#0a2a3a,#0e3a4a 55%,#0a2230)", border:"1.5px solid #2a7a9a", borderRadius:16, padding:"14px 16px", marginBottom:16, boxShadow:"0 4px 24px rgba(20,90,120,0.3)" }}>
            <div style={{ fontSize:13, fontWeight:"bold", color:"#7ad0e8", letterSpacing:0.5, textTransform:"uppercase", marginBottom:12 }}>📊 Total · {rangeLabel}</div>
            {KINDS.map(k => {
              const t = data.totals[k.key];
              const saleStr = k.key === "loose" ? `${fmtTE(t.loadEggs)}` : k.sizes.map(sz => `${sz}×${sz===30?t.loadT30:sz===25?t.loadT25:t.loadT6}`).join(" · ");
              const dirStr = k.key === "loose" ? `${fmtTE(t.dirEggs)}` : k.sizes.map(sz => `${sz}×${sz===30?t.dirT30:sz===25?t.dirT25:t.dirT6}`).join(" · ");
              return (
                <div key={k.key} style={{ borderTop:"1px solid #1a4a5a", paddingTop:10, marginTop:10 }}>
                  <div style={{ fontSize:13, fontWeight:"bold", color:k.accent, marginBottom:6 }}>{k.label}</div>
                  <div style={{ display:"flex", gap:18, flexWrap:"wrap", fontSize:12 }}>
                    <span style={{ color:"#8ad0a0" }}>📦 Production: <strong style={{ color:"#a8e8c0", fontSize:15 }}>{t.prodTrays.toLocaleString()}</strong> trays</span>
                    <span style={{ color:"#c0a860" }}>🚚 Loading sale: <strong style={{ color:"#e8c878" }}>{saleStr}</strong>{k.key!=="loose"?" trays":""}</span>
                    {t.dirEggs > 0 && <span style={{ color:"#c0907a" }}>🛒 Direct sale: <strong style={{ color:"#e8b098" }}>{dirStr}</strong>{k.key!=="loose"?" trays":""}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* DAILY breakdown per kind */}
          {KINDS.map(k => (
            <div key={k.key} style={{ background:"#0c0a10", border:"1px solid #2a3a44", borderRadius:14, overflow:"hidden", marginBottom:14 }}>
              <div style={{ padding:"10px 14px", borderBottom:"1px solid #1a2a32", background:"#0a1820" }}>
                <span style={{ fontSize:14, fontWeight:"bold", color:k.accent }}>{k.label}</span>
                <span style={{ fontSize:10, color:"#5a6a7a", marginLeft:8 }}>day-by-day</span>
              </div>
              {/* header */}
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px", fontSize:8, color:"#5a6a7a", letterSpacing:0.5, textTransform:"uppercase", borderBottom:"1px solid #1a2a32" }}>
                <span style={{ minWidth:80 }}>Date</span>
                <span style={{ flex:1, textAlign:"center" }}>📦 Prod</span>
                <span style={{ flex:1.4, textAlign:"center" }}>🚚 Loading sale</span>
                <span style={{ flex:1.4, textAlign:"center" }}>🛒 Direct sale</span>
              </div>
              {data.days.map(day => {
                const s = day[k.key];
                const noActivity = s.prodTrays===0 && s.loadEggs===0 && s.dirEggs===0;
                if (noActivity) return null;
                const loadStr = k.key === "loose" ? fmtTE(s.loadEggs) : k.sizes.map(sz => `${sz===30?s.loadT30:sz===25?s.loadT25:s.loadT6}`).join("/");
                const dirStr = k.key === "loose" ? (s.dirEggs>0?fmtTE(s.dirEggs):"–") : (s.dirEggs>0 ? k.sizes.map(sz => `${sz===30?s.dirT30:sz===25?s.dirT25:s.dirT6}`).join("/") : "–");
                return (
                  <div key={day.date} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:"1px solid #14202a", fontSize:12 }}>
                    <span style={{ minWidth:80, color:"#9ab0c0", fontWeight:600 }}>{formatDate(day.date)}</span>
                    <span style={{ flex:1, textAlign:"center", color:"#a8e8c0" }}>{s.prodTrays > 0 ? s.prodTrays.toLocaleString() : "–"}</span>
                    <span style={{ flex:1.4, textAlign:"center", color:"#e8c878" }}>{s.loadEggs > 0 ? loadStr : "–"}{k.key!=="loose" && s.loadEggs>0 ? <span style={{ fontSize:8, color:"#7a6a4a" }}> ({k.sizes.join("/")})</span> : null}</span>
                    <span style={{ flex:1.4, textAlign:"center", color:"#e8b098" }}>{dirStr}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
const inputStyle = {
  background:"#0c1a0a", border:"1px solid #223a1e", borderRadius:10,
  color:"#c8f0a8", padding:"11px 14px", fontSize:14, fontFamily:"system-ui,sans-serif",
  width:"100%", boxSizing:"border-box", outline:"none",
};

// ═══ FEED & MEDICINE STOCK MODULE ════════════════════════════════════════════
const FEED_THEME = { accent:"#5cc8a8", dark:"#08201a", border:"#1e4a3a", pill:"#0e2e24", stripe:"#3a8a6a" };
const FEED_CATS_UI = [
  { key:"layer",  label:"Layers",  emoji:"🥚" },
  { key:"grower", label:"Growers", emoji:"🐓" },
  { key:"chick",  label:"Chicks",  emoji:"🐤" },
];
const feedKg = (n) => (Math.round((Number(n)||0)*100)/100).toLocaleString(undefined,{maximumFractionDigits:2});
const feedRs = (n) => "₹" + Math.round(Number(n)||0).toLocaleString();
const feedCatLabel = (k) => (FEED_CATS_UI.find(c=>c.key===k) || {}).label || k;

// Controlled numeric input — local text state, commits on blur; allows decimals.
// `live` commits on every keystroke (for local state). Leave it off when onCommit
// hits the server, so typing doesn't fire a request per character.
function FeedNumInput({ value, onCommit, placeholder="0", width="100%", disabled=false, live=false }) {
  const [text, setText] = useState(value===""||value==null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(value===""||value==null ? "" : String(value)); }, [value, focused]);
  return (
    <input type="number" inputMode="decimal" value={text} placeholder={placeholder} disabled={disabled}
      onChange={e=>{ setText(e.target.value); if (live) onCommit(e.target.value.trim()); }}
      onFocus={()=>setFocused(true)}
      onBlur={()=>{ setFocused(false); onCommit(text.trim()); }}
      style={{ background:"#0a1712", border:"1px solid #24503e", borderRadius:8, color:"#d8f0e4", padding:"8px 10px", fontSize:14, fontFamily:"inherit", width, boxSizing:"border-box", textAlign:"right", opacity:disabled?0.5:1 }} />
  );
}

// Reusable tap-to-open date field (dark).
function FeedDateField({ value, onChange, max }) {
  return (
    <div style={{ position:"relative" }}>
      <div style={{ background:"#0a1712", border:"1px solid #24503e", borderRadius:8, color:value?"#d8f0e4":"#5a8a76", padding:"8px 10px", fontSize:13, display:"flex", justifyContent:"space-between", boxSizing:"border-box" }}>
        <span>{value?formatDate(value):"pick a date"}</span><span style={{ opacity:0.8 }}>📅</span>
      </div>
      <input type="date" value={value} max={max} onChange={e=>e.target.value&&onChange(e.target.value)}
        onClick={e=>{ const el=e.currentTarget; if (el.showPicker) { try { el.showPicker(); } catch (err) { /* older browsers */ } } }}
        style={{ position:"absolute", inset:0, opacity:0, width:"100%", height:"100%", cursor:"pointer" }} />
    </div>
  );
}

function FeedStock({ farm, allFarms = [], perms: pm = {}, reportError }) {
  const T = FEED_THEME;
  const [tab, setTab] = useState("stock"); // stock | receive | recipe | daily | usage | items
  const [state, setState] = useState(null);
  // The recipe editor's draft lives HERE, not inside the tab, so switching tabs
  // and coming back doesn't throw away work in progress.
  const [recipeUI, setRecipeUI] = useState({ cat:"layer", asOf:todayISO(), editing:false, rows:[], kg:{}, effDate:todayISO() });
  const [loading, setLoading] = useState(true);
  const canEdit = pm.canEdit;

  async function load() {
    setLoading(true);
    try { setState(await apiCall(`/farms/${farm.id}/feed-state`)); }
    catch (e) { reportError && reportError(e); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [farm.id]);

  // Mutate then refresh from the returned feed-state. Alerts on error (e.g. FIFO block).
  async function mutate(path, opts) {
    try { const s = await apiCall(path, opts); if (s && s.items) setState(s); else await load(); return s || true; }
    catch (e) { alert(e && e.message ? e.message : "Could not save"); return false; }
  }

  const items = (state && state.items) || [];
  const liveItems = items.filter(i => !i.archived);
  const feedItems = liveItems.filter(i => i.kind === "feed");
  const medItems  = liveItems.filter(i => i.kind === "medicine");
  const archivedItems = items.filter(i => i.archived);

  const card = { background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:14, padding:"14px 16px", marginBottom:12 };
  const lbl  = { fontSize:8, color:"#5a8a76", letterSpacing:0.5, textTransform:"uppercase", marginBottom:3 };
  const sel  = { background:"#0a1712", border:"1px solid #24503e", borderRadius:8, color:"#d8f0e4", padding:"8px 10px", fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box" };
  const btn  = (bg,bd,c) => ({ background:bg, border:`1px solid ${bd}`, borderRadius:9, color:c, fontSize:13, fontWeight:600, padding:"10px 14px", cursor:"pointer", fontFamily:"inherit" });

  const TABS = [
    { key:"stock",   label:"📊 Stock" },
    { key:"receive", label:"➕ Stock In" },
    { key:"recipe",  label:"🧪 Recipe" },
    { key:"daily",   label:"📅 Daily" },
    { key:"usage",   label:"📈 Usage" },
    { key:"reorder", label:"🔔 Reorder" },
    { key:"items",   label:"🏷️ Items" },
  ];

  return (
    <div style={{ padding:"14px 14px 40px" }}>
      <div style={{ display:"flex", gap:5, marginBottom:12, overflowX:"auto", paddingBottom:2 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex:"1 0 auto", whiteSpace:"nowrap", padding:"9px 12px",
            background: tab===t.key ? T.pill : "transparent",
            border:`1px solid ${tab===t.key ? T.border : "#12281f"}`, borderRadius:10,
            color: tab===t.key ? T.accent : "#3a5a4a", fontSize:12.5, fontFamily:"inherit",
            fontWeight: tab===t.key ? 700 : 400, cursor:"pointer",
          }}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign:"center", padding:40, color:"#4a7a66", fontSize:13 }}>Loading…</div>}

      {!loading && state && tab==="stock"   && <FeedStockTab   state={state} T={T} />}
      {!loading && state && tab==="receive" && <FeedReceiveTab state={state} T={T} card={card} lbl={lbl} sel={sel} btn={btn} items={liveItems} canEdit={canEdit} mutate={mutate} farmId={farm.id} />}
      {!loading && state && tab==="recipe"  && <FeedRecipeTab  state={state} T={T} lbl={lbl} btn={btn} feedItems={feedItems} medItems={medItems} canEdit={canEdit} mutate={mutate} farm={farm} allFarms={allFarms} ui={recipeUI} setUi={setRecipeUI} />}
      {!loading && state && tab==="daily"   && <FeedDailyTab   state={state} T={T} card={card} lbl={lbl} canEdit={canEdit} mutate={mutate} farmId={farm.id} />}
      {!loading && state && tab==="usage"   && <FeedUsageTab   T={T} card={card} farmId={farm.id} farm={farm} reportError={reportError} />}
      {!loading && state && tab==="reorder" && <FeedReorderTab state={state} T={T} card={card} lbl={lbl} canEdit={canEdit} mutate={mutate} farmId={farm.id} />}
      {!loading && state && tab==="items"   && <FeedItemsTab   state={state} T={T} card={card} lbl={lbl} sel={sel} btn={btn} feedItems={feedItems} medItems={medItems} archivedItems={archivedItems} canEdit={canEdit} mutate={mutate} farmId={farm.id} farm={farm} allFarms={allFarms} />}
    </div>
  );
}

// ---- STOCK tab ----
function FeedStockTab({ state, T }) {
  const [openId, setOpenId] = useState(null);
  const byId = {}; state.stock.forEach(s => { byId[s.item_id] = s; });
  // Archived items disappear once their stock runs out, but stay while kg remain.
  const visible = state.items.filter(i => !i.archived || ((byId[i.item_id] || {}).qty || 0) > 1e-9);
  const feedItems = visible.filter(i => i.kind === "feed");
  const medItems  = visible.filter(i => i.kind === "medicine");
  const section = (title, list) => {
    if (list.length === 0) return null;
    return (
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:11, color:T.accent, letterSpacing:1, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>{title}</div>
        {list.map(it => {
          const s = byId[it.item_id] || { qty:0, value:0, layers:[], frontRate:0 };
          const open = openId === it.item_id;
          return (
            <div key={it.item_id} style={{ background:"#0a1712", border:`1px solid ${s.qty>1e-9?T.border:"#241a1a"}`, borderRadius:12, marginBottom:8, overflow:"hidden" }}>
              <div onClick={() => setOpenId(open?null:it.item_id)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 14px", cursor:"pointer", gap:8 }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:"#d8f0e4" }}>{it.name}{it.archived ? <span style={{ fontSize:8, color:"#8a7a5a", border:"1px solid #4a3a2a", borderRadius:8, padding:"1px 5px", marginLeft:5 }}>ARCHIVED</span> : null}</div>
                  <div style={{ fontSize:10, color:"#5a8a76" }}>{s.layers.length} batch{s.layers.length!==1?"es":""}{s.layers.length?` · next kg ${feedRs(s.frontRate)}`:""}</div>
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ fontSize:16, fontWeight:"bold", color: s.qty>1e-9?"#8ce0c4":"#7a5a5a" }}>{feedKg(s.qty)} <span style={{ fontSize:10, color:"#5a8a76", fontWeight:400 }}>kg</span></div>
                  <div style={{ fontSize:11, color:"#c8a860" }}>{feedRs(s.value)}</div>
                </div>
              </div>
              {open && (
                <div style={{ borderTop:`1px solid ${T.border}`, padding:"8px 14px 12px", background:"#08130f" }}>
                  {s.layers.length===0 ? <div style={{ fontSize:11, color:"#7a5a5a" }}>Out of stock.</div> : s.layers.map((l,i) => (
                    <div key={l.batch_id} style={{ display:"flex", justifyContent:"space-between", fontSize:11.5, padding:"4px 0", borderBottom:i<s.layers.length-1?"1px solid #12281f":"none" }}>
                      <span style={{ color:"#7ab8a0" }}>{i===0?"⬅ used next":"—"} {formatDate(l.date)} <span style={{ color:"#4a6a5a" }}>({l.kind})</span></span>
                      <span style={{ color:"#d8f0e4" }}>{feedKg(l.remaining)} kg @ {feedRs(l.rate)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div>
      <div style={{ background:`linear-gradient(135deg,${T.dark},#06130e)`, border:`1.5px solid ${T.accent}55`, borderRadius:16, padding:"16px 18px", marginBottom:14, textAlign:"center" }}>
        <div style={{ fontSize:11, color:"#6aa892", letterSpacing:1, textTransform:"uppercase" }}>Total Inventory Value</div>
        <div style={{ fontSize:32, fontWeight:"bold", color:"#8ce0c4", lineHeight:1.15, marginTop:2 }}>{feedRs(state.totalValue)}</div>
        <div style={{ fontSize:11, color:"#6aa892" }}>{state.stock.filter(s=>s.qty>1e-9).length} item(s) in stock · FIFO valued</div>
      </div>
      {visible.length===0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#3a5a4a" }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🌾</div>
          <div style={{ fontSize:13 }}>No items yet. Add items in the Items tab, then opening stock.</div>
        </div>
      ) : (<>{section("🌾 Feed", feedItems)}{section("💊 Medicine", medItems)}</>)}
    </div>
  );
}

// ---- STOCK IN tab ----
function FeedReceiveTab({ state, T, card, lbl, sel, btn, items, canEdit, mutate, farmId }) {
  const [itemId, setItemId] = useState("");
  const [type, setType] = useState("received");
  const [date, setDate] = useState(todayISO());
  const [qty, setQty] = useState("");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!itemId) return alert("Pick an item.");
    if (!(Number(qty) > 0)) return alert("Enter a quantity in kg.");
    setBusy(true);
    const ok = await mutate(`/farms/${farmId}/feed-batches`, { method:"POST", body: JSON.stringify({ item_id:Number(itemId), batch_date:date, qty_kg:Number(qty), rate:Number(rate)||0, kind:type }) });
    setBusy(false);
    if (ok) { setQty(""); setRate(""); }
  }
  const batches = state.batches || [];

  return (
    <div>
      {canEdit && (
        <div style={card}>
          <div style={{ fontSize:12, color:T.accent, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", marginBottom:10 }}>➕ Add Stock (kg)</div>
          <div style={{ display:"flex", gap:6, marginBottom:8 }}>
            {[["received","Received"],["opening","Opening"]].map(([k,l]) => (
              <button key={k} onClick={()=>setType(k)} style={{ flex:1, padding:"8px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12.5,
                background: type===k?T.pill:"#0a1712", border:`1px solid ${type===k?T.accent:"#24503e"}`, color: type===k?T.accent:"#5a8a76", fontWeight: type===k?700:400 }}>{l}</button>
            ))}
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={lbl}>Item</div>
            <select value={itemId} onChange={e=>setItemId(e.target.value)} style={sel}>
              <option value="">— select item —</option>
              <optgroup label="Feed">{items.filter(i=>i.kind==="feed").map(i=><option key={i.item_id} value={i.item_id}>{i.name}</option>)}</optgroup>
              <optgroup label="Medicine">{items.filter(i=>i.kind==="medicine").map(i=><option key={i.item_id} value={i.item_id}>{i.name}</option>)}</optgroup>
            </select>
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <div style={{ flex:1.2 }}><div style={lbl}>Date</div><FeedDateField value={date} onChange={setDate} max={todayISO()} /></div>
            <div style={{ flex:1 }}><div style={lbl}>Qty (kg)</div><FeedNumInput value={qty} onCommit={setQty} live /></div>
            <div style={{ flex:1 }}><div style={lbl}>Rate ₹/kg</div><FeedNumInput value={rate} onCommit={setRate} live /></div>
          </div>
          <button onClick={add} disabled={busy} style={{ ...btn("linear-gradient(135deg,#12604a,#0a3a2c)","#2a8a6a","#a8f0d8"), width:"100%", opacity:busy?0.6:1 }}>{busy?"Saving…":`Add ${type==="opening"?"Opening":"Received"} Stock`}</button>
          <div style={{ fontSize:9.5, color:"#4a6a5a", marginTop:6, textAlign:"center" }}>Oldest stock is always consumed first (FIFO).</div>
        </div>
      )}

      <div style={{ fontSize:11, color:"#5a8a76", letterSpacing:1, textTransform:"uppercase", margin:"14px 4px 8px", fontWeight:700 }}>Stock-in history</div>
      {batches.length===0 ? <div style={{ textAlign:"center", padding:30, color:"#3a5a4a", fontSize:13 }}>No stock added yet.</div> :
        batches.map(b => <FeedBatchRow key={b.batch_id} b={b} T={T} canEdit={canEdit} mutate={mutate} />)}
    </div>
  );
}
function FeedBatchRow({ b, T, canEdit, mutate }) {
  const [edit, setEdit] = useState(false);
  const [date, setDate] = useState(b.date);
  const [qty, setQty] = useState(String(b.qty));
  const [rate, setRate] = useState(String(b.rate));
  const consumed = b.qty - b.remaining;
  async function save() {
    const ok = await mutate(`/feed-batches/${b.batch_id}`, { method:"PUT", body: JSON.stringify({ batch_date:date, qty_kg:Number(qty)||0, rate:Number(rate)||0 }) });
    if (ok) setEdit(false);
  }
  async function del() { if (window.confirm(`Delete this ${b.type} entry of ${feedKg(b.qty)} kg ${b.item_name}?`)) await mutate(`/feed-batches/${b.batch_id}`, { method:"DELETE" }); }
  return (
    <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:11, padding:"10px 13px", marginBottom:7 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:13.5, fontWeight:600, color:"#d8f0e4" }}>{b.item_name} <span style={{ fontSize:9, color: b.type==="opening"?"#c8a860":"#6aa892", border:`1px solid ${b.type==="opening"?"#5a4a2a":"#2a5a48"}`, borderRadius:10, padding:"1px 7px", marginLeft:4 }}>{b.type}</span></div>
          <div style={{ fontSize:11, color:"#5a8a76", marginTop:2 }}>{formatDate(b.date)} · {feedKg(b.qty)} kg @ {feedRs(b.rate)}/kg = {feedRs(b.qty*b.rate)}</div>
          <div style={{ fontSize:10, color: b.remaining>1e-9?"#7ab8a0":"#7a5a5a", marginTop:1 }}>{feedKg(b.remaining)} kg remaining{consumed>1e-9?` · ${feedKg(consumed)} kg used`:""}</div>
        </div>
        {canEdit && !edit && (
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            <button onClick={()=>setEdit(true)} style={{ background:"#0e2a20", border:"1px solid #2a5a48", borderRadius:7, color:"#7ab8a0", fontSize:11, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
            <button onClick={del} style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:11, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit" }}>✕</button>
          </div>
        )}
      </div>
      {edit && (
        <div style={{ marginTop:8, borderTop:`1px solid ${T.border}`, paddingTop:8 }}>
          <div style={{ display:"flex", gap:6, marginBottom:8 }}>
            <div style={{ flex:1.3 }}><FeedDateField value={date} onChange={setDate} max={todayISO()} /></div>
            <FeedNumInput value={qty} onCommit={setQty} width={86} live />
            <FeedNumInput value={rate} onCommit={setRate} width={78} live />
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={save} style={{ flex:1, background:"linear-gradient(135deg,#12604a,#0a3a2c)", border:"1px solid #2a8a6a", borderRadius:8, color:"#a8f0d8", fontSize:13, padding:"8px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Save</button>
            <button onClick={()=>{ setEdit(false); setDate(b.date); setQty(String(b.qty)); setRate(String(b.rate)); }} style={{ background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#8a8a8a", fontSize:13, padding:"8px 14px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- RECIPE tab ----
function FeedRecipeTab({ state, T, lbl, btn, feedItems, medItems, canEdit, mutate, farm, allFarms, ui, setUi }) {
  const [showCopy, setShowCopy] = useState(false);
  const [copyDate, setCopyDate] = useState(todayISO());
  const [openVer, setOpenVer] = useState(null);
  const [addSel, setAddSel] = useState("");
  const otherFarms = (allFarms || []).filter(f => f.id !== farm.id);

  const { cat, asOf, editing, rows, kg, effDate } = ui;
  const set = (patch) => setUi(u => ({ ...u, ...patch }));

  const versions = (state.versions && state.versions[cat]) || [];
  const shown = feedVersionOn(state.versions, cat, asOf);   // the version in force on asOf
  const isToday = asOf === state.today;
  const est = state.formulas[cat] || { weight:0, cost:0, lines:[], effective_date:null };
  const estByItem = {}; (est.lines || []).forEach(l => { estByItem[l.item_id] = l; });

  const allItems = [...feedItems, ...medItems];             // live (non-archived) only
  const itemById = {}; (state.items || []).forEach(it => { itemById[it.item_id] = it; });

  function startEdit() {
    const base = shown ? shown.lines : [];
    const k = {}; base.forEach(l => { k[l.item_id] = String(l.kgPerUnit); });
    set({ editing:true, rows: base.map(l => ({ item_id: l.item_id })), kg: k, effDate: todayISO() });
    setAddSel("");
  }
  function addItem(id) {
    const nid = Number(id);
    if (!nid || rows.some(r => r.item_id === nid)) { setAddSel(""); return; }
    setUi(u => ({ ...u, rows: [...u.rows, { item_id: nid }], kg: { ...u.kg, [nid]: u.kg[nid] ?? "" } }));
    setAddSel("");
  }
  function removeItem(id) {
    setUi(u => { const k = { ...u.kg }; delete k[id]; return { ...u, rows: u.rows.filter(r => r.item_id !== id), kg: k }; });
  }
  async function save() {
    const lines = rows.map(r => ({ item_id: r.item_id, kg_per_unit: Number(kg[r.item_id]) || 0 })).filter(l => l.kg_per_unit > 0);
    if (lines.length === 0 && !window.confirm(`Save an EMPTY ${feedCatLabel(cat)} recipe from ${formatDate(effDate)}?\n\nNo ${feedCatLabel(cat).toLowerCase()} feed can be recorded on or after that date until you add items. Earlier days keep their own recipe.`)) return;
    const ok = await mutate(`/farms/${farm.id}/feed-formula`, { method:"PUT", body: JSON.stringify({ category:cat, effective_date:effDate, lines }) });
    if (ok) set({ editing:false, asOf: effDate });   // jump the view to what was just saved
  }
  async function doCopy(fromFarmId) {
    const ok = await mutate(`/farms/${farm.id}/feed-formula/copy`, { method:"POST", body: JSON.stringify({ fromFarmId, effective_date:copyDate }) });
    if (ok) { setShowCopy(false); set({ asOf: copyDate }); }
  }

  const availableToAdd = allItems.filter(it => !rows.some(r => r.item_id === it.item_id));
  const availFeed = availableToAdd.filter(i => i.kind === "feed");
  const availMed  = availableToAdd.filter(i => i.kind === "medicine");
  const editWeight = rows.reduce((s2,r) => s2 + (Number(kg[r.item_id]) || 0), 0);
  const selStyle = { background:"#0a1712", border:`1px solid ${T.accent}`, borderRadius:8, color:T.accent, padding:"10px 12px", fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box", fontWeight:600, cursor:"pointer" };

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:12 }}>
        {FEED_CATS_UI.map(c => (
          <button key={c.key} onClick={()=>set({ cat:c.key, editing:false })} style={{ flex:1, padding:"9px 0", borderRadius:9, cursor:"pointer", fontFamily:"inherit", fontSize:13,
            background: cat===c.key?T.pill:"transparent", border:`1px solid ${cat===c.key?T.border:"#12281f"}`, color: cat===c.key?T.accent:"#3a5a4a", fontWeight: cat===c.key?700:400 }}>{c.emoji} {c.label}</button>
        ))}
      </div>

      {!editing ? (
        <>
          <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, padding:"10px 12px", marginBottom:12 }}>
            <div style={lbl}>Show the recipe used on</div>
            <FeedDateField value={asOf} onChange={d=>set({ asOf:d })} />
            {!isToday && (
              <button onClick={()=>set({ asOf: state.today })} style={{ marginTop:7, background:T.pill, border:`1px solid ${T.border}`, borderRadius:8, color:T.accent, fontSize:11, padding:"5px 10px", cursor:"pointer", fontFamily:"inherit" }}>↩ Back to today</button>
            )}
          </div>

          <div style={{ background:`linear-gradient(135deg,${T.dark},#06130e)`, border:`1.5px solid ${T.accent}44`, borderRadius:14, padding:"14px 16px", marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:4 }}>
              <div style={{ fontSize:12, color:T.accent, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>1 Unit · {feedCatLabel(cat)}</div>
              <div style={{ fontSize:11, color:"#6aa892" }}>{shown ? `version of ${formatDate(shown.effective_date)}` : "none"}</div>
            </div>
            {!isToday && <div style={{ fontSize:10, color:"#c8a860", marginBottom:6 }}>👁️ Viewing {formatDate(asOf)}{shown && shown.effective_date > state.today ? " — scheduled version" : ""}</div>}
            <div style={{ display:"flex", gap:16, marginTop:6 }}>
              <div><div style={{ fontSize:22, fontWeight:"bold", color:"#8ce0c4" }}>{feedKg(shown?shown.weight:0)} <span style={{ fontSize:11, color:"#5a8a76", fontWeight:400 }}>kg</span></div><div style={{ fontSize:9, color:"#5a8a76" }}>UNIT WEIGHT</div></div>
              {isToday && <div><div style={{ fontSize:22, fontWeight:"bold", color:"#c8a860" }}>{feedRs(est.cost)}</div><div style={{ fontSize:9, color:"#5a8a76" }}>EST. COST / UNIT*</div></div>}
            </div>
            {isToday && <div style={{ fontSize:9, color:"#4a6a5a", marginTop:6 }}>*at current stock prices — the real cost is set by FIFO when you record units made.</div>}
          </div>

          {!shown ? (
            <div style={{ textAlign:"center", padding:"28px 20px", color:"#3a5a4a" }}>
              <div style={{ fontSize:34, marginBottom:6 }}>🧪</div>
              <div style={{ fontSize:13 }}>No {feedCatLabel(cat).toLowerCase()} recipe was in force on {formatDate(asOf)}.</div>
            </div>
          ) : shown.lines.length===0 ? (
            <div style={{ textAlign:"center", padding:"24px 20px", color:"#8a7a5a", background:"#0a1712", border:"1px solid #3a2f1a", borderRadius:12, marginBottom:12 }}>
              <div style={{ fontSize:30, marginBottom:6 }}>📭</div>
              <div style={{ fontSize:12.5 }}>This recipe is <strong>empty</strong> from {formatDate(shown.effective_date)}.</div>
              <div style={{ fontSize:10.5, color:"#5a8a76", marginTop:4 }}>No {feedCatLabel(cat).toLowerCase()} feed can be recorded on/after that date until items are added.</div>
            </div>
          ) : (
            <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden", marginBottom:12 }}>
              {shown.lines.map((l,i) => {
                const e = estByItem[l.item_id];
                return (
                  <div key={l.item_id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 13px", borderBottom:i<shown.lines.length-1?"1px solid #12281f":"none" }}>
                    <div><span style={{ fontSize:13, color:"#d8f0e4" }}>{l.kind==="medicine"?"💊":"🌾"} {l.name}</span>{itemById[l.item_id] && itemById[l.item_id].archived ? <span style={{ fontSize:8, color:"#8a7a5a", marginLeft:5 }}>(archived)</span> : null}</div>
                    <div style={{ textAlign:"right" }}>
                      <span style={{ fontSize:13, fontWeight:600, color:"#8ce0c4", fontVariantNumeric:"tabular-nums" }}>{feedKg(l.kgPerUnit)} kg</span>
                      {isToday && <span style={{ fontSize:10, color:"#5a7a6a", marginLeft:8 }}>{e && e.rate>0?`≈ ${feedRs(e.cost)}`:"no price"}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {versions.length>0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#5a8a76", letterSpacing:1, textTransform:"uppercase", fontWeight:700, marginBottom:6 }}>Recipe versions</div>
              {versions.map(v => {
                const inForce = shown && shown.formula_id === v.formula_id;
                const isFuture = v.effective_date > state.today;
                const vOpen = openVer === v.formula_id;
                return (
                  <div key={v.formula_id} style={{ background:"#0a1712", border:`1px solid ${inForce?T.accent+"66":T.border}`, borderRadius:10, marginBottom:6, overflow:"hidden" }}>
                    <div onClick={()=>setOpenVer(vOpen?null:v.formula_id)} style={{ padding:"9px 12px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                      <div style={{ minWidth:0 }}>
                        <span style={{ fontSize:12.5, color:"#d8f0e4", fontWeight:600 }}>{formatDate(v.effective_date)}</span>
                        {inForce && <span style={{ fontSize:8, color:T.accent, border:`1px solid ${T.accent}`, borderRadius:8, padding:"1px 6px", marginLeft:6 }}>SHOWN</span>}
                        {isFuture && <span style={{ fontSize:8, color:"#c8a860", border:"1px solid #5a4a2a", borderRadius:8, padding:"1px 6px", marginLeft:6 }}>SCHEDULED</span>}
                      </div>
                      <span style={{ fontSize:10.5, color:"#5a8a76", flexShrink:0 }}>{v.lines.length===0?"empty":`${v.lines.length} items · ${feedKg(v.weight)} kg`} ▾</span>
                    </div>
                    {vOpen && (
                      <div style={{ borderTop:`1px solid ${T.border}`, background:"#08130f", padding:"7px 12px 9px" }}>
                        {v.lines.length===0 ? <div style={{ fontSize:11, color:"#8a7a5a" }}>Empty — no feed can be made from this date.</div> : v.lines.map((l,i) => (
                          <div key={l.item_id} style={{ display:"flex", justifyContent:"space-between", fontSize:11, padding:"3px 0", borderBottom:i<v.lines.length-1?"1px solid #12281f":"none" }}>
                            <span style={{ color:"#7ab8a0" }}>{l.kind==="medicine"?"💊":"🌾"} {l.name}</span>
                            <span style={{ color:"#d8f0e4", fontVariantNumeric:"tabular-nums" }}>{feedKg(l.kgPerUnit)} kg</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={{ fontSize:9.5, color:"#4a6a5a", marginTop:4 }}>Each day is costed with the version in force on that day — changing the recipe never alters past days.</div>
            </div>
          )}

          {canEdit && (
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={startEdit} style={{ ...btn("linear-gradient(135deg,#12604a,#0a3a2c)","#2a8a6a","#a8f0d8"), flex:1 }}>{shown && shown.lines.length ? "✎ Edit Recipe" : "+ Set Recipe"}</button>
              {otherFarms.length>0 && <button onClick={()=>{ setCopyDate(todayISO()); setShowCopy(true); }} style={btn("#0e1e2e","#2a5a8a","#5a9abf")}>📋 Copy</button>}
            </div>
          )}
        </>
      ) : (
        <div>
          <div style={{ fontSize:12, color:T.accent, fontWeight:700, marginBottom:4 }}>Edit {feedCatLabel(cat)} recipe · 1 unit</div>
          <div style={{ fontSize:10.5, color:"#5a8a76", marginBottom:10 }}>Set kg-per-unit for each item (~1 ton total). Tap ✕ to remove; use the dropdown to add. An empty recipe is allowed.</div>
          <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, padding:"10px 12px", marginBottom:12 }}>
            {rows.length===0 ? (
              <div style={{ fontSize:12, color:"#8a7a5a", textAlign:"center", padding:"14px 6px" }}>Empty recipe — add items below, or save it empty.</div>
            ) : rows.map((r,i) => {
              const it = itemById[r.item_id];
              return (
                <div key={r.item_id} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 0", borderBottom:i<rows.length-1?"1px solid #12281f":"none" }}>
                  <span style={{ flex:1, fontSize:13, color:"#d8f0e4", minWidth:0 }}>{it ? `${it.kind==="medicine"?"💊":"🌾"} ${it.name}` : "?"}</span>
                  <FeedNumInput value={kg[r.item_id] ?? ""} onCommit={v=>setUi(u=>({ ...u, kg:{ ...u.kg, [r.item_id]: v } }))} width={72} live />
                  <span style={{ fontSize:10, color:"#5a8a76" }}>kg</span>
                  <button onClick={()=>removeItem(r.item_id)} title="Remove item" style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:14, lineHeight:1, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>✕</button>
                </div>
              );
            })}
            <div style={{ display:"flex", justifyContent:"space-between", borderTop:`1px solid ${T.border}`, paddingTop:8, marginTop:8 }}>
              <span style={{ fontSize:12, color:"#6aa892" }}>Unit weight</span>
              <span style={{ fontSize:14, fontWeight:"bold", color:"#8ce0c4" }}>{feedKg(editWeight)} kg</span>
            </div>
          </div>

          {availableToAdd.length>0 ? (
            <div style={{ marginBottom:12 }}>
              <select value={addSel} onChange={e=>addItem(e.target.value)} style={selStyle}>
                <option value="">＋ Add item to recipe…</option>
                {availFeed.length>0 && <optgroup label="🌾 Feed">{availFeed.map(i=><option key={i.item_id} value={i.item_id}>{i.name}</option>)}</optgroup>}
                {availMed.length>0 && <optgroup label="💊 Medicine">{availMed.map(i=><option key={i.item_id} value={i.item_id}>{i.name}</option>)}</optgroup>}
              </select>
            </div>
          ) : allItems.length===0 ? (
            <div style={{ fontSize:11, color:"#c88a6a", marginBottom:12, textAlign:"center" }}>No items exist yet — add feed/medicine in the Items tab first.</div>
          ) : (
            <div style={{ fontSize:10.5, color:"#4a6a5a", marginBottom:12, textAlign:"center" }}>All items are already in this recipe.</div>
          )}

          <div style={{ marginBottom:12 }}>
            <div style={lbl}>Effective from — today or a future date (carries forward until changed again)</div>
            <FeedDateField value={effDate} onChange={d=>set({ effDate:d })} />
            {effDate > state.today && <div style={{ fontSize:10, color:"#c8a860", marginTop:5 }}>⏳ Scheduled: takes effect {formatDate(effDate)}. Every day before that keeps the current recipe.</div>}
            {effDate < state.today && <div style={{ fontSize:10, color:"#c88a6a", marginTop:5 }}>⚠️ Back-dated: this will recalculate feed already made on/after {formatDate(effDate)}.</div>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={save} style={{ ...btn("linear-gradient(135deg,#12604a,#0a3a2c)","#2a8a6a","#a8f0d8"), flex:1 }}>Save Recipe</button>
            <button onClick={()=>set({ editing:false })} style={btn("#1a1a1a","#3a3a3a","#8a8a8a")}>Cancel</button>
          </div>
        </div>
      )}

      {showCopy && (
        <>
          <div onClick={()=>setShowCopy(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, backdropFilter:"blur(2px)" }} />
          <div style={{ position:"fixed", left:0, right:0, bottom:0, zIndex:201, background:"#08130f", borderTop:`2px solid ${T.accent}`, borderRadius:"18px 18px 0 0", padding:"20px 18px 36px", maxHeight:"75vh", overflowY:"auto" }}>
            <div style={{ width:40, height:4, background:T.border, borderRadius:4, margin:"0 auto 16px" }} />
            <div style={{ fontSize:15, fontWeight:"bold", color:T.accent, marginBottom:4 }}>📋 Copy Recipes From</div>
            <div style={{ fontSize:11.5, color:"#5a8a76", marginBottom:12 }}>Copies all three recipes from another farm into <strong style={{ color:"#a8e0cc" }}>{farm.name}</strong>. Items are matched by name (created here if missing).</div>
            <div style={{ marginBottom:14 }}>
              <div style={lbl}>Effective from</div>
              <FeedDateField value={copyDate} onChange={setCopyDate} />
            </div>
            {otherFarms.map(f => (
              <div key={f.id} onClick={()=>{ if (window.confirm(`Copy all recipes from ${f.name} into ${farm.name} (effective ${formatDate(copyDate)})? This replaces recipes dated ${formatDate(copyDate)}.`)) doCopy(f.id); }}
                style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:10, padding:"13px 14px", marginBottom:9, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:20 }}>🏡</span>
                <div style={{ flex:1, fontSize:14, fontWeight:600, color:"#d8f0e4" }}>{f.name}</div>
                <span style={{ color:T.accent, fontSize:18 }}>›</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---- DAILY tab ----
// Resolve the recipe version in force on a given date (versions are newest-first).
function feedVersionOn(versions, cat, date) {
  const list = (versions && versions[cat]) || [];
  return list.find(v => v.effective_date <= date) || null;
}
function FeedDailyTab({ state, T, card, lbl, canEdit, mutate, farmId }) {
  const [date, setDate] = useState(todayISO());
  const runByCat = {};
  state.production.filter(p => p.date === date).forEach(p => { runByCat[p.category] = p; });
  const dayTotal = Object.values(runByCat).reduce((s,p)=>s+p.cost,0);
  const dayKg = Object.values(runByCat).reduce((s,p)=>s+(p.totalKg||0),0);

  async function setUnits(cat, v) {
    // Blank clears the entry (shows a dash again); a number — including 0 — is stored.
    const body = (v === "" || v == null)
      ? { prod_date:date, category:cat, clear:true }
      : { prod_date:date, category:cat, units: Number(v) || 0 };
    await mutate(`/farms/${farmId}/feed-production`, { method:"PUT", body: JSON.stringify(body) });
  }

  const th  = { textAlign:"left",  fontSize:9, color:"#5a8a76", fontWeight:700, letterSpacing:0.4, textTransform:"uppercase", padding:"6px 6px", borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" };
  const thR = { ...th, textAlign:"right" };
  const td  = { fontSize:11.5, color:"#d8f0e4", padding:"6px 6px", borderBottom:"1px solid #12281f", verticalAlign:"top" };
  const tdR = { ...td, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };
  const tf  = { fontSize:11.5, fontWeight:"bold", color:"#8ce0c4", padding:"7px 6px", borderTop:`1.5px solid ${T.accent}66` };
  const tfR = { ...tf, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };

  return (
    <div>
      <div style={card}>
        <div style={lbl}>Date</div>
        <FeedDateField value={date} onChange={setDate} max={todayISO()} />
      </div>

      {FEED_CATS_UI.map(c => {
        const run = runByCat[c.key];
        // The recipe that applies to THIS date — not today's — so past days show
        // exactly what they were made with.
        const ver = feedVersionOn(state.versions, c.key, date);
        const hasRecipe = !!ver && ver.lines.length > 0;
        return (
          <div key={c.key} style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:14, marginBottom:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
                <div style={{ fontSize:15, fontWeight:700, color:"#d8f0e4" }}>{c.emoji} {c.label}</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:10, color:"#5a8a76" }}>units made</span>
                  <div style={{ width:84 }}><FeedNumInput value={run ? run.units : ""} onCommit={v=>setUnits(c.key, v)} disabled={!canEdit || !hasRecipe} placeholder="—" /></div>
                </div>
              </div>

              {!hasRecipe ? (
                <div style={{ fontSize:11, color:"#c88a6a", marginTop:8 }}>⚠️ {ver ? `The ${c.label.toLowerCase()} recipe in force on this day (version of ${formatDate(ver.effective_date)}) is empty` : `No ${c.label.toLowerCase()} recipe was in force on ${formatDate(date)}`} — set one in the Recipe tab, dated on or before this day.</div>
              ) : (
                <div style={{ fontSize:10, color:"#5a8a76", marginTop:7 }}>
                  📋 Recipe used: <span style={{ color:"#7ab8a0", fontWeight:600 }}>version of {formatDate(ver.effective_date)}</span> · {feedKg(ver.weight)} kg/unit · {ver.lines.length} items
                </div>
              )}
            </div>

            {hasRecipe && run && run.units > 0 && (
              <div style={{ borderTop:`1px solid ${T.border}`, background:"#08130f", padding:"4px 8px 10px", overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", minWidth:300 }}>
                  <thead>
                    <tr>
                      <th style={th}>Item</th>
                      <th style={thR}>kg/unit</th>
                      <th style={thR}>× units</th>
                      <th style={thR}>Used kg</th>
                      <th style={thR}>₹/kg</th>
                      <th style={thR}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.lines.map(l => (
                      <tr key={l.item_id}>
                        <td style={td}>
                          <span>{l.kind==="medicine"?"💊":"🌾"} {l.name}</span>
                          {l.draws && l.draws.length>1 && (
                            <div style={{ fontSize:9, color:"#4a7a66", marginTop:2 }}>
                              {l.draws.map((d,j)=>`${feedKg(d.kg)}@${feedRs(d.rate)}`).join(" + ")}
                            </div>
                          )}
                        </td>
                        <td style={tdR}>{feedKg(l.kgPerUnit)}</td>
                        <td style={{ ...tdR, color:"#5a8a76" }}>{feedKg(run.units)}</td>
                        <td style={tdR}>{feedKg(l.consumed)}</td>
                        <td style={{ ...tdR, color:"#7ab8a0" }}>{l.avgRate>0?feedRs(l.avgRate):"—"}</td>
                        <td style={{ ...tdR, color:"#c8a860", fontWeight:600 }}>{feedRs(l.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={tf}>TOTAL</td>
                      <td style={tfR}>{feedKg(run.weightPerUnit)}</td>
                      <td style={tfR}></td>
                      <td style={tfR}>{feedKg(run.totalKg)}</td>
                      <td style={tfR}></td>
                      <td style={{ ...tfR, color:"#c8a860" }}>{feedRs(run.cost)}</td>
                    </tr>
                  </tfoot>
                </table>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginTop:8, padding:"0 6px" }}>
                  <span style={{ fontSize:10, color:"#5a8a76" }}>Cost per unit</span>
                  <span style={{ fontSize:15, fontWeight:"bold", color:"#8ce0c4" }}>{feedRs(run.perUnit)}</span>
                </div>
              </div>
            )}

            {hasRecipe && run && run.units === 0 && (
              <div style={{ borderTop:`1px solid ${T.border}`, background:"#08130f", padding:"9px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span style={{ fontSize:11, color:"#7ab8a0" }}>✓ Entered <strong>0</strong> — no {c.label.toLowerCase()} feed made this day.</span>
                {canEdit && <button onClick={()=>setUnits(c.key, "")} style={{ background:"transparent", border:"none", color:"#7a5a5a", fontSize:10.5, cursor:"pointer", fontFamily:"inherit" }}>clear ✕</button>}
              </div>
            )}

            {hasRecipe && !run && (
              <div style={{ borderTop:`1px solid ${T.border}`, background:"#08130f", padding:"9px 14px" }}>
                <div style={{ fontSize:10.5, color:"#5a8a76" }}>Not entered yet (shown as “—”). Enter units — or 0 — to record this day. Recipe deducts {feedKg(ver.weight)} kg/unit FIFO.</div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ background:`linear-gradient(120deg,${T.dark},#06130e)`, border:`1.5px solid ${T.accent}55`, borderRadius:14, padding:"14px 16px", marginTop:4, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:12, color:T.accent, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Day total · {formatDate(date)}</div>
          <div style={{ fontSize:10, color:"#5a8a76", marginTop:2 }}>{feedKg(dayKg)} kg of raw material used</div>
        </div>
        <span style={{ fontSize:22, fontWeight:"bold", color:"#c8a860" }}>{feedRs(dayTotal)}</span>
      </div>
    </div>
  );
}

// ---- USAGE monitor tab: item consumption over a date range ----
function FeedUsageTab({ T, card, farmId, farm, reportError }) {
  const anchor = todayISO();
  const [from, setFrom] = useState(anchor.slice(0, 7) + "-01");
  const [to, setTo] = useState(anchor);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [view, setView] = useState("item");     // item | day
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const d = await apiCall(`/farms/${farmId}/feed-usage-range?from=${from}&to=${to}`);
        if (alive) setData(d);
      } catch (e) { if (alive) { setData(null); reportError && reportError(e); } }
      if (alive) setBusy(false);
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [farmId, from, to]);

  const th  = { textAlign:"left",  fontSize:9, color:"#5a8a76", fontWeight:700, letterSpacing:0.4, textTransform:"uppercase", padding:"6px 6px", borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" };
  const thR = { ...th, textAlign:"right" };
  const td  = { fontSize:11.5, color:"#d8f0e4", padding:"7px 6px", borderBottom:"1px solid #12281f", verticalAlign:"middle" };
  const tdR = { ...td, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };
  const tf  = { fontSize:11.5, fontWeight:"bold", color:"#8ce0c4", padding:"8px 6px", borderTop:`1.5px solid ${T.accent}66` };
  const tfR = { ...tf, textAlign:"right", fontVariantNumeric:"tabular-nums", whiteSpace:"nowrap" };

  const byItem = (data && data.byItem) || [];
  const byDay  = (data && data.byDay)  || [];
  const totals = (data && data.totals) || { kg:0, cost:0, units:{} };
  const unitsMade = FEED_CATS_UI.map(c => `${c.emoji} ${feedKg((totals.units || {})[c.key] || 0)}`).join("   ");

  return (
    <div>
      <DateRangePicker from={from} to={to} onChange={(f,t)=>{ setFrom(f); setTo(t); }} accent={T.accent} />
      <ReportButton accent={T.accent} disabled={busy || !data} onClick={() => openReport(() => buildFeedReport(farm, data, from, to))} />
      <div style={{ fontSize:11, color:"#4a7a66", textAlign:"center", marginBottom:12, fontStyle:"italic" }}>
        👁️ View-only — every item consumed between the two dates, priced FIFO.
      </div>

      <div style={{ background:`linear-gradient(135deg,${T.dark},#06130e)`, border:`1.5px solid ${T.accent}55`, borderRadius:16, padding:"14px 16px", marginBottom:12 }}>
        <div style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
          <div>
            <div style={{ fontSize:9, color:"#6aa892", letterSpacing:1, textTransform:"uppercase" }}>Raw material used</div>
            <div style={{ fontSize:24, fontWeight:"bold", color:"#8ce0c4", lineHeight:1.2 }}>{feedKg(totals.kg)} <span style={{ fontSize:11, color:"#5a8a76", fontWeight:400 }}>kg</span></div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:9, color:"#6aa892", letterSpacing:1, textTransform:"uppercase" }}>Total cost</div>
            <div style={{ fontSize:24, fontWeight:"bold", color:"#c8a860", lineHeight:1.2 }}>{feedRs(totals.cost)}</div>
          </div>
        </div>
        <div style={{ fontSize:10.5, color:"#6aa892", marginTop:8, borderTop:`1px solid ${T.border}`, paddingTop:7 }}>Units made — {unitsMade}</div>
      </div>

      <div style={{ display:"flex", gap:6, marginBottom:12 }}>
        {[["item","By Item"],["day","By Day"],["flock","By Flock"]].map(([k,l]) => (
          <button key={k} onClick={()=>setView(k)} style={{ flex:1, padding:"8px 0", borderRadius:9, cursor:"pointer", fontFamily:"inherit", fontSize:12.5,
            background: view===k?T.pill:"transparent", border:`1px solid ${view===k?T.border:"#12281f"}`, color: view===k?T.accent:"#3a5a4a", fontWeight: view===k?700:400 }}>{l}</button>
        ))}
      </div>

      {busy && <div style={{ textAlign:"center", padding:36, color:"#4a7a66", fontSize:13 }}>Loading…</div>}

      {!busy && byItem.length===0 && view!=="flock" && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"#3a5a4a" }}>
          <div style={{ fontSize:38, marginBottom:8 }}>📈</div>
          <div style={{ fontSize:13 }}>No feed was made between {formatDate(from)} and {formatDate(to)}.</div>
        </div>
      )}

      {!busy && byItem.length>0 && view==="item" && (
        <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, padding:"4px 8px 8px", overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:300 }}>
            <thead>
              <tr>
                <th style={th}>Item</th>
                <th style={thR}>Used kg</th>
                <th style={thR}>₹/kg</th>
                <th style={thR}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {byItem.map(it => {
                const open = openItem === it.item_id;
                return (
                  <Fragment key={it.item_id}>
                    <tr onClick={()=>setOpenItem(open?null:it.item_id)} style={{ cursor:"pointer" }}>
                      <td style={td}>{it.kind==="medicine"?"💊":"🌾"} {it.name} <span style={{ color:T.accent, fontSize:9 }}>{open?"▾":"▸"}</span>
                        <div style={{ fontSize:9, color:"#4a7a66", marginTop:1 }}>{it.rows.length} entr{it.rows.length===1?"y":"ies"}</div>
                      </td>
                      <td style={{ ...tdR, fontWeight:600 }}>{feedKg(it.kg)}</td>
                      <td style={{ ...tdR, color:"#7ab8a0" }}>{it.avgRate>0?feedRs(it.avgRate):"—"}</td>
                      <td style={{ ...tdR, color:"#c8a860", fontWeight:600 }}>{feedRs(it.cost)}</td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={4} style={{ padding:"0 0 8px", background:"#08130f" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse" }}>
                            <thead>
                              <tr>
                                <th style={{ ...th, fontSize:8 }}>Date</th>
                                <th style={{ ...th, fontSize:8 }}>Feed</th>
                                <th style={{ ...thR, fontSize:8 }}>Units</th>
                                <th style={{ ...thR, fontSize:8 }}>kg/unit</th>
                                <th style={{ ...thR, fontSize:8 }}>Kg used</th>
                                <th style={{ ...thR, fontSize:8 }}>Cost</th>
                              </tr>
                            </thead>
                            <tbody>
                              {it.rows.map((r,i) => (
                                <tr key={i}>
                                  <td style={{ ...td, fontSize:10.5, color:"#7ab8a0" }}>{formatDate(r.date)}</td>
                                  <td style={{ ...td, fontSize:10.5 }}>{(FEED_CATS_UI.find(c=>c.key===r.category)||{}).emoji} {feedCatLabel(r.category)}</td>
                                  <td style={{ ...tdR, fontSize:10.5 }}>{feedKg(r.units)}</td>
                                  <td style={{ ...tdR, fontSize:10.5, color:"#5a8a76" }}>{feedKg(r.kgPerUnit)}</td>
                                  <td style={{ ...tdR, fontSize:10.5 }}>{feedKg(r.kg)}</td>
                                  <td style={{ ...tdR, fontSize:10.5, color:"#c8a860" }}>{feedRs(r.cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td style={tf}>TOTAL</td>
                <td style={tfR}>{feedKg(totals.kg)}</td>
                <td style={tfR}></td>
                <td style={{ ...tfR, color:"#c8a860" }}>{feedRs(totals.cost)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!busy && view==="flock" && (() => {
        // Allocation covers every flock (sold ones included, so shares stay right);
        // sold flocks are then hidden from the table.
        const alloc = allocateFeedToFlocks(farm || { flocks: [] }, byDay, from, to);
        let soldKg = 0, soldCount = 0;
        ((farm && farm.flocks) || []).forEach(fl => {
          if (fl.soldOn && alloc.per[fl.id] && alloc.per[fl.id].birdDays > 0) { soldKg += alloc.per[fl.id].kg; soldCount++; }
        });
        const list = ((farm && farm.flocks) || [])
          .filter(fl => !fl.soldOn && alloc.per[fl.id] && alloc.per[fl.id].birdDays > 0)
          .map(fl => {
            const r = alloc.per[fl.id];
            return { id: fl.id, label: flockLabelOn(farm.flocks, fl, from), birdType: fl.birdType, soldOn: fl.soldOn, hatchDate: fl.hatchDate,
              avgBirds: r.birdDays / Math.max(1, alloc.days), kg: r.kg, cost: r.cost, birdDays: r.birdDays,
              daysPresent: r.daysPresent, catLabel: feedCatSummary(r.catDays),
              g: r.birdDays > 0 ? (r.kg*1000)/r.birdDays : 0, rsBird: r.birdDays > 0 ? r.cost/r.birdDays : 0 };
          })
          .sort((a,b) => {
            const ah = (a.hatchDate || "9999-99-99"), bh = (b.hatchDate || "9999-99-99");
            if (ah !== bh) return ah < bh ? -1 : 1;
            return (a.id || 0) - (b.id || 0);
          });
        if (list.length === 0) {
          return <div style={{ textAlign:"center", padding:"34px 20px", color:"#3a5a4a", fontSize:12.5 }}>No flock had feed in this window.</div>;
        }
        const sKg = list.reduce((s,r)=>s+r.kg,0), sCost = list.reduce((s,r)=>s+r.cost,0), sBD = list.reduce((s,r)=>s+r.birdDays,0);
        return (
          <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, padding:"4px 8px 8px", overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:420 }}>
              <thead>
                <tr>
                  <th style={th}>Flock</th>
                  <th style={th}>Feed</th>
                  <th style={thR}>Days</th>
                  <th style={thR}>Birds avg</th>
                  <th style={thR}>Feed kg</th>
                  <th style={thR}>g/bird/day</th>
                  <th style={thR}>Cost</th>
                  <th style={thR}>₹/bird/day</th>
                </tr>
              </thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id}>
                    <td style={td}>🐥 {r.label}{r.soldOn && <span style={{ fontSize:9, color:"#e0a040" }}> · sold</span>}<BirdTypeTag type={r.birdType} size={8} /></td>
                    <td style={{ ...td, color: r.catLabel==="Layer" ? "#5a8a76" : "#c8a860", fontSize:10.5 }}>{r.catLabel}</td>
                    <td style={{ ...tdR, color: r.daysPresent<alloc.days ? "#c8a860" : "#4a6a5a", fontSize:10.5 }}>{r.daysPresent}/{alloc.days}</td>
                    <td style={{ ...tdR, color:"#5a8a76" }}>{Math.round(r.avgBirds).toLocaleString()}</td>
                    <td style={tdR}>{Math.round(r.kg).toLocaleString()}</td>
                    <td style={{ ...tdR, color:"#7ab8a0", fontWeight:600 }}>{r.g>0?Math.round(r.g).toLocaleString():"—"}</td>
                    <td style={{ ...tdR, color:"#c8a860", fontWeight:600 }}>{feedRs(r.cost)}</td>
                    <td style={{ ...tdR, color:"#c8a860" }}>{r.rsBird>0?`₹${r.rsBird.toFixed(2)}`:"—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={tf}>TOTAL</td>
                  <td style={tf}></td>
                  <td style={tfR}></td>
                  <td style={tfR}>{alloc.days>0?Math.round(sBD/alloc.days).toLocaleString():"—"}</td>
                  <td style={tfR}>{Math.round(sKg).toLocaleString()}</td>
                  <td style={tfR}>{sBD>0?Math.round((sKg*1000)/sBD).toLocaleString():"—"}</td>
                  <td style={{ ...tfR, color:"#c8a860" }}>{feedRs(sCost)}</td>
                  <td style={{ ...tfR, color:"#c8a860" }}>{sBD>0?`₹${(sCost/sBD).toFixed(2)}`:"—"}</td>
                </tr>
              </tfoot>
            </table>
            <div style={{ fontSize:9, color:"#4a6a5a", marginTop:7, lineHeight:1.5, padding:"0 6px" }}>
              Feed is made per category, so each flock's share is apportioned by its bird numbers each day.
              {soldCount > 0 && <span> {soldCount} sold flock{soldCount!==1?"s are":" is"} hidden ({Math.round(soldKg).toLocaleString()} kg), but still counted when splitting the rest.</span>}
              {alloc.unallocKg > 0.5 && <span style={{ color:"#c08850" }}> ⚠️ {Math.round(alloc.unallocKg).toLocaleString()} kg made for a stage with no birds isn't charged to any flock.</span>}
            </div>
          </div>
        );
      })()}

      {!busy && byDay.length>0 && view==="day" && (
        <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, padding:"4px 8px 8px", overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:300 }}>
            <thead>
              <tr>
                <th style={th}>Date</th>
                {FEED_CATS_UI.map(c => <th key={c.key} style={thR}>{c.emoji}</th>)}
                <th style={thR}>Kg used</th>
                <th style={thR}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {byDay.map(d => (
                <tr key={d.date}>
                  <td style={{ ...td, color:"#7ab8a0" }}>{formatDate(d.date)}</td>
                  {FEED_CATS_UI.map(c => <td key={c.key} style={{ ...tdR, color:(d.units[c.key]||0)>0?"#d8f0e4":"#3a5a4a" }}>{feedKg(d.units[c.key]||0)}</td>)}
                  <td style={{ ...tdR, fontWeight:600 }}>{feedKg(d.kg)}</td>
                  <td style={{ ...tdR, color:"#c8a860", fontWeight:600 }}>{feedRs(d.cost)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={tf}>TOTAL</td>
                {FEED_CATS_UI.map(c => <td key={c.key} style={tfR}>{feedKg((totals.units||{})[c.key]||0)}</td>)}
                <td style={tfR}>{feedKg(totals.kg)}</td>
                <td style={{ ...tfR, color:"#c8a860" }}>{feedRs(totals.cost)}</td>
              </tr>
            </tfoot>
          </table>
          <div style={{ fontSize:9, color:"#4a6a5a", marginTop:6, padding:"0 6px" }}>{FEED_CATS_UI.map(c=>`${c.emoji} ${c.label}`).join("  ·  ")} — units made</div>
        </div>
      )}
    </div>
  );
}

// ---- REORDER tab: minimum-stock levels + order flags ----
function FeedReorderTab({ state, T, card, lbl, canEdit, mutate, farmId }) {
  const live = (state.stock || []).filter(s => !s.archived);
  const below = state.reorder || [];
  async function setMin(id, v) {
    await mutate(`/feed-items/${id}`, { method:"PUT", body: JSON.stringify({ min_qty: (v===""||v==null) ? 0 : Number(v)||0 }) });
  }
  async function setOrdered(id, val) {
    await mutate(`/feed-items/${id}`, { method:"PUT", body: JSON.stringify({ ordered: val }) });
  }
  const feedList = live.filter(s => s.kind === "feed");
  const medList  = live.filter(s => s.kind === "medicine");

  const group = (title, list) => list.length === 0 ? null : (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:11, color:T.accent, letterSpacing:1, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>{title}</div>
      <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" }}>
        {list.map((s,i) => (
          <div key={s.item_id} style={{ padding:"10px 13px", borderBottom:i<list.length-1?"1px solid #12281f":"none", background: s.belowMin ? "#170d0a" : "transparent" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:13.5, color:"#d8f0e4", fontWeight:600 }}>{s.name}</div>
                <div style={{ fontSize:10.5, color: s.belowMin ? "#e0954a" : "#5a8a76", marginTop:1 }}>In stock: {feedKg(s.qty)} kg</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                <span style={{ fontSize:9, color:"#5a8a76" }}>min</span>
                <FeedNumInput value={s.minQty || ""} onCommit={v=>setMin(s.item_id, v)} width={68} disabled={!canEdit} placeholder="0" />
                <span style={{ fontSize:9, color:"#5a8a76" }}>kg</span>
              </div>
            </div>
            {s.belowMin && (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, marginTop:8, borderTop:"1px solid #2a1a12", paddingTop:8 }}>
                <span style={{ fontSize:11, color:"#e89060", fontWeight:600 }}>⚠️ {feedKg(s.minQty - s.qty)} kg below minimum</span>
                {canEdit && (s.ordered
                  ? <button onClick={()=>setOrdered(s.item_id, false)} style={{ background:"#0e2a20", border:"1px solid #2a6a4a", borderRadius:8, color:"#7ad0a0", fontSize:11, padding:"5px 11px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>🛒 Ordered — undo</button>
                  : <button onClick={()=>setOrdered(s.item_id, true)} style={{ background:"linear-gradient(135deg,#7a4a10,#5a3208)", border:"1px solid #9a6a1a", borderRadius:8, color:"#f0d0a0", fontSize:11, padding:"5px 11px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Mark ordered</button>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ background: below.length ? "linear-gradient(135deg,#2a1608,#1a0e06)" : `linear-gradient(135deg,${T.dark},#06130e)`, border:`1.5px solid ${below.length ? "#9a6a1a" : T.accent+"55"}`, borderRadius:16, padding:"14px 16px", marginBottom:12 }}>
        {below.length === 0 ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:15, fontWeight:"bold", color:"#8ce0c4" }}>✓ All items above minimum</div>
            <div style={{ fontSize:10.5, color:"#6aa892", marginTop:2 }}>Nothing to reorder right now.</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize:13, fontWeight:"bold", color:"#f0b060", marginBottom:8 }}>⚠️ {below.length} item{below.length!==1?"s":""} to reorder</div>
            {below.map(b => (
              <div key={b.item_id} style={{ display:"flex", justifyContent:"space-between", fontSize:11.5, padding:"3px 0" }}>
                <span style={{ color:"#e8c0a0" }}>{b.kind==="medicine"?"💊":"🌾"} {b.name} {b.ordered && <span style={{ color:"#7ad0a0", fontSize:9 }}>🛒 ordered</span>}</span>
                <span style={{ color:"#e89060", fontVariantNumeric:"tabular-nums" }}>{feedKg(b.qty)} / {feedKg(b.minQty)} kg</span>
              </div>
            ))}
          </>
        )}
      </div>
      <div style={{ fontSize:10.5, color:"#5a8a76", margin:"0 4px 14px", lineHeight:1.5 }}>
        Set a minimum kg for any item. When stock on hand drops below it, it appears here and on the farm dashboard until a receipt brings it back up (then any “ordered” mark clears automatically). Leave 0 for no alert.
      </div>
      {group("🌾 Feed", feedList)}
      {group("💊 Medicine", medList)}
      {live.length === 0 && <div style={{ textAlign:"center", padding:"30px 20px", color:"#3a5a4a", fontSize:13 }}>No items yet — add feed/medicine in the Items tab.</div>}
    </div>
  );
}

// ---- ITEMS tab ----
function FeedItemsTab({ state, T, card, lbl, sel, btn, feedItems, medItems, archivedItems = [], canEdit, mutate, farmId, farm = {}, allFarms = [] }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("feed");
  const [showCopy, setShowCopy] = useState(false);
  const otherFarms = (allFarms || []).filter(f => f.id !== farmId);
  async function add() {
    const n = name.trim(); if (!n) return alert("Enter an item name.");
    const ok = await mutate(`/farms/${farmId}/feed-items`, { method:"POST", body: JSON.stringify({ name:n, kind }) });
    if (ok) setName("");
  }
  async function doCopy(f) {
    if (!window.confirm(`Copy the feed & medicine item names from ${f.name} into ${farm.name || "this farm"}?\n\nOnly the names are copied — stock, rates and recipes stay separate per farm.`)) return;
    const r = await mutate(`/farms/${farmId}/feed-items/copy`, { method:"POST", body: JSON.stringify({ fromFarmId: f.id }) });
    if (r) {
      setShowCopy(false);
      const bits = [`${r.copied || 0} added`];
      if (r.restored) bits.push(`${r.restored} restored from archive`);
      if (r.skipped) bits.push(`${r.skipped} already here`);
      alert(`Copied from ${f.name}: ${bits.join(" · ")}.`);
    }
  }
  const group = (title, list) => (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:11, color:T.accent, letterSpacing:1, textTransform:"uppercase", marginBottom:8, fontWeight:700 }}>{title} ({list.length})</div>
      {list.length===0 ? <div style={{ fontSize:11, color:"#4a6a5a" }}>None yet.</div> : list.map(it => (
        <FeedItemRow key={it.item_id} it={it} T={T} canEdit={canEdit} mutate={mutate} />
      ))}
    </div>
  );
  return (
    <div>
      {canEdit && (
        <div style={card}>
          <div style={{ fontSize:12, color:T.accent, fontWeight:700, letterSpacing:0.5, textTransform:"uppercase", marginBottom:10 }}>🏷️ Add Item</div>
          <div style={{ display:"flex", gap:6, marginBottom:8 }}>
            {[["feed","🌾 Feed"],["medicine","💊 Medicine"]].map(([k,l]) => (
              <button key={k} onClick={()=>setKind(k)} style={{ flex:1, padding:"8px", borderRadius:8, cursor:"pointer", fontFamily:"inherit", fontSize:12.5,
                background: kind===k?T.pill:"#0a1712", border:`1px solid ${kind===k?T.accent:"#24503e"}`, color: kind===k?T.accent:"#5a8a76", fontWeight: kind===k?700:400 }}>{l}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Item name (e.g. Bajra, DDGS, a premix…)" style={{ ...sel, flex:1 }} />
            <button onClick={add} style={btn("linear-gradient(135deg,#12604a,#0a3a2c)","#2a8a6a","#a8f0d8")}>Add</button>
          </div>
        </div>
      )}
      {canEdit && otherFarms.length>0 && (
        <button onClick={()=>setShowCopy(true)} style={{ ...btn("#0e1e2e","#2a5a8a","#5a9abf"), width:"100%", marginBottom:14 }}>📋 Copy items from another farm</button>
      )}
      {group("🌾 Feed items", feedItems)}
      {group("💊 Medicine items", medItems)}
      {archivedItems.length>0 && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:"#8a7a5a", letterSpacing:1, textTransform:"uppercase", marginBottom:4, fontWeight:700 }}>🗄️ Archived ({archivedItems.length})</div>
          <div style={{ fontSize:10, color:"#4a6a5a", marginBottom:7 }}>Kept so past recipes and feed already made still show them. Hidden from new stock entries and recipes.</div>
          {archivedItems.map(it => <FeedItemRow key={it.item_id} it={it} T={T} canEdit={canEdit} mutate={mutate} archived />)}
        </div>
      )}

      {showCopy && (
        <>
          <div onClick={()=>setShowCopy(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, backdropFilter:"blur(2px)" }} />
          <div style={{ position:"fixed", left:0, right:0, bottom:0, zIndex:201, background:"#08130f", borderTop:`2px solid ${T.accent}`, borderRadius:"18px 18px 0 0", padding:"20px 18px 36px", maxHeight:"75vh", overflowY:"auto" }}>
            <div style={{ width:40, height:4, background:T.border, borderRadius:4, margin:"0 auto 16px" }} />
            <div style={{ fontSize:15, fontWeight:"bold", color:T.accent, marginBottom:4 }}>📋 Copy Items From</div>
            <div style={{ fontSize:11.5, color:"#5a8a76", marginBottom:14 }}>
              Adds that farm's feed &amp; medicine <strong style={{ color:"#a8e0cc" }}>names</strong> into <strong style={{ color:"#a8e0cc" }}>{farm.name || "this farm"}</strong>. Stock, rates and recipes stay per-farm. Anything you already have is left untouched.
            </div>
            {otherFarms.map(f => (
              <div key={f.id} onClick={()=>doCopy(f)}
                style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:10, padding:"13px 14px", marginBottom:9, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:20 }}>🏡</span>
                <div style={{ flex:1, fontSize:14, fontWeight:600, color:"#d8f0e4" }}>{f.name}</div>
                <span style={{ color:T.accent, fontSize:18 }}>›</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
function FeedItemRow({ it, T, canEdit, mutate, archived = false }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(it.name);
  const [kind, setKind] = useState(it.kind);
  async function save() {
    const ok = await mutate(`/feed-items/${it.item_id}`, { method:"PUT", body: JSON.stringify({ name:name.trim(), kind }) });
    if (ok) setEdit(false);
  }
  async function del() {
    if (!window.confirm(`Remove "${it.name}"?\n\nIf it has any stock or recipe history it will be ARCHIVED, not deleted — every past day keeps its exact figures.`)) return;
    const r = await mutate(`/feed-items/${it.item_id}`, { method:"DELETE" });
    if (r && r.archivedItem) {
      const extra = (r.stillUsedIn && r.stillUsedIn.length)
        ? `\n\nHeads up: the ${r.stillUsedIn.join(" & ")} recipe in force today still calls for it. Edit that recipe (effective today) to stop using it from now on.`
        : "";
      alert(`"${r.archivedItem}" was archived rather than deleted — it has stock or recipe history, which stays intact and visible.${extra}`);
    }
  }
  async function restore() { await mutate(`/feed-items/${it.item_id}`, { method:"PUT", body: JSON.stringify({ name:it.name, kind:it.kind, archived:false }) }); }
  return (
    <div style={{ background:"#0a1712", border:`1px solid ${T.border}`, borderRadius:10, padding:"9px 13px", marginBottom:7 }}>
      {!edit ? (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:13.5, color: archived?"#8a9a92":"#d8f0e4" }}>{it.name}</span>
          {canEdit && (
            <div style={{ display:"flex", gap:6 }}>
              {archived ? (
                <button onClick={restore} style={{ background:"#0e2a20", border:"1px solid #2a5a48", borderRadius:7, color:"#7ab8a0", fontSize:11, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit" }}>↩ Restore</button>
              ) : (
                <>
                  <button onClick={()=>setEdit(true)} style={{ background:"#0e2a20", border:"1px solid #2a5a48", borderRadius:7, color:"#7ab8a0", fontSize:11, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                  <button onClick={del} style={{ background:"#2a0e10", border:"1px solid #6a2a30", borderRadius:7, color:"#e89090", fontSize:11, padding:"5px 9px", cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          <input value={name} onChange={e=>setName(e.target.value)} style={{ background:"#0a1712", border:"1px solid #24503e", borderRadius:8, color:"#d8f0e4", padding:"8px 10px", fontSize:13, fontFamily:"inherit", width:"100%", boxSizing:"border-box", marginBottom:7 }} />
          <div style={{ display:"flex", gap:6, marginBottom:8 }}>
            {[["feed","🌾 Feed"],["medicine","💊 Medicine"]].map(([k,l]) => (
              <button key={k} onClick={()=>setKind(k)} style={{ flex:1, padding:"7px", borderRadius:7, cursor:"pointer", fontFamily:"inherit", fontSize:12,
                background: kind===k?T.pill:"#0a1712", border:`1px solid ${kind===k?T.accent:"#24503e"}`, color: kind===k?T.accent:"#5a8a76" }}>{l}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={save} style={{ flex:1, background:"linear-gradient(135deg,#12604a,#0a3a2c)", border:"1px solid #2a8a6a", borderRadius:8, color:"#a8f0d8", fontSize:13, padding:"8px", cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>Save</button>
            <button onClick={()=>{ setEdit(false); setName(it.name); setKind(it.kind); }} style={{ background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:8, color:"#8a8a8a", fontSize:13, padding:"8px 14px", cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
